// BEGIN FILE: src/astro/feeds/rules.ts
// @ts-nocheck  ← static rules data; excluded from TS language-server type-checking for IDE performance
import type { DashaContext, TransitContext, LifeArea, HouseNum, PlanetName } from './types';
import { HOUSE_TO_LIFE, PLANET_NATURE } from './types';
import { weightByLevel, natureWeight, normHouseScore, transitReinforcement, aggregateScore } from './scoring';
import { CLASSICAL_YOGAS } from './classical_yogas';
import { TWO_PLANET_COMBINATIONS } from './two_planet_combinations';
import { getHouseLord, getSignLord } from '../../utils/lordshipUtils';
import { OWN_SIGNS } from '../../utils/astroEngine';

export type Rule = {
  key: string;
  priority?: number;
  areas: LifeArea[];
  templateKey: string;
  customPrediction?: string; // Custom prediction text (overrides template)
  remedies?: string; // Suggested remedies
  ruleType?: 'timing' | 'natal'; // 'timing' = dasha-based, 'natal' = permanent chart condition
  private?: boolean; // true if feed is only viewable by admins (sensitive predictions)
  triggers: (dasha: DashaContext, transits: TransitContext) => boolean;
  score: (dasha: DashaContext, transits: TransitContext) => { value:number; notes:string[]; customWindow?: { from: string; to: string }; templateKey?: string };
};

function activatedHouses(d: DashaContext): Array<{level:'MD'|'AD'|'PD'|'SD'; houses:Set<HouseNum>; planet:string}> {
  return d.levels.map(l => ({
    level: l.level,
    houses: new Set<HouseNum>([...l.rules, l.occupies, ...(l.aspects||[])] as HouseNum[]),
    planet: l.planet
  }));
}

function housesToAreas(hs: Set<HouseNum>): LifeArea[] {
  const out: LifeArea[] = [] as any;
  hs.forEach(h => (HOUSE_TO_LIFE[h]||[]).forEach(a => out.push(a)));
  return Array.from(new Set(out));
}

// ---------- Concrete rules ----------

// 1) Mother/Property caution
const motherProperty: Rule = {
  key: 'mother_property_caution',
  areas: ['Mother','Property'],
  templateKey: 'mother_health_alert',
  priority: 10,
  triggers: (dasha, transits) => {
    const acts = activatedHouses(dasha);
    const any4 = acts.some(a => a.houses.has(4));
    const malTransit4or12 = transits.positions.some(p => (p.planet==='Saturn'||p.planet==='Mars'||p.planet==='Rahu'||p.planet==='Ketu') && (p.house===4 || p.house===12));
    return any4 || malTransit4or12;
  },
  score: (dasha, transits) => {
    const acts = activatedHouses(dasha);
    const notes: string[] = [];
    const contrib: number[] = [];
    for (const a of acts) {
      const lvlW = weightByLevel(a.level);
      const naturesgn = natureWeight(a.planet as any);
      const houses = [4,12].filter(h => a.houses.has(h as HouseNum)) as HouseNum[];
      for (const h of houses) {
        const base = normHouseScore(h, dasha.houseScores) * naturesgn * lvlW;
        const reinforce = transitReinforcement({ targetHouse: h, transits, forLifeArea: 'Mother' });
        contrib.push(base + reinforce);
        notes.push(`${a.level} ${a.planet} activates H${h}; base=${(base*100).toFixed(0)} reinforce=${(reinforce*100).toFixed(0)}`);
      }
    }
    return { value: aggregateScore(contrib), notes };
  }
};

// 2) Father/Guidance/Travel (9H)
const fatherGuidance: Rule = {
  key: 'father_guidance_travel',
  areas: ['Father','Travel','Spirituality'],
  templateKey: 'father_guidance',
  priority: 6,
  triggers: (dasha) => activatedHouses(dasha).some(a => a.houses.has(9)),
  score: (dasha, transits) => {
    const acts = activatedHouses(dasha);
    const notes: string[] = [];
    const contrib: number[] = [];
    for (const a of acts) {
      if (!a.houses.has(9)) continue;
      const lvlW = weightByLevel(a.level);
      const naturesgn = natureWeight(a.planet as any);
      const base = normHouseScore(9, dasha.houseScores) * naturesgn * lvlW;
      const reinforce = transitReinforcement({ targetHouse: 9, transits, forLifeArea: 'Father' });
      contrib.push(base + reinforce);
      notes.push(`${a.level} ${a.planet} activates H9; base=${(base*100).toFixed(0)} reinf=${(reinforce*100).toFixed(0)}`);
    }
    const finalScore = aggregateScore(contrib);
    const templateKey = finalScore > 0 ? 'father_guidance_positive' : 'father_guidance';
    return { value: finalScore, notes, templateKey };
  }
};

// 3) Spouse/Partnership (7H)
const spouseBoundaries: Rule = {
  key: 'spouse_partnership',
  areas: ['Spouse','Travel'],
  templateKey: 'spouse_boundaries', // This will be dynamically selected based on score
  priority: 5,
  triggers: (dasha) => activatedHouses(dasha).some(a => a.houses.has(7)),
  score: (dasha, transits) => {
    const acts = activatedHouses(dasha);
    const notes: string[] = [];
    const contrib: number[] = [];
    for (const a of acts) {
      if (!a.houses.has(7)) continue;
      const lvlW = weightByLevel(a.level);
      const naturesgn = natureWeight(a.planet as any);
      const base = normHouseScore(7, dasha.houseScores) * naturesgn * lvlW;
      const reinforce = transitReinforcement({ targetHouse: 7, transits, forLifeArea: 'Spouse' });
      contrib.push(base + reinforce);
      notes.push(`${a.level} ${a.planet} activates H7; base=${(base*100).toFixed(0)} reinf=${(reinforce*100).toFixed(0)}`);
    }
    const finalScore = aggregateScore(contrib);
    
    // Dynamically select template based on score
    const templateKey = finalScore > 0 ? 'spouse_boundaries_positive' : 'spouse_boundaries';
    
    return { value: finalScore, notes, templateKey };
  }
};

// 4) Career/Promotion (10/11)
const careerPromotion: Rule = {
  key: 'career_promotion',
  areas: ['Career','Wealth'],
  templateKey: 'promotion_opportunity_positive',
  priority: 8,
  triggers: (dasha) => activatedHouses(dasha).some(a => a.houses.has(10) || a.houses.has(11)),
  score: (dasha, transits) => {
    const acts = activatedHouses(dasha);
    const notes: string[] = [];
    const contrib: number[] = [];
    for (const a of acts) {
      const rel: HouseNum[] = [10,11].filter(h => a.houses.has(h as HouseNum)) as any;
      if (!rel.length) continue;
      const lvlW = weightByLevel(a.level);
      const naturesgn = natureWeight(a.planet as any);
      for (const h of rel) {
        const base = normHouseScore(h, dasha.houseScores) * naturesgn * lvlW;
        const reinforce = transitReinforcement({ targetHouse: h, transits, forLifeArea: 'Career' });
        contrib.push(base + reinforce);
        notes.push(`${a.level} ${a.planet} activates H${h}; base=${(base*100).toFixed(0)} reinf=${(reinforce*100).toFixed(0)}`);
      }
    }
    return { value: aggregateScore(contrib), notes };
  }
};

// 5) Investments (2/5/11, pressure from 8/12)
const investments: Rule = {
  key: 'investments',
  areas: ['Investments','Wealth'],
  templateKey: 'investment_caution_alert',
  priority: 4,
  triggers: (dasha) => activatedHouses(dasha).some(a => [2,5,11,8,12].some(h => a.houses.has(h as HouseNum))),
  score: (dasha, transits) => {
    const acts = activatedHouses(dasha);
    const notes: string[] = [];
    const contrib: number[] = [];
    for (const a of acts) {
      const rel: HouseNum[] = [2,5,11,8,12].filter(h => a.houses.has(h as HouseNum)) as any;
      if (!rel.length) continue;
      const lvlW = weightByLevel(a.level);
      const naturesgn = natureWeight(a.planet as any);
      for (const h of rel) {
        const base = normHouseScore(h, dasha.houseScores) * naturesgn * lvlW;
        const reinforce = transitReinforcement({ targetHouse: h, transits, forLifeArea: 'Investments' });
        contrib.push(base + reinforce);
        notes.push(`${a.level} ${a.planet} activates H${h}; base=${(base*100).toFixed(0)} reinf=${(reinforce*100).toFixed(0)}`);
      }
    }
    return { value: aggregateScore(contrib), notes };
  }
};

// 6) Accident/Health risk (1/6/8/12 + malefics)
const accidentRisk: Rule = {
  key: 'accident_health_risk',
  areas: ['AccidentRisk','Health','Self','Travel'],
  templateKey: 'accident_risk',
  priority: 9,
  triggers: (dasha, transits) => {
    const risky = [1,6,8,12];
    const acts = activatedHouses(dasha);
    const anyRisk = acts.some(a => risky.some(h => a.houses.has(h as HouseNum)));
    const malTransit = transits.positions.some(p => (p.planet==='Mars'||p.planet==='Rahu'||p.planet==='Ketu') && risky.includes(p.house));
    return anyRisk || malTransit;
  },
  score: (dasha, transits) => {
    const acts = activatedHouses(dasha);
    const notes: string[] = [];
    const contrib: number[] = [];
    const risky: HouseNum[] = [1,6,8,12];
    for (const a of acts) {
      const rel: HouseNum[] = risky.filter(h => a.houses.has(h as HouseNum)) as any;
      if (!rel.length) continue;
      const lvlW = weightByLevel(a.level);
      const naturesgn = natureWeight(a.planet as any);
      for (const h of rel) {
        const base = normHouseScore(h, dasha.houseScores) * naturesgn * lvlW;
        const reinforce = transitReinforcement({ targetHouse: h, transits, forLifeArea: 'AccidentRisk' });
        contrib.push(base + reinforce);
        notes.push(`${a.level} ${a.planet} activates H${h}; base=${(base*100).toFixed(0)} reinf=${(reinforce*100).toFixed(0)}`);
      }
    }
    const finalScore = aggregateScore(contrib);
    const templateKey = finalScore > 0 ? 'accident_risk_positive' : finalScore < -30 ? 'accident_risk_alert' : 'accident_risk';
    return { value: finalScore, notes, templateKey };
  }
};

// 7) General health hygiene (1/6/8/12 with Saturn/Mars)
const healthHygiene: Rule = {
  key: 'health_hygiene',
  areas: ['Health'],
  templateKey: 'health_hygiene',
  priority: 3,
  triggers: (dasha, transits) => {
    const risky = [1,6,8,12];
    const acts = activatedHouses(dasha).some(a => risky.some(h => a.houses.has(h as HouseNum)));
    const satOrMars = transits.positions.some(p => (p.planet==='Saturn'||p.planet==='Mars') && risky.includes(p.house));
    return acts || satOrMars;
  },
  score: (dasha, transits) => {
    const acts = activatedHouses(dasha);
    const notes: string[] = [];
    const contrib: number[] = [];
    const risky: HouseNum[] = [1,6,8,12];
    for (const a of acts) {
      const rel: HouseNum[] = risky.filter(h => a.houses.has(h as HouseNum)) as any;
      if (!rel.length) continue;
      const lvlW = weightByLevel(a.level);
      const naturesgn = natureWeight(a.planet as any);
      for (const h of rel) {
        const base = normHouseScore(h, dasha.houseScores) * naturesgn * lvlW;
        const reinforce = transitReinforcement({ targetHouse: h, transits, forLifeArea: 'Health' });
        contrib.push(base + reinforce);
        notes.push(`${a.level} ${a.planet} activates H${h}; base=${(base*100).toFixed(0)} reinf=${(reinforce*100).toFixed(0)}`);
      }
    }
    const finalScore = aggregateScore(contrib);
    const templateKey = finalScore > 0 ? 'health_hygiene_positive' : finalScore < -30 ? 'health_hygiene_alert' : 'health_hygiene';
    return { value: finalScore, notes, templateKey };
  }
};

// 7b) RED ALERT: 6th Lord Sookshma Dasha Health Hazard (self, spouse, parents)
const sixthLordSookshmaHealthHazard: Rule = {
  key: 'sixth_lord_sookshma_health_hazard',
  areas: ['Health', 'Spouse', 'Mother', 'Father'],
  templateKey: 'health_emergency',
  priority: 15, // High priority for health alerts
  triggers: (dasha, transits) => {
    // Only trigger for Sookshma Dasha (SD level)
    const sdLevel = dasha.levels.find(l => l.level === 'SD');
    if (!sdLevel || !dasha.lagnaSign) return false;
    
    // Calculate 6th house sign and its lord
    const SIGN_LORDS: Record<number, PlanetName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    const sixthSign = ((dasha.lagnaSign - 1 + 5) % 12) + 1;
    const sixthLord = SIGN_LORDS[sixthSign];
    
    // Check if 6th lord is active in SD
    const isSixthLordSD = sdLevel.planet === sixthLord;
    if (!isSixthLordSD) return false;
    
    // Additional checks for severity:
    // 1. 6th lord transiting 6th/8th/12th house (dusthana) = more severe
    // 2. Malefic aspects to 6th house
    // 3. 6th lord conjunct with Mars/Saturn/Rahu/Ketu
    const sixthHouse = 6 as HouseNum;
    const dusthanaHouses = [6, 8, 12];
    
    let hasSevereCondition = false;
    
    // Check transits
    const sixthLordTransit = transits.positions.find(p => p.planet === sixthLord);
    if (sixthLordTransit && dusthanaHouses.includes(sixthLordTransit.house)) {
      hasSevereCondition = true;
    }
    
    // Check if 6th lord is conjunct with malefics in natal chart
    if (dasha.natalPlacements) {
      const sixthLordPlacement = dasha.natalPlacements[sixthLord];
      if (sixthLordPlacement) {
        const malefics: PlanetName[] = ['Mars', 'Saturn', 'Rahu', 'Ketu'];
        const conjunctWithMalefic = malefics.some(malefic => {
          if (malefic === sixthLord) return false; // Don't check self
          const maleficPlacement = dasha.natalPlacements![malefic];
          return maleficPlacement && maleficPlacement.house === sixthLordPlacement.house;
        });
        if (conjunctWithMalefic) {
          hasSevereCondition = true;
        }
      }
    }
    
    // Always trigger if 6th lord is in SD (severe or not)
    return true;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const contrib: number[] = [];
    
    const sdLevel = dasha.levels.find(l => l.level === 'SD');
    if (!sdLevel || !dasha.lagnaSign) {
      return { value: 0, notes: ['No SD level or lagna sign'], templateKey: 'health_emergency' };
    }
    
    const SIGN_LORDS: Record<number, PlanetName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    const sixthSign = ((dasha.lagnaSign - 1 + 5) % 12) + 1;
    const sixthLord = SIGN_LORDS[sixthSign];
    
    // Base penalty for 6th lord in SD (health crisis timing)
    const baseScore = -60; // Severe negative
    contrib.push(baseScore);
    notes.push(`🚨 RED ALERT: 6th Lord (${sixthLord}) active in Sookshma Dasha - Health hazard period`);
    
    // Check severity factors
    const dusthanaHouses = [6, 8, 12];
    const sixthLordTransit = transits.positions.find(p => p.planet === sixthLord);
    
    if (sixthLordTransit && dusthanaHouses.includes(sixthLordTransit.house)) {
      contrib.push(-20);
      notes.push(`6th Lord transiting dusthana (H${sixthLordTransit.house}) - EXTREME CAUTION REQUIRED`);
    }
    
    // Check natal afflictions
    if (dasha.natalPlacements) {
      const sixthLordPlacement = dasha.natalPlacements[sixthLord];
      if (sixthLordPlacement) {
        const malefics: PlanetName[] = ['Mars', 'Saturn', 'Rahu', 'Ketu'];
        const conjunctMalefics: PlanetName[] = [];
        
        malefics.forEach(malefic => {
          if (malefic === sixthLord) return;
          const maleficPlacement = dasha.natalPlacements![malefic];
          if (maleficPlacement && maleficPlacement.house === sixthLordPlacement.house) {
            conjunctMalefics.push(malefic);
          }
        });
        
        if (conjunctMalefics.length > 0) {
          contrib.push(-15 * conjunctMalefics.length);
          notes.push(`6th Lord natally conjunct with ${conjunctMalefics.join(', ')} - Chronic health vulnerability`);
        }
      }
    }
    
    // Check for malefic transits to 6th house (additional risk)
    const maleficTransitsTo6th = transits.positions.filter(p => 
      (p.planet === 'Mars' || p.planet === 'Saturn' || p.planet === 'Rahu' || p.planet === 'Ketu') &&
      p.house === 6
    );
    
    if (maleficTransitsTo6th.length > 0) {
      contrib.push(-10 * maleficTransitsTo6th.length);
      const planets = maleficTransitsTo6th.map(p => p.planet).join(', ');
      notes.push(`Malefics (${planets}) transiting 6th house - Increased illness susceptibility`);
    }
    
    // Check parent health impacts (4th house = mother, 9th house = father)
    const motherHouse = 4 as HouseNum;
    const fatherHouse = 9 as HouseNum;
    
    // Check if 6th lord aspects parent houses
    let affectsParents = false;
    if (dasha.natalPlacements) {
      const sixthLordPlacement = dasha.natalPlacements[sixthLord];
      if (sixthLordPlacement) {
        // 7th aspect
        const oppositeHouse = ((sixthLordPlacement.house - 1 + 6) % 12) + 1;
        if (oppositeHouse === motherHouse || oppositeHouse === fatherHouse) {
          affectsParents = true;
          notes.push(`⚠️ 6th Lord aspects H${oppositeHouse} - Parent health concern`);
        }
        
        // Special aspects for Jupiter, Saturn, Mars
        if (sixthLord === 'Jupiter') {
          const fifthAspect = ((sixthLordPlacement.house - 1 + 4) % 12) + 1;
          const ninthAspect = ((sixthLordPlacement.house - 1 + 8) % 12) + 1;
          if (fifthAspect === motherHouse || fifthAspect === fatherHouse ||
              ninthAspect === motherHouse || ninthAspect === fatherHouse) {
            affectsParents = true;
            notes.push(`⚠️ Jupiter's special aspect to parent house - Parent health concern`);
          }
        }
        
        if (sixthLord === 'Saturn') {
          const thirdAspect = ((sixthLordPlacement.house - 1 + 2) % 12) + 1;
          const tenthAspect = ((sixthLordPlacement.house - 1 + 9) % 12) + 1;
          if (thirdAspect === motherHouse || thirdAspect === fatherHouse ||
              tenthAspect === motherHouse || tenthAspect === fatherHouse) {
            affectsParents = true;
            notes.push(`⚠️ Saturn's special aspect to parent house - Parent health concern`);
          }
        }
        
        if (sixthLord === 'Mars') {
          const fourthAspect = ((sixthLordPlacement.house - 1 + 3) % 12) + 1;
          const eighthAspect = ((sixthLordPlacement.house - 1 + 7) % 12) + 1;
          if (fourthAspect === motherHouse || fourthAspect === fatherHouse ||
              eighthAspect === motherHouse || eighthAspect === fatherHouse) {
            affectsParents = true;
            notes.push(`⚠️ Mars's special aspect to parent house - Parent health concern`);
          }
        }
      }
    }
    
    // Check spouse health (7th house)
    const spouseHouse = 7 as HouseNum;
    let affectsSpouse = false;
    
    if (dasha.natalPlacements) {
      const sixthLordPlacement = dasha.natalPlacements[sixthLord];
      if (sixthLordPlacement) {
        // Check if 6th lord is in 7th house or aspects 7th house
        if (sixthLordPlacement.house === spouseHouse) {
          affectsSpouse = true;
          notes.push(`⚠️ 6th Lord in 7th house - Spouse health concern`);
        }
        
        // 7th aspect to spouse house
        const oppositeHouse = ((sixthLordPlacement.house - 1 + 6) % 12) + 1;
        if (oppositeHouse === spouseHouse) {
          affectsSpouse = true;
          notes.push(`⚠️ 6th Lord aspects 7th house - Spouse health concern`);
        }
      }
    }
    
    const finalScore = aggregateScore(contrib);
    notes.push(`CRITICAL HEALTH ALERT: Final score=${finalScore.toFixed(1)}`);
    notes.push(`ACTION REQUIRED: Medical check-ups, preventive care, avoid risky activities`);
    
    // Custom window note for upcoming caution
    if (sdLevel.to) {
      notes.push(`CAUTION WINDOW: Until ${sdLevel.to}`);
    }
    
    return { value: finalScore, notes, templateKey: 'health_emergency_alert' };
  }
};

// 7c) RED ALERT: 8th Lord Sookshma Dasha Financial Losses & Obstacles
const eighthLordSookshmaLosses: Rule = {
  key: 'eighth_lord_sookshma_losses',
  areas: ['Wealth', 'Investments', 'Health', 'Property'],
  templateKey: 'wealth_caution',
  priority: 14, // High priority for loss alerts
  triggers: (dasha, transits) => {
    // Only trigger for Sookshma Dasha (SD level)
    const sdLevel = dasha.levels.find(l => l.level === 'SD');
    if (!sdLevel || !dasha.lagnaSign) return false;
    
    // Calculate 8th house sign and its lord
    const SIGN_LORDS: Record<number, PlanetName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    const eighthSign = ((dasha.lagnaSign - 1 + 7) % 12) + 1;
    const eighthLord = SIGN_LORDS[eighthSign];
    
    // Check if 8th lord is active in SD
    return sdLevel.planet === eighthLord;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const contrib: number[] = [];
    
    const sdLevel = dasha.levels.find(l => l.level === 'SD');
    if (!sdLevel || !dasha.lagnaSign) {
      return { value: 0, notes: ['No SD level or lagna sign'], templateKey: 'wealth_caution' };
    }
    
    const SIGN_LORDS: Record<number, PlanetName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    const eighthSign = ((dasha.lagnaSign - 1 + 7) % 12) + 1;
    const eighthLord = SIGN_LORDS[eighthSign];
    
    // Base penalty for 8th lord in SD (sudden losses, obstacles)
    const baseScore = -55; // Severe negative
    contrib.push(baseScore);
    notes.push(`🚨 RED ALERT: 8th Lord (${eighthLord}) active in Sookshma Dasha - Loss & Transformation period`);
    
    // Check severity factors
    const dusthanaHouses = [6, 8, 12];
    const eighthLordTransit = transits.positions.find(p => p.planet === eighthLord);
    
    if (eighthLordTransit && dusthanaHouses.includes(eighthLordTransit.house)) {
      contrib.push(-18);
      notes.push(`8th Lord transiting dusthana (H${eighthLordTransit.house}) - INCREASED LOSS POTENTIAL`);
    }
    
    // Check if 8th lord is in 8th house natally (stronger effect)
    if (dasha.natalPlacements) {
      const eighthLordPlacement = dasha.natalPlacements[eighthLord];
      if (eighthLordPlacement && eighthLordPlacement.house === 8) {
        contrib.push(-15);
        notes.push(`8th Lord in own house (8th) natally - Intensified transformation effects`);
      }
    }
    
    // Check for malefic aspects to wealth houses (2, 11)
    const wealthHouses = [2, 11];
    if (dasha.natalPlacements) {
      const eighthLordPlacement = dasha.natalPlacements[eighthLord];
      if (eighthLordPlacement) {
        const oppositeHouse = ((eighthLordPlacement.house - 1 + 6) % 12) + 1;
        
        if (wealthHouses.includes(oppositeHouse)) {
          contrib.push(-12);
          notes.push(`8th Lord aspects wealth house (H${oppositeHouse}) - Financial losses likely`);
        }
        
        // Special aspects
        if (eighthLord === 'Jupiter') {
          const fifthAspect = ((eighthLordPlacement.house - 1 + 4) % 12) + 1;
          const ninthAspect = ((eighthLordPlacement.house - 1 + 8) % 12) + 1;
          if (wealthHouses.includes(fifthAspect) || wealthHouses.includes(ninthAspect)) {
            contrib.push(-12);
            notes.push(`Jupiter's special aspect to wealth house - Financial setbacks`);
          }
        }
        
        if (eighthLord === 'Saturn') {
          const thirdAspect = ((eighthLordPlacement.house - 1 + 2) % 12) + 1;
          const tenthAspect = ((eighthLordPlacement.house - 1 + 9) % 12) + 1;
          if (wealthHouses.includes(thirdAspect) || wealthHouses.includes(tenthAspect)) {
            contrib.push(-12);
            notes.push(`Saturn's special aspect to wealth house - Delayed/blocked finances`);
          }
        }
        
        if (eighthLord === 'Mars') {
          const fourthAspect = ((eighthLordPlacement.house - 1 + 3) % 12) + 1;
          const eighthAspect = ((eighthLordPlacement.house - 1 + 7) % 12) + 1;
          if (wealthHouses.includes(fourthAspect) || wealthHouses.includes(eighthAspect)) {
            contrib.push(-12);
            notes.push(`Mars's special aspect to wealth house - Sudden financial losses`);
          }
        }
      }
    }
    
    // Check for malefic transits to 8th house (amplifies loss potential)
    const maleficTransitsTo8th = transits.positions.filter(p => 
      (p.planet === 'Mars' || p.planet === 'Saturn' || p.planet === 'Rahu' || p.planet === 'Ketu') &&
      p.house === 8
    );
    
    if (maleficTransitsTo8th.length > 0) {
      contrib.push(-10 * maleficTransitsTo8th.length);
      const planets = maleficTransitsTo8th.map(p => p.planet).join(', ');
      notes.push(`Malefics (${planets}) transiting 8th house - Unexpected obstacles/losses`);
    }
    
    // Check for Rahu/Ketu transiting wealth houses (sudden unexpected losses)
    const nodeTransitsToWealth = transits.positions.filter(p => 
      (p.planet === 'Rahu' || p.planet === 'Ketu') &&
      wealthHouses.includes(p.house)
    );
    
    if (nodeTransitsToWealth.length > 0) {
      contrib.push(-15);
      notes.push(`Nodes transiting wealth houses - SUDDEN UNEXPECTED FINANCIAL DISRUPTION`);
    }
    
    // Check if 8th lord is combust (reduces protective factors)
    if (dasha.combustionMap && dasha.combustionMap[eighthLord]) {
      contrib.push(-8);
      notes.push(`8th Lord combust - Weakened judgment in crisis situations`);
    }
    
    // Check property house (4th) for property-related losses
    const propertyHouse = 4 as HouseNum;
    if (dasha.natalPlacements) {
      const eighthLordPlacement = dasha.natalPlacements[eighthLord];
      if (eighthLordPlacement) {
        if (eighthLordPlacement.house === propertyHouse) {
          contrib.push(-10);
          notes.push(`⚠️ 8th Lord in 4th house - Property disputes/losses possible`);
        }
        
        const oppositeHouse = ((eighthLordPlacement.house - 1 + 6) % 12) + 1;
        if (oppositeHouse === propertyHouse) {
          contrib.push(-10);
          notes.push(`⚠️ 8th Lord aspects 4th house - Property-related obstacles`);
        }
      }
    }
    
    const finalScore = aggregateScore(contrib);
    notes.push(`CRITICAL FINANCIAL ALERT: Final score=${finalScore.toFixed(1)}`);
    notes.push(`RISK MITIGATION: Avoid major investments, speculation, and risky ventures`);
    notes.push(`PRECAUTIONS: Secure assets, avoid loans/debts, delay major purchases`);
    
    // Custom window note for upcoming caution
    if (sdLevel.to) {
      notes.push(`CAUTION WINDOW: Until ${sdLevel.to}`);
    }
    
    return { value: finalScore, notes, templateKey: 'wealth_losses_alert' };
  }
};

// 8) Marriage/Partnership Timing (sophisticated 7H activation with karaka transits)
const marriagePartnership: Rule = {
  key: 'marriage_partnership_timing',
  areas: ['Spouse'],
  templateKey: 'marriage_timing',
  priority: 10,
  triggers: (dasha, transits) => {
    const acts = activatedHouses(dasha);
    
    // Age filter: Only applicable for people 23+ years old
    if (dasha.birthDate) {
      const birthYear = new Date(dasha.birthDate).getFullYear();
      const currentYear = new Date().getFullYear();
      const age = currentYear - birthYear;
      if (age < 23) return false; // Skip marriage feed for those under 23
    }
    
    // Check if PD or SD activates H7 (occupies, aspects, or rules)
    const pdsdLevels = acts.filter(a => a.level === 'PD' || a.level === 'SD');
    const has7HActivation = pdsdLevels.some(a => a.houses.has(7));
    
    if (!has7HActivation) return false;
    
    // Special case: Rahu or Saturn in PD/SD automatically triggers regardless of house scores
    const hasRahuOrSaturnPDSD = pdsdLevels.some(a => a.planet === 'Rahu' || a.planet === 'Saturn');
    
    // Gender-based karaka selection: Jupiter for females, Venus for males
    const gender = dasha.gender || 'other';
    const primaryKaraka = gender === 'female' ? 'Jupiter' : 'Venus';
    const secondaryKaraka = gender === 'female' ? 'Venus' : 'Jupiter';
    
    // Transit reinforcement: Check both primary and secondary karakas
    const karakaTransit = transits.positions.some(p => {
      if (p.planet !== primaryKaraka && p.planet !== secondaryKaraka) return false;
      // Direct transit to H7
      if (p.house === 7) return true;
      // Jupiter's 5th/9th aspect to H7 (from H3 or H11)
      if (p.planet === 'Jupiter') {
        const fifthAspect = ((p.house - 1 + 4) % 12) + 1;
        const ninthAspect = ((p.house - 1 + 8) % 12) + 1;
        if (fifthAspect === 7 || ninthAspect === 7) return true;
      }
      // Check 7th aspect (opposite house)
      const seventhAspect = ((p.house - 1 + 6) % 12) + 1;
      return seventhAspect === 7;
    });
    
    // NEW: Check for 7th lord dasha activation
    const SIGN_LORDS: Record<number, PlanetName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    let hasSeventhLordDasha = false;
    if (dasha.lagnaSign) {
      const seventhSign = ((dasha.lagnaSign - 1 + 6) % 12) + 1;
      const seventhLord = SIGN_LORDS[seventhSign];
      hasSeventhLordDasha = dasha.levels.some(l => l.planet === seventhLord);
    }
    
    // NEW: Check for Venus/Jupiter dasha (traditional marriage timing)
    const hasVenusJupiterDasha = dasha.levels.some(l => l.planet === 'Venus' || l.planet === 'Jupiter');
    
    // NEW: Check for Navamsha 7th or 1st lord dasha
    let hasNavamshaLordDasha = false;
    if (dasha.navamshaLagna && dasha.lagnaSign) {
      const d9FirstLord = SIGN_LORDS[dasha.navamshaLagna];
      const d9SeventhSign = ((dasha.navamshaLagna - 1 + 6) % 12) + 1;
      const d9SeventhLord = SIGN_LORDS[d9SeventhSign];
      hasNavamshaLordDasha = dasha.levels.some(l => l.planet === d9FirstLord || l.planet === d9SeventhLord);
    }
    
    // NEW: If 7th house empty, check dasha of planets aspecting H7
    let hasAspectingPlanetDasha = false;
    if (dasha.natalPlacements) {
      const planetsInH7 = Object.values(dasha.natalPlacements).filter(p => p.house === 7);
      if (planetsInH7.length === 0) {
        // 7th house is empty, check for planets aspecting it
        const aspectingH7: PlanetName[] = [];
        Object.entries(dasha.natalPlacements).forEach(([planetName, placement]) => {
          const pName = planetName as PlanetName;
          // Check 7th aspect (opposite house)
          const oppositeHouse = ((placement.house - 1 + 6) % 12) + 1;
          if (oppositeHouse === 7) {
            aspectingH7.push(pName);
          }
          // Jupiter's special aspects: 5th, 7th, 9th
          if (pName === 'Jupiter') {
            const fifthAspect = ((placement.house - 1 + 4) % 12) + 1;
            const ninthAspect = ((placement.house - 1 + 8) % 12) + 1;
            if (fifthAspect === 7 || ninthAspect === 7) {
              aspectingH7.push(pName);
            }
          }
          // Saturn's special aspects: 3rd, 7th, 10th
          if (pName === 'Saturn') {
            const thirdAspect = ((placement.house - 1 + 2) % 12) + 1;
            const tenthAspect = ((placement.house - 1 + 9) % 12) + 1;
            if (thirdAspect === 7 || tenthAspect === 7) {
              aspectingH7.push(pName);
            }
          }
          // Mars's special aspects: 4th, 7th, 8th
          if (pName === 'Mars') {
            const fourthAspect = ((placement.house - 1 + 3) % 12) + 1;
            const eighthAspect = ((placement.house - 1 + 7) % 12) + 1;
            if (fourthAspect === 7 || eighthAspect === 7) {
              aspectingH7.push(pName);
            }
          }
        });
        
        // Check if any aspecting planet is in current dasha
        hasAspectingPlanetDasha = dasha.levels.some(l => aspectingH7.includes(l.planet));
      }
    }
    
    // Trigger if: (H7 activation by PD/SD) AND (any favorable condition OR Rahu/Saturn PDSD OR karaka transit)
    const hasFavorableDasha = hasSeventhLordDasha || hasVenusJupiterDasha || hasNavamshaLordDasha || hasAspectingPlanetDasha;
    return has7HActivation && (hasRahuOrSaturnPDSD || karakaTransit || hasFavorableDasha);
  },
  score: (dasha, transits) => {
    const acts = activatedHouses(dasha);
    const notes: string[] = [];
    const contrib: number[] = [];
    
    // Focus on PD and SD levels only
    const pdsdLevels = acts.filter(a => a.level === 'PD' || a.level === 'SD');
    
    for (const a of pdsdLevels) {
      if (!a.houses.has(7)) continue;
      
      const lvlW = weightByLevel(a.level);
      const planet = a.planet as PlanetName;
      
      // Special scoring: Rahu or Saturn in PD/SD bypasses blended % and forces positive score
      const isRahuOrSaturn = planet === 'Rahu' || planet === 'Saturn';
      
      if (isRahuOrSaturn) {
        // Force positive contribution for Rahu/Saturn timing
        const baseScore = 60; // Strong positive base
        const reinforce = transitReinforcement({ targetHouse: 7, transits, forLifeArea: 'Spouse' });
        contrib.push(baseScore * lvlW + reinforce * 100);
        notes.push(`${a.level} ${planet} activates H7 [MARRIAGE TIMING]; base=${baseScore} (Rahu/Saturn bypass) reinf=${(reinforce * 100).toFixed(0)}`);
      } else {
        // Normal scoring with blended % consideration
        const naturesgn = natureWeight(planet);
        const base = normHouseScore(7, dasha.houseScores) * naturesgn * lvlW;
        const reinforce = transitReinforcement({ targetHouse: 7, transits, forLifeArea: 'Spouse' });
        contrib.push(base + reinforce);
        notes.push(`${a.level} ${planet} activates H7; base=${(base*100).toFixed(0)} reinf=${(reinforce*100).toFixed(0)}`);
      }
    }
    
    // Gender-based karaka bonuses
    const gender = dasha.gender || 'other';
    const primaryKaraka = gender === 'female' ? 'Jupiter' : 'Venus';
    const secondaryKaraka = gender === 'female' ? 'Venus' : 'Jupiter';
    
    // Additional bonus for primary karaka (Jupiter for women, Venus for men) transit to H7
    const primaryKarakaTransit = transits.positions.find(p => p.planet === primaryKaraka && p.house === 7);
    const secondaryKarakaTransit = transits.positions.find(p => p.planet === secondaryKaraka && p.house === 7);
    
    if (primaryKarakaTransit) {
      contrib.push(25); // Strong bonus for primary marriage karaka transiting H7
      notes.push(`${primaryKaraka} (primary karaka for ${gender}) transiting H7 +25`);
    }
    if (secondaryKarakaTransit) {
      contrib.push(15); // Secondary bonus
      notes.push(`${secondaryKaraka} (secondary karaka) transiting H7 +15`);
    }
    
    // Check for primary karaka aspecting H7
    const primaryKarakaAspectsH7 = transits.positions.some(p => {
      if (p.planet !== primaryKaraka) return false;
      if (p.planet === 'Jupiter') {
        const fifthAspect = ((p.house - 1 + 4) % 12) + 1;
        const seventhAspect = ((p.house - 1 + 6) % 12) + 1;
        const ninthAspect = ((p.house - 1 + 8) % 12) + 1;
        return fifthAspect === 7 || seventhAspect === 7 || ninthAspect === 7;
      }
      const seventhAspect = ((p.house - 1 + 6) % 12) + 1;
      return seventhAspect === 7;
    });
    
    if (primaryKarakaAspectsH7 && !primaryKarakaTransit) {
      contrib.push(12);
      notes.push(`${primaryKaraka} (primary karaka) aspecting H7 +12`);
    }
    
    // NEW: Bonus for 7th lord dasha activation
    const SIGN_LORDS: Record<number, PlanetName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    if (dasha.lagnaSign) {
      const seventhSign = ((dasha.lagnaSign - 1 + 6) % 12) + 1;
      const seventhLord = SIGN_LORDS[seventhSign];
      const hasSeventhLordDasha = dasha.levels.some(l => l.planet === seventhLord);
      if (hasSeventhLordDasha) {
        contrib.push(20);
        notes.push(`7th Lord (${seventhLord}) dasha active +20`);
      }
    }
    
    // NEW: Bonus for Venus/Jupiter dasha
    const hasVenusDasha = dasha.levels.some(l => l.planet === 'Venus');
    const hasJupiterDasha = dasha.levels.some(l => l.planet === 'Jupiter');
    if (hasVenusDasha) {
      contrib.push(18);
      notes.push(`Venus dasha active (marriage karaka) +18`);
    }
    if (hasJupiterDasha) {
      contrib.push(18);
      notes.push(`Jupiter dasha active (marriage karaka) +18`);
    }
    
    // NEW: Bonus for Navamsha 7th or 1st lord dasha
    if (dasha.navamshaLagna) {
      const d9FirstLord = SIGN_LORDS[dasha.navamshaLagna];
      const d9SeventhSign = ((dasha.navamshaLagna - 1 + 6) % 12) + 1;
      const d9SeventhLord = SIGN_LORDS[d9SeventhSign];
      const hasD9FirstLordDasha = dasha.levels.some(l => l.planet === d9FirstLord);
      const hasD9SeventhLordDasha = dasha.levels.some(l => l.planet === d9SeventhLord);
      
      if (hasD9FirstLordDasha) {
        contrib.push(15);
        notes.push(`Navamsha 1st Lord (${d9FirstLord}) dasha active +15`);
      }
      if (hasD9SeventhLordDasha) {
        contrib.push(15);
        notes.push(`Navamsha 7th Lord (${d9SeventhLord}) dasha active +15`);
      }
    }
    
    // NEW: Bonus for dasha of planets aspecting empty 7th house
    if (dasha.natalPlacements) {
      const planetsInH7 = Object.values(dasha.natalPlacements).filter(p => p.house === 7);
      if (planetsInH7.length === 0) {
        // 7th house is empty
        const aspectingH7: PlanetName[] = [];
        Object.entries(dasha.natalPlacements).forEach(([planetName, placement]) => {
          const pName = planetName as PlanetName;
          const oppositeHouse = ((placement.house - 1 + 6) % 12) + 1;
          if (oppositeHouse === 7) {
            aspectingH7.push(pName);
          }
          if (pName === 'Jupiter') {
            const fifthAspect = ((placement.house - 1 + 4) % 12) + 1;
            const ninthAspect = ((placement.house - 1 + 8) % 12) + 1;
            if (fifthAspect === 7 || ninthAspect === 7) {
              aspectingH7.push(pName);
            }
          }
          if (pName === 'Saturn') {
            const thirdAspect = ((placement.house - 1 + 2) % 12) + 1;
            const tenthAspect = ((placement.house - 1 + 9) % 12) + 1;
            if (thirdAspect === 7 || tenthAspect === 7) {
              aspectingH7.push(pName);
            }
          }
          if (pName === 'Mars') {
            const fourthAspect = ((placement.house - 1 + 3) % 12) + 1;
            const eighthAspect = ((placement.house - 1 + 7) % 12) + 1;
            if (fourthAspect === 7 || eighthAspect === 7) {
              aspectingH7.push(pName);
            }
          }
        });
        
        const aspectingPlanetInDasha = dasha.levels.find(l => aspectingH7.includes(l.planet));
        if (aspectingPlanetInDasha) {
          contrib.push(12);
          notes.push(`7th house empty but ${aspectingPlanetInDasha.planet} (aspecting H7) dasha active +12`);
        }
      }
    }
    
    // NEW: PENALTY - Takshak Kaal Sarp (Rahu in 7th house natal)
    if (dasha.natalPlacements?.Rahu?.house === 7) {
      // Check for Kaal Sarp Yoga: all planets between Rahu-Ketu axis
      const rahuHouse = dasha.natalPlacements.Rahu.house;
      const ketuHouse = dasha.natalPlacements.Ketu?.house;
      
      if (ketuHouse) {
        const isKaalSarp = Object.entries(dasha.natalPlacements).every(([planetName, placement]) => {
          if (planetName === 'Rahu' || planetName === 'Ketu') return true;
          
          // Check if planet is between Rahu and Ketu (clockwise)
          let house: number = rahuHouse;
          while (house !== ketuHouse) {
            if (placement.house === house) return true;
            house = ((house % 12) + 1);
          }
          return false;
        });
        
        if (isKaalSarp) {
          contrib.push(-35);
          notes.push(`TAKSHAK KAAL SARP (Rahu in 7th): Severe marriage obstacles -35`);
        } else {
          // Just Rahu in 7th, not full Kaal Sarp
          contrib.push(-20);
          notes.push(`Rahu in 7th house natal: Marriage complications -20`);
        }
      }
    }
    
    // NEW: PENALTY - Saturn and Rahu conjunction in 7th house
    if (dasha.natalPlacements?.Saturn?.house === 7 && dasha.natalPlacements?.Rahu?.house === 7) {
      const saturnDeg = dasha.natalPlacements.Saturn.offsetDeg || 0;
      const rahuDeg = dasha.natalPlacements.Rahu.offsetDeg || 0;
      const degreeGap = Math.abs(saturnDeg - rahuDeg);
      
      // Check if within 10 degrees (tight conjunction)
      if (degreeGap <= 10 || degreeGap >= 350) {
        contrib.push(-40);
        notes.push(`Saturn-Rahu CLOSE conjunction in 7th: Extreme marriage delays/obstacles -40`);
      } else {
        // Both in 7th but not close conjunction
        contrib.push(-25);
        notes.push(`Saturn-Rahu both in 7th house: Significant marriage delays -25`);
      }
    } else if (dasha.natalPlacements?.Saturn?.house === 7) {
      // Saturn alone in 7th (already handled by house scores, but add explicit note)
      notes.push(`Saturn in 7th house natal: Natural delay indicator (reflected in house score)`);
    }
    
    const finalScore = aggregateScore(contrib);
    notes.push(`MARRIAGE TIMING: Final score=${finalScore.toFixed(1)}`);
    
    const templateKey = finalScore > 40 ? 'marriage_timing_positive' : finalScore < -20 ? 'marriage_timing_alert' : 'marriage_timing';
    return { value: finalScore, notes, templateKey };
  }
};

// 9) Transformation through 8th House Activation (with Rahu trigger)
const transformation8thHouse: Rule = {
  key: 'transformation_8th_house',
  areas: ['Spirituality', 'Investments', 'Health'], // 8th house significations
  templateKey: 'transformation_8th_rahu',
  priority: 15,
  triggers: (dasha, transits) => {
    // Check if MD or AD planet occupies 8th house
    const mdPlanet = dasha.levels.find(l => l.level === 'MD')?.planet;
    const adPlanet = dasha.levels.find(l => l.level === 'AD')?.planet;
    const pdPlanet = dasha.levels.find(l => l.level === 'PD')?.planet;
    const sdPlanet = dasha.levels.find(l => l.level === 'SD')?.planet;
    
    const mdOccupies8th = dasha.levels.find(l => l.level === 'MD')?.occupies === 8;
    const adOccupies8th = dasha.levels.find(l => l.level === 'AD')?.occupies === 8;
    
    // Check if Rahu is in PD or SD
    const rahuInPDorSD = pdPlanet === 'Rahu' || sdPlanet === 'Rahu';
    
    // Trigger if 8th house activated by MD/AD AND Rahu is in PD/SD
    return (mdOccupies8th || adOccupies8th) && rahuInPDorSD;
  },
  score: (dasha, transits) => {
    const acts = activatedHouses(dasha);
    const notes: string[] = [];
    const contrib: number[] = [];
    
    const pdPlanet = dasha.levels.find(l => l.level === 'PD')?.planet;
    const sdPlanet = dasha.levels.find(l => l.level === 'SD')?.planet;
    const rahuInPDorSD = pdPlanet === 'Rahu' || sdPlanet === 'Rahu';
    
    // Find MD/AD activation of 8th house
    for (const a of acts) {
      if ((a.level === 'MD' || a.level === 'AD') && a.houses.has(8)) {
        const lvlW = weightByLevel(a.level);
        
        // 8th house transformation - use house score
        const h8Score = normHouseScore(8, dasha.houseScores);
        const base = h8Score * lvlW;
        
        // Rahu amplification in PD/SD
        let rahuBonus = 0;
        if (rahuInPDorSD) {
          const rahuLevel = pdPlanet === 'Rahu' ? 'PD' : 'SD';
          const rahuWeight = weightByLevel(rahuLevel);
          rahuBonus = rahuWeight * 0.3; // 30% bonus for Rahu trigger
          notes.push(`Rahu in ${rahuLevel} amplifies transformation: +${(rahuBonus*100).toFixed(0)}%`);
        }
        
        // Transit reinforcement for 8th house
        const reinforce = transitReinforcement({ 
          targetHouse: 8, 
          transits, 
          forLifeArea: 'Spirituality' // 8th house area
        });
        
        const totalContrib = base + rahuBonus + reinforce;
        contrib.push(totalContrib);
        
        notes.push(`${a.level} ${a.planet} occupies H8: base=${(base*100).toFixed(0)} rahuBonus=${(rahuBonus*100).toFixed(0)} transit=${(reinforce*100).toFixed(0)}`);
      }
    }
    
    const finalScore = aggregateScore(contrib);
    notes.push(`TRANSFORMATION: Final score=${finalScore.toFixed(1)} (Gains with losses - duality principle)`);
    
    const templateKey = finalScore > 20 ? 'transformation_8th_rahu_positive' : finalScore < -30 ? 'transformation_8th_rahu_alert' : 'transformation_8th_rahu';
    return { value: finalScore, notes, templateKey };
  }
};

// 10) Secrecy Principle - 8th/12th Lord in Beneficial Houses (Natal Condition - Always Active)
const secrecyPrinciple: Rule = {
  key: 'secrecy_principle_8th_12th_lord',
  areas: ['Career', 'Wealth', 'Self'],
  templateKey: 'secrecy_principle',
  priority: 20,
  ruleType: 'natal', // Permanent chart feature
  customPrediction: '🤫 SECRECY PRINCIPLE ACTIVATED: The 8th or 12th house lord is positioned in a beneficial house (1st, 2nd, 5th, 9th, or 11th) in your natal chart. This classical configuration advises: DO NOT talk about your work, plans, or goals before they materialize. Maintain complete secrecy about upcoming ventures, financial moves, career changes, or personal projects. Premature disclosure can drain the energy and invite obstacles. Work silently, reveal results only after success. This is especially important for negotiations, investments, spiritual practices, and creative endeavors. The power of silence protects your intentions from negative influences and the evil eye. Let your achievements speak for themselves.',
  triggers: (dasha, transits) => {
    // This is a NATAL chart condition, not dasha-dependent
    // Check if ANY planet in the dasha levels rules 8th or 12th and occupies beneficial houses
    // Since this is a natal condition, it should appear in ALL periods if the condition exists
    
    const BENEFICIAL_HOUSES = new Set<HouseNum>([1, 2, 5, 9, 11]);
    
    // Check ALL planets across all dasha levels (natal positions)
    // We want to find if the natal chart has 8th/12th lord in beneficial houses
    const planetsChecked = new Set<PlanetName>();
    
    for (const level of dasha.levels) {
      // Skip if we already checked this planet
      if (planetsChecked.has(level.planet)) continue;
      planetsChecked.add(level.planet);
      
      const rules8thOr12th = level.rules.includes(8) || level.rules.includes(12);
      const occupiesBeneficial = BENEFICIAL_HOUSES.has(level.occupies);
      
      if (rules8thOr12th && occupiesBeneficial) {
        return true; // Natal condition exists - show in all periods
      }
    }
    
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    const BENEFICIAL_HOUSES = new Set<HouseNum>([1, 2, 5, 9, 11]);
    
    // Find all planets that rule 8th or 12th and occupy beneficial houses (natal positions)
    const planetsChecked = new Set<PlanetName>();
    const natalConditions: string[] = [];
    
    for (const level of dasha.levels) {
      if (planetsChecked.has(level.planet)) continue;
      planetsChecked.add(level.planet);
      
      const rules8th = level.rules.includes(8);
      const rules12th = level.rules.includes(12);
      const occupiesBeneficial = BENEFICIAL_HOUSES.has(level.occupies);
      
      if ((rules8th || rules12th) && occupiesBeneficial) {
        const lordType = rules8th && rules12th ? '8th & 12th' : rules8th ? '8th' : '12th';
        natalConditions.push(`${level.planet} rules H${lordType}, occupies H${level.occupies}`);
      }
    }
    
    if (natalConditions.length > 0) {
      notes.push(`NATAL CONDITION: ${natalConditions.join('; ')}`);
      notes.push(`SECRECY PRINCIPLE: Maintain silence about plans and work until materialization`);
      notes.push(`This is a permanent chart feature - applies throughout life`);
    }
    
    // Fixed moderate positive score since this is an important advisory (not outcome-dependent)
    return { value: 35, notes }; // Consistent score to show importance
  }
};

// 11) Trishadaya Lord in Upachaya - Permanent Strength (Natal Condition)
const trishadayaUpachaya: Rule = {
  key: 'trishadaya_upachaya_strength',
  areas: ['Career', 'Wealth', 'Self'],
  templateKey: 'trishadaya_upachaya',
  priority: 25,
  ruleType: 'natal', // Permanent chart feature
  triggers: (dasha, transits) => {
    // This is a NATAL chart condition - check if any planet rules Trishadaya and occupies Upachaya or Dusthana (8th, 12th)
    const TRISHADAYA = new Set<HouseNum>([3, 6, 11]);
    const UPACHAYA = new Set<HouseNum>([3, 6, 10, 11]); // Removed 12th
    const DUSTHANA = new Set<HouseNum>([8, 12]); // Negative placement for trishadaya lords
    
    const planetsChecked = new Set<PlanetName>();
    
    for (const level of dasha.levels) {
      if (planetsChecked.has(level.planet)) continue;
      planetsChecked.add(level.planet);
      
      const rulesTrishadaya = level.rules.some(h => TRISHADAYA.has(h));
      const occupiesUpachaya = UPACHAYA.has(level.occupies);
      const occupiesDusthana = DUSTHANA.has(level.occupies);
      
      if (rulesTrishadaya && (occupiesUpachaya || occupiesDusthana)) {
        return true; // Natal condition exists (either positive or negative)
      }
    }
    
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    const TRISHADAYA = new Set<HouseNum>([3, 6, 11]);
    const UPACHAYA = new Set<HouseNum>([3, 6, 10, 11]); // Removed 12th
    const DUSTHANA = new Set<HouseNum>([8, 12]); // Negative placement
    const NATURAL_MALEFICS = new Set<PlanetName>(['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu']);
    const NATURAL_BENEFICS = new Set<PlanetName>(['Moon', 'Mercury', 'Jupiter', 'Venus']);
    
    const planetsChecked = new Set<PlanetName>();
    const positiveConditions: string[] = [];
    const negativeConditions: string[] = [];
    let hasPositive = false;
    let hasNegative = false;
    
    for (const level of dasha.levels) {
      if (planetsChecked.has(level.planet)) continue;
      planetsChecked.add(level.planet);
      
      const trishadayaHouses = level.rules.filter(h => TRISHADAYA.has(h));
      if (trishadayaHouses.length === 0) continue;
      
      const occupiesUpachaya = UPACHAYA.has(level.occupies);
      const occupiesDusthana = DUSTHANA.has(level.occupies);
      const isMalefic = NATURAL_MALEFICS.has(level.planet);
      const isBenefic = NATURAL_BENEFICS.has(level.planet);
      
      if (occupiesUpachaya) {
        hasPositive = true;
        if (isMalefic) {
          positiveConditions.push(`${level.planet} (malefic) rules H${trishadayaHouses.join(',')} in H${level.occupies} → POWER TO DO HARD WORK`);
        } else if (isBenefic) {
          positiveConditions.push(`${level.planet} (benefic) rules H${trishadayaHouses.join(',')} in H${level.occupies} → GUIDANCE TO HANDLE CHALLENGES`);
        }
      } else if (occupiesDusthana) {
        hasNegative = true;
        negativeConditions.push(`${level.planet} rules H${trishadayaHouses.join(',')} in H${level.occupies} (dusthana) → INCREASED PROBLEMS`);
      }
    }
    
    if (hasPositive) {
      notes.push(`TRISHADAYA LORD IN UPACHAYA: ${positiveConditions.join('; ')}`);
      notes.push(`UPACHAYA PRINCIPLE (3,6,10,11): Houses that improve with age and effort`);
      notes.push(`MALEFIC TRISHADAYA LORD: Gives tremendous power, stamina, and capacity for hard work`);
      notes.push(`BENEFIC TRISHADAYA LORD: Shows the way to intelligently deal with challenges`);
      notes.push(`PERMANENT STRENGTH: Your capacity to overcome obstacles increases with time`);
      notes.push(`MANIFESTATION: Entrepreneurship, competitive fields, litigation, adversity become opportunities`);
    }
    
    if (hasNegative) {
      notes.push(`⚠️ TRISHADAYA LORD IN DUSTHANA (8th/12th): ${negativeConditions.join('; ')}`);
      notes.push(`CHALLENGING PLACEMENT: Difficulties in matters of ruled houses multiply`);
      notes.push(`8TH/12TH HOUSES: Losses, obstacles, hidden enemies, chronic problems`);
      notes.push(`REMEDIES NEEDED: Strengthen the trishadaya lord via mantra, charity, gemstone`);
    }
    
    // Positive if in Upachaya, negative if in 8th/12th
    const value = hasPositive ? (hasNegative ? 20 : 45) : -30;
    return { value, notes };
  }
};

// 12) Malefic Association in Dusthana from Own House (Natal Affliction)
const maleficDusthanaFromOwnHouse: Rule = {
  key: 'malefic_dusthana_from_own_house',
  areas: ['Self'], // Default - will be overridden dynamically based on afflicted houses
  templateKey: 'malefic_affliction_own_house',
  priority: 30,
  ruleType: 'natal', // Permanent chart affliction
  triggers: (dasha, transits) => {
    // Check if any planet sits 6th, 8th, or 12th from its own house
    // AND has malefic association (conjunction or aspect)
    const MALEFICS = new Set<PlanetName>(['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu']);
    const DUSTHANA_OFFSETS = new Set([5, 7, 11]); // 6th, 8th, 12th from a house (0-indexed offsets)
    
    const planetsChecked = new Set<PlanetName>();
    
    for (const level of dasha.levels) {
      if (planetsChecked.has(level.planet)) continue;
      planetsChecked.add(level.planet);
      
      // Check each house this planet rules
      for (const ruledHouse of level.rules) {
        // Calculate offset from ruled house to occupied house
        const offset = (level.occupies - ruledHouse + 12) % 12;
        
        if (DUSTHANA_OFFSETS.has(offset)) {
          // Planet is in dusthana from its own house
          // Now check for malefic association
          
          // Check if any malefic aspects this planet's occupied house
          for (const otherLevel of dasha.levels) {
            if (MALEFICS.has(otherLevel.planet)) {
              // Check if malefic aspects the occupied house
              const aspects = otherLevel.aspects || [];
              if (aspects.includes(level.occupies) || otherLevel.occupies === level.occupies) {
                return true; // Malefic association found
              }
            }
          }
        }
      }
    }
    
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    const MALEFICS = new Set<PlanetName>(['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu']);
    const DUSTHANA_OFFSETS = new Set([5, 7, 11]); // 6th, 8th, 12th offsets
    const OFFSET_TO_HOUSE = { 5: '6th', 7: '8th', 11: '12th' } as const;
    
    const HOUSE_SIGNIFICATIONS: Record<number, string> = {
      1: 'Self/Health', 2: 'Wealth/Family', 3: 'Siblings/Courage', 4: 'Mother/Property',
      5: 'Children/Intelligence', 6: 'Enemies/Disease', 7: 'Spouse/Partnerships',
      8: 'Longevity/Occult', 9: 'Father/Fortune', 10: 'Career/Status',
      11: 'Gains/Aspirations', 12: 'Losses/Spirituality'
    };
    
    const planetsChecked = new Set<PlanetName>();
    const afflictions: string[] = [];
    const afflictedHouses = new Set<HouseNum>();
    
    for (const level of dasha.levels) {
      if (planetsChecked.has(level.planet)) continue;
      planetsChecked.add(level.planet);
      
      for (const ruledHouse of level.rules) {
        const offset = (level.occupies - ruledHouse + 12) % 12;
        
        if (DUSTHANA_OFFSETS.has(offset)) {
          // Find malefic associations
          const maleficsInvolved: string[] = [];
          
          for (const otherLevel of dasha.levels) {
            if (MALEFICS.has(otherLevel.planet)) {
              const aspects = otherLevel.aspects || [];
              if (otherLevel.occupies === level.occupies) {
                maleficsInvolved.push(`${otherLevel.planet} (conjunct)`);
              } else if (aspects.includes(level.occupies)) {
                maleficsInvolved.push(`${otherLevel.planet} (aspects)`);
              }
            }
          }
          
          if (maleficsInvolved.length > 0) {
            const dusthanaName = OFFSET_TO_HOUSE[offset as keyof typeof OFFSET_TO_HOUSE];
            const houseName = HOUSE_SIGNIFICATIONS[ruledHouse] || `H${ruledHouse}`;
            afflictions.push(
              `${level.planet} rules H${ruledHouse} (${houseName}), sits in H${level.occupies} (${dusthanaName} from own house), afflicted by ${maleficsInvolved.join(', ')}`
            );
            afflictedHouses.add(ruledHouse as HouseNum); // Track which house is afflicted
          }
        }
      }
    }
    
    if (afflictions.length > 0) {
      notes.push(`NATAL AFFLICTION: ${afflictions.join('; ')}`);
      notes.push(`EFFECT: Ruled house matters face obstacles and delays`);
      
      // Show relevant life areas based on afflicted houses
      const relevantAreas = housesToAreas(afflictedHouses);
      if (relevantAreas.length > 0) {
        notes.push(`AFFECTED LIFE AREAS: ${relevantAreas.join(', ')}`);
      }
      
      notes.push(`Example: 7th lord in 6th/8th/12th from 7th with malefic = partnership challenges`);
      notes.push(`This is a permanent chart feature requiring remedial measures`);
    }
    
    // Negative score indicating affliction
    return { value: -40, notes }; // Caution level
  }
};

// 13) Kaal Sarp Yoga (Natal Condition)
// NOTE: This rule is DISABLED because it only checks dasha planets (MD/AD/PD/SD), not all 7 classical planets.
// Kaal Sarp Yoga is correctly detected in App.tsx using checkKaalSarpYoga() and added to feeds via kaalSarpData.
const kaalSarpYoga: Rule = {
  key: 'kaal_sarp_yoga',
  areas: ['Self', 'Spirituality', 'Career', 'Health'],
  templateKey: 'kaal_sarp_yoga',
  priority: 5, // High priority for major yoga
  ruleType: 'natal', // Permanent chart feature
  triggers: (dasha, transits) => {
    // DISABLED: This rule incorrectly uses only dasha.levels (4 planets) instead of all natal placements (7 planets)
    // Kaal Sarp detection is handled in App.tsx via checkKaalSarpYoga() which correctly checks all planets
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    const planets = dasha.levels.map(l => ({ planet: l.planet, house: l.occupies }));
    const rahuLevel = dasha.levels.find(l => l.planet === 'Rahu');
    const ketuLevel = dasha.levels.find(l => l.planet === 'Ketu');
    
    if (!rahuLevel || !ketuLevel) {
      return { value: 0, notes: ['Rahu/Ketu not found'] };
    }
    
    const classicalPlanets = planets.filter(p => !['Rahu', 'Ketu'].includes(p.planet));
    const rahuHouse = rahuLevel.occupies;
    const ketuHouse = ketuLevel.occupies;
    
    const planetsBetween: string[] = [];
    for (const p of classicalPlanets) {
      const house = p.house;
      let isBetween = false;
      
      if (rahuHouse < ketuHouse) {
        isBetween = house > rahuHouse && house < ketuHouse;
      } else {
        isBetween = house > rahuHouse || house < ketuHouse;
      }
      
      if (isBetween) planetsBetween.push(`${p.planet}(H${house})`);
    }
    
    const betweenCount = planetsBetween.length;
    const totalCount = classicalPlanets.length;
    
    let severity = 0;
    let yogaType = '';
    
    if (betweenCount === totalCount) {
      severity = -35; // Moderate negative (karmic challenges)
      yogaType = 'FULL';
      notes.push(`FULL Kaal Sarp Yoga: All ${totalCount} planets hemmed between Rahu (H${rahuHouse}) and Ketu (H${ketuHouse})`);
    } else if (betweenCount === 0) {
      severity = -35;
      yogaType = 'FULL (Reverse)';
      notes.push(`FULL Kaal Sarp Yoga (Reverse): All planets on Ketu's side - strong spiritual inclination`);
    } else if (betweenCount >= 5) {
      severity = -25;
      yogaType = 'PARTIAL';
      notes.push(`PARTIAL Kaal Sarp Yoga: ${betweenCount}/${totalCount} planets between Rahu-Ketu axis`);
    }
    
    notes.push(`Planets between: ${planetsBetween.join(', ')}`);
    notes.push(`EFFECTS: Karmic obstacles, delays in life goals, spiritual growth through challenges`);
    notes.push(`REMEDIES: Worship Lord Shiva, Rahu-Ketu remedies, spiritual practices strongly recommended`);
    
    return { value: severity, notes };
  }
};

// 14) Saturn Sade Sati - Rising Phase (Timing-based)
const sadeSatiRising: Rule = {
  key: 'sade_sati_rising',
  areas: ['Self', 'Health', 'Career', 'Wealth'],
  templateKey: 'sade_sati_rising',
  priority: 10,
  ruleType: 'timing', // Time-based yoga
  triggers: (dasha, transits) => {
    // Check if Saturn is transiting 12th sign from natal Moon sign
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!moonPlacement || moonPlacement.sign === undefined) {
      return false;
    }
    
    // Find Saturn's current transit position with sign info
    const saturnTransit = transits.positions.find(p => p.planet === 'Saturn');
    if (!saturnTransit) {
      return false;
    }
    
    if (saturnTransit.sign === undefined) {
      return false;
    }
    
    // Calculate 12th sign from Moon's natal sign (1-indexed: 1-12)
    const moonSign = moonPlacement.sign; // 1-12
    const twelfthSign = moonSign === 1 ? 12 : moonSign - 1; // 12th sign from Moon (one sign before)
    
    console.log('[SADE SATI RISING] Moon sign:', moonSign, 'Saturn sign:', saturnTransit.sign, 'Expected 12th sign:', twelfthSign, 'Match?', saturnTransit.sign === twelfthSign);
    
    // Check if Saturn is transiting the 12th sign from Moon
    return saturnTransit.sign === twelfthSign;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    // Check Saturn's natal house position
    const saturnNatalHouse = dasha.natalPlacements?.Saturn?.house;
    
    // Beneficial houses: 2, 5, 9 (Sade Sati gives GOOD results)
    const beneficialHouses = [2, 5, 9];
    // Painful houses: 3, 6, 8, 12 (Sade Sati gives PAINFUL results, can bring person to zero)
    const painfulHouses = [3, 6, 8, 12];
    
    let baseScore = -35; // Default caution level
    
    // Calculate Sade Sati phase window
    // Try to use ephemeris-based dates if available, otherwise use approximation
    let customWindow: { from: string; to: string };
    
    if (transits.saturnSignTransits) {
      // Use accurate ephemeris dates
      customWindow = {
        from: transits.saturnSignTransits.ingressDate,
        to: transits.saturnSignTransits.egressDate
      };
    } else {
      // Fallback to approximate window (~2.5 years per sign)
      // Used when astronomy-engine calculation fails
      const now = new Date();
      const approxStartDate = new Date(now);
      approxStartDate.setMonth(now.getMonth() - 15); // Assume started ~15 months ago (mid-phase)
      const approxEndDate = new Date(now);
      approxEndDate.setMonth(now.getMonth() + 15); // Will end in ~15 months
      
      customWindow = {
        from: approxStartDate.toISOString(),
        to: approxEndDate.toISOString()
      };
      
      notes.push('⚠️ NOTE: Using approximate dates (~2.5 year standard). Calculation was unavailable.');
    }
    
    if (saturnNatalHouse && beneficialHouses.includes(saturnNatalHouse)) {
      baseScore = 15; // Positive results when Saturn is well-placed natally
      notes.push(`⭐ SATURN WELL-PLACED: Saturn in natal ${saturnNatalHouse}${['st','nd','rd','th','th','th','th','th','th','th','th','th'][saturnNatalHouse-1]} house (beneficial position)`);
      notes.push(`SADE SATI - RISING PHASE: Saturn transiting 12th house from natal Moon sign`);
      notes.push(`Duration: ~2.5 years (first phase of 7.5 year cycle)`);
      notes.push(`SPECIAL RESULT: Sade Sati gives FAVORABLE outcomes due to Saturn's strong natal placement`);
      notes.push(`EFFECTS: Disciplined growth, structured progress, steady gains through hard work`);
      notes.push(`NATURE: Testing phase but with support - Saturn rewards your efforts`);
      notes.push(`UTILIZE: Major projects, long-term investments, building foundations, career advancement`);
      notes.push(`REMEDIES: Saturday Shani worship to amplify positive effects`);
    } else if (saturnNatalHouse && painfulHouses.includes(saturnNatalHouse)) {
      baseScore = -70; // Severe negative when Saturn is poorly placed natally
      notes.push(`⚠️ SATURN CHALLENGING POSITION: Saturn in natal ${saturnNatalHouse}${['st','nd','rd','th','th','th','th','th','th','th','th','th'][saturnNatalHouse-1]} house (painful position)`);
      notes.push(`SADE SATI - RISING PHASE: Saturn transiting 12th house from natal Moon sign`);
      notes.push(`Duration: ~2.5 years (first phase of 7.5 year cycle)`);
      notes.push(`⚠️ SEVERE WARNING: Sade Sati gives EXTREMELY PAINFUL results due to Saturn's difficult natal placement`);
      notes.push(`EFFECTS: Can bring person to ZERO - severe financial loss, health crisis, emotional breakdown, career collapse`);
      notes.push(`NATURE: Extreme testing phase - maximum karmic pressure, life-altering challenges`);
      notes.push(`CRITICAL: Avoid ALL major risks - no investments, business ventures, relocations, marriages during this period`);
      notes.push(`SURVIVAL MODE: Focus on preservation, not growth. Maintain emergency reserves, health insurance, support network`);
      notes.push(`REMEDIES ESSENTIAL: Daily Shani mantra (108 times), Saturday fasting, charity to poor/disabled, blue sapphire (ONLY if astrologer approves after thorough analysis), visit Shani temples, help laborers/elderly`);
      notes.push(`PSYCHOLOGICAL: Seek counseling support, maintain spiritual practices, this is temporary karmic cleansing`);
    } else {
      notes.push(`SADE SATI - RISING PHASE: Saturn transiting 12th house from natal Moon sign`);
      notes.push(`Duration: ~2.5 years (first phase of 7.5 year cycle)`);
      notes.push(`EFFECTS: Initial karmic pressure, financial caution needed, health awareness`);
      notes.push(`NATURE: Testing phase - avoid major risks, build resilience, focus on duties`);
      notes.push(`REMEDIES: Saturday fasting, Shani mantra, charity to elderly/laborers`);
    }
    
    return { value: baseScore, notes, customWindow };
  }
};

// 15) Saturn Sade Sati - Peak Phase (Timing-based)
const sadeSatiPeak: Rule = {
  key: 'sade_sati_peak',
  areas: ['Self', 'Health', 'Career', 'Spouse'],
  templateKey: 'sade_sati_peak',
  priority: 10,
  ruleType: 'timing',
  triggers: (dasha, transits) => {
    // Check if Saturn is transiting natal Moon sign (peak/most intense phase)
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!moonPlacement || moonPlacement.sign === undefined) return false;
    
    // Find Saturn's current transit position with sign info
    const saturnTransit = transits.positions.find(p => p.planet === 'Saturn');
    if (!saturnTransit || saturnTransit.sign === undefined) return false;
    
    console.log('[SADE SATI PEAK] Moon sign:', moonPlacement.sign, 'Saturn sign:', saturnTransit.sign, 'Match?', saturnTransit.sign === moonPlacement.sign);
    
    // Check if Saturn is transiting the same sign as natal Moon
    return saturnTransit.sign === moonPlacement.sign;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    // Check Saturn's natal house position
    const saturnNatalHouse = dasha.natalPlacements?.Saturn?.house;
    
    // Beneficial houses: 2, 5, 9 (Sade Sati gives GOOD results)
    const beneficialHouses = [2, 5, 9];
    // Painful houses: 3, 6, 8, 12 (Sade Sati gives PAINFUL results, can bring person to zero)
    const painfulHouses = [3, 6, 8, 12];
    
    let baseScore = -50; // Default alert level (most intense phase)
    
    // Calculate Sade Sati phase window
    // Try to use ephemeris-based dates if available, otherwise use approximation
    let customWindow: { from: string; to: string };
    
    if (transits.saturnSignTransits) {
      // Use accurate ephemeris dates
      customWindow = {
        from: transits.saturnSignTransits.ingressDate,
        to: transits.saturnSignTransits.egressDate
      };
    } else {
      // Fallback to approximate window (~2.5 years per sign)
      const now = new Date();
      const approxStartDate = new Date(now);
      approxStartDate.setMonth(now.getMonth() - 15); // Assume started ~15 months ago (mid-phase)
      const approxEndDate = new Date(now);
      approxEndDate.setMonth(now.getMonth() + 15); // Will end in ~15 months
      
      customWindow = {
        from: approxStartDate.toISOString(),
        to: approxEndDate.toISOString()
      };
    }
    
    if (saturnNatalHouse && beneficialHouses.includes(saturnNatalHouse)) {
      baseScore = 25; // Positive results even in peak phase when Saturn well-placed
      notes.push(`⭐ SATURN WELL-PLACED: Saturn in natal ${saturnNatalHouse}${['st','nd','rd','th','th','th','th','th','th','th','th','th'][saturnNatalHouse-1]} house (beneficial position)`);
      notes.push(`SADE SATI - PEAK PHASE: Saturn transiting natal Moon sign`);
      notes.push(`Duration: ~2.5 years (middle phase - normally most intense)`);
      notes.push(`SPECIAL RESULT: Even peak Sade Sati gives FAVORABLE outcomes due to Saturn's strong natal placement`);
      notes.push(`EFFECTS: Disciplined transformation, structured breakthroughs, authority through hard work`);
      notes.push(`OPPORTUNITY: This is your time to SHINE through discipline - major career elevation possible`);
      notes.push(`MANIFESTATION: Promotions, recognition for sustained effort, leadership roles, property gains`);
      notes.push(`NATURE: Intense but productive - Saturn tests you and simultaneously rewards you`);
      notes.push(`UTILIZE: Take on major responsibilities, launch important projects, make bold career moves`);
      notes.push(`REMEDIES: Shani worship to amplify already-strong effects, charity to multiply blessings`);
    } else if (saturnNatalHouse && painfulHouses.includes(saturnNatalHouse)) {
      baseScore = -90; // Most severe negative - peak phase with bad natal Saturn
      notes.push(`⚠️ SATURN CHALLENGING POSITION: Saturn in natal ${saturnNatalHouse}${['st','nd','rd','th','th','th','th','th','th','th','th','th'][saturnNatalHouse-1]} house (painful position)`);
      notes.push(`SADE SATI - PEAK PHASE ⚠️⚠️⚠️: Saturn transiting natal Moon sign`);
      notes.push(`Duration: ~2.5 years (middle phase - MOST INTENSE)`);
      notes.push(`🚨 EXTREME DANGER PERIOD: Peak Sade Sati with poorly-placed natal Saturn can bring COMPLETE DEVASTATION`);
      notes.push(`EFFECTS: Can reduce person to ABSOLUTE ZERO - total financial ruin, severe health crisis, mental breakdown, relationship collapse, career destruction, loss of status, homelessness possible`);
      notes.push(`NATURE: Dark night of the soul - maximum karmic debt payment, life-shattering events, rock bottom experiences`);
      notes.push(`SURVIVAL CRITICAL: This is the MOST DANGEROUS period of your life. Every decision is crucial.`);
      notes.push(`ABSOLUTE PROHIBITIONS: NO investments, NO business ventures, NO major purchases, NO relocations, NO marriages, NO legal disputes, NO confrontations, NO loans/debts`);
      notes.push(`EMERGENCY PROTOCOL: Build maximum emergency reserves (12+ months expenses), health insurance mandatory, maintain support network, document everything, avoid ALL risks`);
      notes.push(`PSYCHOLOGICAL: Professional counseling MANDATORY - depression, anxiety, suicidal thoughts are common. You are NOT alone. This is temporary karmic cleansing.`);
      notes.push(`REMEDIES CRITICAL (do ALL): Daily Shani Stotra 108 times, Saturday fasting (only black/blue items), Hanuman Chalisa daily, feed crows/laborers every Saturday, visit Shani temples monthly, donate iron/black clothes to poor, help disabled people, light mustard oil lamp to Shani every evening, wear iron ring (right hand middle finger), blue sapphire ONLY after expert consultation`);
      notes.push(`SPIRITUAL: Complete surrender to God, accept this as karmic purification, maintain daily meditation/prayer, this too shall pass - you will emerge stronger`);
      notes.push(`LIGHT AHEAD: After this peak phase passes, gradual relief begins. Your resilience now builds unshakeable character.`);
    } else {
      notes.push(`SADE SATI - PEAK PHASE: Saturn transiting natal Moon sign`);
      notes.push(`Duration: ~2.5 years (middle phase - most intense)`);
      notes.push(`EFFECTS: Maximum karmic pressure, major life restructuring, emotional challenges`);
      notes.push(`NATURE: Transformation through discipline - patience absolutely critical`);
      notes.push(`GUIDANCE: This too shall pass - maintain dharma, avoid shortcuts, embrace responsibility`);
      notes.push(`REMEDIES: Daily Shani prayers, blue sapphire (only if astrologer approves), seva to poor`);
    }
    
    return { value: baseScore, notes, customWindow };
  }
};

// 16) Saturn Sade Sati - Setting Phase (Timing-based)
const sadeSatiSetting: Rule = {
  key: 'sade_sati_setting',
  areas: ['Self', 'Wealth', 'Career', 'Property'],
  templateKey: 'sade_sati_setting',
  priority: 10,
  ruleType: 'timing',
  triggers: (dasha, transits) => {
    // Check if Saturn is transiting 2nd sign from natal Moon sign (final phase)
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!moonPlacement || moonPlacement.sign === undefined) return false;
    
    // Find Saturn's current transit position with sign info
    const saturnTransit = transits.positions.find(p => p.planet === 'Saturn');
    if (!saturnTransit || saturnTransit.sign === undefined) return false;
    
    // Calculate which Moon sign would have Saturn's current transit as 2nd house
    // If Saturn is in sign X, then Moon should be in sign (X - 1) for Setting Phase
    // Example: Saturn in Pisces (12), Moon should be in Aquarius (11)
    // because 2nd from Aquarius = Pisces
    const saturnSign = saturnTransit.sign; // 1-12
    const moonSign = moonPlacement.sign; // 1-12
    const expectedMoonSign = saturnSign === 1 ? 12 : saturnSign - 1; // Moon should be one sign before Saturn
    
    console.log('[SADE SATI SETTING] Moon sign:', moonSign, 'Saturn sign:', saturnSign, 'Expected Moon:', expectedMoonSign, 'Match?', moonSign === expectedMoonSign);
    
    // Check if natal Moon is in the correct position for Setting Phase
    return moonSign === expectedMoonSign;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    // Check Saturn's natal house position
    const saturnNatalHouse = dasha.natalPlacements?.Saturn?.house;
    
    // Beneficial houses: 2, 5, 9 (Sade Sati gives GOOD results)
    const beneficialHouses = [2, 5, 9];
    // Painful houses: 3, 6, 8, 12 (Sade Sati gives PAINFUL results, can bring person to zero)
    const painfulHouses = [3, 6, 8, 12];
    
    let baseScore = -25; // Default mixed/caution (less intense than peak)
    
    // Calculate Sade Sati phase window
    // Try to use ephemeris-based dates if available, otherwise use approximation
    let customWindow: { from: string; to: string };
    
    if (transits.saturnSignTransits) {
      // Use accurate ephemeris dates
      customWindow = {
        from: transits.saturnSignTransits.ingressDate,
        to: transits.saturnSignTransits.egressDate
      };
    } else {
      // Fallback to approximate window (~2.5 years per sign)
      const now = new Date();
      const approxStartDate = new Date(now);
      approxStartDate.setMonth(now.getMonth() - 15); // Assume started ~15 months ago (mid-phase)
      const approxEndDate = new Date(now);
      approxEndDate.setMonth(now.getMonth() + 15); // Will end in ~15 months
      
      customWindow = {
        from: approxStartDate.toISOString(),
        to: approxEndDate.toISOString()
      };
    }
    
    if (saturnNatalHouse && beneficialHouses.includes(saturnNatalHouse)) {
      baseScore = 35; // Positive recovery when Saturn well-placed
      notes.push(`⭐ SATURN WELL-PLACED: Saturn in natal ${saturnNatalHouse}${['st','nd','rd','th','th','th','th','th','th','th','th','th'][saturnNatalHouse-1]} house (beneficial position)`);
      notes.push(`SADE SATI - SETTING PHASE: Saturn transiting 2nd house from natal Moon sign`);
      notes.push(`Duration: ~2.5 years (final phase)`);
      notes.push(`SPECIAL RESULT: Setting phase brings EXCELLENT outcomes due to Saturn's strong natal placement`);
      notes.push(`EFFECTS: Accelerated recovery, financial gains materialize, accumulated efforts bear fruit`);
      notes.push(`HARVEST TIME: Reap rewards of discipline maintained during earlier phases`);
      notes.push(`MANIFESTATION: Wealth accumulation (2nd house), property gains, family prosperity, recognition`);
      notes.push(`NATURE: Victory lap - Saturn rewards your perseverance with tangible success`);
      notes.push(`UTILIZE: Investments pay off, savings grow, receive back-payments, promotions finalize, family harmony`);
      notes.push(`POSITIVE: Not just relief but actual GAINS - this is Saturn's blessing for your patience`);
      notes.push(`CONTINUE: Maintain Saturn remedies to lock in positive momentum`);
    } else if (saturnNatalHouse && painfulHouses.includes(saturnNatalHouse)) {
      baseScore = -55; // Still severe but lessening from peak
      notes.push(`⚠️ SATURN CHALLENGING POSITION: Saturn in natal ${saturnNatalHouse}${['st','nd','rd','th','th','th','th','th','th','th','th','th'][saturnNatalHouse-1]} house (painful position)`);
      notes.push(`SADE SATI - SETTING PHASE: Saturn transiting 2nd house from natal Moon sign`);
      notes.push(`Duration: ~2.5 years (final phase)`);
      notes.push(`⚠️ CONTINUED CHALLENGE: Even setting phase remains DIFFICULT due to Saturn's poor natal placement`);
      notes.push(`EFFECTS: Slow recovery, financial struggles continue, wounds still healing, family/speech issues (2nd house affliction)`);
      notes.push(`NATURE: Relief phase but challenges persist - progress is gradual, not dramatic`);
      notes.push(`FINANCIAL: Money problems ease slowly, avoid over-optimism, conservative approach essential`);
      notes.push(`FAMILY: Relationships remain strained, speech/communication careful, eye problems possible`);
      notes.push(`GUIDANCE: Worst is over but full recovery takes time. Patient rebuilding required.`);
      notes.push(`CAUTION: Still avoid major risks - small steps forward, no aggressive moves yet`);
      notes.push(`REMEDIES CONTINUE: Maintain all Shani remedies until Saturn completely exits - don't stop early!`);
      notes.push(`HOPE: Each month brings incremental improvement. Foundation is stabilizing. Better days ahead.`);
      notes.push(`TIMELINE: Complete relief comes after Saturn exits 2nd house - continue discipline until then`);
    } else {
      notes.push(`SADE SATI - SETTING PHASE: Saturn transiting 2nd house from natal Moon sign`);
      notes.push(`Duration: ~2.5 years (final phase)`);
      notes.push(`EFFECTS: Gradual relief, financial recovery possible, lessons integration`);
      notes.push(`NATURE: Harvest phase - reap results of discipline during earlier phases`);
      notes.push(`GUIDANCE: Cautious optimism - continue Saturn remedies until complete exit`);
      notes.push(`POSITIVE: Light at end of tunnel, maturity gained, wisdom accumulated`);
    }
    
    return { value: baseScore, notes, customWindow };
  }
};

// ===== YOGA HELPER FUNCTIONS =====

// Helper: Find all kendra-trikona raj yogas in the chart
function findKendraTrikonaRajYogas(dasha: DashaContext): Array<{
  kendraLord: PlanetName;
  trikonaLord: PlanetName;
  kendraHouse: number;
  trikonaHouse: number;
  connection: 'conjunction' | 'aspect' | 'exchange' | 'trikona_lord_in_kendra' | 'kendra_lord_in_trikona';
}> {
  if (!dasha.natalPlacements) return [];
  
  const yogas: Array<{
    kendraLord: PlanetName;
    trikonaLord: PlanetName;
    kendraHouse: number;
    trikonaHouse: number;
    connection: 'conjunction' | 'aspect' | 'exchange' | 'trikona_lord_in_kendra' | 'kendra_lord_in_trikona';
  }> = [];
  
  const KENDRAS = [1, 4, 7, 10];
  const TRIKONAS = [1, 5, 9];
  
  const allPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  
  // Find lords of kendras and trikonas
  const kendraLords = new Map<number, PlanetName[]>();
  const trikonaLords = new Map<number, PlanetName[]>();
  
  for (const planet of allPlanets) {
    const placement = dasha.natalPlacements[planet];
    if (!placement?.rules) continue;
    
    for (const house of placement.rules) {
      if (KENDRAS.includes(house)) {
        if (!kendraLords.has(house)) kendraLords.set(house, []);
        kendraLords.get(house)!.push(planet);
      }
      if (TRIKONAS.includes(house)) {
        if (!trikonaLords.has(house)) trikonaLords.set(house, []);
        trikonaLords.get(house)!.push(planet);
      }
    }
  }
  
  // Check for various raj yoga formations
  for (const [kendraHouse, kLords] of kendraLords.entries()) {
    for (const [trikonaHouse, tLords] of trikonaLords.entries()) {
      
      for (const kLord of kLords) {
        const kPlacement = dasha.natalPlacements[kLord];
        if (!kPlacement) continue;
        
        for (const tLord of tLords) {
          // Skip if same planet rules both (unless it's house 1 which is both kendra and trikona)
          if (kLord === tLord && kendraHouse !== 1 && trikonaHouse !== 1) continue;
          
          const tPlacement = dasha.natalPlacements[tLord];
          if (!tPlacement) continue;
          
          // Type 1: Conjunction (same house)
          if (kPlacement.house === tPlacement.house && kLord !== tLord) {
            yogas.push({
              kendraLord: kLord,
              trikonaLord: tLord,
              kendraHouse,
              trikonaHouse,
              connection: 'conjunction'
            });
          }
          
          // Type 2: Mutual aspect
          if (kLord !== tLord && kPlacement.house !== tPlacement.house) {
            if (kPlacement.aspects?.includes(tPlacement.house) && 
                tPlacement.aspects?.includes(kPlacement.house)) {
              yogas.push({
                kendraLord: kLord,
                trikonaLord: tLord,
                kendraHouse,
                trikonaHouse,
                connection: 'aspect'
              });
            }
          }
          
          // Type 3: Parivartana (exchange) - kendra lord in trikona house, trikona lord in kendra house
          if (kLord !== tLord && 
              TRIKONAS.includes(kPlacement.house) && 
              KENDRAS.includes(tPlacement.house)) {
            yogas.push({
              kendraLord: kLord,
              trikonaLord: tLord,
              kendraHouse,
              trikonaHouse,
              connection: 'exchange'
            });
          }
          
          // Type 4: Trikona lord occupying a kendra house
          if (kLord !== tLord && KENDRAS.includes(tPlacement.house)) {
            yogas.push({
              kendraLord: kLord,
              trikonaLord: tLord,
              kendraHouse,
              trikonaHouse,
              connection: 'trikona_lord_in_kendra'
            });
          }
          
          // Type 5: Kendra lord occupying a trikona house
          if (kLord !== tLord && TRIKONAS.includes(kPlacement.house)) {
            yogas.push({
              kendraLord: kLord,
              trikonaLord: tLord,
              kendraHouse,
              trikonaHouse,
              connection: 'kendra_lord_in_trikona'
            });
          }
        }
      }
    }
  }
  
  // Remove duplicates
  const seen = new Set<string>();
  const uniqueYogas = yogas.filter(y => {
    const key = `${y.kendraLord}-${y.trikonaLord}-${y.connection}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  
  return uniqueYogas;
}

// Helper: Check if Dharma-Karma Raj Yoga exists in natal chart
function hasDharmaKarmaYoga(dasha: DashaContext): { exists: boolean; lord9: PlanetName[]; lord10: PlanetName[] } {
  const lord9th = new Set<PlanetName>();
  const lord10th = new Set<PlanetName>();
  
  // Use natalPlacements if available
  if (dasha.natalPlacements) {
    // Find all planets that rule house 9 and 10
    const allPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
    for (const planet of allPlanets) {
      const placement = dasha.natalPlacements[planet];
      if (placement?.rules) {
        if (placement.rules.includes(9)) lord9th.add(planet);
        if (placement.rules.includes(10)) lord10th.add(planet);
      }
    }
    
    if (lord9th.size === 0 || lord10th.size === 0) {
      return { exists: false, lord9: [], lord10: [] };
    }
    
    // Check for conjunction or aspect between 9th and 10th lords
    for (const p9 of lord9th) {
      const plc9 = dasha.natalPlacements[p9];
      if (!plc9) continue;
      
      for (const p10 of lord10th) {
        const plc10 = dasha.natalPlacements[p10];
        if (!plc10) continue;
        
        // Conjunction: same house
        if (plc9.house === plc10.house) {
          return { exists: true, lord9: Array.from(lord9th), lord10: Array.from(lord10th) };
        }
        
        // Aspect: one aspects the house of the other
        if (plc9.aspects?.includes(plc10.house) || plc10.aspects?.includes(plc9.house)) {
          return { exists: true, lord9: Array.from(lord9th), lord10: Array.from(lord10th) };
        }
      }
    }
    
    return { exists: false, lord9: [], lord10: [] };
  }
  
  // Fallback to old logic using dasha.levels
  for (const level of dasha.levels) {
    if (level.rules.includes(9)) lord9th.add(level.planet);
    if (level.rules.includes(10)) lord10th.add(level.planet);
  }
  
  if (lord9th.size === 0 || lord10th.size === 0) {
    return { exists: false, lord9: [], lord10: [] };
  }
  
  // Check for conjunction or aspect
  for (const level of dasha.levels) {
    if (lord9th.has(level.planet)) {
      for (const other of dasha.levels) {
        if (lord10th.has(other.planet)) {
          if (level.occupies === other.occupies ||
              level.aspects?.includes(other.occupies) ||
              other.aspects?.includes(level.occupies)) {
            return { exists: true, lord9: Array.from(lord9th), lord10: Array.from(lord10th) };
          }
        }
      }
    }
  }
  
  return { exists: false, lord9: [], lord10: [] };
}

// Helper: Check if yoga lords are active in current dasha
function areYogaLordsActive(dasha: DashaContext, lords: PlanetName[]): boolean {
  const activePlanets = new Set(dasha.levels.map(l => l.planet));
  return lords.some(p => activePlanets.has(p));
}

// ===== RAJ YOGAS - NATAL DETECTION (Permanent yogas in chart) =====

// 16) General Kendra-Trikona Raj Yoga - NATAL
const kendraTrikonaRajYogaNatal: Rule = {
  key: 'kendra_trikona_raj_yoga_natal',
  areas: ['Career', 'Wealth', 'Self', 'Spirituality'],
  templateKey: 'kendra_trikona_raj_yoga_natal',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    const yogas = findKendraTrikonaRajYogas(dasha);
    return yogas.length > 0;
  },
  score: (dasha, transits) => {
    const yogas = findKendraTrikonaRajYogas(dasha);
    const notes: string[] = [];
    
    if (yogas.length === 0) {
      return { value: 0, notes: ['No kendra-trikona raj yogas found'] };
    }
    
    notes.push(`👑 NATAL RAJ YOGAS DETECTED: ${yogas.length} kendra-trikona combination(s)`);
    
    const connectionLabels: Record<string, string> = {
      'conjunction': 'conjoins',
      'aspect': 'aspects',
      'exchange': 'exchanges with',
      'trikona_lord_in_kendra': '(trikona lord) sits in kendra with',
      'kendra_lord_in_trikona': '(kendra lord) sits in trikona with'
    };
    
    for (const yoga of yogas) {
      const kendraName = ['', '1st', '', '', '4th', '', '', '7th', '', '', '10th'][yoga.kendraHouse] || `${yoga.kendraHouse}th`;
      const trikonaName = ['', '1st', '', '', '', '5th', '', '', '', '9th'][yoga.trikonaHouse] || `${yoga.trikonaHouse}th`;
      
      const kLordPlacement = dasha.natalPlacements?.[yoga.kendraLord];
      const tLordPlacement = dasha.natalPlacements?.[yoga.trikonaLord];
      
      if (yoga.connection === 'trikona_lord_in_kendra' || yoga.connection === 'kendra_lord_in_trikona') {
        notes.push(
          `✨ ${yoga.trikonaLord} (${trikonaName} lord) in house ${tLordPlacement?.house || '?'} ` +
          `connects with ${yoga.kendraLord} (${kendraName} lord) in house ${kLordPlacement?.house || '?'}`
        );
      } else {
        notes.push(
          `✨ ${yoga.kendraLord} (${kendraName} lord) ${connectionLabels[yoga.connection] || yoga.connection} ` +
          `${yoga.trikonaLord} (${trikonaName} lord)`
        );
      }
    }
    
    notes.push(`PERMANENT BLESSING: These royal combinations exist in your birth chart`);
    notes.push(`NATURE: Kendra-trikona yogas bring success, recognition, and prosperity`);
    notes.push(`ACTIVATION: Results manifest during dashas of involved planets`);
    notes.push(`QUALITY: Conjunction > Exchange > Trikona-in-Kendra > Aspect`);
    
    // Score based on number and quality of yogas
    let totalScore = 40;
    for (const yoga of yogas) {
      if (yoga.connection === 'conjunction') totalScore += 7;
      else if (yoga.connection === 'exchange') totalScore += 6;
      else if (yoga.connection === 'trikona_lord_in_kendra') totalScore += 5;
      else if (yoga.connection === 'kendra_lord_in_trikona') totalScore += 4;
      else if (yoga.connection === 'aspect') totalScore += 3;
    }
    
    return { value: Math.min(totalScore, 65), notes };
  }
};

// 17a) Dharma-Karmadhipati Raj Yoga - NATAL
const dharmaKarmaRajYogaNatal: Rule = {
  key: 'dharma_karma_raj_yoga_natal',
  areas: ['Career', 'Wealth', 'Father', 'Spirituality'],
  templateKey: 'dharma_karma_raj_yoga_natal',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    return hasDharmaKarmaYoga(dasha).exists;
  },
  score: (dasha, transits) => {
    const yoga = hasDharmaKarmaYoga(dasha);
    const notes: string[] = [];
    
    notes.push(`NATAL RAJ YOGA: Dharma-Karmadhipati (9th-10th lords connection)`);
    notes.push(`9th Lords: ${yoga.lord9.join(', ')}`);
    notes.push(`10th Lords: ${yoga.lord10.join(', ')}`);
    notes.push(`PERMANENT FEATURE: This powerful combination exists in your birth chart`);
    notes.push(`ACTIVATION: Results manifest powerfully during dashas of ${[...yoga.lord9, ...yoga.lord10].join(', ')}`);
    notes.push(`NATURE: Royal yoga for career success, dharmic wealth, recognition, father's blessings`);
    
    return { value: 45, notes };
  }
};

// 17b) Dharma-Karmadhipati Raj Yoga - TIMING ACTIVATION
const dharmaKarmaRajYoga: Rule = {
  key: 'dharma_karma_raj_yoga',
  areas: ['Career', 'Wealth', 'Father', 'Spirituality'],
  templateKey: 'dharma_karma_raj_yoga',
  priority: 6,
  ruleType: 'timing',
  triggers: (dasha, transits) => {
    const yoga = hasDharmaKarmaYoga(dasha);
    if (!yoga.exists) return false;
    
    // Both lords must be active
    const allLords = [...yoga.lord9, ...yoga.lord10];
    const activePlanets = new Set(dasha.levels.map(l => l.planet));
    const lord9Active = yoga.lord9.some(p => activePlanets.has(p));
    const lord10Active = yoga.lord10.some(p => activePlanets.has(p));
    
    return lord9Active && lord10Active;
  },
  score: (dasha, transits) => {
    const yoga = hasDharmaKarmaYoga(dasha);
    const notes: string[] = [];
    
    const activeDashaLevels: string[] = [];
    const allLords = new Set([...yoga.lord9, ...yoga.lord10]);
    
    for (const level of dasha.levels) {
      if (allLords.has(level.planet)) {
        const type = yoga.lord9.includes(level.planet) ? '9th lord' : '10th lord';
        activeDashaLevels.push(`${level.level}: ${level.planet} (${type})`);
      }
    }
    
    notes.push(`👑 RAJ YOGA ACTIVATED: Dharma-Karmadhipati`);
    notes.push(`Active Dashas: ${activeDashaLevels.join(', ')}`);
    notes.push(`EFFECTS: Career elevation, recognition, dharmic wealth, father's blessings NOW`);
    notes.push(`TIMING: Major promotions, awards, spiritual+material balance, ethical success`);
    notes.push(`UTILIZE: Launch initiatives, seek elder blessings, align work with dharma`);
    
    return { value: 60, notes };
  }
};

// 18) Lakshmi Yoga (Venus + 9th lord in kendra/trikona)
const lakshmiYoga: Rule = {
  key: 'lakshmi_yoga',
  areas: ['Wealth', 'Spouse', 'Property'],
  templateKey: 'lakshmi_yoga_natal',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    // Use natal placements if available
    if (dasha.natalPlacements) {
      const venusHouse = dasha.natalPlacements.Venus?.house;
      if (!venusHouse) return false;
      
      const KENDRA_TRIKONA = new Set([1, 4, 5, 7, 9, 10]);
      const venusInAuspicious = KENDRA_TRIKONA.has(venusHouse);
      
      // Find 9th lord from natalPlacements
      const lord9th = new Set<PlanetName>();
      const allPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
      for (const planet of allPlanets) {
        const placement = dasha.natalPlacements[planet];
        if (placement?.rules?.includes(9)) {
          lord9th.add(planet);
        }
      }
      
      return venusInAuspicious && lord9th.size > 0;
    }
    
    // Fallback to old logic
    const venusLevel = dasha.levels.find(l => l.planet === 'Venus');
    if (!venusLevel) return false;
    
    const KENDRA_TRIKONA = new Set([1, 4, 5, 7, 9, 10]);
    const venusInAuspicious = KENDRA_TRIKONA.has(venusLevel.occupies);
    
    // Also check if 9th lord exists
    const lord9th = new Set<PlanetName>();
    for (const level of dasha.levels) {
      if (level.rules.includes(9)) lord9th.add(level.planet);
    }
    
    return venusInAuspicious && lord9th.size > 0;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const activePlanets = new Set(dasha.levels.map(l => l.planet));
    
    // Check if both Venus and 9th lord are active
    const isVenusActive = activePlanets.has('Venus');
    const lord9th = new Set<PlanetName>();
    for (const level of dasha.levels) {
      if (level.rules.includes(9)) lord9th.add(level.planet);
    }
    const lord9Active = dasha.levels.some(l => lord9th.has(l.planet));
    
    if (isVenusActive && lord9Active) {
      notes.push(`⚡ LAKSHMI YOGA ACTIVATED: Venus + 9th lord connection in auspicious houses`);
      notes.push(`EFFECTS NOW: Sudden wealth gains, luxuries, beautiful possessions, marital harmony`);
      notes.push(`MANIFESTATION: Liquid wealth, jewelry, vehicles, real estate, spouse's support`);
      notes.push(`TIMING: Excellent for investments, marriage, property purchase, luxury items`);
      notes.push(`GODDESS BLESSING: Worship Lakshmi on Fridays, wear white/pink, charity to women`);
      return { value: 62, notes };
    } else {
      notes.push(`NATAL LAKSHMI YOGA: Venus + 9th lord in auspicious houses`);
      notes.push(`PERMANENT BLESSING: This wealth yoga exists in your chart`);
      notes.push(`NATURE: Potential for prosperity, luxury, marital happiness, goddess grace`);
      notes.push(`ACTIVATION: Full manifestation during Venus and 9th lord dasha periods`);
      notes.push(`UTILIZE: Maintain ethical wealth practices, honor spouse, worship Lakshmi`);
      return { value: 45, notes };
    }
  }
};

// 19a) Gaja Kesari Yoga - NATAL
const gajaKesariYogaNatal: Rule = {
  key: 'gaja_kesari_yoga_natal',
  areas: ['Wealth', 'Career', 'Self', 'Children'],
  templateKey: 'gaja_kesari_yoga_natal',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (dasha.natalPlacements) {
      const jupiterHouse = dasha.natalPlacements.Jupiter?.house;
      const moonHouse = dasha.natalPlacements.Moon?.house;
      
      if (!jupiterHouse || !moonHouse) return false;
      
      // Check if they are in kendra (1, 4, 7, 10) from each other
      const diff = Math.abs(jupiterHouse - moonHouse);
      const isKendra = diff === 0 || diff === 3 || diff === 6 || diff === 9;
      
      return isKendra;
    }
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    notes.push(`NATAL GAJA KESARI YOGA: Jupiter-Moon in kendra relationship`);
    notes.push(`PERMANENT BLESSING: This royal yoga exists in your chart`);
    notes.push(`NATURE: Potential for wisdom, prosperity, leadership, popularity`);
    notes.push(`ACTIVATION: Full manifestation during Jupiter or Moon dasha periods`);
    notes.push(`UTILIZE: Cultivate wisdom, kindness, teaching abilities throughout life`);
    return { value: 45, notes };
  }
};

// 19) Gaja Kesari Yoga (Jupiter-Moon connection)
const gajaKesariYoga: Rule = {
  key: 'gaja_kesari_yoga',
  areas: ['Wealth', 'Career', 'Self', 'Children'],
  templateKey: 'gaja_kesari_yoga',
  priority: 6,
  ruleType: 'timing',
  triggers: (dasha, transits) => {
    // Use natal placements if available, otherwise fall back to dasha levels
    if (dasha.natalPlacements) {
      const jupiterHouse = dasha.natalPlacements.Jupiter?.house;
      const moonHouse = dasha.natalPlacements.Moon?.house;
      
      if (!jupiterHouse || !moonHouse) return false;
      
      // Check if they are in kendra (1, 4, 7, 10) from each other
      const diff = Math.abs(jupiterHouse - moonHouse);
      const isKendra = diff === 0 || diff === 3 || diff === 6 || diff === 9;
      
      return isKendra;
    }
    
    // Fallback to old logic (will only work if both planets are in dasha)
    const jupiterLevel = dasha.levels.find(l => l.planet === 'Jupiter');
    const moonLevel = dasha.levels.find(l => l.planet === 'Moon');
    
    if (!jupiterLevel || !moonLevel) return false;
    
    // Check if they are in kendra (1, 4, 7, 10) from each other
    const diff = Math.abs(jupiterLevel.occupies - moonLevel.occupies);
    const isKendra = diff === 0 || diff === 3 || diff === 6 || diff === 9;
    
    return isKendra;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const activePlanets = new Set(dasha.levels.map(l => l.planet));
    const isJupiterActive = activePlanets.has('Jupiter');
    const isMoonActive = activePlanets.has('Moon');
    
    if (isJupiterActive || isMoonActive) {
      notes.push(`⚡ GAJA KESARI YOGA ACTIVATED: Jupiter-Moon in kendra (elephant-lion combination)`);
      notes.push(`EFFECTS NOW: Tremendous wisdom, wealth, fame, righteous conduct, leadership`);
      notes.push(`CLASSIC POWER: Like elephant's strength + lion's courage combined`);
      notes.push(`MANIFESTATION: Respect in society, good reputation, advisor role, prosperity`);
      notes.push(`CHILDREN: Excellent for children's education, blessings, intelligent offspring`);
      notes.push(`UTILIZE: Public speaking, teaching, counseling, charitable work, investments`);
      return { value: 65, notes };
    } else {
      notes.push(`NATAL GAJA KESARI YOGA: Jupiter-Moon in kendra relationship`);
      notes.push(`PERMANENT BLESSING: This royal yoga exists in your chart`);
      notes.push(`NATURE: Potential for wisdom, prosperity, leadership, popularity`);
      notes.push(`ACTIVATION: Full manifestation during Jupiter or Moon dasha periods`);
      notes.push(`UTILIZE: Cultivate wisdom, kindness, teaching abilities throughout life`);
      return { value: 45, notes };
    }
  }
};

// 20) Adhi Yoga (Benefics in 6th, 7th, 8th from Moon)
const adhiYoga: Rule = {
  key: 'adhi_yoga',
  areas: ['Career', 'Wealth', 'Self'],
  templateKey: 'adhi_yoga',
  priority: 7,
  ruleType: 'timing',
  triggers: (dasha, transits) => {
    // Use natal placements if available
    if (dasha.natalPlacements) {
      const moonHouse = dasha.natalPlacements.Moon?.house;
      if (!moonHouse) return false;
      
      // Calculate 6th, 7th, 8th from Moon
      const house6 = ((moonHouse - 1 + 5) % 12) + 1;
      const house7 = ((moonHouse - 1 + 6) % 12) + 1;
      const house8 = ((moonHouse - 1 + 7) % 12) + 1;
      
      const targetHouses = new Set([house6, house7, house8]);
      
      // Check if benefics (Jupiter, Venus, Mercury) are in these houses
      const BENEFICS: PlanetName[] = ['Jupiter', 'Venus', 'Mercury'];
      let beneficCount = 0;
      
      for (const planet of BENEFICS) {
        const house = dasha.natalPlacements[planet]?.house;
        if (house && targetHouses.has(house)) {
          beneficCount++;
        }
      }
      
      // Yoga forms if at least 2 benefics in these houses
      return beneficCount >= 2;
    }
    
    // Fallback to old logic
    const moonLevel = dasha.levels.find(l => l.planet === 'Moon');
    if (!moonLevel) return false;
    
    const moonHouse = moonLevel.occupies;
    
    // Calculate 6th, 7th, 8th from Moon
    const house6 = ((moonHouse - 1 + 5) % 12) + 1;
    const house7 = ((moonHouse - 1 + 6) % 12) + 1;
    const house8 = ((moonHouse - 1 + 7) % 12) + 1;
    
    const targetHouses = new Set([house6, house7, house8]);
    
    // Check if benefics (Jupiter, Venus, Mercury) are in these houses
    const BENEFICS = new Set<PlanetName>(['Jupiter', 'Venus', 'Mercury']);
    const beneficsInTargets = dasha.levels.filter(l => 
      BENEFICS.has(l.planet) && targetHouses.has(l.occupies)
    );
    
    // Yoga forms if at least 2 benefics in these houses (always trigger if exists)
    return beneficsInTargets.length >= 2;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const activePlanets = new Set(dasha.levels.map(l => l.planet));
    
    // Find which benefics are in the yoga positions (use natalPlacements if available)
    let moonHouse: number;
    let beneficsInTargets: string[] = [];
    
    if (dasha.natalPlacements) {
      moonHouse = dasha.natalPlacements.Moon!.house;
      const house6 = ((moonHouse - 1 + 5) % 12) + 1;
      const house7 = ((moonHouse - 1 + 6) % 12) + 1;
      const house8 = ((moonHouse - 1 + 7) % 12) + 1;
      const targetHouses = new Set([house6, house7, house8]);
      
      const BENEFICS: PlanetName[] = ['Jupiter', 'Venus', 'Mercury'];
      for (const planet of BENEFICS) {
        const house = dasha.natalPlacements[planet]?.house;
        if (house && targetHouses.has(house)) {
          beneficsInTargets.push(planet);
        }
      }
    } else {
      const moonLevel = dasha.levels.find(l => l.planet === 'Moon');
      moonHouse = moonLevel!.occupies;
      const house6 = ((moonHouse - 1 + 5) % 12) + 1;
      const house7 = ((moonHouse - 1 + 6) % 12) + 1;
      const house8 = ((moonHouse - 1 + 7) % 12) + 1;
      const targetHouses = new Set([house6, house7, house8]);
      
      const BENEFICS = new Set<PlanetName>(['Jupiter', 'Venus', 'Mercury']);
      beneficsInTargets = dasha.levels.filter(l => 
        BENEFICS.has(l.planet) && targetHouses.has(l.occupies)
      ).map(l => l.planet);
    }
    
    const hasActiveBenefic = beneficsInTargets.some(b => activePlanets.has(b as PlanetName));
    
    if (hasActiveBenefic) {
      notes.push(`⚡ ADHI YOGA ACTIVATED: Multiple benefics in 6th/7th/8th from Moon`);
      notes.push(`BENEFICS: ${beneficsInTargets.join(', ')}`);
      notes.push(`EFFECTS NOW: Commander/leadership qualities, authority, political power possible`);
      notes.push(`CLASSIC: Born to rule - administrative abilities, decision-making prowess`);
      notes.push(`MANIFESTATION: Managerial roles, government positions, power, influence`);
      notes.push(`UTILIZE: Take leadership, make bold decisions, competitive exams, promotions`);
      return { value: 55, notes };
    } else {
      notes.push(`NATAL ADHI YOGA: Benefics positioned in 6th/7th/8th from Moon`);
      notes.push(`BENEFICS: ${beneficsInTargets.join(', ')}`);
      notes.push(`PERMANENT BLESSING: Leadership and authority yoga exists in your chart`);
      notes.push(`NATURE: Potential for command, administration, political success`);
      notes.push(`ACTIVATION: Full manifestation during benefic planet dasha periods`);
      notes.push(`UTILIZE: Develop leadership skills, administrative abilities throughout life`);
      return { value: 45, notes };
    }
  }
};

// 21a) Budhaditya Yoga - NATAL
const budhadityaYogaNatal: Rule = {
  key: 'budhaditya_yoga_natal',
  areas: ['Career', 'Self', 'Father'],
  templateKey: 'budhaditya_yoga_natal',
  priority: 8,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (dasha.natalPlacements) {
      const sunHouse = dasha.natalPlacements.Sun?.house;
      const mercuryHouse = dasha.natalPlacements.Mercury?.house;
      return sunHouse === mercuryHouse;
    }
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    notes.push(`NATAL BUDHADITYA YOGA: Sun-Mercury conjunction in your chart`);
    notes.push(`PERMANENT BLESSING: Intelligence combined with authority exists naturally`);
    notes.push(`NATURE: Potential for business, communication, intellectual pursuits`);
    notes.push(`ACTIVATION: Full manifestation during Sun or Mercury dasha periods`);
    notes.push(`UTILIZE: Develop writing, speaking, analytical skills throughout life`);
    return { value: 45, notes };
  }
};

// 21) Budhaditya Yoga (Sun-Mercury conjunction)
const budhadityaYoga: Rule = {
  key: 'budhaditya_yoga',
  areas: ['Career', 'Self', 'Father'],
  templateKey: 'budhaditya_yoga',
  priority: 8,
  ruleType: 'timing',
  triggers: (dasha, transits) => {
    // Use natal placements if available
    if (dasha.natalPlacements) {
      const sunHouse = dasha.natalPlacements.Sun?.house;
      const mercuryHouse = dasha.natalPlacements.Mercury?.house;
      
      if (!sunHouse || !mercuryHouse) return false;
      
      // Always trigger if conjunction exists
      return sunHouse === mercuryHouse;
    }
    
    // Fallback to old logic
    const sunLevel = dasha.levels.find(l => l.planet === 'Sun');
    const mercuryLevel = dasha.levels.find(l => l.planet === 'Mercury');
    
    if (!sunLevel || !mercuryLevel) return false;
    
    // Always trigger if conjunction exists
    return sunLevel.occupies === mercuryLevel.occupies;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const activePlanets = new Set(dasha.levels.map(l => l.planet));
    const isSunActive = activePlanets.has('Sun');
    const isMercuryActive = activePlanets.has('Mercury');
    
    if (isSunActive || isMercuryActive) {
      notes.push(`⚡ BUDHADITYA YOGA ACTIVATED: Sun-Mercury conjunction (Intelligence+Authority)`);
      notes.push(`EFFECTS NOW: Sharp intellect, business acumen, communication skills, wit`);
      notes.push(`MANIFESTATION: Success in writing, speaking, media, commerce, consultancy`);
      notes.push(`CAVEAT: If too close (<10°), Mercury combusts - delays possible initially`);
      notes.push(`UTILIZE: Start business, write books, teach, negotiate deals, public speaking`);
      return { value: 50, notes };
    } else {
      notes.push(`NATAL BUDHADITYA YOGA: Sun-Mercury conjunction in your chart`);
      notes.push(`PERMANENT BLESSING: Intelligence combined with authority exists naturally`);
      notes.push(`NATURE: Potential for business, communication, intellectual pursuits`);
      notes.push(`ACTIVATION: Full manifestation during Sun or Mercury dasha periods`);
      notes.push(`UTILIZE: Develop writing, speaking, analytical skills throughout life`);
      return { value: 45, notes };
    }
  }
};

// 22) Hamsa Yoga (Jupiter in kendra in own/exalted sign)
const hamsaYoga: Rule = {
  key: 'hamsa_yoga',
  areas: ['Spirituality', 'Wealth', 'Children', 'Career'],
  templateKey: 'hamsa_yoga',
  priority: 5,
  ruleType: 'timing', // Still timing type, but triggers always if yoga exists
  triggers: (dasha, transits) => {
    // Use natal placements if available
    if (dasha.natalPlacements) {
      const jupiterData = dasha.natalPlacements.Jupiter;
      if (!jupiterData?.house || jupiterData.sign === undefined) return false;
      
      const KENDRA = new Set([1, 4, 7, 10]);
      // Must be in kendra AND in own/exalted sign
      return KENDRA.has(jupiterData.house) && isInOwnOrExaltedSign('Jupiter', (jupiterData.sign - 1) % 12);
    }
    
    // Fallback to old logic (cannot check sign, so skip)
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const activePlanets = new Set(dasha.levels.map(l => l.planet));
    const isJupiterActive = activePlanets.has('Jupiter');
    
    if (isJupiterActive) {
      notes.push(`⚡ HAMSA YOGA ACTIVATED: Jupiter in kendra in own/exalted sign (Pancha Mahapurusha Yoga)`);
      notes.push(`EFFECTS NOW: Saintly character, righteousness, spiritual wisdom, respected elder`);
      notes.push(`MANIFESTATION: Teaching roles, guru status, advisory positions, religious authority`);
      notes.push(`VIRTUE: Charitable nature, ethical conduct, philosophical depth, good health`);
      notes.push(`UTILIZE: Spiritual practices, teaching, counseling, philanthropic work, investments`);
      return { value: 65, notes };
    } else {
      notes.push(`NATAL HAMSA YOGA: Jupiter in kendra in own/exalted sign (Pancha Mahapurusha Yoga)`);
      notes.push(`PERMANENT BLESSING: This sacred yoga exists in your chart`);
      notes.push(`NATURE: Potential for saintly conduct, wisdom, spiritual authority, respect`);
      notes.push(`ACTIVATION: Full manifestation during Jupiter dasha periods`);
      notes.push(`UTILIZE: Cultivate knowledge, ethics, teaching abilities throughout life`);
      return { value: 45, notes };
    }
  }
};

// 23) Shasha Yoga (Saturn in kendra in own/exalted sign)
const shashaYoga: Rule = {
  key: 'shasha_yoga',
  areas: ['Career', 'Property', 'Wealth'],
  templateKey: 'shasha_yoga',
  priority: 7,
  ruleType: 'timing',
  triggers: (dasha, transits) => {
    // Use natal placements if available
    if (dasha.natalPlacements) {
      const saturnData = dasha.natalPlacements.Saturn;
      if (!saturnData?.house || saturnData.sign === undefined) return false;
      
      const KENDRA = new Set([1, 4, 7, 10]);
      // Must be in kendra AND in own/exalted sign
      return KENDRA.has(saturnData.house) && isInOwnOrExaltedSign('Saturn', (saturnData.sign - 1) % 12);
    }
    
    // Fallback to old logic (cannot check sign, so skip)
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const activePlanets = new Set(dasha.levels.map(l => l.planet));
    const isSaturnActive = activePlanets.has('Saturn');
    
    if (isSaturnActive) {
      notes.push(`⚡ SHASHA YOGA ACTIVATED: Saturn in kendra in own/exalted sign (Pancha Mahapurusha Yoga)`);
      notes.push(`EFFECTS NOW: Administrative power, land/property gains, servants/workers under command`);
      notes.push(`MANIFESTATION: Government jobs, real estate success, mining, labor management`);
      notes.push(`CHARACTER: Disciplined, hard-working, organized, systematic, enduring`);
      notes.push(`UTILIZE: Real estate deals, long-term investments, infrastructure projects`);
      return { value: 55, notes };
    } else {
      notes.push(`NATAL SHASHA YOGA: Saturn in kendra in own/exalted sign (Pancha Mahapurusha Yoga)`);
      notes.push(`PERMANENT FEATURE: This powerful yoga exists in your chart`);
      notes.push(`NATURE: Potential for administrative success, property, disciplined leadership`);
      notes.push(`ACTIVATION: Full manifestation during Saturn dasha periods (19 years)`);
      notes.push(`UTILIZE: Build systematic work habits, invest in land/property long-term`);
      return { value: 45, notes };
    }
  }
};

// 24) Ruchaka Yoga (Mars in kendra in own/exalted sign)
const ruchakaYoga: Rule = {
  key: 'ruchaka_yoga',
  areas: ['Career', 'Health', 'Litigation'],
  templateKey: 'ruchaka_yoga',
  priority: 7,
  ruleType: 'timing',
  triggers: (dasha, transits) => {
    // Use natal placements if available
    if (dasha.natalPlacements) {
      const marsData = dasha.natalPlacements.Mars;
      if (!marsData?.house || marsData.sign === undefined) return false;
      
      const KENDRA = new Set([1, 4, 7, 10]);
      // Must be in kendra AND in own/exalted sign
      return KENDRA.has(marsData.house) && isInOwnOrExaltedSign('Mars', (marsData.sign - 1) % 12);
    }
    
    // Fallback to old logic (cannot check sign, so skip)
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const activePlanets = new Set(dasha.levels.map(l => l.planet));
    const isMarsActive = activePlanets.has('Mars');
    
    if (isMarsActive) {
      notes.push(`⚡ RUCHAKA YOGA ACTIVATED: Mars in kendra in own/exalted sign (Pancha Mahapurusha Yoga)`);
      notes.push(`EFFECTS NOW: Warrior qualities, courage, victory over enemies, athletic prowess`);
      notes.push(`MANIFESTATION: Military/police success, sports, surgery, real estate, engineering`);
      notes.push(`CHARACTER: Brave, competitive, aggressive achiever, strong physique`);
      notes.push(`UTILIZE: Competitive exams, sports, litigation, property disputes, leadership`);
      return { value: 56, notes };
    } else {
      notes.push(`NATAL RUCHAKA YOGA: Mars in kendra in own/exalted sign (Pancha Mahapurusha Yoga)`);
      notes.push(`PERMANENT FEATURE: This warrior yoga exists in your chart`);
      notes.push(`NATURE: Potential for courage, athletic ability, competitive success`);
      notes.push(`ACTIVATION: Full manifestation during Mars dasha periods (7 years)`);
      notes.push(`UTILIZE: Develop physical strength, competitive skills, courage throughout life`);
      return { value: 45, notes };
    }
  }
};

// 25a) Malavya Yoga - NATAL
const malavyaYogaNatal: Rule = {
  key: 'malavya_yoga_natal',
  areas: ['Wealth', 'Spouse', 'Property'],
  templateKey: 'malavya_yoga',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (dasha.natalPlacements) {
      const venusData = dasha.natalPlacements.Venus;
      if (!venusData?.house || venusData.sign === undefined) return false;
      
      const KENDRA = new Set([1, 4, 7, 10]);
      // Must be in kendra AND in own/exalted sign
      return KENDRA.has(venusData.house) && isInOwnOrExaltedSign('Venus', (venusData.sign - 1) % 12);
    }
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    notes.push(`NATAL MALAVYA YOGA: Venus in kendra in own/exalted sign (Pancha Mahapurusha Yoga)`);
    notes.push(`PERMANENT FEATURE: This artistic yoga exists in your chart`);
    notes.push(`NATURE: Potential for beauty, luxury, artistic talent, romantic success`);
    notes.push(`ACTIVATION: Full manifestation during Venus dasha periods (20 years)`);
    notes.push(`UTILIZE: Cultivate artistic skills, refinement, diplomacy throughout life`);
    return { value: 45, notes };
  }
};

// 25) Malavya Yoga (Venus in kendra in own/exalted sign)
const malavyaYoga: Rule = {
  key: 'malavya_yoga',
  areas: ['Wealth', 'Spouse', 'Property'],
  templateKey: 'malavya_yoga',
  priority: 7,
  ruleType: 'timing',
  triggers: (dasha, transits) => {
    // Use natal placements if available
    if (dasha.natalPlacements) {
      const venusData = dasha.natalPlacements.Venus;
      if (!venusData?.house || venusData.sign === undefined) return false;
      
      const KENDRA = new Set([1, 4, 7, 10]);
      // Must be in kendra AND in own/exalted sign
      return KENDRA.has(venusData.house) && isInOwnOrExaltedSign('Venus', (venusData.sign - 1) % 12);
    }
    
    // Fallback to old logic (cannot check sign, so skip)
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const activePlanets = new Set(dasha.levels.map(l => l.planet));
    const isVenusActive = activePlanets.has('Venus');
    
    if (isVenusActive) {
      notes.push(`⚡ MALAVYA YOGA ACTIVATED: Venus in kendra in own/exalted sign (Pancha Mahapurusha Yoga)`);
      notes.push(`EFFECTS NOW: Luxury, beauty, vehicles, fine arts, romantic success, wealth`);
      notes.push(`MANIFESTATION: Fashion, jewelry, entertainment, hospitality, luxury goods`);
      notes.push(`CHARACTER: Charming, artistic, sensual, diplomatic, refined taste`);
      notes.push(`UTILIZE: Creative ventures, marriage, luxury purchases, entertainment industry`);
      return { value: 58, notes };
    } else {
      notes.push(`NATAL MALAVYA YOGA: Venus in kendra in own/exalted sign (Pancha Mahapurusha Yoga)`);
      notes.push(`PERMANENT FEATURE: This artistic yoga exists in your chart`);
      notes.push(`NATURE: Potential for beauty, luxury, artistic talent, romantic success`);
      notes.push(`ACTIVATION: Full manifestation during Venus dasha periods (20 years)`);
      notes.push(`UTILIZE: Cultivate artistic skills, refinement, diplomacy throughout life`);
      return { value: 45, notes };
    }
  }
};

// 26) Bhadra Yoga (Mercury in kendra in own/exalted sign)
const bhadraYoga: Rule = {
  key: 'bhadra_yoga',
  areas: ['Career', 'Wealth', 'Children'],
  templateKey: 'bhadra_yoga',
  priority: 7,
  ruleType: 'timing',
  triggers: (dasha, transits) => {
    // Use natal placements if available
    if (dasha.natalPlacements) {
      const mercuryData = dasha.natalPlacements.Mercury;
      if (!mercuryData?.house || mercuryData.sign === undefined) return false;
      
      const KENDRA = new Set([1, 4, 7, 10]);
      // Must be in kendra AND in own/exalted sign
      return KENDRA.has(mercuryData.house) && isInOwnOrExaltedSign('Mercury', (mercuryData.sign - 1) % 12);
    }
    
    // Fallback to old logic (cannot check sign, so skip)
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const activePlanets = new Set(dasha.levels.map(l => l.planet));
    const isMercuryActive = activePlanets.has('Mercury');
    
    if (isMercuryActive) {
      notes.push(`⚡ BHADRA YOGA ACTIVATED: Mercury in kendra in own/exalted sign (Pancha Mahapurusha Yoga)`);
      notes.push(`EFFECTS NOW: Business success, eloquence, learning, mathematical ability, wit`);
      notes.push(`MANIFESTATION: Commerce, trade, writing, education, IT, consultancy, astrology`);
      notes.push(`CHARACTER: Intelligent, humorous, versatile, quick learner, good memory`);
      notes.push(`UTILIZE: Start business, write, teach, trade, invest in tech, competitive exams`);
      return { value: 57, notes };
    } else {
      notes.push(`NATAL BHADRA YOGA: Mercury in kendra in own/exalted sign (Pancha Mahapurusha Yoga)`);
      notes.push(`PERMANENT FEATURE: This intellectual yoga exists in your chart`);
      notes.push(`NATURE: Potential for business, communication, learning, versatility`);
      notes.push(`ACTIVATION: Full manifestation during Mercury dasha periods (17 years)`);
      notes.push(`UTILIZE: Develop communication, learning, business skills throughout life`);
      return { value: 45, notes };
    }
  }
};

// ===== FOREIGN SETTLEMENT YOGA =====
// Permanent natal condition: Rahu, Ketu, Sun, or Saturn in 12th house indicates foreign settlement potential
const foreignSettlementYoga: Rule = {
  key: 'foreign_settlement_yoga',
  areas: ['Travel', 'Career'],
  templateKey: 'foreign_settlement_yoga',
  priority: 5,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (dasha.natalPlacements) {
      const rahuHouse = dasha.natalPlacements.Rahu?.house;
      const ketuHouse = dasha.natalPlacements.Ketu?.house;
      const sunHouse = dasha.natalPlacements.Sun?.house;
      const saturnHouse = dasha.natalPlacements.Saturn?.house;
      
      // Check if any of these planets are in 12th house
      const has12thHousePlanet = rahuHouse === 12 || ketuHouse === 12 || sunHouse === 12 || saturnHouse === 12;
      
      return has12thHousePlanet;
    }
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const planets: string[] = [];
    
    if (dasha.natalPlacements) {
      if (dasha.natalPlacements.Rahu?.house === 12) planets.push('Rahu');
      if (dasha.natalPlacements.Ketu?.house === 12) planets.push('Ketu');
      if (dasha.natalPlacements.Sun?.house === 12) planets.push('Sun');
      if (dasha.natalPlacements.Saturn?.house === 12) planets.push('Saturn');
    }
    
    notes.push(`FOREIGN SETTLEMENT YOGA: ${planets.join(', ')} in 12th house (foreign lands)`);
    notes.push(`PERMANENT INDICATION: Strong potential for foreign settlement or significant foreign connections`);
    notes.push(`12TH HOUSE: House of foreign lands, distant places, residence abroad, international connections`);
    notes.push(`MANIFESTATION: Immigration, foreign employment, overseas education, international business, NRI status`);
    notes.push(`ACTIVATION: Results intensify during dasha periods of ${planets.join(' or ')}`);
    notes.push(`TIMING: Best opportunities for relocation during involved planet dashas`);
    notes.push(`UTILIZE: Explore international opportunities, learn foreign languages, build overseas networks`);
    
    return { value: 40, notes };
  }
};

// ===== COMBUSTION AFFLICTION =====
// Permanent natal condition: Combust planets cause lack of recognition for house matters
const combustionAffliction: Rule = {
  key: 'combustion_affliction',
  areas: [], // Will be dynamically set based on which planets are combust
  templateKey: 'combustion_affliction',
  priority: 4,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (dasha.combustionMap) {
      // Check if any planet is combust
      const combustPlanets = Object.entries(dasha.combustionMap).filter(([_, isCombust]) => isCombust);
      return combustPlanets.length > 0;
    }
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const combustDetails: string[] = [];
    const affectedAreas: Set<string> = new Set();
    
    if (dasha.combustionMap && dasha.natalPlacements) {
      // Identify all combust planets and their houses
      for (const [planet, isCombust] of Object.entries(dasha.combustionMap)) {
        if (isCombust && planet !== 'Sun') { // Sun can't be combust
          const planetName = planet as PlanetName;
          const house = dasha.natalPlacements[planetName]?.house;
          
          if (house) {
            combustDetails.push(`${planet} (combust in house ${house})`);
            
            // Map house to life areas
            const houseAreas = {
              1: ['Self', 'Health'],
              2: ['Wealth'],
              3: ['Self'],
              4: ['Property', 'Mother'],
              5: ['Children'],
              6: ['Health', 'Litigation'],
              7: ['Spouse'],
              8: ['Health', 'Spirituality'],
              9: ['Father', 'Spirituality', 'Travel'],
              10: ['Career'],
              11: ['Wealth'],
              12: ['Spirituality', 'Travel', 'Health']
            };
            
            const areas = houseAreas[house as keyof typeof houseAreas] || [];
            areas.forEach(area => affectedAreas.add(area));
          }
        }
      }
    }
    
    // If no combustion found, return minimal impact
    if (combustDetails.length === 0) {
      return { value: 0, notes: [] };
    }
    
    notes.push(`COMBUSTION AFFLICTION: ${combustDetails.join(', ')}`);
    notes.push(`PERMANENT CONDITION: Combust planets are too close to Sun (within 10-15 degrees)`);
    notes.push(`EFFECT: Efforts related to these houses/planets will NOT be acknowledged or recognized by others`);
    notes.push(`MANIFESTATION: Hard work without recognition, behind-the-scenes contributions, lack of visibility`);
    notes.push(`NOT A WEAKNESS: You have the ability but others may not see/appreciate it initially`);
    notes.push(`REMEDIES: Worship Sun (Surya mantras), Sunday charity, strengthen combust planets via gemstones`);
    notes.push(`TIMING: Recognition improves during friendly planet dashas or when combust planet exits combustion zone`);
    notes.push(`UTILIZE: Focus on substance over recognition, build long-term value, eventual acknowledgment will come`);
    
    return { value: -25, notes }; // Negative score as it's a challenge
  }
};

// ===== RAHU ALONE RAJ YOGA (VIPAREETA RAJ YOGA VARIANT) =====
// Permanent natal condition: Rahu sitting alone with no aspects can make person a king
const rahuAloneRajYoga: Rule = {
  key: 'rahu_alone_raj_yoga',
  areas: ['Career', 'Wealth', 'Self'],
  templateKey: 'rahu_alone_raj_yoga',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (dasha.natalPlacements) {
      const rahuHouse = dasha.natalPlacements.Rahu?.house;
      
      if (!rahuHouse) return false;
      
      // Check if any other planet is in the same house as Rahu (conjunction)
      let hasConjunction = false;
      for (const [planet, placement] of Object.entries(dasha.natalPlacements)) {
        if (planet !== 'Rahu' && placement?.house === rahuHouse) {
          hasConjunction = true;
          break;
        }
      }
      
      if (hasConjunction) return false; // Rahu is not alone
      
      // Check if any planet aspects the house where Rahu sits
      let hasAspect = false;
      for (const [planet, placement] of Object.entries(dasha.natalPlacements)) {
        if (planet !== 'Rahu' && placement?.aspects) {
          if (placement.aspects.includes(rahuHouse)) {
            hasAspect = true;
            break;
          }
        }
      }
      
      // Rahu is alone if no conjunction and no aspects
      return !hasAspect;
    }
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    if (dasha.natalPlacements) {
      const rahuHouse = dasha.natalPlacements.Rahu?.house;
      
      notes.push(`👑 RAHU ALONE RAJ YOGA: Rahu sits alone in house ${rahuHouse} with NO conjunctions or aspects`);
      notes.push(`PERMANENT BLESSING: This is a powerful royal combination—Rahu's undiluted energy manifests`);
      notes.push(`KING-MAKING YOGA: Potential to rise to positions of power, authority, and recognition`);
      notes.push(`RAHU'S NATURE: Ambition, innovation, breaking boundaries, material success, foreign connections`);
      notes.push(`MANIFESTATION: Sudden rise in status, unconventional path to success, modern/tech industries`);
      notes.push(`HOUSE ${rahuHouse} MATTERS: Rahu's isolated energy intensely focuses on this life area`);
      notes.push(`ACTIVATION: Full results manifest during Rahu Mahadasha (18 years) or Antardasha`);
      notes.push(`CHARACTER: Revolutionary thinking, fearless ambition, ability to achieve the impossible`);
      notes.push(`UTILIZE: Embrace innovation, take calculated risks, leverage technology, think globally`);
    }
    
    return { value: 55, notes }; // High value for this special raj yoga
  }
};

// ===== PRIVATE PREDICTIONS (Admin-only) =====

// Criminal mindset: Mercury or Mars with Rahu
const criminalMindsetRahuConjunction: Rule = {
  key: 'criminal_mindset_rahu_conjunction',
  areas: ['Self'],
  templateKey: 'criminal_mindset_rahu',
  priority: 8,
  ruleType: 'natal',
  private: true, // Admin-only visibility
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    
    const rahuHouse = dasha.natalPlacements.Rahu?.house;
    if (!rahuHouse) return false;
    
    // Check if Mercury or Mars is in same house as Rahu
    const mercuryHouse = dasha.natalPlacements.Mercury?.house;
    const marsHouse = dasha.natalPlacements.Mars?.house;
    
    return mercuryHouse === rahuHouse || marsHouse === rahuHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    if (dasha.natalPlacements) {
      const rahuHouse = dasha.natalPlacements.Rahu?.house;
      const mercuryHouse = dasha.natalPlacements.Mercury?.house;
      const marsHouse = dasha.natalPlacements.Mars?.house;
      
      if (mercuryHouse === rahuHouse) {
        notes.push(`⚠️ CRIMINAL TENDENCY INDICATOR: Mercury conjunct Rahu in house ${rahuHouse}`);
        notes.push(`PSYCHOLOGICAL PATTERN: Rahu amplifies Mercury's cunning and intellect toward deceptive means`);
        notes.push(`MANIFESTATION: Fraudulent schemes, white-collar crime, manipulation, unethical business practices`);
        notes.push(`MENTAL TRAITS: Sharp mind used for shortcuts, lying, forgery, scams, theft through intelligence`);
      }
      
      if (marsHouse === rahuHouse) {
        notes.push(`⚠️ CRIMINAL TENDENCY INDICATOR: Mars conjunct Rahu in house ${rahuHouse}`);
        notes.push(`PSYCHOLOGICAL PATTERN: Rahu amplifies Mars's aggression and violence toward criminal acts`);
        notes.push(`MANIFESTATION: Violent crimes, assault, robbery, gang activity, physical confrontations`);
        notes.push(`BEHAVIORAL TRAITS: Explosive anger, reckless courage, breaking laws through force`);
      }
      
      notes.push(`ACTIVATION PERIODS: Criminal tendencies most active during Rahu MD/AD or Mercury/Mars dashas`);
      notes.push(`IMPORTANT: This is a TENDENCY indicator, not a certainty. Upbringing, environment, free will matter.`);
      notes.push(`REMEDIES CRITICAL: Rahu mantra, charity, spiritual practices, ethical mentorship, counseling`);
      notes.push(`POSITIVE CHANNELING: Law enforcement, military, detective work, cybersecurity, ethical hacking`);
    }
    
    return { value: -75, notes }; // Severe negative indicator
  }
};

// Rapist mindset: Multiple malefics afflicting 8th house
const rapistMindset8thHouseAffliction: Rule = {
  key: 'rapist_mindset_8th_affliction',
  areas: ['Self'],
  templateKey: 'rapist_mindset_8th',
  priority: 8,
  ruleType: 'natal',
  private: true, // Admin-only visibility
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    
    // Count malefics (Sun, Mars, Saturn, Rahu, Ketu) associated with 8th house
    const malefics: PlanetName[] = ['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu'];
    let maleficCount = 0;
    const associations: string[] = [];
    
    for (const malefic of malefics) {
      const placement = dasha.natalPlacements[malefic];
      if (!placement) continue;
      
      // Check if malefic occupies 8th house
      if (placement.house === 8) {
        maleficCount++;
        associations.push(`${malefic} in 8th house`);
      }
      
      // Check if malefic aspects 8th house
      if (placement.aspects && placement.aspects.includes(8)) {
        maleficCount++;
        associations.push(`${malefic} aspects 8th house`);
      }
      
      // Check if malefic rules 8th house
      if (placement.rules && placement.rules.includes(8)) {
        maleficCount++;
        associations.push(`${malefic} rules 8th house`);
      }
    }
    
    // Trigger if 3 or more malefic associations with 8th house
    return maleficCount >= 3;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    if (dasha.natalPlacements) {
      const malefics: PlanetName[] = ['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu'];
      const associations: string[] = [];
      
      for (const malefic of malefics) {
        const placement = dasha.natalPlacements[malefic];
        if (!placement) continue;
        
        if (placement.house === 8) associations.push(`${malefic} in 8th`);
        if (placement.aspects && placement.aspects.includes(8)) associations.push(`${malefic} aspects 8th`);
        if (placement.rules && placement.rules.includes(8)) associations.push(`${malefic} rules 8th`);
      }
      
      notes.push(`⚠️ SEVERE CRIMINAL TENDENCY: Multiple malefic afflictions to 8th house detected`);
      notes.push(`AFFLICTIONS FOUND: ${associations.join(', ')}`);
      notes.push(`PSYCHOLOGICAL PATTERN: 8th house (taboo, secrets, sexual desires) heavily corrupted by malefics`);
      notes.push(`MANIFESTATION: Sexual violence, power & control through sexual assault, dark sexual compulsions`);
      notes.push(`KARMIC INDICATION: Deep psychological disturbance, sadistic tendencies, lack of sexual boundaries`);
      notes.push(`ACTIVATION PERIODS: Most dangerous during dashas of afflicting planets (Mars, Saturn, Rahu particularly)`);
      notes.push(`IMPORTANT: This is a SEVERE tendency indicator requiring immediate intervention if patterns emerge`);
      notes.push(`REMEDIES ESSENTIAL: Psychiatric counseling, strict ethical discipline, spiritual transformation required`);
      notes.push(`8TH HOUSE: Represents hidden desires, taboos, transformation—when heavily afflicted, darkest impulses manifest`);
      notes.push(`MONITORING: Close observation during activation periods, preventive intervention critical`);
    }
    
    return { value: -90, notes }; // Most severe negative indicator
  }
};

// ===== NEECH BHANG RAJ YOGA (Debilitation Cancellation) =====

// Debilitation signs (rashi 0-11 where planets are debilitated)
const DEBIL_SIGNS: Partial<Record<PlanetName, number>> = {
  Sun: 6,      // Libra
  Moon: 7,     // Scorpio
  Mars: 3,     // Cancer
  Mercury: 11, // Pisces
  Jupiter: 9,  // Capricorn
  Venus: 5,    // Virgo
  Saturn: 0    // Aries
};

// Exaltation signs (rashi 0-11 where planets are exalted)
const EXALT_SIGNS: Partial<Record<PlanetName, number>> = {
  Sun: 0,      // Aries
  Moon: 1,     // Taurus
  Mars: 9,     // Capricorn
  Mercury: 5,  // Virgo
  Jupiter: 3,  // Cancer
  Venus: 11,   // Pisces
  Saturn: 6    // Libra
};

// Rashi lords (which planet rules each sign 0-11)
const RASHI_LORDS: PlanetName[] = [
  'Mars',    // 0: Aries
  'Venus',   // 1: Taurus
  'Mercury', // 2: Gemini
  'Moon',    // 3: Cancer
  'Sun',     // 4: Leo
  'Mercury', // 5: Virgo
  'Venus',   // 6: Libra
  'Mars',    // 7: Scorpio
  'Jupiter', // 8: Sagittarius
  'Saturn',  // 9: Capricorn
  'Saturn',  // 10: Aquarius
  'Jupiter'  // 11: Pisces
];

// Helper: Check if a planet is debilitated in the chart
function isDebilitated(planet: PlanetName, sign: number): boolean {
  return DEBIL_SIGNS[planet] === sign;
}

// Helper: Check if planet is in own sign or exalted
function isInOwnOrExaltedSign(planet: PlanetName, sign: number): boolean {
  // Check exaltation
  if (EXALT_SIGNS[planet] === sign) return true;
  
  // Check own signs
  const ownSigns = OWN_SIGNS[planet];
  if (ownSigns && ownSigns.includes(sign)) return true;
  
  return false;
}

// Helper: Get sign from natal placements
// Sign is stored as 1-12 in natalPlacements.sign
function getPlanetSign(planet: PlanetName, natalPlacements: any): number | null {
  const sign = natalPlacements[planet]?.sign;
  if (sign === undefined || sign === null) return null;
  
  // Convert to 0-indexed for RASHI_LORDS lookup
  return (sign - 1) % 12;
}

// Neech Bhang Rule 1: Debilitated planet aspected/conjunct by its dispositor
const neechBhangDispositor: Rule = {
  key: 'neech_bhang_dispositor',
  areas: ['Career', 'Wealth', 'Self'],
  templateKey: 'neech_bhang_raj_yoga',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    
    const sevenPlanets: PlanetName[] = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'];
    
    for (const planet of sevenPlanets) {
      const planetData = dasha.natalPlacements[planet];
      if (!planetData) continue;
      
      // Check if planet is actually debilitated in natal chart
      if (planetData.debilitated !== true) continue;
      
      // Get planet's sign to find dispositor
      const planetSign = getPlanetSign(planet, dasha.natalPlacements);
      if (planetSign === null) continue;
      
      // Get dispositor (lord of debilitation sign)
      const dispositor = RASHI_LORDS[planetSign];
      
      // Check conjunction (same house) or aspect
      const planetHouse = planetData.house;
      const dispositorHouse = dasha.natalPlacements[dispositor]?.house;
      const dispositorAspects = dasha.natalPlacements[dispositor]?.aspects || [];
      
      if (planetHouse === dispositorHouse || dispositorAspects.includes(planetHouse as any)) {
        return true; // Neech Bhang triggered
      }
    }
    
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const cancelledPlanets: string[] = [];
    
    if (dasha.natalPlacements) {
      const sevenPlanets: PlanetName[] = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'];
      
      for (const planet of sevenPlanets) {
        const planetData = dasha.natalPlacements[planet];
        if (!planetData || planetData.debilitated !== true) continue;
        
        const planetSign = getPlanetSign(planet, dasha.natalPlacements);
        if (planetSign === null) continue;
        
        const dispositor = RASHI_LORDS[planetSign];
        const planetHouse = planetData.house;
        const dispositorHouse = dasha.natalPlacements[dispositor]?.house;
        const dispositorAspects = dasha.natalPlacements[dispositor]?.aspects || [];
        
        if (planetHouse === dispositorHouse) {
          cancelledPlanets.push(`${planet} (conjunct ${dispositor})`);
        } else if (dispositorAspects.includes(planetHouse as any)) {
          cancelledPlanets.push(`${planet} (aspected by ${dispositor})`);
        }
      }
      
      notes.push(`👑 NEECH BHANG RAJ YOGA - RULE 1 (${cancelledPlanets.map(p => p.split(' ')[0]).join(', ')}): Debilitation cancelled by dispositor`);
      notes.push(`CANCELLED DEBILITATIONS: ${cancelledPlanets.join(', ')}`);
      notes.push(`PRINCIPLE: When debilitated planet is conjunct/aspected by its dispositor (sign lord), debilitation is cancelled`);
      notes.push(`TRANSFORMATION: Weakness becomes strength - what was deficit becomes asset`);
      notes.push(`MANIFESTATION: Rise from adversity, overcoming obstacles through unexpected support, hidden blessings`);
      notes.push(`ACTIVATION: Full benefits during dasha/antardasha of the cancelled planet(s): ${cancelledPlanets.map(p => p.split(' ')[0]).join(', ')}`);
      notes.push(`NATURE: Royal yoga - turns debility into dignity, produces leaders who understand struggle`);
      notes.push(`UTILIZE: Leverage the houses ruled/occupied by cancelled planets during their dasha periods`);
    }
    
    return { value: 60, notes }; // Strong raj yoga
  }
};

// Neech Bhang Rule 2: Debilitated planet aspected/conjunct by exaltation lord
const neechBhangExaltationLord: Rule = {
  key: 'neech_bhang_exaltation_lord',
  areas: ['Career', 'Wealth', 'Self'],
  templateKey: 'neech_bhang_raj_yoga',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    
    const sevenPlanets: PlanetName[] = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'];
    
    for (const planet of sevenPlanets) {
      const planetData = dasha.natalPlacements[planet];
      if (!planetData || planetData.debilitated !== true) continue;
      
      // Get lord of exaltation sign
      const exaltSign = EXALT_SIGNS[planet];
      if (exaltSign === undefined) continue;
      
      const exaltLord = RASHI_LORDS[exaltSign];
      
      // CRITICAL FIX: Skip if planet is its own exaltation lord (e.g., Mercury exalts in Virgo ruled by Mercury)
      if (planet === exaltLord) continue;
      
      // Check conjunction (same house) or aspect
      const planetHouse = planetData.house;
      const exaltLordHouse = dasha.natalPlacements[exaltLord]?.house;
      const exaltLordAspects = dasha.natalPlacements[exaltLord]?.aspects || [];
      
      if (planetHouse === exaltLordHouse || exaltLordAspects.includes(planetHouse as any)) {
        return true; // Neech Bhang triggered
      }
    }
    
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const cancelledPlanets: string[] = [];
    
    if (dasha.natalPlacements) {
      const sevenPlanets: PlanetName[] = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'];
      
      for (const planet of sevenPlanets) {
        const planetData = dasha.natalPlacements[planet];
        if (!planetData || planetData.debilitated !== true) continue;
        
        const exaltSign = EXALT_SIGNS[planet];
        if (exaltSign === undefined) continue;
        
        const exaltLord = RASHI_LORDS[exaltSign];
        
        // CRITICAL FIX: Skip if planet is its own exaltation lord
        if (planet === exaltLord) continue;
        
        const planetHouse = planetData.house;
        const exaltLordHouse = dasha.natalPlacements[exaltLord]?.house;
        const exaltLordAspects = dasha.natalPlacements[exaltLord]?.aspects || [];
        
        if (planetHouse === exaltLordHouse) {
          cancelledPlanets.push(`${planet} (conjunct ${exaltLord})`);
        } else if (exaltLordAspects.includes(planetHouse as any)) {
          cancelledPlanets.push(`${planet} (aspected by ${exaltLord})`);
        }
      }
      
      notes.push(`👑 NEECH BHANG RAJ YOGA - RULE 2 (${cancelledPlanets.map(p => p.split(' ')[0]).join(', ')}): Debilitation cancelled by exaltation lord`);
      notes.push(`CANCELLED DEBILITATIONS: ${cancelledPlanets.join(', ')}`);
      notes.push(`PRINCIPLE: When debilitated planet is conjunct/aspected by lord of its exaltation sign, creates powerful Raj Yoga`);
      notes.push(`SPECIAL: This is stronger than Rule 1 - exaltation energy directly empowers the debilitated planet`);
      notes.push(`TRANSFORMATION: Complete reversal - profound weakness becomes extraordinary strength`);
      notes.push(`MANIFESTATION: Meteoric rise, spectacular success from humble beginnings, legendary achievements`);
      notes.push(`ACTIVATION: Peak results during dasha/antardasha of cancelled planet(s): ${cancelledPlanets.map(p => p.split(' ')[0]).join(', ')}`);
      notes.push(`NATURE: Supreme royal yoga - native rises to positions of power and influence despite early struggles`);
      notes.push(`CHARACTER: Resilience personified - those who fall and rise become unstoppable`);
    }
    
    return { value: 70, notes }; // Very strong raj yoga
  }
};

// Neech Bhang Rule 3: Two debilitated planets aspect each other
const neechBhangMutualAspect: Rule = {
  key: 'neech_bhang_mutual_aspect',
  areas: ['Career', 'Wealth', 'Self'],
  templateKey: 'neech_bhang_raj_yoga',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    
    const sevenPlanets: PlanetName[] = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'];
    const debilitatedPlanets: Array<{planet: PlanetName; house: number; aspects: number[]}> = [];
    
    // Find all debilitated planets (must be actually debilitated in natal chart)
    for (const planet of sevenPlanets) {
      const planetData = dasha.natalPlacements[planet];
      if (!planetData || planetData.debilitated !== true) continue;
      
      const house = planetData.house;
      const aspects = planetData.aspects || [];
      if (house) {
        debilitatedPlanets.push({ planet, house, aspects });
      }
    }
    
    // Check if any two debilitated planets aspect each other
    for (let i = 0; i < debilitatedPlanets.length; i++) {
      for (let j = i + 1; j < debilitatedPlanets.length; j++) {
        const p1 = debilitatedPlanets[i];
        const p2 = debilitatedPlanets[j];
        
        // Check if p1 aspects p2's house OR p2 aspects p1's house
        if (p1.aspects.includes(p2.house) || p2.aspects.includes(p1.house)) {
          return true; // Mutual aspect found
        }
      }
    }
    
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const mutualPairs: string[] = [];
    
    if (dasha.natalPlacements) {
      const sevenPlanets: PlanetName[] = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'];
      const debilitatedPlanets: Array<{planet: PlanetName; house: number; aspects: number[]}> = [];
      
      for (const planet of sevenPlanets) {
        const planetData = dasha.natalPlacements[planet];
        if (!planetData || planetData.debilitated !== true) continue;
        
        const house = planetData.house;
        const aspects = planetData.aspects || [];
        if (house) {
          debilitatedPlanets.push({ planet, house, aspects });
        }
      }
      
      for (let i = 0; i < debilitatedPlanets.length; i++) {
        for (let j = i + 1; j < debilitatedPlanets.length; j++) {
          const p1 = debilitatedPlanets[i];
          const p2 = debilitatedPlanets[j];
          
          if (p1.aspects.includes(p2.house) || p2.aspects.includes(p1.house)) {
            mutualPairs.push(`${p1.planet} ↔ ${p2.planet}`);
          }
        }
      }
      
      notes.push(`👑 NEECH BHANG RAJ YOGA - RULE 3 (${mutualPairs.join(', ').replace(' ↔ ', ' & ')}): Mutual debilitation cancellation`);
      notes.push(`MUTUAL ASPECT PAIRS: ${mutualPairs.join(', ')}`);
      notes.push(`PRINCIPLE: When two debilitated planets aspect each other, BOTH debilitations are cancelled`);
      notes.push(`ALCHEMY: Two weaknesses create mutual strength - partnership of vulnerabilities becomes power`);
      notes.push(`TRANSFORMATION: Shared struggle creates unbreakable bond - combined deficit becomes surplus`);
      notes.push(`MANIFESTATION: Success through partnerships, collaborative victories, strength in unity`);
      notes.push(`ACTIVATION: Benefits flow during dashas of BOTH planets - synergistic periods of growth`);
      notes.push(`NATURE: Unique raj yoga - demonstrates power of mutual support and collective resilience`);
      notes.push(`CHARACTER: Native excels in teamwork, coalition-building, turning competitors into allies`);
    }
    
    return { value: 65, notes }; // Strong raj yoga
  }
};

// Neech Bhang Rule 4: Dispositor of debilitated planet in kendra from lagna
const neechBhangDispositorInKendra: Rule = {
  key: 'neech_bhang_dispositor_kendra',
  areas: ['Career', 'Wealth', 'Self'],
  templateKey: 'neech_bhang_raj_yoga',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    
    const kendraHouses = [1, 4, 7, 10]; // Angular houses from lagna
    const sevenPlanets: PlanetName[] = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'];
    
    for (const planet of sevenPlanets) {
      const planetData = dasha.natalPlacements[planet];
      if (!planetData || planetData.debilitated !== true) continue;
      
      // Get planet's sign to find dispositor
      const planetSign = getPlanetSign(planet, dasha.natalPlacements);
      if (planetSign === null) continue;
      
      // Get dispositor (lord of debilitation sign)
      const dispositor = RASHI_LORDS[planetSign];
      const dispositorHouse = dasha.natalPlacements[dispositor]?.house;
      
      // Check if dispositor is in kendra
      if (dispositorHouse && kendraHouses.includes(dispositorHouse)) {
        return true; // Neech Bhang triggered
      }
    }
    
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const cancelledPlanets: string[] = [];
    
    if (dasha.natalPlacements) {
      const kendraHouses = [1, 4, 7, 10];
      const sevenPlanets: PlanetName[] = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn'];
      
      for (const planet of sevenPlanets) {
        const planetData = dasha.natalPlacements[planet];
        if (!planetData || planetData.debilitated !== true) continue;
        
        const planetSign = getPlanetSign(planet, dasha.natalPlacements);
        if (planetSign === null) continue;
        
        const dispositor = RASHI_LORDS[planetSign];
        const dispositorHouse = dasha.natalPlacements[dispositor]?.house;
        
        if (dispositorHouse && kendraHouses.includes(dispositorHouse)) {
          cancelledPlanets.push(`${planet} (dispositor ${dispositor} in kendra house ${dispositorHouse})`);
        }
      }
      
      notes.push(`👑 NEECH BHANG RAJ YOGA - RULE 4 (${cancelledPlanets.map(p => p.split(' ')[0]).join(', ')}): Debilitation cancelled by dispositor in kendra`);
      notes.push(`CANCELLED DEBILITATIONS: ${cancelledPlanets.join(', ')}`);
      notes.push(`PRINCIPLE: When dispositor of debilitated planet sits in kendra (1,4,7,10) from lagna, debilitation is cancelled`);
      notes.push(`KENDRA POWER: Angular houses are pillars of chart - dispositor here provides foundational strength`);
      notes.push(`TRANSFORMATION: Structural support converts weakness into capability - foundation lifts fallen planet`);
      notes.push(`MANIFESTATION: Institutional success, organizational achievements, recognized authority in field`);
      notes.push(`ACTIVATION: Peak benefits during dasha/antardasha of cancelled planet(s): ${cancelledPlanets.map(p => p.split(' ')[0]).join(', ')}`);
      notes.push(`NATURE: Stable raj yoga - long-lasting success built on solid foundations`);
      notes.push(`CAREER: Excellent for leadership roles, administrative positions, structured organizations`);
      notes.push(`UTILIZE: Plan major initiatives during dashas of cancelled planets - success is guaranteed through systematic effort`);
    }
    
    return { value: 65, notes }; // Strong raj yoga
  }
};

// ===== SATURN ANALYSIS RULES =====

// Saturn with Ketu position analysis
const saturnKetuPositionAnalysis: Rule = {
  key: 'saturn_ketu_position',
  areas: ['Self', 'Career', 'Wealth', 'Health'],
  templateKey: 'saturn_ketu_analysis',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    
    const ketuHouse = dasha.natalPlacements.Ketu?.house;
    if (!ketuHouse) return false;
    
    // Trigger if Ketu is in any house (we'll determine good/bad in score function)
    return true;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    if (dasha.natalPlacements) {
      const ketuHouse = dasha.natalPlacements.Ketu?.house;
      const saturnHouse = dasha.natalPlacements.Saturn?.house;
      
      // Good Saturn: Ketu in houses 1-6
      if (ketuHouse && ketuHouse >= 1 && ketuHouse <= 6) {
        notes.push(`⭐ GOOD SATURN: Ketu positioned in house ${ketuHouse} (1st-6th houses)`);
        notes.push(`SATURN HOUSE: ${saturnHouse}`);
        notes.push(`🌟 EXCEPTIONAL CHARACTER TRAITS:`);
        notes.push(`• DISCIPLINE: Disciplined, responsible and reliable individual`);
        notes.push(`• DEDICATION: Approach any tasks with complete dedication & perseverance`);
        notes.push(`• RESILIENCE: Face tough situations with composure and fortitude - highly resilient, never give up`);
        notes.push(`• WISDOM: Mature and wise beyond years, make sound decisions with practical approach`);
        notes.push(`• WORK ETHIC: Believe in attaining success through hard work - no shortcuts`);
        notes.push(`• RESPECT: Honor traditions and respect figures of authority`);
        notes.push(`• SOCIAL RESPONSIBILITY: Strong sense of duty towards common masses, not just family`);
        notes.push(`• FINANCIAL ACUMEN: Manage resources wisely, financially stable through prudent planning`);
        notes.push(`• SPIRITUAL INCLINATION: Strong interest in philosophy and spiritual practices`);
        notes.push(`MANIFESTATION: Natural leaders, trusted advisors, pillars of community, dependable in crisis`);
        notes.push(`CAREER: Excel in positions requiring discipline, long-term planning, administrative skills`);
        notes.push(`UTILIZE: Channel Saturn's energy into building lasting foundations, institutions, legacy projects`);
        
        return { value: 70, notes, templateKey: 'saturn_ketu_good' }; // Very positive
      }
      
      // Bad Saturn: Ketu in houses 7-12
      if (ketuHouse && ketuHouse >= 7 && ketuHouse <= 12) {
        notes.push(`⚠️ CHALLENGING SATURN: Ketu positioned in house ${ketuHouse} (7th-12th houses)`);
        notes.push(`SATURN HOUSE: ${saturnHouse}`);
        notes.push(`⚠️ LIFE CHALLENGES:`);
        notes.push(`• SETBACKS: Frequent setbacks and delays in life goals - patience required`);
        notes.push(`• BURDEN: Overwhelming and burdening obligations - feels weight of responsibilities`);
        notes.push(`• ANXIETY: Self-doubt, anxiety & insecurity about the future - fear of failure`);
        notes.push(`• STRUGGLES: Difficulties in career advancement, financial instability, relationship problems`);
        notes.push(`• AUTHORITY ISSUES: Problems from authoritarian figures - bosses, government, father`);
        notes.push(`• ISOLATION: Lack of connection with people leading to loneliness and depression`);
        notes.push(`• HEALTH: Prone to health issues & chronic ailments - especially bones, joints, teeth`);
        notes.push(`KARMIC LESSON: Saturn tests through hardship to build unshakeable inner strength`);
        notes.push(`REMEDIES CRITICAL: Saturday Shani worship, blue sapphire (after consultation), charity to poor/elderly/disabled, mustard oil lamp, iron ring, help laborers`);
        notes.push(`PSYCHOLOGICAL: Counseling recommended - manage anxiety, build support network, avoid isolation`);
        notes.push(`TIMELINE: Challenges peak during Saturn Mahadasha/Antardasha - prepare with discipline & remedies`);
        notes.push(`POSITIVE: Hardships create profound wisdom - you become guide for others facing struggles`);
        
        return { value: -60, notes, templateKey: 'saturn_ketu_challenging' }; // Challenging but manageable with remedies
      }
    }
    
    return { value: 0, notes: ['Saturn-Ketu analysis requires complete natal data'] };
  }
};

// Saturn conjunction analysis - all key combinations
// Saturn conjunction with Sun
const saturnSunConjunction: Rule = {
  key: 'saturn_sun_conjunction',
  areas: ['Career', 'Self'],
  templateKey: 'saturn_sun_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const saturnHouse = dasha.natalPlacements.Saturn?.house;
    const sunHouse = dasha.natalPlacements.Sun?.house;
    return saturnHouse !== undefined && saturnHouse === sunHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    notes.push(`☀️ SATURN + SUN CONJUNCTION:`);
    notes.push(`• HOUSE FEATURE: Direct sunlight enters house from southern direction`);
    notes.push(`• ARCHITECTURE: Big iron-clad windows in southern direction possible`);
    notes.push(`• GOVERNMENT: Good connections in government departments and bureaucracy`);
    notes.push(`• AUTHORITY: Relationship with father figure may be strained but respectful`);
    notes.push(`• CAREER: Success in government service, administration, public sector`);
    notes.push(`• TIMING: Benefits peak during Sun or Saturn dasha periods`);
    return { value: 20, notes };
  }
};

// Saturn conjunction with Mars
const saturnMarsConjunction: Rule = {
  key: 'saturn_mars_conjunction',
  areas: ['Career', 'Health'],
  templateKey: 'saturn_mars_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const saturnHouse = dasha.natalPlacements.Saturn?.house;
    const marsHouse = dasha.natalPlacements.Mars?.house;
    return saturnHouse !== undefined && saturnHouse === marsHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    notes.push(`⚔️ SATURN + MARS CONJUNCTION:`);
    notes.push(`• WORKPLACE: Can cause fights and conflicts at workplace - ego clashes`);
    notes.push(`• TECHNICAL WORK: Problem avoided if involved in technical/engineering work`);
    notes.push(`• HEALTH: Prone to chronic disease - especially related to bones, muscles, injuries`);
    notes.push(`• ARMED FORCES: Excellent for service in military, police, security, discipline-oriented careers`);
    notes.push(`• DISCIPLINE: Extreme discipline meets extreme drive - can be workaholic`);
    notes.push(`• REMEDIES: Mars mantra, Hanuman worship to reduce conflict energy`);
    notes.push(`• UTILIZE: Channel energy into structured physical work, sports, martial arts`);
    return { value: -15, notes };
  }
};

// Saturn conjunction with Rahu
const saturnRahuConjunction: Rule = {
  key: 'saturn_rahu_conjunction',
  areas: ['Career', 'Spirituality'],
  templateKey: 'saturn_rahu_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const saturnHouse = dasha.natalPlacements.Saturn?.house;
    const rahuHouse = dasha.natalPlacements.Rahu?.house;
    return saturnHouse !== undefined && saturnHouse === rahuHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    notes.push(`🌑 SATURN + RAHU CONJUNCTION (Shadow Planet Effect):`);
    notes.push(`• FOREIGN LANDS: Should move to foreign country for maximum success - emigration highly favorable`);
    notes.push(`• UNORTHODOX CAREER: Choose unconventional work - acting, movies, YouTube, social media, content creation`);
    notes.push(`• SPIRITUALITY: Can attain higher levels of spirituality - mystical inclinations`);
    notes.push(`• OCCULT: Take up work related to astrology, occult sciences, alternative healing`);
    notes.push(`• MAGICAL POWERS: Can attain special siddhis (spiritual powers) through disciplined practice`);
    notes.push(`• INNOVATION: Success in cutting-edge technology, AI, blockchain, cryptocurrency`);
    notes.push(`• TIMING: Life transforms during Rahu or Saturn Mahadasha - embrace unconventional path`);
    notes.push(`• INTERNATIONAL: Best results abroad or in international companies`);
    
    // Context-aware scoring based on house placement, functional classification, and planet strength
    let baseScore = 40; // Base positive score for opportunities
    
    if (dasha.natalPlacements) {
      const saturnHouse = dasha.natalPlacements.Saturn?.house;
      
      // Factor 1: House placement (most important for manifestation)
      if (saturnHouse === 6 || saturnHouse === 8 || saturnHouse === 12) {
        baseScore = 18; // Dusthana - opportunities exist but with obstacles
        notes.push(`• PLACEMENT: Conjunction in dusthana house (${saturnHouse}th) - opportunities require overcoming obstacles`);
      } else if (saturnHouse === 1 || saturnHouse === 4 || saturnHouse === 5 || saturnHouse === 7 || saturnHouse === 9 || saturnHouse === 10) {
        notes.push(`• PLACEMENT: ${saturnHouse === 1 || saturnHouse === 4 || saturnHouse === 7 || saturnHouse === 10 ? 'Kendra' : 'Trikona'} house (${saturnHouse}th) - powerful manifestation`);
      }
      
      // Factor 2: Functional classification (affects ease of manifestation)
      if (dasha.functional?.Saturn) {
        const saturnFunc = dasha.functional.Saturn;
        if (saturnFunc === 'malefic') {
          baseScore = Math.round(baseScore * 0.7); // 40→28, 18→13
          notes.push(`• FUNCTIONAL: Saturn is malefic for your lagna - requires discipline and effort`);
        } else if (saturnFunc === 'benefic') {
          baseScore = Math.round(baseScore * 1.15); // 40→46, 18→21
          notes.push(`• FUNCTIONAL: Saturn is benefic for your lagna - opportunities flow easier`);
        }
      }
      
      // Factor 3: Planet strength (affects power of results)
      if (dasha.blendedPct) {
        const saturnStrength = dasha.blendedPct.Saturn ?? 0;
        const rahuStrength = dasha.blendedPct.Rahu ?? 0;
        const avgStrength = (saturnStrength + rahuStrength) / 2;
        
        if (avgStrength < -30) {
          baseScore = Math.round(baseScore * 0.75); // Weak planets
          notes.push(`• STRENGTH: Both planets weak (${avgStrength.toFixed(0)}%) - results take longer to manifest`);
        } else if (avgStrength > 30) {
          baseScore = Math.round(baseScore * 1.2); // Strong planets
          notes.push(`• STRENGTH: Both planets strong (${avgStrength.toFixed(0)}%) - powerful results`);
        }
      }
    }
    
    return { value: baseScore, notes };
  }
};

// Saturn conjunction with Venus
const saturnVenusConjunction: Rule = {
  key: 'saturn_venus_conjunction',
  areas: ['Wealth', 'Career'],
  templateKey: 'saturn_venus_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const saturnHouse = dasha.natalPlacements.Saturn?.house;
    const venusHouse = dasha.natalPlacements.Venus?.house;
    return saturnHouse !== undefined && saturnHouse === venusHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    notes.push(`💎 SATURN + VENUS CONJUNCTION:`);
    notes.push(`• WEALTH: Blessed with abundant wealth - financial prosperity assured`);
    notes.push(`• TEXTILE INDUSTRY: Engage in work/business related to textiles, garments, fashion`);
    notes.push(`• LUXURY GOODS: Success in luxury items, jewelry, cosmetics, beauty products`);
    notes.push(`• DELAY IN MARRIAGE: May face delays in marriage but eventual stability`);
    notes.push(`• ARTISTIC DISCIPLINE: Combines artistic talent with disciplined execution`);
    notes.push(`• REAL ESTATE: Good for property dealing, interior design, architecture`);
    notes.push(`• MANIFESTATION: Slow but steady accumulation of wealth and comforts`);
    notes.push(`• TIMING: Venus or Saturn dasha brings material prosperity`);
    return { value: 50, notes };
  }
};

// Saturn conjunction with Jupiter
const saturnJupiterConjunction: Rule = {
  key: 'saturn_jupiter_conjunction',
  areas: ['Career', 'Children'],
  templateKey: 'saturn_jupiter_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const saturnHouse = dasha.natalPlacements.Saturn?.house;
    const jupiterHouse = dasha.natalPlacements.Jupiter?.house;
    return saturnHouse !== undefined && saturnHouse === jupiterHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    notes.push(`🎓 SATURN + JUPITER CONJUNCTION:`);
    notes.push(`• TEACHING: Will be an excellent teacher - natural educator and mentor`);
    notes.push(`• WISDOM: Combines practical wisdom (Saturn) with philosophical knowledge (Jupiter)`);
    notes.push(`• ACADEMICS: Success in academic careers, research, scholarly pursuits`);
    notes.push(`• LAW: Good for legal profession, judiciary, counseling`);
    notes.push(`• MENTORSHIP: Becomes respected guide and advisor to many`);
    notes.push(`• PATIENCE: Teaches with patience, discipline and systematic approach`);
    notes.push(`• INSTITUTIONS: Can build educational institutions or training centers`);
    notes.push(`• TIMING: Jupiter or Saturn dasha brings recognition as authority in field`);
    return { value: 55, notes };
  }
};

// Saturn conjunction with Moon
const saturnMoonConjunction: Rule = {
  key: 'saturn_moon_conjunction',
  areas: ['Health', 'Career'],
  templateKey: 'saturn_moon_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const saturnHouse = dasha.natalPlacements.Saturn?.house;
    const moonHouse = dasha.natalPlacements.Moon?.house;
    return saturnHouse !== undefined && saturnHouse === moonHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    notes.push(`🌙 SATURN + MOON CONJUNCTION:`);
    notes.push(`• MOTHER'S HEALTH: Mother can be prone to arthritis, joint problems, chronic ailments`);
    notes.push(`• FOOD BUSINESS: Businesses related to food, restaurants, catering prove excellent`);
    notes.push(`• MENTAL HEALTH: Can be mentally disturbed, prone to depression and anxiety`);
    notes.push(`• EMOTIONAL BURDEN: Feels emotional weight of responsibilities - heavy heart`);
    notes.push(`• DAIRY: Success in dairy products, milk distribution, food preservation`);
    notes.push(`• REAL ESTATE: Good for property dealing, especially residential properties`);
    notes.push(`• REMEDIES: Moon mantra, white clothing on Mondays, charity to elderly women`);
    notes.push(`• COUNSELING: Seek mental health support during difficult periods - Saturn's pressure on mind`);
    notes.push(`• TIMING: Emotional challenges peak during Moon or Saturn dasha - maintain support system`);
    return { value: -10, notes };
  }
};

// Saturn conjunction with Mercury
const saturnMercuryConjunction: Rule = {
  key: 'saturn_mercury_conjunction',
  areas: ['Career', 'Wealth'],
  templateKey: 'saturn_mercury_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const saturnHouse = dasha.natalPlacements.Saturn?.house;
    const mercuryHouse = dasha.natalPlacements.Mercury?.house;
    return saturnHouse !== undefined && saturnHouse === mercuryHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    notes.push(`📊 SATURN + MERCURY CONJUNCTION:`);
    notes.push(`• ENTREPRENEUR: Can be excellent entrepreneur - business acumen with discipline`);
    notes.push(`• COMMISSION WORK: Success in commission-based work, brokerage, agency`);
    notes.push(`• TRADING: Good for trading, stock market, commodities, import-export`);
    notes.push(`• DOCUMENTATION: Excel in work related to documentation, legal papers, contracts`);
    notes.push(`• ACCOUNTING: Excellent accountants, auditors, chartered accountants`);
    notes.push(`• ANALYSIS: Systematic analytical thinking - research, data analysis`);
    notes.push(`• COMMUNICATION: Serious, measured communication style - trustworthy spokesperson`);
    notes.push(`• TIMING: Mercury or Saturn dasha brings business success through careful planning`);
    return { value: 45, notes };
  }
};

// Saturn conjunction with Ketu
const saturnKetuConjunction: Rule = {
  key: 'saturn_ketu_conjunction',
  areas: ['Spirituality', 'Career'],
  templateKey: 'saturn_ketu_conjunction',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    const saturnHouse = dasha.natalPlacements.Saturn?.house;
    const ketuHouse = dasha.natalPlacements.Ketu?.house;
    return saturnHouse !== undefined && saturnHouse === ketuHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    notes.push(`🔱 SATURN + KETU CONJUNCTION:`);
    notes.push(`• CONSULTANT: Proves to be very good consultant - expert advisor`);
    notes.push(`• ASTROLOGY: Excellent astrologer - intuition combined with systematic study`);
    notes.push(`• SPIRITUAL BLESSINGS: Have blessings of spiritual power and divine grace`);
    notes.push(`• MYSTICAL: Deep interest in mysticism, occult, Tantra, ancient wisdom`);
    notes.push(`• DETACHMENT: Philosophical detachment from material world while functioning in it`);
    notes.push(`• RESEARCH: Deep research abilities in esoteric subjects`);
    notes.push(`• HEALING: Can be powerful healer using alternative methods`);
    notes.push(`• PAST LIFE: Strong past life spiritual karma - this life continues that journey`);
    notes.push(`• TIMING: Ketu or Saturn dasha brings spiritual awakening and consulting success`);
    return { value: 50, notes };
  }
};

// Original bundled rule (backwards compatibility - triggers only if no specific conjunction triggered)
const saturnConjunctionAnalysis: Rule = {
  key: 'saturn_conjunctions',
  areas: ['Career', 'Wealth', 'Health', 'Self'],
  templateKey: 'saturn_conjunction_results', // Generic template for edge cases
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    
    const saturnHouse = dasha.natalPlacements.Saturn?.house;
    if (!saturnHouse) return false;
    
    // Check if Saturn is conjunct with any other planet (same house)
    const planets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Rahu', 'Ketu'];
    for (const planet of planets) {
      const planetHouse = dasha.natalPlacements[planet]?.house;
      if (planetHouse === saturnHouse) {
        return true; // Saturn has conjunction
      }
    }
    
    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    const conjunctions: Array<{planet: PlanetName; effects: string[]}> = [];
    let totalScore = 0;
    
    if (dasha.natalPlacements) {
      const saturnHouse = dasha.natalPlacements.Saturn?.house;
      
      notes.push(`🪐 SATURN CONJUNCTIONS IN HOUSE ${saturnHouse}:`);
      notes.push(``);
      
      // Saturn + Sun
      if (dasha.natalPlacements.Sun?.house === saturnHouse) {
        conjunctions.push({
          planet: 'Sun',
          effects: [
            '☀️ SATURN + SUN CONJUNCTION:',
            '• HOUSE FEATURE: Direct sunlight enters house from southern direction',
            '• ARCHITECTURE: Big iron-clad windows in southern direction possible',
            '• GOVERNMENT: Good connections in government departments and bureaucracy',
            '• AUTHORITY: Relationship with father figure may be strained but respectful',
            '• CAREER: Success in government service, administration, public sector',
            '• TIMING: Benefits peak during Sun or Saturn dasha periods'
          ]
        });
        totalScore += 20; // Moderate positive
      }
      
      // Saturn + Mars
      if (dasha.natalPlacements.Mars?.house === saturnHouse) {
        conjunctions.push({
          planet: 'Mars',
          effects: [
            '⚔️ SATURN + MARS CONJUNCTION:',
            '• WORKPLACE: Can cause fights and conflicts at workplace - ego clashes',
            '• TECHNICAL WORK: Problem avoided if involved in technical/engineering work',
            '• HEALTH: Prone to chronic disease - especially related to bones, muscles, injuries',
            '• ARMED FORCES: Excellent for service in military, police, security, discipline-oriented careers',
            '• DISCIPLINE: Extreme discipline meets extreme drive - can be workaholic',
            '• REMEDIES: Mars mantra, Hanuman worship to reduce conflict energy',
            '• UTILIZE: Channel energy into structured physical work, sports, martial arts'
          ]
        });
        totalScore -= 15; // Challenging but channelable
      }
      
      // Saturn + Rahu
      if (dasha.natalPlacements.Rahu?.house === saturnHouse) {
        conjunctions.push({
          planet: 'Rahu',
          effects: [
            '🌑 SATURN + RAHU CONJUNCTION (Shadow Planet Effect):',
            '• FOREIGN LANDS: Should move to foreign country for maximum success - emigration highly favorable',
            '• UNORTHODOX CAREER: Choose unconventional work - acting, movies, YouTube, social media, content creation',
            '• SPIRITUALITY: Can attain higher levels of spirituality - mystical inclinations',
            '• OCCULT: Take up work related to astrology, occult sciences, alternative healing',
            '• MAGICAL POWERS: Can attain special siddhis (spiritual powers) through disciplined practice',
            '• INNOVATION: Success in cutting-edge technology, AI, blockchain, cryptocurrency',
            '• TIMING: Life transforms during Rahu or Saturn Mahadasha - embrace unconventional path',
            '• INTERNATIONAL: Best results abroad or in international companies'
          ]
        });
        totalScore += 40; // Strong positive if channeled correctly
      }
      
      // Saturn + Venus
      if (dasha.natalPlacements.Venus?.house === saturnHouse) {
        conjunctions.push({
          planet: 'Venus',
          effects: [
            '💎 SATURN + VENUS CONJUNCTION:',
            '• WEALTH: Blessed with abundant wealth - financial prosperity assured',
            '• TEXTILE INDUSTRY: Engage in work/business related to textiles, garments, fashion',
            '• LUXURY GOODS: Success in luxury items, jewelry, cosmetics, beauty products',
            '• DELAY IN MARRIAGE: May face delays in marriage but eventual stability',
            '• ARTISTIC DISCIPLINE: Combines artistic talent with disciplined execution',
            '• REAL ESTATE: Good for property dealing, interior design, architecture',
            '• MANIFESTATION: Slow but steady accumulation of wealth and comforts',
            '• TIMING: Venus or Saturn dasha brings material prosperity'
          ]
        });
        totalScore += 50; // Very positive for wealth
      }
      
      // Saturn + Jupiter
      if (dasha.natalPlacements.Jupiter?.house === saturnHouse) {
        conjunctions.push({
          planet: 'Jupiter',
          effects: [
            '🎓 SATURN + JUPITER CONJUNCTION:',
            '• TEACHING: Will be an excellent teacher - natural educator and mentor',
            '• WISDOM: Combines practical wisdom (Saturn) with philosophical knowledge (Jupiter)',
            '• ACADEMICS: Success in academic careers, research, scholarly pursuits',
            '• LAW: Good for legal profession, judiciary, counseling',
            '• MENTORSHIP: Becomes respected guide and advisor to many',
            '• PATIENCE: Teaches with patience, discipline and systematic approach',
            '• INSTITUTIONS: Can build educational institutions or training centers',
            '• TIMING: Jupiter or Saturn dasha brings recognition as authority in field'
          ]
        });
        totalScore += 55; // Very positive for knowledge careers
      }
      
      // Saturn + Moon
      if (dasha.natalPlacements.Moon?.house === saturnHouse) {
        conjunctions.push({
          planet: 'Moon',
          effects: [
            '🌙 SATURN + MOON CONJUNCTION:',
            '• MOTHER\'S HEALTH: Mother can be prone to arthritis, joint problems, chronic ailments',
            '• FOOD BUSINESS: Businesses related to food, restaurants, catering prove excellent',
            '• MENTAL HEALTH: Can be mentally disturbed, prone to depression and anxiety',
            '• EMOTIONAL BURDEN: Feels emotional weight of responsibilities - heavy heart',
            '• DAIRY: Success in dairy products, milk distribution, food preservation',
            '• REAL ESTATE: Good for property dealing, especially residential properties',
            '• REMEDIES: Moon mantra, white clothing on Mondays, charity to elderly women',
            '• COUNSELING: Seek mental health support during difficult periods - Saturn\'s pressure on mind',
            '• TIMING: Emotional challenges peak during Moon or Saturn dasha - maintain support system'
          ]
        });
        totalScore -= 10; // Challenging emotionally but good for food business
      }
      
      // Saturn + Mercury
      if (dasha.natalPlacements.Mercury?.house === saturnHouse) {
        conjunctions.push({
          planet: 'Mercury',
          effects: [
            '📊 SATURN + MERCURY CONJUNCTION:',
            '• ENTREPRENEUR: Can be excellent entrepreneur - business acumen with discipline',
            '• COMMISSION WORK: Success in commission-based work, brokerage, agency',
            '• TRADING: Good for trading, stock market, commodities, import-export',
            '• DOCUMENTATION: Excel in work related to documentation, legal papers, contracts',
            '• ACCOUNTING: Excellent accountants, auditors, chartered accountants',
            '• ANALYSIS: Systematic analytical thinking - research, data analysis',
            '• COMMUNICATION: Serious, measured communication style - trustworthy spokesperson',
            '• TIMING: Mercury or Saturn dasha brings business success through careful planning'
          ]
        });
        totalScore += 45; // Very positive for business
      }
      
      // Saturn + Ketu
      if (dasha.natalPlacements.Ketu?.house === saturnHouse) {
        conjunctions.push({
          planet: 'Ketu',
          effects: [
            '🔱 SATURN + KETU CONJUNCTION:',
            '• CONSULTANT: Proves to be very good consultant - expert advisor',
            '• ASTROLOGY: Excellent astrologer - intuition combined with systematic study',
            '• SPIRITUAL BLESSINGS: Have blessings of spiritual power and divine grace',
            '• MYSTICAL: Deep interest in mysticism, occult, Tantra, ancient wisdom',
            '• DETACHMENT: Philosophical detachment from material world while functioning in it',
            '• RESEARCH: Deep research abilities in esoteric subjects',
            '• HEALING: Can be powerful healer using alternative methods',
            '• PAST LIFE: Strong past life spiritual karma - this life continues that journey',
            '• TIMING: Ketu or Saturn dasha brings spiritual awakening and consulting success'
          ]
        });
        totalScore += 50; // Positive for spiritual/consulting work
      }
      
      // Compile all conjunction effects
      for (const conj of conjunctions) {
        for (const effect of conj.effects) {
          notes.push(effect);
        }
        notes.push(``); // Blank line between conjunctions
      }
      
      if (conjunctions.length > 0) {
        notes.push(`OVERALL SATURN CONJUNCTIONS: ${conjunctions.length} planet(s) conjunct Saturn`);
        notes.push(`ACTIVATION: All effects intensify during Saturn Mahadasha (19 years)`);
        notes.push(`STRATEGY: Understand and leverage each conjunction's specific benefits`);
        notes.push(`REMEDIES: Saturday Shani worship amplifies positive effects, minimizes negative`);
      }
    }
    
    return { value: totalScore, notes };
  }
};

// Mangal Dosha (Kuja Dosha) - Mars affliction for marriage
const mangalDosha: Rule = {
  key: 'mangal_dosha_natal',
  areas: ['Spouse'],
  templateKey: 'mangal_dosha',
  priority: 7,
  ruleType: 'natal',
  customPrediction: '', // Will be set from notes[0]
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    
    const marsHouse = dasha.natalPlacements.Mars?.house;
    if (!marsHouse) return false;
    
    // Mangal Dosha houses from Lagna: 1, 4, 7, 8, 12
    const mangalDoshaHouses = [1, 4, 7, 8, 12];
    return mangalDoshaHouses.includes(marsHouse);
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    if (!dasha.natalPlacements) return { value: 0, notes };
    
    const marsHouse = dasha.natalPlacements.Mars?.house;
    const moonHouse = dasha.natalPlacements.Moon?.house;
    const venusHouse = dasha.natalPlacements.Venus?.house;
    
    if (!marsHouse) return { value: 0, notes };
    
    notes.push('🔥 MANGAL DOSHA (KUJA DOSHA) DETECTED:');
    notes.push(`• Mars is in ${marsHouse}th house from Lagna - creates marriage affliction`);
    
    // Count severity from multiple reference points
    let severity = 1;
    const mangalDoshaHouses = [1, 4, 7, 8, 12];
    
    // Check from Moon
    if (moonHouse) {
      const marsFromMoon = ((marsHouse - moonHouse + 12) % 12) || 12;
      if (mangalDoshaHouses.includes(marsFromMoon)) {
        severity++;
        notes.push(`• MOON REFERENCE: Mars also in house ${marsFromMoon} from Moon - SEVERE`);
      }
    }
    
    // Check from Venus
    if (venusHouse) {
      const marsFromVenus = ((marsHouse - venusHouse + 12) % 12) || 12;
      if (mangalDoshaHouses.includes(marsFromVenus)) {
        severity++;
        notes.push(`• VENUS REFERENCE: Mars also in house ${marsFromVenus} from Venus - VERY SEVERE`);
      }
    }
    
    // Specific house effects
    if (marsHouse === 1) {
      notes.push('• 1ST HOUSE: Aggressive personality, dominance issues, late marriage recommended');
    } else if (marsHouse === 4) {
      notes.push('• 4TH HOUSE: Domestic discord, lack of peace at home, property disputes');
    } else if (marsHouse === 7) {
      notes.push('• 7TH HOUSE: Direct impact on marriage house - partner may face health/temperament issues');
    } else if (marsHouse === 8) {
      notes.push('• 8TH HOUSE: Sudden breaks in relationship, accidents, mysterious problems in marriage');
    } else if (marsHouse === 12) {
      notes.push('• 12TH HOUSE: Expenses on spouse, separation, foreign settlement after marriage');
    }
    
    // Severity classification
    if (severity >= 3) {
      notes.push('');
      notes.push('⚠️ TRIPLE MANGAL DOSHA: Extremely severe - Mars afflicts from all three reference points');
      notes.push('• MARRIAGE IMPACT: Very high risk of discord, delays, separations');
      notes.push('• PARTNER MATCHING: MUST marry someone with Mangal Dosha for cancellation');
    } else if (severity === 2) {
      notes.push('');
      notes.push('⚠️ DOUBLE MANGAL DOSHA: Severe affliction from two reference points');
      notes.push('• MARRIAGE IMPACT: Significant challenges, delays, temperament clashes');
    } else {
      notes.push('');
      notes.push('⚠️ SINGLE MANGAL DOSHA: Moderate affliction from Lagna only');
      notes.push('• MARRIAGE IMPACT: Manageable with proper matching and remedies');
    }
    
    // Cancellation conditions
    notes.push('');
    notes.push('✅ MANGAL DOSHA CANCELLATION CONDITIONS:');
    notes.push('• Both partners have Mangal Dosha - mutual cancellation');
    notes.push('• Mars in own sign (Aries/Scorpio) or exalted (Capricorn) - reduced effect');
    notes.push('• Jupiter aspects Mars or 7th house - protective blessing');
    notes.push('• Marriage after age 28 - natural weakening of Dosha');
    
    // Check for cancellation factors
    const marsSign = dasha.natalPlacements.Mars?.sign;
    const marsExalted = dasha.natalPlacements.Mars?.exalted;
    const marsOwnSign = marsSign === 1 || marsSign === 8; // Aries or Scorpio (1-based)
    
    if (marsExalted) {
      notes.push('');
      notes.push('🌟 PARTIAL CANCELLATION: Mars is exalted in Capricorn - reduced negative effects');
      severity = Math.max(1, severity - 1);
    } else if (marsOwnSign) {
      notes.push('');
      notes.push('🌟 PARTIAL CANCELLATION: Mars in own sign (Aries/Scorpio) - Mars comfortable, less affliction');
      severity = Math.max(1, severity - 1);
    }
    
    // Remedies
    notes.push('');
    notes.push('🙏 REMEDIES FOR MANGAL DOSHA:');
    notes.push('• Tuesday fasting - strengthen Mars positively');
    notes.push('• Hanuman Chalisa recitation - 11 times daily');
    notes.push('• Red coral gemstone - only if Mars not debilitated');
    notes.push('• Kuja Shanti puja before marriage');
    notes.push('• Marry after age 28 or to Manglik partner');
    notes.push('• Visit Hanuman temple every Tuesday');
    
    // Score based on severity
    const baseScore = -30; // Negative impact
    const severityMultiplier = severity * 0.5; // 0.5x per severity level
    const totalScore = baseScore * (1 + severityMultiplier);
    
    // Set customPrediction to use notes as message
    (mangalDosha as any).customPrediction = notes.join('\n');
    
    return { value: Math.round(totalScore), notes };
  }
};

// 7th Lord in Dusthana with Malefic Association - Marriage/Partnership Affliction
const seventhLordDusthanaAffliction: Rule = {
  key: 'seventh_lord_dusthana_affliction',
  areas: ['Spouse'],
  templateKey: 'seventh_lord_affliction',
  priority: 8,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    // Require both lagnaSign and natalPlacements for this rule to work
    if (!dasha.natalPlacements || !dasha.lagnaSign) {
      return false;
    }
    
    // Find which planet rules the 7th house
    const lagnaSign = dasha.lagnaSign;
    
    // Calculate 7th house sign (opposite to lagna)
    const seventhSign = ((lagnaSign - 1 + 6) % 12) + 1;
    
    // Determine 7th lord based on sign lordship
    const SIGN_LORDS: Record<number, PlanetName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    const seventhLord = SIGN_LORDS[seventhSign];
    const seventhLordData = dasha.natalPlacements[seventhLord];
    
    if (!seventhLordData) return false;
    
    const seventhLordHouse = seventhLordData.house;
    
    // Check BOTH conditions:
    // 1. Absolute: 7th lord in dusthana houses (6, 8, 12) from ascendant
    // 2. Relative: 7th lord in 6th/8th/12th from 7th house itself
    
    const absoluteDusthana = [6, 8, 12].includes(seventhLordHouse);
    
    const dusthanaPositionsFromSeventh = [
      ((7 - 1 + 5) % 12) + 1,  // 6th from 7th = 12th house
      ((7 - 1 + 7) % 12) + 1,  // 8th from 7th = 2nd house
      ((7 - 1 + 11) % 12) + 1  // 12th from 7th = 6th house
    ];
    
    const relativeDusthana = dusthanaPositionsFromSeventh.includes(seventhLordHouse);
    
    // Rule triggers if EITHER dusthana condition is met
    // Malefic association affects SEVERITY in score(), not whether rule triggers
    return absoluteDusthana || relativeDusthana;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    if (!dasha.natalPlacements || !dasha.lagnaSign) {
      return { value: 0, notes };
    }
    
    const lagnaSign = dasha.lagnaSign;
    const seventhSign = ((lagnaSign - 1 + 6) % 12) + 1;
    
    const SIGN_NAMES = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 
                        'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
    
    const SIGN_LORDS: Record<number, PlanetName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    const seventhLord = SIGN_LORDS[seventhSign];
    const seventhLordData = dasha.natalPlacements[seventhLord];
    
    if (!seventhLordData) return { value: 0, notes };
    
    const seventhLordHouse = seventhLordData.house;
    
    notes.push('⚠️ 7TH LORD AFFLICTION DETECTED:');
    notes.push(`• 7th house is ${SIGN_NAMES[seventhSign - 1]}, ruled by ${seventhLord}`);
    notes.push(`• ${seventhLord} is placed in house ${seventhLordHouse}`);
    
    // Determine which dusthana position
    let dusthanaType = '';
    if (seventhLordHouse === ((7 - 1 + 5) % 12) + 1) {
      dusthanaType = '6th from 7th (12th house) - EXPENSES & LOSSES IN PARTNERSHIPS';
      notes.push(`• Position: 6th from own house (12th house) - losses in partnerships`);
    } else if (seventhLordHouse === ((7 - 1 + 7) % 12) + 1) {
      dusthanaType = '8th from 7th (2nd house) - SUDDEN BREAKS IN RELATIONSHIPS';
      notes.push(`• Position: 8th from own house (2nd house) - sudden breaks, transformations`);
    } else if (seventhLordHouse === ((7 - 1 + 11) % 12) + 1) {
      dusthanaType = '12th from 7th (6th house) - ENEMIES & OBSTACLES IN MARRIAGE';
      notes.push(`• Position: 12th from own house (6th house) - enemies, obstacles, legal issues`);
    }
    
    // Find malefic associations
    const MALEFICS: PlanetName[] = ['Mars', 'Saturn', 'Rahu', 'Ketu'];
    const maleficsInvolved: string[] = [];
    
    for (const malefic of MALEFICS) {
      const maleficData = dasha.natalPlacements[malefic];
      if (!maleficData) continue;
      
      const maleficHouse = maleficData.house;
      
      // Conjunction
      if (maleficHouse === seventhLordHouse) {
        maleficsInvolved.push(`${malefic} (conjunct in H${seventhLordHouse})`);
        continue;
      }
      
      // Aspects
      const seventhAspect = ((maleficHouse - 1 + 6) % 12) + 1;
      if (seventhAspect === seventhLordHouse) {
        maleficsInvolved.push(`${malefic} (7th aspect from H${maleficHouse})`);
      }
      
      if (malefic === 'Saturn') {
        const thirdAspect = ((maleficHouse - 1 + 2) % 12) + 1;
        const tenthAspect = ((maleficHouse - 1 + 9) % 12) + 1;
        if (thirdAspect === seventhLordHouse) {
          maleficsInvolved.push(`Saturn (3rd aspect from H${maleficHouse})`);
        }
        if (tenthAspect === seventhLordHouse) {
          maleficsInvolved.push(`Saturn (10th aspect from H${maleficHouse})`);
        }
      }
      
      if (malefic === 'Mars') {
        const fourthAspect = ((maleficHouse - 1 + 3) % 12) + 1;
        const eighthAspect = ((maleficHouse - 1 + 7) % 12) + 1;
        if (fourthAspect === seventhLordHouse) {
          maleficsInvolved.push(`Mars (4th aspect from H${maleficHouse})`);
        }
        if (eighthAspect === seventhLordHouse) {
          maleficsInvolved.push(`Mars (8th aspect from H${maleficHouse})`);
        }
      }
    }
    
    if (maleficsInvolved.length > 0) {
      notes.push(`• Malefic Association: ${maleficsInvolved.join(', ')}`);
    }
    
    notes.push('');
    notes.push('💔 MARRIAGE & PARTNERSHIP IMPACT:');
    notes.push('• Love marriage faces severe obstacles and delays');
    notes.push('• Partnerships (business/personal) encounter challenges');
    notes.push('• Spouse selection may be compromised');
    notes.push('• Relationship harmony requires conscious effort');
    notes.push('• Trust issues or miscommunication in partnerships');
    
    notes.push('');
    notes.push('📋 CLASSICAL INTERPRETATION:');
    notes.push('• When a house lord sits 6th/8th/12th from its own house AND');
    notes.push('• Has malefic association (conjunction or aspect),');
    notes.push('• It gives bad results for matters of that house');
    notes.push('• 7th house = marriage, partnerships, business collaborations');
    notes.push('• The disturbance is at the foundational level of the house');
    
    notes.push('');
    notes.push('✨ REMEDIAL MEASURES:');
    notes.push('• Strengthen 7th house and 7th lord through worship');
    notes.push(`• ${seventhLord} remedies: appropriate gemstone, mantra, charity`);
    notes.push('• Venus worship for love and relationships (Fridays)');
    notes.push('• Ganesh puja to remove obstacles in marriage');
    notes.push('• Consider arranged marriage or elder guidance in selection');
    notes.push('• Pre-marital counseling and compatibility matching essential');
    notes.push('• Patience in relationship matters - timing is crucial');
    
    notes.push('');
    notes.push('⏰ TIMING CONSIDERATIONS:');
    notes.push(`• Avoid marriage during ${seventhLord} dasha if afflicted`);
    notes.push('• Wait for favorable transits of Venus and Jupiter to 7th house');
    notes.push('• Dasha of benefic planets aspecting 7th house more favorable');
    notes.push('• Post-28 years often brings more stability');
    
    // Severity based on number of malefic associations
    let severity = maleficsInvolved.length;
    const baseScore = -35;
    const severityMultiplier = Math.min(severity * 0.3, 1.0);
    const totalScore = baseScore * (1 + severityMultiplier);
    
    // Set customPrediction to use notes as message (same as mangalDosha)
    (seventhLordDusthanaAffliction as any).customPrediction = notes.join('\n');
    
    return { value: Math.round(totalScore), notes };
  }
};

// 7th Lord in Dusthana with Malefic Association - Timing Version (Activates during dasha)
const seventhLordDusthanaAfflictionTiming: Rule = {
  key: 'seventh_lord_dusthana_affliction_timing',
  areas: ['Spouse'],
  templateKey: 'seventh_lord_affliction_active',
  priority: 9,
  ruleType: 'timing',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements || !dasha.lagnaSign) return false;
    
    // Find 7th lord
    const lagnaSign = dasha.lagnaSign;
    const seventhSign = ((lagnaSign - 1 + 6) % 12) + 1;
    const SIGN_LORDS: Record<number, PlanetName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    const seventhLord = SIGN_LORDS[seventhSign];
    const seventhLordData = dasha.natalPlacements[seventhLord];
    
    if (!seventhLordData) return false;
    
    // Check if affliction exists (BOTH absolute and relative dusthana)
    const seventhLordHouse = seventhLordData.house;
    
    const absoluteDusthana = [6, 8, 12].includes(seventhLordHouse);
    
    const dusthanaPositionsFromSeventh = [
      ((7 - 1 + 5) % 12) + 1,
      ((7 - 1 + 7) % 12) + 1,
      ((7 - 1 + 11) % 12) + 1
    ];
    
    const relativeDusthana = dusthanaPositionsFromSeventh.includes(seventhLordHouse);
    
    // Rule triggers if EITHER dusthana condition is met
    if (!absoluteDusthana && !relativeDusthana) return false;
    
    // Check if 7th lord is in current dasha (timing activation)
    const activePlanets = new Set(dasha.levels.map(l => l.planet));
    return activePlanets.has(seventhLord);
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    if (!dasha.natalPlacements || !dasha.lagnaSign) {
      return { value: 0, notes };
    }
    
    const lagnaSign = dasha.lagnaSign;
    const seventhSign = ((lagnaSign - 1 + 6) % 12) + 1;
    
    const SIGN_NAMES = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 
                        'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
    
    const SIGN_LORDS: Record<number, PlanetName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    const seventhLord = SIGN_LORDS[seventhSign];
    const seventhLordData = dasha.natalPlacements[seventhLord];
    
    if (!seventhLordData) return { value: 0, notes };
    
    const seventhLordHouse = seventhLordData.house;
    const activePlanets = new Set(dasha.levels.map(l => l.planet));
    const isSeventhLordActive = activePlanets.has(seventhLord);
    
    // Find active malefics
    const MALEFICS: PlanetName[] = ['Mars', 'Saturn', 'Rahu', 'Ketu'];
    const activeMalefics: string[] = [];
    
    for (const malefic of MALEFICS) {
      const maleficData = dasha.natalPlacements[malefic];
      if (!maleficData) continue;
      
      const maleficHouse = maleficData.house;
      let isAssociated = false;
      let associationType = '';
      
      if (maleficHouse === seventhLordHouse) {
        isAssociated = true;
        associationType = 'conjunct';
      } else {
        const seventhAspect = ((maleficHouse - 1 + 6) % 12) + 1;
        if (seventhAspect === seventhLordHouse) {
          isAssociated = true;
          associationType = 'aspects';
        }
      }
      
      if (isAssociated && activePlanets.has(malefic)) {
        activeMalefics.push(`${malefic} (${associationType})`);
      }
    }
    
    notes.push('⚠️ 7TH LORD AFFLICTION ACTIVE THIS PERIOD:');
    notes.push(`• Marriage house ${SIGN_NAMES[seventhSign - 1]} ruled by ${seventhLord}`);
    notes.push(`• ${seventhLord} placed in house ${seventhLordHouse} (dusthana from 7th)`);
    
    if (isSeventhLordActive) {
      notes.push(`• ⚡ ${seventhLord} DASHA ACTIVE - Affliction manifesting now`);
    }
    
    if (activeMalefics.length > 0) {
      notes.push(`• ⚡ MALEFIC DASHA ACTIVE: ${activeMalefics.join(', ')}`);
    }
    
    notes.push('');
    notes.push('🚨 CURRENT PERIOD EFFECTS:');
    notes.push('• Love marriage attempts face maximum resistance NOW');
    notes.push('• Partnership decisions should be delayed if possible');
    notes.push('• Existing relationships may experience strain');
    notes.push('• Business collaborations require extra caution');
    notes.push('• Misunderstandings with spouse/partner likely');
    notes.push('• Trust issues or conflicts may surface');
    
    notes.push('');
    notes.push('📅 TIMING ADVICE:');
    if (isSeventhLordActive) {
      notes.push(`• ${seventhLord} dasha brings affliction to forefront`);
      notes.push('• AVOID marriage/partnership commitments during this dasha');
      notes.push('• Focus on self-improvement and remedial measures');
      notes.push('• Work on healing relationship patterns');
    }
    
    if (activeMalefics.length > 0) {
      notes.push('• Malefic dasha amplifies relationship challenges');
      notes.push('• Exercise patience and avoid confrontations');
      notes.push('• Seek elder guidance before major decisions');
    }
    
    notes.push('');
    notes.push('✨ IMMEDIATE REMEDIES:');
    notes.push(`• Daily worship of ${seventhLord} - strengthen lord's positive effects`);
    notes.push('• Venus mantra on Fridays - "Om Shukraya Namaha" 108 times');
    notes.push('• Ganesh puja to remove obstacles in marriage');
    notes.push('• Donate white items on Fridays (rice, milk, sugar)');
    notes.push('• Avoid initiating love proposals during this period');
    notes.push('• If married - extra effort in communication and understanding');
    
    notes.push('');
    notes.push('🎯 STRATEGIC APPROACH:');
    notes.push('• Consider arranged marriage route (family guidance)');
    notes.push('• Thorough compatibility matching before commitment');
    notes.push('• Pre-marital counseling highly recommended');
    notes.push('• Wait for benefic dasha (Jupiter/Venus) for marriage timing');
    notes.push('• Use this period for self-reflection and personal growth');
    
    // Higher negative score when dasha is active
    const baseScore = -45;
    const dashaMultiplier = (isSeventhLordActive || activeMalefics.length > 0) ? 1.3 : 1.0;
    const totalScore = baseScore * dashaMultiplier;
    
    return { value: Math.round(totalScore), notes };
  }
};

// Rahu in 9th House - Intercaste Marriage Yoga (Natal/Permanent)
const rahuNinthIntercasteYoga: Rule = {
  key: 'rahu_ninth_intercaste_yoga',
  areas: ['Spouse'],
  templateKey: 'rahu_ninth_intercaste',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;
    
    const rahuData = dasha.natalPlacements['Rahu'];
    if (!rahuData) return false;
    
    // Triggers if Rahu is in 9th house
    return rahuData.house === 9;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    if (!dasha.natalPlacements) {
      return { value: 0, notes };
    }
    
    notes.push('🌟 RAHU IN 9TH HOUSE - INTERCASTE MARRIAGE YOGA');
    notes.push('');
    notes.push('📋 PRIMARY INDICATIONS:');
    notes.push('• Strong likelihood of intercaste or inter-religion marriage');
    notes.push('• Attraction to partners from different cultural background');
    notes.push('• Breaking traditional family marriage patterns');
    notes.push('• Unconventional approach to relationships and dharma');
    notes.push('• Foreign connection in marriage is possible');
    notes.push('');
    notes.push('🎯 RELATIONSHIP PATTERNS:');
    notes.push('• May face initial family resistance to partner choice');
    notes.push('• Partner likely from different social or religious background');
    notes.push('• Love marriage more probable than arranged marriage');
    notes.push('• Attraction to modern, progressive partners');
    notes.push('• May meet partner through education or travel');
    notes.push('');
    notes.push('💡 GUIDANCE:');
    notes.push('• Open communication with family early on');
    notes.push('• Focus on partner\'s character over community background');
    notes.push('• Pre-marital compatibility assessment recommended');
    notes.push('• Patience during family acceptance process');
    notes.push('• Legal marriage registration advisable');
    notes.push('');
    notes.push('✅ POSITIVE ASPECTS:');
    notes.push('• Broadened worldview through partnership');
    notes.push('• Rich cultural exchange in married life');
    notes.push('• Children benefit from diverse heritage');
    notes.push('• Breaking limiting social barriers');
    
    // Neutral to slightly positive score (it\'s a yoga, not affliction)
    const baseScore = 25;
    
    (rahuNinthIntercasteYoga as any).customPrediction = notes.join('\n');
    
    return { value: baseScore, notes };
  }
};

// Rahu in 9th House - Timing Version (Activates during 7th house or Rahu dasha)
const rahuNinthIntercasteYogaTiming: Rule = {
  key: 'rahu_ninth_intercaste_yoga_timing',
  areas: ['Spouse'],
  templateKey: 'rahu_ninth_intercaste_active',
  priority: 8,
  ruleType: 'timing',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements || !dasha.lagnaSign) return false;
    
    // Check if Rahu is in 9th house
    const rahuData = dasha.natalPlacements['Rahu'];
    if (!rahuData || rahuData.house !== 9) return false;
    
    // Check if Rahu dasha is active
    const activePlanets = new Set<PlanetName>();
    for (const level of dasha.levels) {
      activePlanets.add(level.planet);
      if (level.occupies) activePlanets.add(level.planet);
    }
    
    const isRahuDashaActive = activePlanets.has('Rahu');
    
    // Check if 7th house lord dasha is active
    const lagnaSign = dasha.lagnaSign;
    const seventhSign = ((lagnaSign - 1 + 6) % 12) + 1;
    const SIGN_LORDS: Record<number, PlanetName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    const seventhLord = SIGN_LORDS[seventhSign];
    const isSeventhLordActive = activePlanets.has(seventhLord);
    
    // Trigger if EITHER Rahu dasha OR 7th house lord dasha is active
    return isRahuDashaActive || isSeventhLordActive;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];
    
    if (!dasha.natalPlacements || !dasha.lagnaSign) {
      return { value: 0, notes };
    }
    
    const lagnaSign = dasha.lagnaSign;
    const seventhSign = ((lagnaSign - 1 + 6) % 12) + 1;
    const SIGN_LORDS: Record<number, PlanetName> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    const seventhLord = SIGN_LORDS[seventhSign];
    
    const activePlanets = new Set<PlanetName>();
    for (const level of dasha.levels) {
      activePlanets.add(level.planet);
    }
    
    const isRahuDashaActive = activePlanets.has('Rahu');
    const isSeventhLordActive = activePlanets.has(seventhLord);
    
    notes.push('⚡ RAHU 9TH HOUSE YOGA - ACTIVE PERIOD');
    notes.push('');
    
    if (isRahuDashaActive) {
      notes.push('🔥 RAHU DASHA ACTIVE - Peak Time for Intercaste Marriage:');
      notes.push('• Maximum likelihood of meeting partner from different background');
      notes.push('• Strong unconventional choices in relationships NOW');
      notes.push('• Family may express concerns - stay patient and diplomatic');
      notes.push('• This is THE period for breaking traditional patterns');
      notes.push('');
    }
    
    if (isSeventhLordActive) {
      notes.push(`🔥 ${seventhLord.toUpperCase()} DASHA ACTIVE (7th Lord) - Marriage Window:`);
      notes.push('• Favorable time for marriage decisions and commitments');
      notes.push('• Partner from different community highly likely NOW');
      notes.push('• Important relationship developments expected');
      notes.push('• Family negotiations and approvals in focus');
      notes.push('');
    }
    
    notes.push('🎯 CURRENT PERIOD GUIDANCE:');
    notes.push('• Be prepared to defend partner choice with logic and maturity');
    notes.push('• Demonstrate partner\'s good character to family');
    notes.push('• Seek elder blessings even if marriage is unconventional');
    notes.push('• Legal documentation and formal marriage registration');
    notes.push('• Consider pre-marital counseling for cultural differences');
    notes.push('');
    notes.push('💫 OPPORTUNITY WINDOW:');
    notes.push('• This dasha period offers cosmic support for intercaste union');
    notes.push('• Society is more accepting during this time');
    notes.push('• Partner met now likely to be life-changing');
    notes.push('• Family resistance will eventually transform to acceptance');
    notes.push('');
    notes.push('✨ REMEDIES FOR SMOOTH UNION:');
    notes.push('• Rahu mantra: "Om Rahave Namaha" 108 times daily');
    notes.push('• Donate to charitable causes on Saturdays');
    notes.push('• Worship Goddess Durga for removing obstacles');
    notes.push('• Keep coconut at home for positive energy');
    notes.push('• Involve neutral family elders as mediators');
    
    // Higher positive score when dasha is active
    const baseScore = 40;
    const dashaBonus = (isRahuDashaActive || isSeventhLordActive) ? 15 : 0;
    const totalScore = baseScore + dashaBonus;
    
    return { value: totalScore, notes };
  }
};

export const RULES: Rule[] = [
  motherProperty,
  fatherGuidance,
  spouseBoundaries,
  careerPromotion,
  investments,
  accidentRisk,
  healthHygiene,
  sixthLordSookshmaHealthHazard,  // RED ALERT: 6th lord SD health hazard (self/spouse/parents)
  eighthLordSookshmaLosses,       // RED ALERT: 8th lord SD financial losses & obstacles
  marriagePartnership,
  transformation8thHouse,
  secrecyPrinciple,
  trishadayaUpachaya,
  maleficDusthanaFromOwnHouse,
  foreignSettlementYoga,     // Foreign settlement potential
  combustionAffliction,       // Combust planets lack recognition
  rahuAloneRajYoga,          // Rahu alone (no aspects) - king-making yoga
  kaalSarpYoga,
  sadeSatiRising,
  sadeSatiPeak,
  sadeSatiSetting,
  kendraTrikonaRajYogaNatal,  // General kendra-trikona raj yogas - catches all combinations
  dharmaKarmaRajYogaNatal,  // Natal version - permanent feature
  dharmaKarmaRajYoga,        // Timing version - activated by dasha
  lakshmiYoga,
  gajaKesariYogaNatal,       // Natal version
  gajaKesariYoga,
  adhiYoga,
  budhadityaYogaNatal,       // Natal version
  budhadityaYoga,
  hamsaYoga,
  shashaYoga,
  ruchakaYoga,
  malavyaYogaNatal,          // Natal version
  malavyaYoga,
  bhadraYoga,
  neechBhangDispositor,      // Neech Bhang Rule 1: Dispositor conjunction/aspect
  neechBhangExaltationLord,  // Neech Bhang Rule 2: Exaltation lord conjunction/aspect
  neechBhangMutualAspect,    // Neech Bhang Rule 3: Two debilitated planets aspect each other
  neechBhangDispositorInKendra, // Neech Bhang Rule 4: Dispositor in kendra
  saturnKetuPositionAnalysis,    // Saturn analysis based on Ketu position (houses 1-6 good, 7-12 bad)
  saturnSunConjunction,          // Saturn + Sun conjunction effects
  saturnMoonConjunction,         // Saturn + Moon conjunction effects
  saturnMarsConjunction,         // Saturn + Mars conjunction effects
  saturnMercuryConjunction,      // Saturn + Mercury conjunction effects
  saturnJupiterConjunction,      // Saturn + Jupiter conjunction effects
  saturnVenusConjunction,        // Saturn + Venus conjunction effects
  saturnRahuConjunction,         // Saturn + Rahu conjunction effects
  saturnKetuConjunction,         // Saturn + Ketu conjunction effects
  criminalMindsetRahuConjunction,  // PRIVATE: Criminal tendencies (Mercury/Mars + Rahu)
  rapistMindset8thHouseAffliction, // PRIVATE: Sexual violence tendencies (8th house malefic affliction)
  
  // Classical Parashara Yogas from BPHS
  ...CLASSICAL_YOGAS,            // Vipareeta Raj, Chandra-Mangala, Amala, Sunapha, Anapha, Durudhara, Parivartana, Kahala, Parvata, Saraswati
  
  // Two-Planet Conjunction Yogas
  ...TWO_PLANET_COMBINATIONS,    // Sun-Moon, Sun-Jupiter, Sun-Venus, Moon-Jupiter, Mars-Jupiter, Mars-Venus, Mercury-Jupiter, Mercury-Venus, Jupiter-Venus, Moon-Mercury, Moon-Venus
];

// ===== NATAL PREDICTION RULES (Planet Combinations) =====
// These are permanent natal conditions based on specific planet placements and combinations

// Sun in 1st house combinations
// NOTE: For functional malefic check, these trigger when Sun is in 1st with specific combinations
// User should verify if Sun is functional malefic for their ascendant
const sunMalefic1stVenus7th: Rule = {
  key: 'sun_malefic_1st_venus_7th_natal',
  areas: ['Health'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  private: false,
  customPrediction: 'TUBERCULOSIS RISK: Sun in 1st house + Venus in 7th house indicates potential tuberculosis susceptibility (especially if Sun is functional malefic for your ascendant). CAUTION: Monitor respiratory health, avoid smoking, maintain strong immunity. REMEDY: Sun worship on Sundays, pranayama breathing exercises, vitamin D supplementation.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const venusPlacement = dasha.natalPlacements?.Venus;
    return !!sunPlacement && sunPlacement.house === 1 && !!venusPlacement && venusPlacement.house === 7;
  },
  score: () => ({ value: -0.40, notes: ['Sun in 1st + Venus in 7th → tuberculosis risk'] })
};

const sunMalefic1stMars5th: Rule = {
  key: 'sun_malefic_1st_mars_5th_natal',
  areas: ['Children'],
  templateKey: 'children_challenges',
  priority: 9,
  ruleType: 'natal',
  private: true, // Sensitive prediction
  customPrediction: 'SONS MORTALITY RISK: Sun in 1st house + Mars in 5th house indicates potential sequential loss of sons (especially if Sun is functional malefic). CAUTION: Special care needed during sons\' early years, health vigilance required. REMEDY: Perform Santana Gopala puja, donate to children\'s hospitals, Mars remedies on Tuesdays.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const marsPlacement = dasha.natalPlacements?.Mars;
    return !!sunPlacement && sunPlacement.house === 1 && !!marsPlacement && marsPlacement.house === 5;
  },
  score: () => ({ value: -0.60, notes: ['Sun in 1st + Mars in 5th → sons mortality risk'] })
};

const sunMalefic1stSaturn8th: Rule = {
  key: 'sun_malefic_1st_saturn_8th_natal',
  areas: ['Spouse'],
  templateKey: 'spouse_challenges',
  priority: 9,
  ruleType: 'natal',
  private: true, // Sensitive prediction
  customPrediction: 'SPOUSE MORTALITY RISK: Sun in 1st house + Saturn in 8th house indicates potential sequential loss of spouses (especially if Sun is functional malefic). CAUTION: Special health vigilance for spouse, particularly during Saturn periods. REMEDY: Perform Mrityunjaya mantra, Saturn remedies on Saturdays, joint spiritual practices with spouse.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    return !!sunPlacement && sunPlacement.house === 1 && !!saturnPlacement && saturnPlacement.house === 8;
  },
  score: () => ({ value: -0.65, notes: ['Sun in 1st + Saturn in 8th → spouse mortality risk'] })
};

const sunMalefic1stEmpty7thMarriageTiming: Rule = {
  key: 'sun_malefic_1st_empty_7th_marriage_timing_natal',
  areas: ['Spouse'],
  templateKey: 'marriage_timing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MARRIAGE TIMING CRITICAL: Sun in 1st house + empty 7th house requires marriage before age 24 for fortune (especially if Sun is functional malefic). TIMING: Marry before 24 = lucky and prosperous; after 24 = potential misfortunes. STRATEGY: Plan marriage between ages 21-24 for best outcomes. REMEDY: Venus strengthening, 7th lord worship.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const house7Empty = !Object.values(dasha.natalPlacements || {}).some((p: any) => p?.house === 7);
    return !!sunPlacement && sunPlacement.house === 1 && house7Empty;
  },
  score: () => ({ value: 0.20, notes: ['Sun in 1st + empty 7th → marry before 24 for fortune'] })
};

// Moon in 1st house combinations
const moon1stVenus7thInLawHarmony: Rule = {
  key: 'moon_1st_venus_7th_inlaw_harmony_natal',
  areas: ['Spouse', 'Mother'],
  templateKey: 'family_harmony',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'IN-LAW HARMONY: Moon in 1st + Venus in 7th creates exceptional mother-in-law and daughter-in-law relationship. BLESSING: Both women will live like mother and daughter, mutual love and respect. This is a rare and fortunate combination for family peace.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const venusPlacement = dasha.natalPlacements?.Venus;
    return !!moonPlacement && moonPlacement.house === 1 && !!venusPlacement && venusPlacement.house === 7;
  },
  score: () => ({ value: 0.50, notes: ['Moon in 1st + Venus in 7th → MIL & DIL like mother & daughter'] })
};

const moon1stVenus7thSaturnSun6thWealth: Rule = {
  key: 'moon_1st_venus_7th_saturn_sun_6th_wealth_natal',
  areas: ['Wealth', 'Health'],
  templateKey: 'wealth_caution',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'WEALTH AFFLICTION: Moon in 1st + Venus in 7th with Saturn/Sun in 6th damages native\'s wealth. CAUTION: Financial challenges, health expenses, litigation costs. REMEDY: Moon worship on Mondays, avoid speculation, build emergency fund.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const venusPlacement = dasha.natalPlacements?.Venus;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const sunPlacement = dasha.natalPlacements?.Sun;
    const maleficIn6th = (!!saturnPlacement && saturnPlacement.house === 6) || (!!sunPlacement && sunPlacement.house === 6);
    return !!moonPlacement && moonPlacement.house === 1 && !!venusPlacement && venusPlacement.house === 7 && maleficIn6th;
  },
  score: () => ({ value: -0.35, notes: ['Moon 1st + Venus 7th + Saturn/Sun 6th → wealth damage'] })
};

// Mars in 1st house combinations
const mars1stSaturnConjunctionHealth: Rule = {
  key: 'mars_1st_saturn_conjunction_health_natal',
  areas: ['Health'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'HEALTH VIGILANCE NEEDED: Mars in 1st conjunct Saturn requires constant health monitoring. CAUTION: Prone to accidents, inflammation, chronic issues, bone/blood problems. STRATEGY: Regular health checkups, avoid risky activities, maintain disciplined lifestyle. REMEDY: Mars remedies (Tuesdays), Saturn remedies (Saturdays), Hanuman worship.',
  triggers: (dasha) => {
    const marsPlacement = dasha.natalPlacements?.Mars;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    return !!marsPlacement && marsPlacement.house === 1 && !!saturnPlacement && saturnPlacement.house === 1;
  },
  score: () => ({ value: -0.45, notes: ['Mars + Saturn conjunct in 1st → health vigilance critical'] })
};

// Mercury in 1st house combinations
const mercury1stSunAspectRichSpouse: Rule = {
  key: 'mercury_1st_sun_aspect_rich_spouse_natal',
  areas: ['Spouse', 'Wealth'],
  templateKey: 'spouse_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'NOBLE SPOUSE: Sun with or aspecting Mercury in 1st house blesses spouse from rich/noble family with good nature. BLESSING: Marriage brings wealth, status, and a well-mannered partner. This is fortunate for matrimonial happiness.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const sunPlacement = dasha.natalPlacements?.Sun;
    if (!mercuryPlacement || mercuryPlacement.house !== 1) return false;
    // Check conjunction
    const sunConjunctMercury = !!sunPlacement && sunPlacement.house === 1;
    // Check if Sun aspects Mercury's house (Mercury is in 1st, so check if Sun aspects house 1)
    const sunAspects1st = !!sunPlacement && sunPlacement.aspects?.includes(1 as HouseNum);
    return sunConjunctMercury || sunAspects1st || false;
  },
  score: () => ({ value: 0.55, notes: ['Sun with/aspecting Mercury in 1st → spouse from rich/noble family'] })
};

const mercury1stSaturnMoonMentalHealth: Rule = {
  key: 'mercury_1st_saturn_moon_mental_health_natal',
  areas: ['Health', 'Self'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  private: false,
  customPrediction: 'MENTAL HEALTH CONCERN: Mercury + Saturn OR Mercury + Moon in 1st house indicates mental health challenges. CAUTION: Anxiety, depression, overthinking, mood instability possible. STRATEGY: Mental health support, meditation, avoid stress triggers. REMEDY: Mercury remedies (Wednesdays), counseling, mindfulness practices.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!mercuryPlacement || mercuryPlacement.house !== 1) return false;
    const mercurySaturn = !!saturnPlacement && saturnPlacement.house === 1;
    const mercuryMoon = !!moonPlacement && moonPlacement.house === 1;
    return mercurySaturn || mercuryMoon;
  },
  score: () => ({ value: -0.40, notes: ['Mercury + Saturn/Moon in 1st → mental health issues'] })
};

const mercury1stRahuConfusion: Rule = {
  key: 'mercury_1st_rahu_confusion_natal',
  areas: ['Self', 'Career'],
  templateKey: 'career_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'CONFUSION & MISGUIDED THINKING: Mercury + Rahu in 1st house creates mental confusion and misguided decisions. CAUTION: Prone to deception, wrong choices, scattered thinking, manipulation. STRATEGY: Seek trusted advisors, verify information, avoid impulsive decisions. REMEDY: Ganesha worship, Mercury strengthening, clarity meditation.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    return !!mercuryPlacement && mercuryPlacement.house === 1 && !!rahuPlacement && rahuPlacement.house === 1;
  },
  score: () => ({ value: -0.35, notes: ['Mercury + Rahu in 1st → confusion/misguided thinking'] })
};

const mercury1stAloneFortune34: Rule = {
  key: 'mercury_1st_alone_fortune_34_natal',
  areas: ['Career', 'Wealth', 'Self'],
  templateKey: 'late_bloomer',
  priority: 5,
  ruleType: 'natal',
  customPrediction: 'LATE BLOOMER FORTUNE: Mercury as the ONLY planet in 1st house brings great fortune after age 34. TIMING: Struggles before 34, then sudden success and recognition. PATIENCE: First half of life is preparation; second half is reward. STRATEGY: Persevere through early challenges, major breakthrough comes at 34+.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    if (!mercuryPlacement || mercuryPlacement.house !== 1) return false;
    const onlyPlanetIn1st = !Object.entries(dasha.natalPlacements || {})
      .some(([planet, p]: [string, any]) => planet !== 'Mercury' && p?.house === 1);
    return onlyPlanetIn1st;
  },
  score: () => ({ value: 0.65, notes: ['Mercury ONLY planet in 1st → great fortune after age 34'] })
};

const rahuKetuInLawsOffspring: Rule = {
  key: 'rahu_ketu_inlaws_offspring_adverse_natal',
  areas: ['Spouse', 'Children'],
  templateKey: 'family_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'RAHU-KETU IMPACT: Mercury in 1st + Rahu-Ketu axis adversely affects in-laws and offspring relationships. CAUTION: Tensions with in-laws, challenges with children, karmic family patterns, Mercury in ascendant with nodes creates family complexities. REMEDY: Rahu-Ketu remedies, Mercury strengthening, ancestor worship, patience with family, spiritual practices.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    // Only triggers when Mercury is in 1st house (and Rahu-Ketu exist, which is always)
    return !!mercuryPlacement && mercuryPlacement.house === 1;
  },
  score: () => ({ value: -0.30, notes: ['Mercury 1st + Rahu-Ketu axis → adverse for in-laws and offspring'] })
};

// Venus in 1st house combinations
const venus1stRahu3rdSpouseMentalHealth: Rule = {
  key: 'venus_1st_rahu_3rd_spouse_mental_health_natal',
  areas: ['Spouse', 'Health'],
  templateKey: 'spouse_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'SPOUSE MENTAL HEALTH: Venus in 1st + Rahu in 3rd indicates spouse may face mental health challenges. CAUTION: Partner may struggle with anxiety, confusion, or psychological issues. SUPPORT: Be understanding, encourage professional help, practice patience. REMEDY: Venus remedies, Rahu pacification, couple therapy.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    return !!venusPlacement && venusPlacement.house === 1 && !!rahuPlacement && rahuPlacement.house === 3;
  },
  score: () => ({ value: -0.40, notes: ['Venus 1st + Rahu 3rd → spouse mental health issues'] })
};

const venus1stMoon8thMotherInLawTension: Rule = {
  key: 'venus_1st_moon_8th_mother_inlaw_tension_natal',
  areas: ['Spouse', 'Mother'],
  templateKey: 'family_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MOTHER-IN-LAW TENSIONS: Venus in 1st + Moon in 8th creates friction with mother-in-law. CAUTION: Emotional conflicts, misunderstandings, domestic disharmony possible. STRATEGY: Maintain boundaries, practice diplomacy, avoid confrontations. REMEDY: Moon worship, Venus strengthening, family counseling.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const moonPlacement = dasha.natalPlacements?.Moon;
    return !!venusPlacement && venusPlacement.house === 1 && !!moonPlacement && moonPlacement.house === 8;
  },
  score: () => ({ value: -0.30, notes: ['Venus 1st + Moon 8th → mother-in-law tensions'] })
};

// Saturn in 1st house combinations
const saturn1stPlanets7thGoodResults: Rule = {
  key: 'saturn_1st_planets_7th_good_results_natal',
  areas: ['Spouse', 'Career', 'Self'],
  templateKey: 'saturn_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'SATURN BENEFITS ACTIVATED: Saturn in 1st with Mercury/Venus/Rahu/Ketu in 7th gives excellent Saturn results. BLESSING: Discipline brings rewards, partnerships flourish, longevity, steady progress. Saturn\'s positive qualities manifest strongly. LEVERAGE: Use Saturn\'s structure and patience for long-term success.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const venusPlacement = dasha.natalPlacements?.Venus;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    if (!saturnPlacement || saturnPlacement.house !== 1) return false;
    const beneficIn7th = (!!mercuryPlacement && mercuryPlacement.house === 7) || 
                        (!!venusPlacement && venusPlacement.house === 7) || 
                        (!!rahuPlacement && rahuPlacement.house === 7) || 
                        (!!ketuPlacement && ketuPlacement.house === 7);
    return beneficIn7th;
  },
  score: () => ({ value: 0.50, notes: ['Saturn 1st + Mercury/Venus/Rahu/Ketu 7th → good Saturn results'] })
};

const saturn1stVacantHousesBenefic: Rule = {
  key: 'saturn_1st_vacant_houses_benefic_natal',
  areas: ['Career', 'Self', 'Spouse'],
  templateKey: 'saturn_blessing',
  priority: 5,
  ruleType: 'natal',
  customPrediction: 'SATURN BECOMES BENEFIC: Saturn in 1st with vacant 3rd, 7th, 10th houses OR no Saturn enemies there makes Saturn benefic. BLESSING: Saturn\'s discipline, longevity, and success qualities activate without obstacles. Career advancement, stable relationships, and personal growth flourish.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    if (!saturnPlacement || saturnPlacement.house !== 1) return false;
    
    // Check if 3rd, 7th, 10th are vacant OR have no Saturn enemies (Sun, Moon, Mars)
    const enemies = ['Sun', 'Moon', 'Mars'];
    const houses = [3, 7, 10];
    
    const allClear = houses.every(house => {
      const planetsInHouse = Object.entries(dasha.natalPlacements || {})
        .filter(([planet, p]: [string, any]) => p?.house === house && enemies.includes(planet));
      return planetsInHouse.length === 0;
    });
    
    return allClear;
  },
  score: () => ({ value: 0.45, notes: ['Saturn 1st + no enemies in 3/7/10 → Saturn benefic'] })
};

const saturn1stMars2ndFinancialStruggle: Rule = {
  key: 'saturn_1st_mars_2nd_financial_struggle_natal',
  areas: ['Wealth', 'Property'],
  templateKey: 'wealth_caution',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'FINANCIAL STRUGGLES: Saturn in 1st + Mars in 2nd indicates financial difficulties and property loss. CAUTION: Wealth accumulation challenges, property disputes, family wealth issues. STRATEGY: Conservative financial planning, avoid speculation, build slowly. REMEDY: Saturn and Mars remedies, Lakshmi worship, debt avoidance.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const marsPlacement = dasha.natalPlacements?.Mars;
    return !!saturnPlacement && saturnPlacement.house === 1 && !!marsPlacement && marsPlacement.house === 2;
  },
  score: () => ({ value: -0.50, notes: ['Saturn 1st + Mars 2nd → financial struggles/property loss'] })
};

// Rahu in 1st house combinations
const rahu1stSunGlobalDebility: Rule = {
  key: 'rahu_1st_sun_global_debility_natal',
  areas: ['Father', 'Career', 'Self'],
  templateKey: 'planetary_affliction',
  priority: 9,
  ruleType: 'natal',
  customPrediction: 'RAHU HARMS SUN GLOBALLY: Rahu in 1st house damages Sun wherever Sun sits in the chart. IMPACT: Father issues, authority conflicts, ego challenges, career obstacles, confidence issues. The Sun\'s house placement suffers from Rahu\'s shadowy influence regardless of position. REMEDY: Strong Sun remedies, Surya mantra, father\'s blessings.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    return !!rahuPlacement && rahuPlacement.house === 1;
  },
  score: () => ({ value: -0.40, notes: ['Rahu in 1st → global Sun debility (harms Sun wherever it sits)'] })
};

const rahu1stSun4thCashFlow: Rule = {
  key: 'rahu_1st_sun_4th_cash_flow_natal',
  areas: ['Wealth', 'Property', 'Mother'],
  templateKey: 'wealth_caution',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'CASH-FLOW PROBLEMS: Rahu in 1st + Sun in 4th creates liquidity and cash management issues. CAUTION: Money comes and goes quickly, difficulty saving, property-related financial stress. STRATEGY: Strong budgeting, avoid real estate speculation, maintain liquid reserves. REMEDY: Sun strengthening, Rahu pacification, Lakshmi worship.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const sunPlacement = dasha.natalPlacements?.Sun;
    return !!rahuPlacement && rahuPlacement.house === 1 && !!sunPlacement && sunPlacement.house === 4;
  },
  score: () => ({ value: -0.35, notes: ['Rahu 1st + Sun 4th → cash-flow problems'] })
};

const rahu1stSun12thBedroomFights: Rule = {
  key: 'rahu_1st_sun_12th_bedroom_fights_natal',
  areas: ['Spouse', 'Health'],
  templateKey: 'spouse_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'BEDROOM CONFLICTS: Rahu in 1st + Sun in 12th causes marital bedroom fights and sleep disorders. CAUTION: Intimate relationship tensions, sleep disruption, subconscious stress, bed pleasure issues. STRATEGY: Separate sleeping if needed, couple therapy, bedroom Vastu. REMEDY: Sun and Rahu remedies, sleep hygiene, stress management.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const sunPlacement = dasha.natalPlacements?.Sun;
    return !!rahuPlacement && rahuPlacement.house === 1 && !!sunPlacement && sunPlacement.house === 12;
  },
  score: () => ({ value: -0.35, notes: ['Rahu 1st + Sun 12th → bedroom fights, sleep disorder'] })
};

// Ketu in 1st house combinations
const ketu1stMaleficSaturnFatherTeacher: Rule = {
  key: 'ketu_1st_malefic_saturn_father_teacher_natal',
  areas: ['Father', 'Spirituality'],
  templateKey: 'father_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'FATHER & TEACHER PROBLEMS: Ketu in 1st with Saturn creates potential problems for father and teachers/gurus (especially if Saturn is functional malefic). CAUTION: Strained father relationship, guru conflicts, spiritual confusion, authority figure issues. IMPACT: Difficulty learning from elders, father health concerns. REMEDY: Ketu remedies, Saturn pacification, respect for father/guru.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    // Note: Functional classification not available in DashaContext, but Saturn presence is concerning
    return !!ketuPlacement && ketuPlacement.house === 1 && !!saturnPlacement;
  },
  score: () => ({ value: -0.45, notes: ['Ketu 1st + Saturn → father & teacher problems'] })
};

const ketu1stEmpty2nd7thMercuryVenusBad: Rule = {
  key: 'ketu_1st_empty_2nd_7th_mercury_venus_bad_natal',
  areas: ['Wealth', 'Spouse'],
  templateKey: 'wealth_spouse_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MERCURY & VENUS AFFLICTED: Ketu in 1st with empty 2nd and 7th houses makes Mercury and Venus give bad results. CAUTION: Wealth struggles, speech issues, relationship problems, marriage challenges. Mercury and Venus blessings blocked. REMEDY: Mercury and Venus strengthening, Ketu pacification, marital harmony efforts.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    if (!ketuPlacement || ketuPlacement.house !== 1) return false;
    const house2Empty = !Object.entries(dasha.natalPlacements || {}).some(([_, p]: [string, any]) => p?.house === 2);
    const house7Empty = !Object.entries(dasha.natalPlacements || {}).some(([_, p]: [string, any]) => p?.house === 7);
    return house2Empty && house7Empty;
  },
  score: () => ({ value: -0.40, notes: ['Ketu 1st + empty 2nd & 7th → Mercury & Venus give bad results'] })
};

// ===== SUN IN 2ND HOUSE COMBINATIONS =====
const sun2ndMoon6thAuspicious: Rule = {
  key: 'sun_2nd_moon_6th_auspicious_natal',
  areas: ['Health', 'Wealth', 'Litigation'],
  templateKey: 'health_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'ENHANCED AUSPICIOUSNESS: Sun in 2nd + Moon in 6th enhances overall auspiciousness. BLESSING: Victory over enemies, good health, financial stability, ability to overcome obstacles. The 2nd-6th axis creates protective energy. LEVERAGE: Use this fortunate placement for career advancement and health maintenance.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const moonPlacement = dasha.natalPlacements?.Moon;
    return !!sunPlacement && sunPlacement.house === 2 && !!moonPlacement && moonPlacement.house === 6;
  },
  score: () => ({ value: 0.45, notes: ['Sun 2nd + Moon 6th → enhanced auspiciousness'] })
};

const sun2ndRahu9thArtistRecognition: Rule = {
  key: 'sun_2nd_rahu_9th_artist_recognition_natal',
  areas: ['Career', 'Spirituality', 'Travel'],
  templateKey: 'career_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'ARTISTIC RECOGNITION: Sun in 2nd + Rahu in 9th brings recognition as an artist. BLESSING: Creative talents acknowledged, fame through artistic pursuits, international recognition possible. Strong communication skills combined with dharmic action. LEVERAGE: Pursue artistic careers, public speaking, creative expression.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    return !!sunPlacement && sunPlacement.house === 2 && !!rahuPlacement && rahuPlacement.house === 9;
  },
  score: () => ({ value: 0.55, notes: ['Sun 2nd + Rahu 9th → recognition as artist'] })
};

const sun2ndKetu7thHonesty: Rule = {
  key: 'sun_2nd_ketu_7th_honesty_natal',
  areas: ['Spouse', 'Self', 'Career'],
  templateKey: 'character_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'HONESTY & SINCERITY: Sun in 2nd + Ketu in 7th gives honesty and sincerity. BLESSING: Truthful nature, sincere partnerships, authentic self-expression. May prefer spiritual over material partnerships. VIRTUE: Strong moral character, trustworthiness, spiritual inclination in relationships.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    return !!sunPlacement && sunPlacement.house === 2 && !!ketuPlacement && ketuPlacement.house === 7;
  },
  score: () => ({ value: 0.40, notes: ['Sun 2nd + Ketu 7th → honesty/sincerity'] })
};

const sun2ndMars9thStylish: Rule = {
  key: 'sun_2nd_mars_9th_stylish_natal',
  areas: ['Self', 'Career', 'Travel'],
  templateKey: 'personality_blessing',
  priority: 5,
  ruleType: 'natal',
  customPrediction: 'STYLISH & CLASSY PERSONA: Sun in 2nd + Mars in 9th creates a stylish, classy persona. BLESSING: Charismatic presence, refined taste, dignified bearing, attractive personality. Commands respect and admiration. SOCIAL: Strong social presence, leadership qualities, elegant communication.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const marsPlacement = dasha.natalPlacements?.Mars;
    return !!sunPlacement && sunPlacement.house === 2 && !!marsPlacement && marsPlacement.house === 9;
  },
  score: () => ({ value: 0.35, notes: ['Sun 2nd + Mars 9th → stylish/classy persona'] })
};

const sun2ndMoon12thMars1stDifficulties: Rule = {
  key: 'sun_2nd_moon_12th_mars_1st_difficulties_natal',
  areas: ['Health', 'Wealth', 'Self'],
  templateKey: 'life_challenges',
  priority: 9,
  ruleType: 'natal',
  customPrediction: 'SEVERE DIFFICULTIES: Sun in 2nd + Moon in 12th + Mars in 1st creates severe life difficulties. CAUTION: Financial struggles, health issues, emotional turmoil, aggressive tendencies, loss of peace. This triple combination creates intense karmic challenges. REMEDY: Strong remedies for all three planets, spiritual practices, anger management, financial discipline.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const moonPlacement = dasha.natalPlacements?.Moon;
    const marsPlacement = dasha.natalPlacements?.Mars;
    return !!sunPlacement && sunPlacement.house === 2 && 
           !!moonPlacement && moonPlacement.house === 12 && 
           !!marsPlacement && marsPlacement.house === 1;
  },
  score: () => ({ value: -0.70, notes: ['Sun 2nd + Moon 12th + Mars 1st → severe difficulties'] })
};

const sun2ndMars8thGreedConflicts: Rule = {
  key: 'sun_2nd_mars_8th_greed_conflicts_natal',
  areas: ['Wealth', 'Property', 'Health'],
  templateKey: 'family_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'GREED & FAMILY CONFLICTS: Sun in 2nd + Mars in 8th indicates greed and family conflicts. CAUTION: Money disputes, inheritance battles, aggressive pursuit of wealth, family property conflicts, potential for accidents. The 2nd-8th axis of wealth becomes afflicted. REMEDY: Mars pacification, cultivate generosity, avoid speculation, family mediation.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const marsPlacement = dasha.natalPlacements?.Mars;
    return !!sunPlacement && sunPlacement.house === 2 && !!marsPlacement && marsPlacement.house === 8;
  },
  score: () => ({ value: -0.55, notes: ['Sun 2nd + Mars 8th → greed & family conflicts'] })
};

// ===== MOON IN 2ND HOUSE COMBINATIONS =====
const moon2ndKetu12thEducationOffspring: Rule = {
  key: 'moon_2nd_ketu_12th_education_offspring_natal',
  areas: ['Children', 'Spirituality', 'Wealth'],
  templateKey: 'children_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'EDUCATION/OFFSPRING DEPRIVATION: Moon in 2nd + Ketu in 12th deprives education or male offspring. CAUTION: Educational obstacles, difficulty with formal learning, challenges conceiving/raising male children, or children may pursue spiritual/foreign paths. REMEDY: Moon and Ketu remedies, Saraswati worship for education, Jupiter strengthening for offspring.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    return !!moonPlacement && moonPlacement.house === 2 && !!ketuPlacement && ketuPlacement.house === 12;
  },
  score: () => ({ value: -0.50, notes: ['Moon 2nd + Ketu 12th → education/male offspring deprivation'] })
};

// ===== JUPITER IN 2ND HOUSE COMBINATIONS =====
const jupiter2ndKetu6thDeathFear: Rule = {
  key: 'jupiter_2nd_ketu_6th_death_fear_natal',
  areas: ['Health', 'Spirituality'],
  templateKey: 'health_alert',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'FEAR OF DEATH: Jupiter in 2nd + Ketu in 6th creates fear of death or health anxiety. CAUTION: Phobias about mortality, health paranoia, spiritual anxiety, may face serious health scares. The wisdom of Jupiter meets the detachment of Ketu creating existential concerns. REMEDY: Jupiter strengthening, Ketu pacification, Mahamrityunjaya mantra, spiritual counseling.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    return !!jupiterPlacement && jupiterPlacement.house === 2 && !!ketuPlacement && ketuPlacement.house === 6;
  },
  score: () => ({ value: -0.35, notes: ['Jupiter 2nd + Ketu 6th → fear of death'] })
};

// ===== MARS IN 2ND HOUSE COMBINATIONS =====
const mars2ndMercuryWeakWillpower: Rule = {
  key: 'mars_2nd_mercury_weak_willpower_natal',
  areas: ['Self', 'Career', 'Wealth'],
  templateKey: 'personality_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'WEAK WILLPOWER & INFLUENCE: Mars + Mercury in 2nd house reduces willpower and influence. CAUTION: Indecisiveness, difficulty asserting oneself, communication lacks force, reduced impact in family/career. The warrior (Mars) and communicator (Mercury) conflict in wealth house. REMEDY: Mars strengthening, confidence building, assertiveness training, speech therapy.',
  triggers: (dasha) => {
    const marsPlacement = dasha.natalPlacements?.Mars;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    return !!marsPlacement && marsPlacement.house === 2 && !!mercuryPlacement && mercuryPlacement.house === 2;
  },
  score: () => ({ value: -0.40, notes: ['Mars + Mercury in 2nd → weak willpower, reduced influence'] })
};

// ===== MERCURY IN 2ND HOUSE COMBINATIONS =====
const mercury2ndEmpty8thEarnNoSave: Rule = {
  key: 'mercury_2nd_empty_8th_earn_no_save_natal',
  areas: ['Wealth', 'Property'],
  templateKey: 'wealth_caution',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'EARNS WELL BUT STRUGGLES TO SAVE: Mercury in 2nd + empty 8th house = earns well but struggles to save. CAUTION: Good income but poor savings, money flows out easily, transformation (8th) of wealth is blocked. Earnings are present but wealth accumulation difficult. STRATEGY: Forced savings plans, investments, financial discipline, plug spending leaks.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    if (!mercuryPlacement || mercuryPlacement.house !== 2) return false;
    const house8Empty = !Object.entries(dasha.natalPlacements || {}).some(([_, p]: [string, any]) => p?.house === 8);
    return house8Empty;
  },
  score: () => ({ value: -0.30, notes: ['Mercury 2nd + empty 8th → earns well but cannot save'] })
};

// ===== VENUS IN 2ND HOUSE COMBINATIONS =====
const venus2ndSaturnDisciplinedSavings: Rule = {
  key: 'venus_2nd_saturn_disciplined_savings_natal',
  areas: ['Wealth', 'Property'],
  templateKey: 'wealth_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'DISCIPLINED SAVINGS: Venus in 2nd with Saturn present/aspecting 2nd helps disciplined savings. BLESSING: Saturn\'s discipline combines with Venus\'s wealth-giving nature. Systematic wealth accumulation, long-term financial stability, asset building. LEVERAGE: Use this combination for real estate, systematic investments, retirement planning.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    if (!venusPlacement || venusPlacement.house !== 2) return false;
    // Check if Saturn is in 2nd or aspects it (simplified: check common aspect houses)
    const saturnIn2nd = !!saturnPlacement && saturnPlacement.house === 2;
    // Saturn aspects 3rd, 7th, 10th from its position - check if any aspect house 2
    const saturnAspects2nd = !!saturnPlacement && saturnPlacement.aspects?.includes(2 as HouseNum);
    return saturnIn2nd || saturnAspects2nd || false;
  },
  score: () => ({ value: 0.45, notes: ['Venus 2nd + Saturn present/aspecting → disciplined savings'] })
};

// ===== SATURN IN 2ND HOUSE COMBINATIONS =====
const saturn2ndDepends8thPlanets: Rule = {
  key: 'saturn_2nd_depends_8th_planets_natal',
  areas: ['Wealth', 'Property', 'Health'],
  templateKey: 'saturn_mixed',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'OUTCOMES DEPEND ON 8TH HOUSE: Saturn in 2nd house outcomes depend on planets in 8th (longevity axis). INSIGHT: 2nd-8th axis represents wealth-longevity. Benefics in 8th support Saturn\'s 2nd house position; malefics create challenges. Check 8th house for timing of wealth/health events. STRATEGY: Analyze 8th house carefully for life trajectory.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    if (!saturnPlacement || saturnPlacement.house !== 2) return false;
    // Check if any planets are in 8th
    const planetsIn8th = Object.entries(dasha.natalPlacements || {}).some(([_, p]: [string, any]) => p?.house === 8);
    return planetsIn8th;
  },
  score: () => ({ value: 0, notes: ['Saturn 2nd + planets in 8th → outcomes vary by 8th house'] })
};

// ===== RAHU IN 2ND HOUSE COMBINATIONS =====
const rahu2ndJupiterStrongProsperity: Rule = {
  key: 'rahu_2nd_jupiter_strong_prosperity_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'EASY PROSPERITY: Rahu in 2nd + strong Jupiter = easy prosperity. BLESSING: Jupiter\'s expansion and wisdom channels Rahu\'s ambition positively. Wealth comes through unconventional means, foreign connections, technology. LEVERAGE: Use Jupiter\'s blessings to guide Rahu\'s material pursuits ethically.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    if (!rahuPlacement || rahuPlacement.house !== 2 || !jupiterPlacement) return false;
    // Simplified "strong Jupiter" check: Jupiter in own sign, exalted, or in kendra/trikona
    const strongHouses = [1, 4, 5, 7, 9, 10]; // Kendra + Trikona
    const jupiterStrong = strongHouses.includes(jupiterPlacement.house);
    return jupiterStrong;
  },
  score: () => ({ value: 0.50, notes: ['Rahu 2nd + strong Jupiter → easy prosperity'] })
};

// ===== KETU IN 2ND HOUSE COMBINATIONS =====
const ketu2ndJupiterStrongCrores: Rule = {
  key: 'ketu_2nd_jupiter_strong_crores_natal',
  areas: ['Wealth', 'Spirituality'],
  templateKey: 'wealth_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'POTENTIAL TO EARN CRORES: Ketu in 2nd + strong Jupiter = potential to earn crores. BLESSING: Spiritual detachment (Ketu) combined with Jupiter\'s expansion creates massive wealth potential, often through unexpected or spiritual means. LEVERAGE: Use wealth for dharmic purposes; spiritual wealth practices.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    if (!ketuPlacement || ketuPlacement.house !== 2 || !jupiterPlacement) return false;
    const strongHouses = [1, 4, 5, 7, 9, 10];
    const jupiterStrong = strongHouses.includes(jupiterPlacement.house);
    return jupiterStrong;
  },
  score: () => ({ value: 0.65, notes: ['Ketu 2nd + strong Jupiter → potential crores earnings'] })
};

const ketu2ndVenusWellPlaced: Rule = {
  key: 'ketu_2nd_venus_well_placed_natal',
  areas: ['Wealth', 'Spouse'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'EXCELLENT VENUS RESULTS: Ketu in 2nd + Venus well-placed = excellent results regardless of Venus\'s house. BLESSING: Venus thrives when Ketu occupies wealth house. Love, luxury, artistic talents, marital harmony all enhanced. The spiritual (Ketu) and material (Venus) balance beautifully. LEVERAGE: Pursue creative, artistic, or beauty-related wealth generation.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const venusPlacement = dasha.natalPlacements?.Venus;
    if (!ketuPlacement || ketuPlacement.house !== 2 || !venusPlacement) return false;
    // Well-placed Venus: own sign, exalted, or in good houses (1,4,5,7,9,10,11)
    const goodHouses = [1, 4, 5, 7, 9, 10, 11];
    const venusWellPlaced = goodHouses.includes(venusPlacement.house);
    return venusWellPlaced;
  },
  score: () => ({ value: 0.55, notes: ['Ketu 2nd + Venus well-placed → excellent Venus results'] })
};

const ketu2ndMoonBadResults: Rule = {
  key: 'ketu_2nd_moon_bad_results_natal',
  areas: ['Health', 'Mother', 'Wealth'],
  templateKey: 'planetary_affliction',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'MOON GIVES BAD RESULTS: Ketu in 2nd with Moon (conjunction or aspect) = Moon gives bad results. CAUTION: Emotional instability, mother issues, mental stress, family disharmony, speech problems. The Moon\'s nurturing is blocked by Ketu\'s detachment in family/wealth house. REMEDY: Strong Moon remedies, mother\'s blessings, emotional healing.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!ketuPlacement || ketuPlacement.house !== 2 || !moonPlacement) return false;
    // Check conjunction in 2nd
    const moonIn2nd = moonPlacement.house === 2;
    // Check if Moon aspects 2nd (simplified)
    const moonAspects2nd = moonPlacement.aspects?.includes(2 as HouseNum);
    return moonIn2nd || moonAspects2nd || false;
  },
  score: () => ({ value: -0.45, notes: ['Ketu 2nd + Moon → Moon gives bad results'] })
};

const ketu2ndMoonMars8thShorterLifespan: Rule = {
  key: 'ketu_2nd_moon_mars_8th_shorter_lifespan_natal',
  areas: ['Health', 'Property'],
  templateKey: 'health_alert',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'LONGEVITY CONCERNS: Ketu in 2nd + (Moon OR Mars) in 8th = shorter lifespan or major difficulties at age 16 or 20. CAUTION: Critical health vulnerabilities, accidents possible, transformative life crises at specific ages. The 2nd-8th longevity axis is severely afflicted. REMEDY: Mahamrityunjaya mantra, strong remedies for Ketu and afflicting planet, protective rituals at ages 16 and 20.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const moonPlacement = dasha.natalPlacements?.Moon;
    const marsPlacement = dasha.natalPlacements?.Mars;
    if (!ketuPlacement || ketuPlacement.house !== 2) return false;
    const moonIn8th = !!moonPlacement && moonPlacement.house === 8;
    const marsIn8th = !!marsPlacement && marsPlacement.house === 8;
    return moonIn8th || marsIn8th;
  },
  score: () => ({ value: -0.75, notes: ['Ketu 2nd + Moon/Mars 8th → longevity concerns, crises at 16/20'] })
};

const ketu2ndEmpty8thMaleficIntensify: Rule = {
  key: 'ketu_2nd_empty_8th_malefic_intensify_natal',
  areas: ['Wealth', 'Health', 'Property'],
  templateKey: 'ketu_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'MALEFIC RESULTS INTENSIFY: Ketu in 2nd + empty 8th house = Ketu\'s malefic results intensify. CAUTION: Wealth instability, speech difficulties, family discord, lack of transformative support (8th empty). The detachment of Ketu in family house becomes more pronounced. STRATEGY: Extra financial discipline, strong family bonds cultivation, speech care.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    if (!ketuPlacement || ketuPlacement.house !== 2) return false;
    const house8Empty = !Object.entries(dasha.natalPlacements || {}).some(([_, p]: [string, any]) => p?.house === 8);
    return house8Empty;
  },
  score: () => ({ value: -0.40, notes: ['Ketu 2nd + empty 8th → malefic results intensify'] })
};

// ===== SUN IN 3RD HOUSE COMBINATIONS =====
const sun3rdSaturnRahuVenus1stMalefic: Rule = {
  key: 'sun_3rd_saturn_rahu_venus_1st_malefic_natal',
  areas: ['Self', 'Wealth', 'Health'],
  templateKey: 'planetary_affliction',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'SUN BECOMES MALEFIC: Sun in 3rd + Saturn/Rahu/Venus in 1st makes Sun malefic. CAUTION: Courage weakened, communication problems, sibling conflicts, self-esteem issues. The malefic in ascendant afflicts Sun\'s 3rd house position. REMEDY: Sun strengthening, confidence building, avoid conflicts with authority.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const venusPlacement = dasha.natalPlacements?.Venus;
    if (!sunPlacement || sunPlacement.house !== 3) return false;
    const maleficIn1st = (!!saturnPlacement && saturnPlacement.house === 1) || 
                         (!!rahuPlacement && rahuPlacement.house === 1) || 
                         (!!venusPlacement && venusPlacement.house === 1);
    return maleficIn1st;
  },
  score: () => ({ value: -0.40, notes: ['Sun 3rd + Saturn/Rahu/Venus 1st → Sun malefic'] })
};

const sun3rdConjunctMalefic: Rule = {
  key: 'sun_3rd_conjunct_saturn_rahu_venus_malefic_natal',
  areas: ['Self', 'Wealth', 'Career'],
  templateKey: 'planetary_affliction',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'SUN CONJUNCT MALEFIC: Sun conjunct Saturn/Rahu/Venus in 3rd = malefic combination. CAUTION: Courage compromised, communication afflicted, sibling rivalry, efforts yield poor results. The conjunction directly damages Sun\'s significations. REMEDY: Strong Sun remedies, avoid ego battles, patience with siblings.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const venusPlacement = dasha.natalPlacements?.Venus;
    if (!sunPlacement || sunPlacement.house !== 3) return false;
    const maleficConjunct = (!!saturnPlacement && saturnPlacement.house === 3) || 
                            (!!rahuPlacement && rahuPlacement.house === 3) || 
                            (!!venusPlacement && venusPlacement.house === 3);
    return maleficConjunct;
  },
  score: () => ({ value: -0.45, notes: ['Sun conjunct Saturn/Rahu/Venus in 3rd → malefic'] })
};

const sun3rdMaleficMoonRobbery: Rule = {
  key: 'sun_3rd_malefic_moon_robbery_natal',
  areas: ['Wealth', 'Property', 'Self'],
  templateKey: 'wealth_alert',
  priority: 9,
  ruleType: 'natal',
  private: false,
  customPrediction: 'DAYLIGHT ROBBERY RISK: Malefic Moon + Sun in 3rd = daylight robbery of native/house. CAUTION: Vulnerable to theft, scams, deception in broad daylight, property loss. The afflicted Moon-Sun in courage house creates security vulnerabilities. REMEDY: Home security, vigilance, Moon and Sun remedies, protective rituals.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!sunPlacement || sunPlacement.house !== 3 || !moonPlacement || moonPlacement.house !== 3) return false;
    // Simplified malefic Moon check (would need actual functional classification)
    // For now, check if Moon is with malefics Saturn/Rahu/Ketu
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const moonAfflicted = (!!saturnPlacement && saturnPlacement.house === 3) || 
                          (!!rahuPlacement && rahuPlacement.house === 3) || 
                          (!!ketuPlacement && ketuPlacement.house === 3);
    return moonAfflicted;
  },
  score: () => ({ value: -0.60, notes: ['Malefic Moon + Sun 3rd → daylight robbery risk'] })
};

const sun3rd9thAfflictedImpoverishedAncestors: Rule = {
  key: 'sun_3rd_9th_afflicted_impoverished_ancestors_natal',
  areas: ['Father', 'Spirituality', 'Wealth'],
  templateKey: 'ancestral_karma',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'IMPOVERISHED ANCESTORS: Malefic Sun in 3rd + 9th house afflicted = impoverished ancestors. INSIGHT: Ancestral poverty, weak father lineage, dharma compromised. The father house (9th) affliction combined with weak Sun in 3rd shows ancestral struggles. KARMA: Clear ancestral debts, honor elders, spiritual practices for lineage healing.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    if (!sunPlacement || sunPlacement.house !== 3) return false;
    // Check if 9th house has malefics
    const house9Malefics = Object.entries(dasha.natalPlacements || {})
      .filter(([planet, p]: [string, any]) => {
        if (!p || p.house !== 9) return false;
        return ['Saturn', 'Mars', 'Rahu', 'Ketu'].includes(planet);
      });
    return house9Malefics.length > 0;
  },
  score: () => ({ value: -0.35, notes: ['Malefic Sun 3rd + 9th afflicted → impoverished ancestors'] })
};

const sun3rd1stAfflictedNeighborDownfall: Rule = {
  key: 'sun_3rd_1st_afflicted_neighbor_downfall_natal',
  areas: ['Self', 'Property'],
  templateKey: 'neighborhood_karma',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'NEIGHBOR\'S DOWNFALL: Sun in 3rd + 1st house afflicted = neighbor\'s downfall. CAUTION: Neighbors face troubles, neighborhood conflicts, local environment deteriorates. The self (1st) affliction with Sun in immediate environment (3rd) creates neighborhood karma. IMPACT: May need to move or face neighborhood issues.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    if (!sunPlacement || sunPlacement.house !== 3) return false;
    // Check if 1st house has malefics
    const house1Malefics = Object.entries(dasha.natalPlacements || {})
      .filter(([planet, p]: [string, any]) => {
        if (!p || p.house !== 1) return false;
        return ['Saturn', 'Mars', 'Rahu', 'Ketu'].includes(planet);
      });
    return house1Malefics.length > 0;
  },
  score: () => ({ value: -0.30, notes: ['Sun 3rd + 1st afflicted → neighbor downfall'] })
};

const sun3rdMaternalUncleNoMaleProgeny: Rule = {
  key: 'sun_3rd_maternal_uncle_no_male_progeny_natal',
  areas: ['Mother', 'Children'],
  templateKey: 'family_challenges',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'MATERNAL UNCLE\'S PROGENY ISSUES: Afflicted Sun in 3rd indicates maternal uncle has no male progeny. INSIGHT: Mother\'s brother faces offspring challenges, particularly male children. The 3rd house (maternal relatives) affliction shows maternal uncle\'s difficulties. REMEDY: Support maternal family, ancestral rituals, Jupiter strengthening for uncle.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    if (!sunPlacement || sunPlacement.house !== 3) return false;
    // Check if Sun is afflicted (with malefics or in difficult state)
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const sunAfflicted = (!!saturnPlacement && saturnPlacement.house === 3) || 
                         (!!rahuPlacement && rahuPlacement.house === 3) || 
                         (!!ketuPlacement && ketuPlacement.house === 3) ||
                         (!!marsPlacement && marsPlacement.house === 3);
    return sunAfflicted;
  },
  score: () => ({ value: -0.25, notes: ['Afflicted Sun 3rd → maternal uncle no male progeny'] })
};

// ===== MOON IN 3RD HOUSE COMBINATIONS =====
const moon3rdEmpty9th11thMarsVenusGood: Rule = {
  key: 'moon_3rd_empty_9th_11th_mars_venus_good_natal',
  areas: ['Wealth', 'Career', 'Spouse'],
  templateKey: 'planetary_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MARS & VENUS GIVE GOOD RESULTS: Moon in 3rd + no planets in 9th & 11th = Mars and Venus give good results. BLESSING: Courage, passion (Mars), love, luxury (Venus) all enhanced. The empty 9th/11th allows Mars and Venus to function without interference. LEVERAGE: Pursue creative ventures, relationships, bold actions.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!moonPlacement || moonPlacement.house !== 3) return false;
    const house9Empty = !Object.entries(dasha.natalPlacements || {}).some(([_, p]: [string, any]) => p?.house === 9);
    const house11Empty = !Object.entries(dasha.natalPlacements || {}).some(([_, p]: [string, any]) => p?.house === 11);
    return house9Empty && house11Empty;
  },
  score: () => ({ value: 0.50, notes: ['Moon 3rd + empty 9th/11th → Mars & Venus good results'] })
};

const moon3rdBeneficKetuEducation: Rule = {
  key: 'moon_3rd_benefic_ketu_education_natal',
  areas: ['Spirituality', 'Career'],
  templateKey: 'education_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'GOOD FRUITS OF EDUCATION: Moon in 3rd + benefic Ketu that doesn\'t harm Moon = good education results. BLESSING: Spiritual education, communication skills, intuitive learning, wisdom. Ketu\'s detachment combines well with Moon\'s mind in learning house. LEVERAGE: Pursue higher education, spiritual studies, teaching.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    if (!moonPlacement || moonPlacement.house !== 3 || !ketuPlacement) return false;
    // Check Ketu doesn't harm Moon (not conjunct)
    const ketuNotConjunct = ketuPlacement.house !== 3;
    // Simplified benefic Ketu check - in good houses (1,4,5,7,9,10)
    const beneficHouses = [1, 4, 5, 7, 9, 10];
    const ketuBenefic = beneficHouses.includes(ketuPlacement.house);
    return ketuNotConjunct && ketuBenefic;
  },
  score: () => ({ value: 0.45, notes: ['Moon 3rd + benefic Ketu (not harming Moon) → good education'] })
};

const moon3rdMaleficWealthLoss: Rule = {
  key: 'moon_3rd_malefic_wealth_loss_natal',
  areas: ['Wealth', 'Property'],
  templateKey: 'wealth_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'GREAT LOSS OF WEALTH: Malefic Moon in 3rd = great loss of wealth. CAUTION: Financial instability, poor financial decisions, wealth depletion, family wealth loss. The afflicted mind (Moon) in effort house (3rd) creates wealth destruction. REMEDY: Moon strengthening, financial advisors, conservative investments, avoid speculation.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!moonPlacement || moonPlacement.house !== 3) return false;
    // Check if Moon is afflicted by malefics in 3rd
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const moonAfflicted = (!!saturnPlacement && saturnPlacement.house === 3) || 
                          (!!rahuPlacement && rahuPlacement.house === 3) || 
                          (!!ketuPlacement && ketuPlacement.house === 3) ||
                          (!!marsPlacement && marsPlacement.house === 3);
    return moonAfflicted;
  },
  score: () => ({ value: -0.55, notes: ['Malefic Moon 3rd → great wealth loss'] })
};

// ===== MERCURY IN 3RD HOUSE COMBINATIONS =====
const mercury3rdEmpty9th11thWealth: Rule = {
  key: 'mercury_3rd_empty_9th_11th_wealth_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'AMASS SIGNIFICANT WEALTH: Mercury in 3rd + 9th & 11th empty = amass significant wealth. BLESSING: Communication skills, business acumen, trade expertise create substantial wealth. Empty dharma (9th) and gains (11th) houses allow Mercury\'s commerce to flourish. LEVERAGE: Business, trading, communication-based income.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    if (!mercuryPlacement || mercuryPlacement.house !== 3) return false;
    const house9Empty = !Object.entries(dasha.natalPlacements || {}).some(([_, p]: [string, any]) => p?.house === 9);
    const house11Empty = !Object.entries(dasha.natalPlacements || {}).some(([_, p]: [string, any]) => p?.house === 11);
    return house9Empty && house11Empty;
  },
  score: () => ({ value: 0.60, notes: ['Mercury 3rd + empty 9th/11th → significant wealth'] })
};

const mercury3rdPlanets9th11thAdverse: Rule = {
  key: 'mercury_3rd_planets_9th_11th_adverse_natal',
  areas: ['Spirituality', 'Wealth', 'Career'],
  templateKey: 'planetary_affliction',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MERCURY ADVERSELY AFFECTS 9TH/11TH: Mercury in 3rd + planets in 9th/11th = Mercury harms those planets. CAUTION: Dharma compromised, gains obstructed, friendships strained. Mercury\'s commercial nature conflicts with 9th/11th house planets. IMPACT: Spiritual confusion, income instability, network issues.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    if (!mercuryPlacement || mercuryPlacement.house !== 3) return false;
    const planetsIn9thOr11th = Object.entries(dasha.natalPlacements || {})
      .some(([planet, p]: [string, any]) => p && (p.house === 9 || p.house === 11) && planet !== 'Mercury');
    return planetsIn9thOr11th;
  },
  score: () => ({ value: -0.35, notes: ['Mercury 3rd + planets in 9th/11th → adverse effects'] })
};

const mercury3rdKetuOverthinking: Rule = {
  key: 'mercury_3rd_ketu_overthinking_natal',
  areas: ['Self', 'Career', 'Health'],
  templateKey: 'mental_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'OVERTHINKING & POOR EXECUTION: Mercury + Ketu in 3rd = overthinking with poor execution. CAUTION: Analysis paralysis, spiritual confusion, communication scattered, ideas don\'t materialize. The intellect (Mercury) meets detachment (Ketu) creating execution problems. REMEDY: Action over analysis, grounding practices, practical mentors.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    return !!mercuryPlacement && mercuryPlacement.house === 3 && !!ketuPlacement && ketuPlacement.house === 3;
  },
  score: () => ({ value: -0.40, notes: ['Mercury + Ketu 3rd → overthinking, poor execution'] })
};

const mercury3rdSaturnHardworkingObstacles: Rule = {
  key: 'mercury_3rd_saturn_hardworking_obstacles_natal',
  areas: ['Career', 'Self', 'Wealth'],
  templateKey: 'saturn_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'HARDWORKING WITH OBSTACLES: Mercury + Saturn in 3rd = hardworking but faces many obstacles. REALITY: Disciplined efforts, persistent struggles, delayed results, communication challenges. Saturn restricts Mercury\'s quick nature in effort house. PATIENCE: Success comes through sustained effort despite obstacles. Long-term gains.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    return !!mercuryPlacement && mercuryPlacement.house === 3 && !!saturnPlacement && saturnPlacement.house === 3;
  },
  score: () => ({ value: -0.20, notes: ['Mercury + Saturn 3rd → hardworking but obstacles'] })
};

const mercury3rdRahuClever: Rule = {
  key: 'mercury_3rd_rahu_clever_natal',
  areas: ['Career', 'Self', 'Wealth'],
  templateKey: 'mental_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'HEIGHTENED CLEVERNESS: Mercury + Rahu in 3rd = heightened cleverness. BLESSING: Sharp intellect, cunning business mind, innovative communication, technology skills. The amplifier (Rahu) boosts Mercury\'s intelligence in skills house. LEVERAGE: Technology, unconventional businesses, strategic thinking. CAUTION: Avoid manipulation.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    return !!mercuryPlacement && mercuryPlacement.house === 3 && !!rahuPlacement && rahuPlacement.house === 3;
  },
  score: () => ({ value: 0.45, notes: ['Mercury + Rahu 3rd → heightened cleverness'] })
};

const mercury3rdKetu11thWalkingChildrenIssues: Rule = {
  key: 'mercury_3rd_ketu_11th_walking_children_natal',
  areas: ['Health', 'Children'],
  templateKey: 'health_alert',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'WALKING & MALE CHILDREN ISSUES: Mercury in 3rd + Ketu in 11th = walking difficulties and issues with male children. CAUTION: Leg problems, mobility challenges, male offspring struggles. The gains house (11th) with Ketu affects children and lower limbs. REMEDY: Ketu remedies, physiotherapy, Jupiter strengthening for children.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    return !!mercuryPlacement && mercuryPlacement.house === 3 && !!ketuPlacement && ketuPlacement.house === 11;
  },
  score: () => ({ value: -0.40, notes: ['Mercury 3rd + Ketu 11th → walking issues, male children problems'] })
};

// ===== VENUS IN 3RD HOUSE COMBINATIONS =====
const venus3rdPlanets9th11thManyDaughters: Rule = {
  key: 'venus_3rd_planets_9th_11th_many_daughters_natal',
  areas: ['Children', 'Spouse'],
  templateKey: 'children_prediction',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'MANY DAUGHTERS: Venus in 3rd + planets in 9th/11th unfavorable to Venus = many daughters. INSIGHT: Female offspring predominate, daughters bring blessings, family lineage through daughters. Venus (feminine) in 3rd with afflictions creates daughter-heavy progeny. BLESSING: Daughters as source of support and love.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    if (!venusPlacement || venusPlacement.house !== 3) return false;
    // Check for planets in 9th/11th that are unfavorable to Venus (malefics)
    const unfavorableIn9thOr11th = Object.entries(dasha.natalPlacements || {})
      .filter(([planet, p]: [string, any]) => {
        if (!p || (p.house !== 9 && p.house !== 11)) return false;
        return ['Saturn', 'Mars', 'Rahu', 'Ketu', 'Sun'].includes(planet);
      });
    return unfavorableIn9thOr11th.length > 0;
  },
  score: () => ({ value: 0.30, notes: ['Venus 3rd + malefics in 9th/11th → many daughters'] })
};

// ===== SATURN IN 3RD HOUSE COMBINATIONS =====
const saturn3rdEmpty9th12thWealth: Rule = {
  key: 'saturn_3rd_empty_9th_12th_wealth_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'saturn_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'FAVORABLE FOR WEALTH: Saturn in 3rd + 9th & 12th empty = favorable for wealth. BLESSING: Disciplined efforts, systematic wealth building, long-term gains. Empty dharma (9th) and loss (12th) houses allow Saturn\'s discipline to create wealth without obstacles. LEVERAGE: Conservative investments, real estate, structured businesses.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    if (!saturnPlacement || saturnPlacement.house !== 3) return false;
    const house9Empty = !Object.entries(dasha.natalPlacements || {}).some(([_, p]: [string, any]) => p?.house === 9);
    const house12Empty = !Object.entries(dasha.natalPlacements || {}).some(([_, p]: [string, any]) => p?.house === 12);
    return house9Empty && house12Empty;
  },
  score: () => ({ value: 0.50, notes: ['Saturn 3rd + empty 9th/12th → wealth favorable'] })
};

// ===== RAHU IN 3RD HOUSE COMBINATIONS =====
const rahu3rdSunMercurySisterWidow: Rule = {
  key: 'rahu_3rd_sun_mercury_sister_widow_natal',
  areas: ['Mother', 'Spouse'],
  templateKey: 'family_challenges',
  priority: 8,
  ruleType: 'natal',
  private: true,
  customPrediction: 'SISTER WIDOWHOOD RISK: Rahu + Sun + Mercury in 3rd = sister may become widow at age 22 or 32. CAUTION: Sister faces marital loss, critical ages 22/32. The siblings house (3rd) with this specific combination affects sister\'s marriage. REMEDY: Protective rituals for sister, Jupiter strengthening, marriage timing care for sister.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const sunPlacement = dasha.natalPlacements?.Sun;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    return !!rahuPlacement && rahuPlacement.house === 3 && 
           !!sunPlacement && sunPlacement.house === 3 && 
           !!mercuryPlacement && mercuryPlacement.house === 3;
  },
  score: () => ({ value: -0.50, notes: ['Rahu + Sun + Mercury 3rd → sister widow risk at 22/32'] })
};

// ===== KETU IN 3RD HOUSE COMBINATIONS =====
const ketu3rdMars12thSonBefore24: Rule = {
  key: 'ketu_3rd_mars_12th_son_before_24_natal',
  areas: ['Children', 'Health', 'Wealth'],
  templateKey: 'children_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'SON BEFORE 24 STRENGTHENS LIFE: Ketu in 3rd + Mars in 12th = son born before age 24 who strengthens native\'s health, wealth, and longevity. BLESSING: Early fatherhood brings vitality, son is source of strength and prosperity. The courage (Mars in 12th) and detachment (Ketu in 3rd) combination creates powerful offspring. TIMING: Have children before 24 for maximum benefit.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const marsPlacement = dasha.natalPlacements?.Mars;
    return !!ketuPlacement && ketuPlacement.house === 3 && !!marsPlacement && marsPlacement.house === 12;
  },
  score: () => ({ value: 0.55, notes: ['Ketu 3rd + Mars 12th → son before 24 strengthens life'] })
};

// ===== SUN IN 4TH HOUSE COMBINATIONS =====
const sun4thMoonResearchProfits: Rule = {
  key: 'sun_4th_moon_research_profits_natal',
  areas: ['Career', 'Wealth', 'Property'],
  templateKey: 'career_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'PROFITS VIA RESEARCH: Moon with Sun in 4th = profits via research. BLESSING: Intellectual pursuits yield wealth, research & development success, academic income, property through knowledge work. The mind (Moon) and soul (Sun) in home/mother house create research aptitude. LEVERAGE: Academia, R&D, consulting, intellectual property.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const moonPlacement = dasha.natalPlacements?.Moon;
    return !!sunPlacement && sunPlacement.house === 4 && !!moonPlacement && moonPlacement.house === 4;
  },
  score: () => ({ value: 0.50, notes: ['Sun + Moon in 4th → research profits'] })
};

const sun4thMercury4th10thTrader: Rule = {
  key: 'sun_4th_mercury_4th_10th_trader_natal',
  areas: ['Career', 'Wealth'],
  templateKey: 'career_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'SUCCESSFUL TRADER: Sun in 4th + Mercury in 4th or 10th = successful trader. BLESSING: Trading skills, business acumen, communication expertise, commercial success. The Sun-Mercury combination across home-career axis creates trading fortune. LEVERAGE: Stock market, commodities, import-export, brokerage.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    if (!sunPlacement || sunPlacement.house !== 4 || !mercuryPlacement) return false;
    return mercuryPlacement.house === 4 || mercuryPlacement.house === 10;
  },
  score: () => ({ value: 0.55, notes: ['Sun 4th + Mercury 4th/10th → successful trader'] })
};

const sun4thJupiterGoldSilverBusiness: Rule = {
  key: 'sun_4th_jupiter_gold_silver_business_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'GOLD/SILVER BUSINESS PROFITS: Jupiter with Sun in 4th = profits in gold/silver businesses. BLESSING: Precious metals trade, jewelry business, treasury management, luxury goods. The guru (Jupiter) and soul (Sun) in property house create wealth through valuables. LEVERAGE: Gold trading, jewelry stores, precious metals investment.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    return !!sunPlacement && sunPlacement.house === 4 && !!jupiterPlacement && jupiterPlacement.house === 4;
  },
  score: () => ({ value: 0.60, notes: ['Jupiter + Sun 4th → gold/silver business profits'] })
};

const sun4thJupiter10thDailyLuck: Rule = {
  key: 'sun_4th_jupiter_10th_daily_luck_natal',
  areas: ['Career', 'Wealth', 'Spirituality'],
  templateKey: 'fortune_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'ABUNDANT DAILY LUCK: Jupiter in 10th + Sun in 4th = abundant daily luck. BLESSING: Fortune favors daily activities, career success, constant opportunities, divine protection. The 4-10 axis with Sun-Jupiter creates perpetual good fortune. GRATITUDE: Count blessings, share prosperity, spiritual practices.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    return !!sunPlacement && sunPlacement.house === 4 && !!jupiterPlacement && jupiterPlacement.house === 10;
  },
  score: () => ({ value: 0.70, notes: ['Sun 4th + Jupiter 10th → abundant daily luck'] })
};

const sun4thSaturn7thNightBlindness: Rule = {
  key: 'sun_4th_saturn_7th_night_blindness_natal',
  areas: ['Health', 'Spouse'],
  templateKey: 'health_alert',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'NIGHT BLINDNESS RISK: Sun in 4th + Saturn in 7th = night blindness risk. CAUTION: Vision problems in darkness, eye strain, vitamin A deficiency, retinal issues. Saturn\'s darkness in 7th afflicts Sun\'s light in 4th. REMEDY: Eye care, vitamin A, Sun remedies, avoid night driving, regular eye exams.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    return !!sunPlacement && sunPlacement.house === 4 && !!saturnPlacement && saturnPlacement.house === 7;
  },
  score: () => ({ value: -0.35, notes: ['Sun 4th + Saturn 7th → night blindness risk'] })
};

const sun4thMaleficMars10thEyeDamage: Rule = {
  key: 'sun_4th_malefic_mars_10th_eye_damage_natal',
  areas: ['Health', 'Career'],
  templateKey: 'health_alert',
  priority: 9,
  ruleType: 'natal',
  private: false,
  customPrediction: 'SEVERE EYE DAMAGE: Malefic Sun in 4th + Mars in 10th = severe eye damage. CAUTION: Serious eye injuries, accidents, vision loss risk, surgical interventions. The Mars-Sun axis across 4-10 creates dangerous eye affliction. REMEDY: Extreme eye protection, avoid hazardous work, Sun and Mars remedies, protective eyewear.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const marsPlacement = dasha.natalPlacements?.Mars;
    if (!sunPlacement || sunPlacement.house !== 4 || !marsPlacement || marsPlacement.house !== 10) return false;
    // Simplified malefic Sun check - would need functional classification
    // For now, check if Sun is with malefics
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const sunAfflicted = (!!saturnPlacement && saturnPlacement.house === 4) || 
                         (!!rahuPlacement && rahuPlacement.house === 4) || 
                         (!!ketuPlacement && ketuPlacement.house === 4);
    return sunAfflicted;
  },
  score: () => ({ value: -0.65, notes: ['Malefic Sun 4th + Mars 10th → severe eye damage'] })
};

const sun4thComplexImpotency: Rule = {
  key: 'sun_4th_complex_impotency_natal',
  areas: ['Health', 'Spouse'],
  templateKey: 'health_alert',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'IMPOTENCY RISK: Sun in 4th + Saturn in 7th + Venus in 5th + Moon in 1st or 2nd = impotency. CAUTION: Sexual health issues, reproductive challenges, hormonal imbalances, relationship difficulties. This specific 4-planet combination severely afflicts vitality. REMEDY: Medical consultation, Venus and Mars strengthening, hormonal balance, relationship therapy.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const venusPlacement = dasha.natalPlacements?.Venus;
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!sunPlacement || sunPlacement.house !== 4) return false;
    if (!saturnPlacement || saturnPlacement.house !== 7) return false;
    if (!venusPlacement || venusPlacement.house !== 5) return false;
    if (!moonPlacement) return false;
    return moonPlacement.house === 1 || moonPlacement.house === 2;
  },
  score: () => ({ value: -0.70, notes: ['Sun 4th + Saturn 7th + Venus 5th + Moon 1st/2nd → impotency risk'] })
};

// ===== MOON IN 4TH HOUSE COMBINATIONS =====
const moon4thJupiter6thAncestralProfession: Rule = {
  key: 'moon_4th_jupiter_6th_ancestral_profession_natal',
  areas: ['Career', 'Father', 'Wealth'],
  templateKey: 'career_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'SUCCESS IN ANCESTRAL PROFESSIONS: Jupiter in 6th + Moon in 4th = success in ancestral professions. BLESSING: Family business thrives, traditional occupation brings wealth, heritage work, ancestral property profits. The mother house (4th) Moon with service (6th) Jupiter creates family trade success. LEVERAGE: Continue family business, honor traditions.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    return !!moonPlacement && moonPlacement.house === 4 && !!jupiterPlacement && jupiterPlacement.house === 6;
  },
  score: () => ({ value: 0.55, notes: ['Moon 4th + Jupiter 6th → ancestral profession success'] })
};

const moon4thRahuJupiter10thHeadInjuries: Rule = {
  key: 'moon_4th_rahu_jupiter_10th_head_injuries_natal',
  areas: ['Health', 'Career'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'HEAD INJURIES & MIGRAINES: Moon in 4th + Rahu or Jupiter in 10th = head injuries/migraines. CAUTION: Cranial trauma risk, severe headaches, neurological issues, stress-related head problems. The 4-10 axis with Moon afflicted creates head vulnerabilities. REMEDY: Head protection, stress management, neurological checkups, Moon remedies.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    if (!moonPlacement || moonPlacement.house !== 4) return false;
    const rahuIn10th = !!rahuPlacement && rahuPlacement.house === 10;
    const jupiterIn10th = !!jupiterPlacement && jupiterPlacement.house === 10;
    return rahuIn10th || jupiterIn10th;
  },
  score: () => ({ value: -0.45, notes: ['Moon 4th + Rahu/Jupiter 10th → head injuries/migraines'] })
};

const moon4thFourPlanetsWealth: Rule = {
  key: 'moon_4th_four_planets_wealth_natal',
  areas: ['Wealth', 'Property', 'Mother'],
  templateKey: 'wealth_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'STRONG WEALTH: Moon with four other planets in 4th = strong wealth. BLESSING: Massive wealth accumulation, property empire, real estate fortune, maternal wealth. Five planets in home house create exceptional wealth concentration. LEVERAGE: Real estate investment, property development, family wealth management.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!moonPlacement || moonPlacement.house !== 4) return false;
    // Count planets in 4th house excluding Moon
    const planetsIn4th = Object.entries(dasha.natalPlacements || {})
      .filter(([planet, p]: [string, any]) => p?.house === 4 && planet !== 'Moon');
    return planetsIn4th.length >= 4;
  },
  score: () => ({ value: 0.75, notes: ['Moon + 4 planets in 4th → strong wealth'] })
};

const moon4thJupiter10thNotGood: Rule = {
  key: 'moon_4th_jupiter_10th_not_good_natal',
  areas: ['Career', 'Mother', 'Spirituality'],
  templateKey: 'planetary_affliction',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'JUPITER IN 10TH NOT FAVORABLE: Moon in 4th + Jupiter in 10th = not good (requires special remedy). CAUTION: Career-home imbalance, dharma conflicts, mother-career tension, spiritual-material struggle. The guru in career afflicts emotional home. REMEDY: Balance work-life, honor mother while pursuing career, Jupiter remedies, spiritual practices.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    return !!moonPlacement && moonPlacement.house === 4 && !!jupiterPlacement && jupiterPlacement.house === 10;
  },
  score: () => ({ value: -0.30, notes: ['Moon 4th + Jupiter 10th → not good, remedy needed'] })
};

// ===== JUPITER IN 4TH HOUSE COMBINATIONS =====
const jupiter4thSaturn2ndClevernessWealth: Rule = {
  key: 'jupiter_4th_saturn_2nd_cleverness_wealth_natal',
  areas: ['Wealth', 'Career', 'Property'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'CLEVERNESS & WEALTH ACCUMULATION: Jupiter in 4th + Saturn in 2nd = cleverness and wealth accumulation. BLESSING: Sharp intellect, strategic financial planning, systematic wealth building, property investments. Saturn\'s discipline in wealth house with Jupiter\'s wisdom in home creates fortune. LEVERAGE: Long-term investments, real estate, conservative wealth strategies.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    return !!jupiterPlacement && jupiterPlacement.house === 4 && !!saturnPlacement && saturnPlacement.house === 2;
  },
  score: () => ({ value: 0.60, notes: ['Jupiter 4th + Saturn 2nd → cleverness, wealth accumulation'] })
};

const jupiter4thMercury10thSociallyAdaptive: Rule = {
  key: 'jupiter_4th_mercury_10th_socially_adaptive_natal',
  areas: ['Career', 'Wealth', 'Self'],
  templateKey: 'personality_mixed',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'SOCIALLY ADAPTIVE BUT POOR FORECASTING: Jupiter in 4th + Mercury in 10th = socially adaptive, generous, but poor self-forecasting. MIXED: Excellent social skills, charitable nature, career communication strength, BUT struggles with personal planning and future prediction. CAUTION: Overcommit, overgenerous, don\'t plan adequately for self. BALANCE: Maintain reserves while being generous.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    return !!jupiterPlacement && jupiterPlacement.house === 4 && !!mercuryPlacement && mercuryPlacement.house === 10;
  },
  score: () => ({ value: 0.20, notes: ['Jupiter 4th + Mercury 10th → socially adaptive, generous, poor self-forecasting'] })
};

// ===== MERCURY IN 4TH HOUSE COMBINATIONS =====
const mercury4thMarsElderSiblingsHealth: Rule = {
  key: 'mercury_4th_mars_elder_siblings_health_natal',
  areas: ['Health', 'Mother', 'Property'],
  templateKey: 'family_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'ELDER SIBLINGS HEALTH ISSUES: Mercury + Mars in 4th (conjunction) = elder siblings face health issues. CAUTION: Older brothers/sisters have health vulnerabilities, sibling conflicts, family health stress. The warrior (Mars) with communicator (Mercury) in home creates sibling health afflictions. REMEDY: Support siblings\' health, family harmony, Mars remedies.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const marsPlacement = dasha.natalPlacements?.Mars;
    return !!mercuryPlacement && mercuryPlacement.house === 4 && !!marsPlacement && marsPlacement.house === 4;
  },
  score: () => ({ value: -0.35, notes: ['Mercury + Mars in 4th → elder siblings health issues'] })
};

// ===== VENUS IN 4TH HOUSE COMBINATIONS =====
const venus4thJupiter10thMidasTouch: Rule = {
  key: 'venus_4th_jupiter_10th_midas_touch_natal',
  areas: ['Wealth', 'Career', 'Spirituality'],
  templateKey: 'wealth_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'MIDAS TOUCH BUT BEWARE: Jupiter in 10th + Venus in 4th = "Midas touch" BUT excessive religiosity hinders finances. BLESSING: Wealth creation ability, everything turns to gold, luxury lifestyle. CAUTION: Over-religiosity blocks money flow, dogmatic practices harm prosperity, spiritual pride. BALANCE: Spirituality with practicality, avoid excessive rituals that drain resources.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    return !!venusPlacement && venusPlacement.house === 4 && !!jupiterPlacement && jupiterPlacement.house === 10;
  },
  score: () => ({ value: 0.40, notes: ['Venus 4th + Jupiter 10th → Midas touch but excess religion hinders'] })
};

const venus4thJupiter1stMILDiscord: Rule = {
  key: 'venus_4th_jupiter_1st_mil_discord_natal',
  areas: ['Spouse', 'Mother'],
  templateKey: 'family_challenges',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'MOTHER-IN-LAW DISCORD RISK: Venus in 4th + Jupiter in 1st = raises mother-in-law discord risk. CAUTION: Tensions with spouse\'s mother, family conflicts, domestic disharmony, cultural clashes. Venus in home with Jupiter in self creates family friction. STRATEGY: Maintain boundaries, respectful distance, diplomatic approach, separate living if needed.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    return !!venusPlacement && venusPlacement.house === 4 && !!jupiterPlacement && jupiterPlacement.house === 1;
  },
  score: () => ({ value: -0.30, notes: ['Venus 4th + Jupiter 1st → MIL discord risk'] })
};

// ===== RAHU IN 4TH HOUSE COMBINATIONS =====
const rahu4thMoonExaltedWealthy: Rule = {
  key: 'rahu_4th_moon_exalted_wealthy_natal',
  areas: ['Wealth', 'Career', 'Mother'],
  templateKey: 'wealth_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'WEALTHY & INTELLIGENT: Rahu in 4th + Moon exalted OR Moon in 1st/4th/5th/9th = wealthy and intelligent. BLESSING: Exceptional intelligence, substantial wealth, property success, emotional stability, innovative thinking. Rahu\'s ambition with strong Moon creates fortune. LEVERAGE: Real estate, technology, unconventional property ventures.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!rahuPlacement || rahuPlacement.house !== 4 || !moonPlacement) return false;
    // Simplified: Check if Moon is in favorable houses (1,4,5,9) - exaltation would need sign check
    const favorableHouses = [1, 4, 5, 9];
    return favorableHouses.includes(moonPlacement.house);
  },
  score: () => ({ value: 0.65, notes: ['Rahu 4th + Moon exalted/1st/4th/5th/9th → wealthy, intelligent'] })
};

// ===== KETU IN 4TH HOUSE COMBINATIONS =====
const ketu4thMoon3rd4thAdvisorySkills: Rule = {
  key: 'ketu_4th_moon_3rd_4th_advisory_skills_natal',
  areas: ['Career', 'Wealth', 'Spirituality'],
  templateKey: 'career_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'STRONG ADVISORY SKILLS & FINANCIAL STABILITY: Ketu in 4th + Moon in 3rd or 4th = strong advisory skills, financial stability, "never short of money." BLESSING: Excellent counselor, consulting expertise, continuous income, wealth security, intuitive guidance. The mind (Moon) with spiritual wisdom (Ketu) creates exceptional advisory capacity. LEVERAGE: Consulting, coaching, spiritual counseling, financial advising.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!ketuPlacement || ketuPlacement.house !== 4 || !moonPlacement) return false;
    return moonPlacement.house === 3 || moonPlacement.house === 4;
  },
  score: () => ({ value: 0.60, notes: ['Ketu 4th + Moon 3rd/4th → advisory skills, never short of money'] })
};

// ===== SUN IN 5TH HOUSE COMBINATIONS =====
const sun5thMarsRahuKetuSaturnLuxuryLifestyle: Rule = {
  key: 'sun_5th_mars_rahu_ketu_saturn_luxury_natal',
  areas: ['Wealth', 'Property', 'Self'],
  templateKey: 'wealth_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'GRAND LUXURIOUS LIFESTYLE: Sun in 5th + Mars in 1st/8th + (Rahu/Ketu/Saturn) in 9th/12th = grand, luxurious lifestyle. BLESSING: Opulent living, high status, luxury properties, lavish spending capacity, royal lifestyle. The specific planetary alignment creates exceptional material abundance. LEVERAGE: Luxury businesses, high-end real estate, premium services.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    if (!sunPlacement || sunPlacement.house !== 5) return false;
    if (!marsPlacement) return false;
    
    const marsIn1stOr8th = marsPlacement.house === 1 || marsPlacement.house === 8;
    if (!marsIn1stOr8th) return false;
    
    const rahuIn9thOr12th = !!rahuPlacement && (rahuPlacement.house === 9 || rahuPlacement.house === 12);
    const ketuIn9thOr12th = !!ketuPlacement && (ketuPlacement.house === 9 || ketuPlacement.house === 12);
    const saturnIn9thOr12th = !!saturnPlacement && (saturnPlacement.house === 9 || saturnPlacement.house === 12);
    
    return rahuIn9thOr12th || ketuIn9thOr12th || saturnIn9thOr12th;
  },
  score: () => ({ value: 0.70, notes: ['Sun 5th + Mars 1st/8th + Rahu/Ketu/Saturn 9th/12th → luxurious lifestyle'] })
};

const sun5thJupiter9th12thResilience: Rule = {
  key: 'sun_5th_jupiter_9th_12th_resilience_children_natal',
  areas: ['Children', 'Litigation', 'Spirituality'],
  templateKey: 'children_mixed',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'RESILIENCE VS ENEMIES BUT CHILDREN AFFECTED: Sun in 5th + Jupiter in 9th/12th = resilience against enemies BUT can negatively affect children. MIXED: Victory over adversaries, spiritual strength, legal protection, BUT children face challenges, health issues, or delays. BALANCE: Protect children with remedies while leveraging resilience.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    if (!sunPlacement || sunPlacement.house !== 5 || !jupiterPlacement) return false;
    return jupiterPlacement.house === 9 || jupiterPlacement.house === 12;
  },
  score: () => ({ value: 0.10, notes: ['Sun 5th + Jupiter 9th/12th → enemy resilience but children negatively affected'] })
};

const sun5thAfflictedJupiter10thSpouseLoss: Rule = {
  key: 'sun_5th_afflicted_jupiter_10th_spouse_loss_natal',
  areas: ['Spouse', 'Health'],
  templateKey: 'spouse_challenges',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'RECURRING SPOUSE LOSS (MALE NATIVE): Afflicted Sun in 5th + Jupiter in 10th (male native) = recurring loss of spouses. CAUTION: Multiple marriages due to spouse death, health crises, serious marital losses. This difficult combination affects male natives\' marital longevity. REMEDY: Strong Jupiter and Venus remedies, protective rituals for spouse, Mahamrityunjaya mantra.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    if (!sunPlacement || sunPlacement.house !== 5 || !jupiterPlacement || jupiterPlacement.house !== 10) return false;
    
    // Check if Sun is afflicted
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const sunAfflicted = (!!saturnPlacement && saturnPlacement.house === 5) || 
                         (!!rahuPlacement && rahuPlacement.house === 5) || 
                         (!!ketuPlacement && ketuPlacement.house === 5) ||
                         (!!marsPlacement && marsPlacement.house === 5);
    return sunAfflicted;
  },
  score: () => ({ value: -0.75, notes: ['Afflicted Sun 5th + Jupiter 10th (male) → recurring spouse loss'] })
};

const sun5thSaturn3rdDeathOfSons: Rule = {
  key: 'sun_5th_saturn_3rd_death_sons_natal',
  areas: ['Children', 'Health'],
  templateKey: 'children_challenges',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'SONS\' MORTALITY RISK: Sun in 5th + Saturn in 3rd = death of sons. CAUTION: Male children face severe health risks, early mortality, life-threatening conditions. This combination severely afflicts progeny. REMEDY: Strong remedies for Sun and Saturn, Santana Gopala mantra, protective rituals for children, medical vigilance.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    return !!sunPlacement && sunPlacement.house === 5 && !!saturnPlacement && saturnPlacement.house === 3;
  },
  score: () => ({ value: -0.80, notes: ['Sun 5th + Saturn 3rd → sons mortality risk'] })
};

// ===== MOON IN 5TH HOUSE COMBINATIONS =====
const moon5thBeneficKetuFiveSons: Rule = {
  key: 'moon_5th_benefic_ketu_five_sons_natal',
  areas: ['Children'],
  templateKey: 'children_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'FIVE SONS BLESSING: Moon in 5th + benefic Ketu well-placed = gives five sons (even with malefics conjunct Moon). BLESSING: Abundant male progeny, strong sons, family lineage continuation, children bring prosperity. The benefic Ketu overrides Moon afflictions. FORTUNE: Large family, sons\' support in old age.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    if (!moonPlacement || moonPlacement.house !== 5 || !ketuPlacement) return false;
    
    // Simplified benefic Ketu check - in good houses (1,4,5,7,9,10,11)
    const beneficHouses = [1, 4, 5, 7, 9, 10, 11];
    const ketuBenefic = beneficHouses.includes(ketuPlacement.house);
    return ketuBenefic;
  },
  score: () => ({ value: 0.65, notes: ['Moon 5th + benefic Ketu → five sons blessing'] })
};

// ===== JUPITER IN 5TH HOUSE COMBINATIONS =====
const jupiter5thMercuryVenusRahuHarmsChildren: Rule = {
  key: 'jupiter_5th_mercury_venus_rahu_harms_children_natal',
  areas: ['Children', 'Wealth', 'Spirituality'],
  templateKey: 'children_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'HARMS CHILDREN & LUCK: Jupiter in 5th + Mercury/Venus/Rahu in 2nd/9th/11th/12th = harms children and luck. CAUTION: Children face difficulties, progeny delays, luck obstructed, spiritual challenges, wealth-luck conflicts. These planets in specific houses damage Jupiter\'s 5th house blessings. REMEDY: Jupiter strengthening, Santana Gopala worship, progeny protection rituals.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const venusPlacement = dasha.natalPlacements?.Venus;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    
    if (!jupiterPlacement || jupiterPlacement.house !== 5) return false;
    
    const targetHouses = [2, 9, 11, 12];
    const mercuryInTarget = !!mercuryPlacement && targetHouses.includes(mercuryPlacement.house);
    const venusInTarget = !!venusPlacement && targetHouses.includes(venusPlacement.house);
    const rahuInTarget = !!rahuPlacement && targetHouses.includes(rahuPlacement.house);
    
    return mercuryInTarget || venusInTarget || rahuInTarget;
  },
  score: () => ({ value: -0.50, notes: ['Jupiter 5th + Mercury/Venus/Rahu in 2nd/9th/11th/12th → harms children & luck'] })
};

const jupiter5thSunKetuMotherFamily: Rule = {
  key: 'jupiter_5th_sun_ketu_mother_family_natal',
  areas: ['Mother', 'Children', 'Spirituality'],
  templateKey: 'family_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MOTHER\'S FAMILY TROUBLES IN KETU PERIODS: Jupiter + Sun in 5th + Ketu poorly placed = troubles for mother\'s family during Ketu periods. CAUTION: Maternal relatives face difficulties in Ketu dashas, mother\'s side family issues, timing-specific challenges. REMEDY: Ketu remedies, support maternal family, ancestor worship, Ganesha mantras.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const sunPlacement = dasha.natalPlacements?.Sun;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    if (!jupiterPlacement || jupiterPlacement.house !== 5) return false;
    if (!sunPlacement || sunPlacement.house !== 5) return false;
    if (!ketuPlacement) return false;
    
    // Simplified "poorly placed" Ketu check - in dusthana (6,8,12) or with malefics
    const poorHouses = [6, 8, 12];
    const ketuPoor = poorHouses.includes(ketuPlacement.house);
    return ketuPoor;
  },
  score: () => ({ value: -0.35, notes: ['Jupiter + Sun 5th + Ketu poor → mother family troubles in Ketu periods'] })
};

// ===== MERCURY IN 5TH HOUSE COMBINATIONS =====
const mercury5thJupiterAspectInsecurities: Rule = {
  key: 'mercury_5th_jupiter_aspect_insecurities_natal',
  areas: ['Self', 'Children', 'Career'],
  templateKey: 'mental_challenges',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'INSECURITIES ARISE: Mercury in 5th + Jupiter aspects Mercury = insecurities arise. CAUTION: Self-doubt, intellectual insecurity, overthinking about children/creativity, confidence issues. Jupiter\'s aspect creates pressure on Mercury\'s analytical nature. REMEDY: Build confidence, avoid over-analysis, Mercury strengthening, positive affirmations.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    
    if (!mercuryPlacement || mercuryPlacement.house !== 5 || !jupiterPlacement) return false;
    
    // Check if Jupiter aspects Mercury (simplified: check if Jupiter aspects house 5)
    const jupiterAspects5th = jupiterPlacement.aspects?.includes(5 as HouseNum);
    // Also check conjunction
    const jupiterIn5th = jupiterPlacement.house === 5;
    
    return jupiterAspects5th || jupiterIn5th || false;
  },
  score: () => ({ value: -0.25, notes: ['Mercury 5th + Jupiter aspect → insecurities arise'] })
};

// ===== VENUS IN 5TH HOUSE COMBINATIONS =====
const venus5thRahuExtramaritalPublic: Rule = {
  key: 'venus_5th_rahu_extramarital_public_natal',
  areas: ['Spouse', 'Self'],
  templateKey: 'relationship_challenges',
  priority: 8,
  ruleType: 'natal',
  private: false,
  customPrediction: 'EXTRAMARITAL AFFAIRS GO PUBLIC: Venus + Rahu in 5th = extramarital affairs may go public. CAUTION: Infidelity scandals, public exposure of affairs, romantic indiscretions, reputation damage, relationship chaos. The romance house (5th) with Venus-Rahu creates publicized affairs. CONSEQUENCE: Social embarrassment, family breakdown, legal issues. AVOID: Maintain marital fidelity.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    return !!venusPlacement && venusPlacement.house === 5 && !!rahuPlacement && rahuPlacement.house === 5;
  },
  score: () => ({ value: -0.60, notes: ['Venus + Rahu in 5th → extramarital affairs may go public'] })
};

const venus5thMarsMiscarriages: Rule = {
  key: 'venus_5th_mars_miscarriages_natal',
  areas: ['Children', 'Health', 'Spouse'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  private: false,
  customPrediction: 'MISCARRIAGE RISK: Venus + Mars in 5th = miscarriages. CAUTION: Pregnancy loss, reproductive health issues, complications in conception, fetal health risks. The aggressive Mars with fertility Venus in progeny house creates pregnancy challenges. REMEDY: Medical care, Mars and Venus remedies, Santana Gopala worship, avoid stress during pregnancy.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const marsPlacement = dasha.natalPlacements?.Mars;
    return !!venusPlacement && venusPlacement.house === 5 && !!marsPlacement && marsPlacement.house === 5;
  },
  score: () => ({ value: -0.55, notes: ['Venus + Mars in 5th → miscarriage risk'] })
};

// ===== RAHU IN 5TH HOUSE COMBINATIONS =====
const rahu5thSonBirthSpouseHealth: Rule = {
  key: 'rahu_5th_son_birth_spouse_health_natal',
  areas: ['Spouse', 'Children', 'Health'],
  templateKey: 'spouse_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'SPOUSE HEALTH ISSUES AFTER SON\'S BIRTH: Rahu in 5th = after son\'s birth, spouse may face health issues. CAUTION: Partner\'s health deteriorates post-childbirth (especially after male child), postpartum complications, chronic health issues emerge. Rahu in progeny house affects spouse. REMEDY: Extra care for spouse after delivery, medical vigilance, Rahu remedies, health checkups.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    return !!rahuPlacement && rahuPlacement.house === 5;
  },
  score: () => ({ value: -0.40, notes: ['Rahu in 5th → spouse health issues after son birth'] })
};

// ===== KETU IN 5TH HOUSE COMBINATIONS =====
const ketu5thJupiterSunMoonExcellentFinances: Rule = {
  key: 'ketu_5th_jupiter_sun_moon_excellent_finances_natal',
  areas: ['Wealth', 'Career', 'Spirituality'],
  templateKey: 'wealth_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'EXCELLENT FINANCES: Ketu in 5th + Jupiter/Sun/Moon in 4th/6th/12th = excellent finances. BLESSING: Strong financial position, wealth through spiritual or unconventional means, property prosperity, service-based wealth. The detachment (Ketu) in intelligence house with benefics positioned well creates financial success. LEVERAGE: Spiritual businesses, service industries, consulting.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const sunPlacement = dasha.natalPlacements?.Sun;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    if (!ketuPlacement || ketuPlacement.house !== 5) return false;
    
    const targetHouses = [4, 6, 12];
    const jupiterInTarget = !!jupiterPlacement && targetHouses.includes(jupiterPlacement.house);
    const sunInTarget = !!sunPlacement && targetHouses.includes(sunPlacement.house);
    const moonInTarget = !!moonPlacement && targetHouses.includes(moonPlacement.house);
    
    return jupiterInTarget || sunInTarget || moonInTarget;
  },
  score: () => ({ value: 0.65, notes: ['Ketu 5th + Jupiter/Sun/Moon in 4th/6th/12th → excellent finances'] })
};

const ketu5thBeneficAfter24: Rule = {
  key: 'ketu_5th_benefic_after_24_natal',
  areas: ['Wealth', 'Children', 'Spirituality'],
  templateKey: 'wealth_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'BENEFIC AFTER 24: Benefic Ketu in 5th = gives benefic results after age 24. BLESSING: Late-blooming success, spiritual maturity brings wealth, children/creativity thrive post-24, wisdom-based prosperity. The intelligence house (5th) with Ketu matures with age. TIMING: Major improvements after 24 years.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    if (!ketuPlacement || ketuPlacement.house !== 5) return false;
    
    // Simplified benefic Ketu check - in good houses relative to itself or well-placed
    // For simplicity, consider Ketu benefic if not in 6/8/12 from itself (always true for 5th position)
    return true; // Ketu in 5th itself can be benefic
  },
  score: () => ({ value: 0.45, notes: ['Benefic Ketu in 5th → benefic results after age 24'] })
};

// ===== SUN IN 6TH HOUSE COMBINATIONS =====
const sun6thJupiterMarsMoon2ndBoostsFortune: Rule = {
  key: 'sun_6th_jupiter_mars_moon_2nd_boosts_fortune_natal',
  areas: ['Wealth', 'Litigation', 'Health'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'FORTUNE BOOST: Sun in 6th + Jupiter/Mars/Moon in 2nd = boosts fortune. BLESSING: Victory over enemies, financial gains, family wealth, competitive success. The service/enemy house (6th) Sun with wealth house benefics creates financial resilience. LEVERAGE: Legal victories, debt recovery, competitive businesses.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    if (!sunPlacement || sunPlacement.house !== 6) return false;
    
    const jupiterIn2nd = !!jupiterPlacement && jupiterPlacement.house === 2;
    const marsIn2nd = !!marsPlacement && marsPlacement.house === 2;
    const moonIn2nd = !!moonPlacement && moonPlacement.house === 2;
    
    return jupiterIn2nd || marsIn2nd || moonIn2nd;
  },
  score: () => ({ value: 0.60, notes: ['Sun 6th + Jupiter/Mars/Moon in 2nd → boosts fortune'] })
};

const sun6thKetu1st7thMaleChild: Rule = {
  key: 'sun_6th_ketu_1st_7th_male_child_natal',
  areas: ['Children'],
  templateKey: 'children_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'MALE CHILD CHANCES INCREASE: Sun in 6th + Ketu in 1st/7th = increases chances of male child. BLESSING: Higher probability of sons, male progeny favored, family lineage continuation. The enemy house Sun with Ketu in angular positions favors male offspring. FORTUNE: Sons bring prosperity.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    if (!sunPlacement || sunPlacement.house !== 6 || !ketuPlacement) return false;
    return ketuPlacement.house === 1 || ketuPlacement.house === 7;
  },
  score: () => ({ value: 0.50, notes: ['Sun 6th + Ketu 1st/7th → male child chances increase'] })
};

const sun6thEmpty2ndGovtJob22: Rule = {
  key: 'sun_6th_empty_2nd_govt_job_22_natal',
  areas: ['Career'],
  templateKey: 'career_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'GOVT JOB AT 22: Sun in 6th + empty 2nd house = possible government job at age 22. BLESSING: Government service opportunity, stable career at young age, authority position early, service-based success. TIMING: Career breakthrough around 22 years. LEVERAGE: Apply for government positions at this age.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    if (!sunPlacement || sunPlacement.house !== 6) return false;
    
    // Check if 2nd house is empty
    const planetsIn2nd = Object.entries(dasha.natalPlacements || {}).filter(
      ([_, placement]) => placement?.house === 2
    );
    return planetsIn2nd.length === 0;
  },
  score: () => ({ value: 0.55, notes: ['Sun 6th + empty 2nd → govt job possible at 22'] })
};

const sun6thMercury12thHealthBP: Rule = {
  key: 'sun_6th_mercury_12th_health_bp_natal',
  areas: ['Health'],
  templateKey: 'health_alert',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'HEALTH CONCERNS - HIGH BP: Sun in 6th + Mercury in 12th = health concerns including high blood pressure. CAUTION: Hypertension risk, stress-related health issues, expenditure on health, chronic BP problems. The 6th-12th axis with Sun-Mercury creates health-expense patterns. REMEDY: Regular BP monitoring, stress management, Mercury remedies, healthy lifestyle.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    return !!sunPlacement && sunPlacement.house === 6 && !!mercuryPlacement && mercuryPlacement.house === 12;
  },
  score: () => ({ value: -0.45, notes: ['Sun 6th + Mercury 12th → health concerns incl. high BP'] })
};

const sun6thMars10thTragicLossSons: Rule = {
  key: 'sun_6th_mars_10th_tragic_loss_sons_natal',
  areas: ['Children', 'Health'],
  templateKey: 'children_challenges',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'TRAGIC LOSS OF SONS: Sun in 6th + Mars in 10th = tragic loss of sons. CAUTION: Male children face severe mortality risks, accidents to sons, life-threatening health issues, profound grief. This combination severely afflicts male progeny. REMEDY: Strong Mars and Sun remedies, Santana Gopala worship, protective rituals, extreme medical vigilance for sons.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const marsPlacement = dasha.natalPlacements?.Mars;
    return !!sunPlacement && sunPlacement.house === 6 && !!marsPlacement && marsPlacement.house === 10;
  },
  score: () => ({ value: -0.85, notes: ['Sun 6th + Mars 10th → tragic loss of sons'] })
};

// ===== MOON IN 6TH HOUSE COMBINATIONS =====
const moon6thMaleficMercury2nd12thSuicidal: Rule = {
  key: 'moon_6th_malefic_mercury_2nd_12th_suicidal_natal',
  areas: ['Health', 'Self'],
  templateKey: 'health_alert',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'SUICIDAL TENDENCIES: Malefic Moon in 6th + Mercury in 2nd/12th = suicidal tendencies. CAUTION: Severe mental health risks, depression, self-harm ideation, psychological crisis. The afflicted mind (Moon) in enemy house with Mercury in challenging positions creates危机mental instability. URGENT: Seek professional mental health support, Moon remedies, family support system, crisis intervention.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    if (!moonPlacement || moonPlacement.house !== 6 || !mercuryPlacement) return false;
    
    // Check if Mercury in 2nd or 12th
    const mercuryIn2ndOr12th = mercuryPlacement.house === 2 || mercuryPlacement.house === 12;
    if (!mercuryIn2ndOr12th) return false;
    
    // Check if Moon is malefic (afflicted by Saturn, Mars, Rahu, Ketu, or waning)
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    const moonAfflicted = (!!saturnPlacement && saturnPlacement.house === 6) || 
                          (!!marsPlacement && marsPlacement.house === 6) ||
                          (!!rahuPlacement && rahuPlacement.house === 6) ||
                          (!!ketuPlacement && ketuPlacement.house === 6);
    return moonAfflicted;
  },
  score: () => ({ value: -0.90, notes: ['Malefic Moon 6th + Mercury 2nd/12th → suicidal tendencies'] })
};

const moon6thMaleficSun12thEyeDefects: Rule = {
  key: 'moon_6th_malefic_sun_12th_eye_defects_natal',
  areas: ['Health', 'Spouse'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  private: false,
  customPrediction: 'SEVERE EYE DEFECTS: Malefic Moon in 6th + Sun in 12th = severe eye defects for native/spouse/both. CAUTION: Vision problems, eye diseases, blindness risk, spouse eye issues, both partners affected. The 6th-12th axis with malefic Moon-Sun severely damages eyesight. REMEDY: Regular eye checkups, Sun and Moon remedies, Surya Namaskar, protective eyecare.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const sunPlacement = dasha.natalPlacements?.Sun;
    
    if (!moonPlacement || moonPlacement.house !== 6 || !sunPlacement || sunPlacement.house !== 12) return false;
    
    // Check if Moon is malefic
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    const moonAfflicted = (!!saturnPlacement && saturnPlacement.house === 6) || 
                          (!!marsPlacement && marsPlacement.house === 6) ||
                          (!!rahuPlacement && rahuPlacement.house === 6) ||
                          (!!ketuPlacement && ketuPlacement.house === 6);
    return moonAfflicted;
  },
  score: () => ({ value: -0.70, notes: ['Malefic Moon 6th + Sun 12th → severe eye defects native/spouse'] })
};

const moon6thRahuKetu4thMaternalAffection: Rule = {
  key: 'moon_6th_rahu_ketu_4th_maternal_affection_natal',
  areas: ['Mother', 'Self'],
  templateKey: 'family_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'LACK OF MATERNAL AFFECTION: Moon in 6th + Rahu/Ketu in 4th = lack of maternal affection. CAUTION: Mother emotionally distant, childhood lacks warmth, maternal relationship strained, emotional deprivation. The mind (Moon) in enemy house with nodes in mother\'s house creates maternal disconnect. REMEDY: Heal mother relationship, Moon remedies, compassion practices, therapy.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    if (!moonPlacement || moonPlacement.house !== 6) return false;
    
    const rahuIn4th = !!rahuPlacement && rahuPlacement.house === 4;
    const ketuIn4th = !!ketuPlacement && ketuPlacement.house === 4;
    
    return rahuIn4th || ketuIn4th;
  },
  score: () => ({ value: -0.50, notes: ['Moon 6th + Rahu/Ketu 4th → lack of maternal affection'] })
};

// ===== JUPITER IN 6TH HOUSE COMBINATIONS =====
const jupiter6thMercury2nd12thFatherDeath: Rule = {
  key: 'jupiter_6th_mercury_2nd_12th_father_death_natal',
  areas: ['Father', 'Health'],
  templateKey: 'family_challenges',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'FATHER CHALLENGES/DEATH AT 16-19: Jupiter in 6th + Mercury in 2nd/12th = father may face serious challenges or death between ages 16-19. CAUTION: Paternal health crisis during teenage years, father mortality risk at 16-19, family trauma, early loss of father. REMEDY: Strong Jupiter and Sun remedies for father, Mahamrityunjaya mantra, protective rituals during 16-19 age window.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    if (!jupiterPlacement || jupiterPlacement.house !== 6 || !mercuryPlacement) return false;
    return mercuryPlacement.house === 2 || mercuryPlacement.house === 12;
  },
  score: () => ({ value: -0.75, notes: ['Jupiter 6th + Mercury 2nd/12th → father challenges/death at 16-19'] })
};

// ===== MARS IN 6TH HOUSE COMBINATIONS =====
const mars6thSunSaturnMarsStrain: Rule = {
  key: 'mars_6th_sun_saturn_mars_strain_natal',
  areas: ['Self', 'Mother', 'Spouse'],
  templateKey: 'relationship_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'FAMILY STRAINS: Sun + Saturn + Mars together in 6th = strains with brothers, mother, sisters, wife. CAUTION: Family conflicts, sibling rivalry, maternal tensions, marital discord, relationship stress across family. The triple malefic conjunction in enemy house damages family harmony. REMEDY: Patience, Mars-Saturn-Sun remedies, family counseling, anger management.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    if (!marsPlacement || marsPlacement.house !== 6) return false;
    if (!sunPlacement || sunPlacement.house !== 6) return false;
    if (!saturnPlacement || saturnPlacement.house !== 6) return false;
    
    return true; // All three in 6th
  },
  score: () => ({ value: -0.60, notes: ['Sun + Saturn + Mars in 6th → strains with brothers/mother/sisters/wife'] })
};

// ===== MERCURY IN 6TH HOUSE COMBINATIONS =====
const mercury6thMars4th8thMotherIssues: Rule = {
  key: 'mercury_6th_mars_4th_8th_mother_issues_natal',
  areas: ['Mother', 'Health'],
  templateKey: 'family_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'MOTHER ISSUES/EARLY LOSS: Mercury in 6th + Mars in 4th/8th = mother\'s issues; possible early loss. CAUTION: Maternal health problems, mother faces challenges, risk of early maternal loss, property disputes. Mars in mother houses with Mercury in enemies creates maternal affliction. REMEDY: Mars remedies, support mother\'s health, Durga mantras, protective rituals.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    if (!mercuryPlacement || mercuryPlacement.house !== 6 || !marsPlacement) return false;
    return marsPlacement.house === 4 || marsPlacement.house === 8;
  },
  score: () => ({ value: -0.65, notes: ['Mercury 6th + Mars 4th/8th → mother issues/early loss'] })
};

const mercury6thMarsConjunctionAccidents: Rule = {
  key: 'mercury_6th_mars_conjunction_accidents_natal',
  areas: ['Health', 'Litigation', 'AccidentRisk'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'ACCIDENTS & BLOOD ISSUES: Mars conjunct Mercury in 6th = accidents, chronic blood issues, enemies\' troubles. CAUTION: Accident-prone, blood disorders, enemy conflicts escalate, injury risks, chronic health issues. The fiery Mars with intellectual Mercury in enemy house creates volatile situations. REMEDY: Careful driving, Mars remedies, avoid risky activities, blood health monitoring.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    return !!mercuryPlacement && mercuryPlacement.house === 6 && 
           !!marsPlacement && marsPlacement.house === 6;
  },
  score: () => ({ value: -0.60, notes: ['Mars conjunct Mercury in 6th → accidents, chronic blood issues, enemies troubles'] })
};

const mercury6thMaleficsSaturnNegativity: Rule = {
  key: 'mercury_6th_malefics_saturn_negativity_natal',
  areas: ['Self', 'Litigation'],
  templateKey: 'mental_challenges',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'ARGUMENTS & DULL INFLUENCE: Mercury in 6th with malefics or Saturn = arguments, negativity dull influence. CAUTION: Constant arguments, pessimistic thinking, communication conflicts, intellectual dullness, litigation-prone. Mercury afflicted in enemy house creates negative mental patterns. REMEDY: Positive communication, Mercury strengthening, avoid toxic people, mindfulness.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    if (!mercuryPlacement || mercuryPlacement.house !== 6) return false;
    
    const saturnIn6th = !!saturnPlacement && saturnPlacement.house === 6;
    const marsIn6th = !!marsPlacement && marsPlacement.house === 6;
    const rahuIn6th = !!rahuPlacement && rahuPlacement.house === 6;
    const ketuIn6th = !!ketuPlacement && ketuPlacement.house === 6;
    
    return saturnIn6th || marsIn6th || rahuIn6th || ketuIn6th;
  },
  score: () => ({ value: -0.40, notes: ['Mercury 6th + malefics/Saturn → arguments, negativity dull influence'] })
};

// ===== RAHU IN 6TH HOUSE COMBINATIONS =====
const rahu6thMercuryMars12thHealthIssues: Rule = {
  key: 'rahu_6th_mercury_mars_12th_health_issues_natal',
  areas: ['Health', 'Property'],
  templateKey: 'health_alert',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'HEALTH ISSUES AT HOME: Rahu in 6th + Mercury/Mars in 12th = Rahu may bring health issues at home. CAUTION: Home environment affects health, property-related health problems, chronic issues at residence, expenditure on home health. The 6th-12th axis with Rahu-Mercury/Mars creates health-home conflicts. REMEDY: Vastu corrections, Rahu remedies, maintain healthy home environment.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    if (!rahuPlacement || rahuPlacement.house !== 6) return false;
    
    const mercuryIn12th = !!mercuryPlacement && mercuryPlacement.house === 12;
    const marsIn12th = !!marsPlacement && marsPlacement.house === 12;
    
    return mercuryIn12th || marsIn12th;
  },
  score: () => ({ value: -0.45, notes: ['Rahu 6th + Mercury/Mars 12th → health issues at home'] })
};

// ===== KETU IN 6TH HOUSE COMBINATIONS =====
const ketu6thTwoExaltedStronglyFavorable: Rule = {
  key: 'ketu_6th_two_exalted_strongly_favorable_natal',
  areas: ['Litigation', 'Health', 'Spirituality'],
  templateKey: 'spiritual_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'KETU ACTS STRONGLY IN YOUR FAVOR: Ketu in 6th + two of Sun/Jupiter/Mars exalted = Ketu acts strongly in your favor. BLESSING: Enemy destruction, disease victory, debt elimination, spiritual protection, strong competitive advantage. Exalted planets empower Ketu in the upachaya house. LEVERAGE: Legal victories, overcoming obstacles, spiritual practices yield results.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    if (!ketuPlacement || ketuPlacement.house !== 6) return false;
    
    // Check if at least two of Sun/Jupiter/Mars are exalted
    // Simplified: Sun exalted in Aries (assume sign 1), Jupiter in Cancer (4), Mars in Capricorn (10)
    // Since we don't have sign info in DashaContext, check if these planets are in specific beneficial positions
    // For simplicity, assume strong placement = in kendra/trikona (1,4,5,7,9,10)
    const sunPlacement = dasha.natalPlacements?.Sun;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    const strongHouses = [1, 4, 5, 7, 9, 10];
    const sunStrong = !!sunPlacement && strongHouses.includes(sunPlacement.house);
    const jupiterStrong = !!jupiterPlacement && strongHouses.includes(jupiterPlacement.house);
    const marsStrong = !!marsPlacement && strongHouses.includes(marsPlacement.house);
    
    const exaltedCount = [sunStrong, jupiterStrong, marsStrong].filter(Boolean).length;
    return exaltedCount >= 2;
  },
  score: () => ({ value: 0.70, notes: ['Ketu 6th + two of Sun/Jupiter/Mars exalted → Ketu strongly favorable'] })
};

const ketu6thMoon6thMotherUncleHealth: Rule = {
  key: 'ketu_6th_moon_6th_mother_uncle_health_natal',
  areas: ['Mother', 'Health'],
  templateKey: 'health_alert',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MOTHER & MATERNAL UNCLE HEALTH TROUBLES: Ketu + Moon in 6th = health troubles for mother & maternal uncle. CAUTION: Maternal health issues, mother\'s brother (uncle) health problems, family health concerns, double affliction on maternal side. Moon-Ketu in enemy house damages maternal family health. REMEDY: Support maternal family health, Moon and Ketu remedies, Durga worship, health checkups for mother.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    return !!ketuPlacement && ketuPlacement.house === 6 && 
           !!moonPlacement && moonPlacement.house === 6;
  },
  score: () => ({ value: -0.55, notes: ['Ketu + Moon in 6th → mother & maternal uncle health troubles'] })
};

const ketu6thMoon2ndMotherDifficulties: Rule = {
  key: 'ketu_6th_moon_2nd_mother_difficulties_natal',
  areas: ['Mother', 'Wealth'],
  templateKey: 'family_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MOTHER DIFFICULTIES & OLD AGE CHALLENGES: Ketu in 6th + Moon in 2nd = difficulties for mother; challenges in old age. CAUTION: Mother faces hardships, maternal financial struggles, old age brings difficulties to mother, family wealth-mother conflicts. REMEDY: Support mother financially, Moon remedies, ensure maternal care in old age, family compassion.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    return !!ketuPlacement && ketuPlacement.house === 6 && 
           !!moonPlacement && moonPlacement.house === 2;
  },
  score: () => ({ value: -0.50, notes: ['Ketu 6th + Moon 2nd → mother difficulties, old age challenges'] })
};

// ===== SUN IN 7TH HOUSE COMBINATIONS =====
const sun7thMoonMarsJupiter2ndGovtPosition: Rule = {
  key: 'sun_7th_moon_mars_jupiter_2nd_govt_position_natal',
  areas: ['Career', 'Spouse'],
  templateKey: 'career_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'HIGH GOVERNMENT POSITION: Sun in 7th + Moon/Mars/Jupiter in 2nd = high government position. BLESSING: Senior government role, authority in public service, prestigious career, political success, leadership position. The partnership house Sun with wealth house benefics/malefics creates governmental power. LEVERAGE: Public sector careers, government contracts, political connections.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const moonPlacement = dasha.natalPlacements?.Moon;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    
    if (!sunPlacement || sunPlacement.house !== 7) return false;
    
    const moonIn2nd = !!moonPlacement && moonPlacement.house === 2;
    const marsIn2nd = !!marsPlacement && marsPlacement.house === 2;
    const jupiterIn2nd = !!jupiterPlacement && jupiterPlacement.house === 2;
    
    return moonIn2nd || marsIn2nd || jupiterIn2nd;
  },
  score: () => ({ value: 0.75, notes: ['Sun 7th + Moon/Mars/Jupiter 2nd → high government position'] })
};

const sun7thMercury5th7thContinuousIncome: Rule = {
  key: 'sun_7th_mercury_5th_7th_continuous_income_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'ABUNDANT CONTINUOUS INCOME: Sun in 7th + Mercury in 5th/7th (or exalted) = abundant, continuous income. BLESSING: Steady cash flow, uninterrupted earnings, multiple income streams, financial stability, business success. Mercury in intelligence/partnership houses with Sun creates reliable income. LEVERAGE: Consulting, trading, partnerships, intellectual work.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    if (!sunPlacement || sunPlacement.house !== 7 || !mercuryPlacement) return false;
    
    // Mercury in 5th or 7th (simplified exaltation check: assume Mercury strong if in these houses)
    return mercuryPlacement.house === 5 || mercuryPlacement.house === 7;
  },
  score: () => ({ value: 0.70, notes: ['Sun 7th + Mercury 5th/7th/exalted → abundant continuous income'] })
};

const sun7thJupiterVenusMalefic12thFamilyLosses: Rule = {
  key: 'sun_7th_jupiter_venus_malefic_12th_family_losses_natal',
  areas: ['Health', 'Spouse'],
  templateKey: 'health_alert',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'MULTIPLE FAMILY MEMBER LOSSES: Sun in 7th + Jupiter/Venus/malefic in 12th + malefic Mercury = multiple family-member losses. CAUTION: Repeated family deaths, spouse/relatives mortality, profound grief, family tragedy. The 7th-12th axis with multiple malefics creates family loss patterns. REMEDY: Protective rituals for family, Mahamrityunjaya mantra, Sun and Mercury remedies, family health vigilance.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const venusPlacement = dasha.natalPlacements?.Venus;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    if (!sunPlacement || sunPlacement.house !== 7 || !mercuryPlacement) return false;
    
    // Check if Jupiter/Venus/malefic in 12th
    const jupiterIn12th = !!jupiterPlacement && jupiterPlacement.house === 12;
    const venusIn12th = !!venusPlacement && venusPlacement.house === 12;
    const marsIn12th = !!marsPlacement && marsPlacement.house === 12;
    const saturnIn12th = !!saturnPlacement && saturnPlacement.house === 12;
    const rahuIn12th = !!rahuPlacement && rahuPlacement.house === 12;
    const ketuIn12th = !!ketuPlacement && ketuPlacement.house === 12;
    
    const planetIn12th = jupiterIn12th || venusIn12th || marsIn12th || saturnIn12th || rahuIn12th || ketuIn12th;
    if (!planetIn12th) return false;
    
    // Check if Mercury is malefic (afflicted by Saturn/Mars/Rahu/Ketu or in 6/8/12)
    const mercuryAfflicted = mercuryPlacement.house === 6 || mercuryPlacement.house === 8 || mercuryPlacement.house === 12;
    return mercuryAfflicted;
  },
  score: () => ({ value: -0.85, notes: ['Sun 7th + Jupiter/Venus/malefic 12th + malefic Mercury → multiple family losses'] })
};

const sun7thAfflictedMarsSaturnMoonWorsenedHealth: Rule = {
  key: 'sun_7th_afflicted_mars_saturn_moon_worsened_health_natal',
  areas: ['Health', 'Spouse'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'WORSENED HEALTH: Afflicted Sun in 7th + Mars/Saturn in 2nd/12th + Moon in 1st = worsened health. CAUTION: Chronic health deterioration, spouse health issues, combined afflictions damage vitality, immune system weakened. Multiple malefic positions create severe health challenges. REMEDY: Comprehensive health monitoring, Sun-Mars-Saturn remedies, lifestyle discipline, medical care.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    if (!sunPlacement || sunPlacement.house !== 7 || !moonPlacement || moonPlacement.house !== 1) return false;
    
    // Check if Sun is afflicted
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const sunAfflicted = (!!marsPlacement && marsPlacement.house === 7) || 
                         (!!saturnPlacement && saturnPlacement.house === 7) ||
                         (!!rahuPlacement && rahuPlacement.house === 7) ||
                         (!!ketuPlacement && ketuPlacement.house === 7);
    if (!sunAfflicted) return false;
    
    // Check if Mars or Saturn in 2nd or 12th
    const marsIn2ndOr12th = !!marsPlacement && (marsPlacement.house === 2 || marsPlacement.house === 12);
    const saturnIn2ndOr12th = !!saturnPlacement && (saturnPlacement.house === 2 || saturnPlacement.house === 12);
    
    return marsIn2ndOr12th || saturnIn2ndOr12th;
  },
  score: () => ({ value: -0.65, notes: ['Afflicted Sun 7th + Mars/Saturn 2nd/12th + Moon 1st → worsened health'] })
};

// ===== MOON IN 7TH HOUSE COMBINATIONS =====
const moon7thMotherLegProblemsMaritalStress: Rule = {
  key: 'moon_7th_mother_leg_problems_marital_stress_natal',
  areas: ['Mother', 'Spouse', 'Health'],
  templateKey: 'family_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MOTHER LEG PROBLEMS & MARITAL STRESS: Moon in 7th = mother leg problems; marital stress through mother. CAUTION: Mother suffers leg/mobility issues, mother-in-law conflicts, maternal interference in marriage, relationship stress via mother. Moon in partnership house affects mother and marriage. REMEDY: Support mother\'s health, navigate mother-spouse dynamics carefully, Moon remedies, family therapy.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    return !!moonPlacement && moonPlacement.house === 7;
  },
  score: () => ({ value: -0.45, notes: ['Moon in 7th → mother leg problems, marital stress through mother'] })
};

// ===== MARS IN 7TH HOUSE COMBINATIONS =====
const mars7thMercuryConjunctionSistersProblems: Rule = {
  key: 'mars_7th_mercury_conjunction_sisters_problems_natal',
  areas: ['Spouse', 'Self'],
  templateKey: 'relationship_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'PROBLEMS WITH SISTERS & MERCURY-RULED RELATIONS: Mars + Mercury in 7th = problems with sisters, nurses, maids, Mercury-ruled relations. CAUTION: Sister conflicts, domestic help issues, communication-based relationship problems, arguments with feminine Mercury-ruled people. Mars-Mercury in partnership house creates these specific conflicts. REMEDY: Patience with sisters/helpers, Mars-Mercury remedies, clear communication.',
  triggers: (dasha) => {
    const marsPlacement = dasha.natalPlacements?.Mars;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    return !!marsPlacement && marsPlacement.house === 7 && 
           !!mercuryPlacement && mercuryPlacement.house === 7;
  },
  score: () => ({ value: -0.40, notes: ['Mars + Mercury in 7th → problems with sisters, nurses, maids, Mercury relations'] })
};

// ===== MERCURY IN 7TH HOUSE COMBINATIONS =====
const mercury7thSaturn3rdSpouseFamilyRich: Rule = {
  key: 'mercury_7th_saturn_3rd_spouse_family_rich_natal',
  areas: ['Spouse', 'Wealth'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'SPOUSE\'S FAMILY RICH: Mercury in 7th + Saturn in 3rd = spouse\'s family rich. BLESSING: In-laws wealthy, spouse brings family wealth, marital alliance financially beneficial, rich in-law connections. Mercury in partnership with Saturn in courage house creates prosperous marital family. LEVERAGE: In-law business connections, family wealth access.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    return !!mercuryPlacement && mercuryPlacement.house === 7 && 
           !!saturnPlacement && saturnPlacement.house === 3;
  },
  score: () => ({ value: 0.60, notes: ['Mercury 7th + Saturn 3rd → spouse family rich'] })
};

const mercury7thMoon1stOverseasBeneficial: Rule = {
  key: 'mercury_7th_moon_1st_overseas_beneficial_natal',
  areas: ['Travel', 'Career', 'Spouse'],
  templateKey: 'travel_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'OVERSEAS JOURNEYS BENEFICIAL: Mercury in 7th + Moon in 1st = overseas journeys beneficial. BLESSING: Foreign travel success, international career opportunities, beneficial partnerships abroad, global connections. Mercury-Moon axis creates favorable foreign outcomes. LEVERAGE: International business, foreign education, overseas partnerships.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    return !!mercuryPlacement && mercuryPlacement.house === 7 && 
           !!moonPlacement && moonPlacement.house === 1;
  },
  score: () => ({ value: 0.55, notes: ['Mercury 7th + Moon 1st → overseas journeys beneficial'] })
};

// ===== VENUS IN 7TH HOUSE COMBINATIONS =====
const venus7thInimicalPlanet1stMaritalDiscord: Rule = {
  key: 'venus_7th_inimical_planet_1st_marital_discord_natal',
  areas: ['Spouse'],
  templateKey: 'relationship_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'MARITAL DISCORD: Venus in 7th + planet inimical to Venus (Rahu/Ketu/Saturn) in 1st = marital discord. CAUTION: Marriage conflicts, spouse relationship strained, chronic marital unhappiness, incompatibility. Venus in own house (7th) afflicted by enemy in 1st creates marital challenges. REMEDY: Venus strengthening, relationship counseling, patience, avoid ego conflicts.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    if (!venusPlacement || venusPlacement.house !== 7) return false;
    
    // Planets inimical to Venus: Rahu, Ketu, Saturn (simplified)
    const rahuIn1st = !!rahuPlacement && rahuPlacement.house === 1;
    const ketuIn1st = !!ketuPlacement && ketuPlacement.house === 1;
    const saturnIn1st = !!saturnPlacement && saturnPlacement.house === 1;
    
    return rahuIn1st || ketuIn1st || saturnIn1st;
  },
  score: () => ({ value: -0.60, notes: ['Venus 7th + Rahu/Ketu/Saturn 1st → marital discord'] })
};

const venus7thMercuryMaleficAddictions: Rule = {
  key: 'venus_7th_mercury_malefic_addictions_natal',
  areas: ['Health', 'Spouse'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'ADDICTIONS/HEALTH ISSUES AFTER 17: Mercury + Venus conjunction (malefic) in 7th = addictions/health issues after age 17. CAUTION: Substance abuse after 17, partner-related health problems, relationship-driven addictions, chronic issues emerge. Malefic Mercury-Venus creates vulnerability. TIMING: Risk increases after 17 years. REMEDY: Avoid substances, healthy relationships, Venus-Mercury remedies.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    if (!venusPlacement || venusPlacement.house !== 7 || !mercuryPlacement || mercuryPlacement.house !== 7) return false;
    
    // Check if conjunction is malefic (afflicted by Mars/Saturn/Rahu/Ketu)
    const marsPlacement = dasha.natalPlacements?.Mars;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    const maleficIn7th = (!!marsPlacement && marsPlacement.house === 7) || 
                         (!!saturnPlacement && saturnPlacement.house === 7) ||
                         (!!rahuPlacement && rahuPlacement.house === 7) ||
                         (!!ketuPlacement && ketuPlacement.house === 7);
    return maleficIn7th;
  },
  score: () => ({ value: -0.55, notes: ['Malefic Mercury + Venus in 7th → addictions/health issues after 17'] })
};

// ===== SATURN IN 7TH HOUSE COMBINATIONS =====
const saturn7thJupiter1stGovtBenefits: Rule = {
  key: 'saturn_7th_jupiter_1st_govt_benefits_natal',
  areas: ['Career', 'Spouse'],
  templateKey: 'career_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'GOVERNMENT BENEFITS: Saturn in 7th + Jupiter in 1st = government benefits. BLESSING: Public sector advantages, governmental support, spouse connected to government, official benefits, bureaucratic success. Jupiter-Saturn axis creates governmental favor. LEVERAGE: Government contracts, public service, official partnerships.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    
    return !!saturnPlacement && saturnPlacement.house === 7 && 
           !!jupiterPlacement && jupiterPlacement.house === 1;
  },
  score: () => ({ value: 0.60, notes: ['Saturn 7th + Jupiter 1st → government benefits'] })
};

const saturn7thEmpty1stBenefic: Rule = {
  key: 'saturn_7th_empty_1st_benefic_natal',
  areas: ['Spouse', 'Self'],
  templateKey: 'relationship_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'SATURN PLACEMENT BENEFIC: Saturn in 7th + empty 1st house = placement benefic. BLESSING: Saturn\'s delay/discipline benefits marriage, stable partnerships, mature relationships, spouse brings structure. Empty 1st allows Saturn 7th to function positively. FORTUNE: Long-lasting marriage, responsible spouse.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    if (!saturnPlacement || saturnPlacement.house !== 7) return false;
    
    // Check if 1st house is empty
    const planetsIn1st = Object.entries(dasha.natalPlacements || {}).filter(
      ([_, placement]) => placement?.house === 1
    );
    return planetsIn1st.length === 0;
  },
  score: () => ({ value: 0.50, notes: ['Saturn 7th + empty 1st → benefic placement'] })
};

// ===== RAHU IN 7TH HOUSE COMBINATIONS =====
const rahu7thJupiterAspectReducesDissatisfaction: Rule = {
  key: 'rahu_7th_jupiter_aspect_reduces_dissatisfaction_natal',
  areas: ['Spouse'],
  templateKey: 'relationship_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MARITAL DISSATISFACTION REDUCED: Rahu in 7th + Jupiter aspect = reduces marital dissatisfaction. BLESSING: Jupiter\'s grace mitigates Rahu\'s marital chaos, relationship stability improved, spouse satisfaction increases, marriage protected. Jupiter\'s aspect on Rahu 7th creates relief. LEVERAGE: Strengthen Jupiter, relationship wisdom, patience.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    
    if (!rahuPlacement || rahuPlacement.house !== 7 || !jupiterPlacement) return false;
    
    // Check if Jupiter aspects 7th house
    const jupiterAspects7th = jupiterPlacement.aspects?.includes(7 as HouseNum);
    // Also check conjunction
    const jupiterIn7th = jupiterPlacement.house === 7;
    
    return jupiterAspects7th || jupiterIn7th || false;
  },
  score: () => ({ value: 0.45, notes: ['Rahu 7th + Jupiter aspect → reduces marital dissatisfaction'] })
};

const rahu7thMercuryVenusKetu11thFemaleDestruction: Rule = {
  key: 'rahu_7th_mercury_venus_ketu_11th_female_destruction_natal',
  areas: ['Spouse', 'Wealth'],
  templateKey: 'relationship_challenges',
  priority: 8,
  ruleType: 'natal',
  private: false,
  customPrediction: 'FEMALE FAMILY MEMBER CAUSES DESTRUCTION: Rahu in 7th + Mercury/Venus/Ketu in 11th = female family member causes destruction. CAUTION: Sister/daughter/female relative creates family chaos, women cause financial losses, feminine family members bring trouble, gains house afflicted. REMEDY: Set boundaries with female relatives, protect family assets, Rahu remedies.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const venusPlacement = dasha.natalPlacements?.Venus;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    if (!rahuPlacement || rahuPlacement.house !== 7) return false;
    
    const mercuryIn11th = !!mercuryPlacement && mercuryPlacement.house === 11;
    const venusIn11th = !!venusPlacement && venusPlacement.house === 11;
    const ketuIn11th = !!ketuPlacement && ketuPlacement.house === 11;
    
    return mercuryIn11th || venusIn11th || ketuIn11th;
  },
  score: () => ({ value: -0.55, notes: ['Rahu 7th + Mercury/Venus/Ketu 11th → female family member causes destruction'] })
};

const rahu7thFemaleMarriageBefore22DivorceRisk: Rule = {
  key: 'rahu_7th_female_marriage_before_22_divorce_risk_natal',
  areas: ['Spouse'],
  templateKey: 'relationship_challenges',
  priority: 8,
  ruleType: 'natal',
  private: true,
  customPrediction: 'DIVORCE/SPOUSE DEATH RISK (FEMALE): Rahu in 7th + female marries before 22 = risk of divorce or spouse\'s death. CAUTION: Early marriage ends in divorce, widowhood risk for young brides, immature marriage fails, spouse mortality. Rahu in 7th damages early marriages. TIMING: Avoid marriage before 22. REMEDY: Delay marriage, Rahu remedies, relationship maturity.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    // Note: This rule is timing-based (marriage before 22) and gender-specific
    // We can only check Rahu in 7th; the age/gender check would need additional context
    return !!rahuPlacement && rahuPlacement.house === 7;
  },
  score: () => ({ value: -0.70, notes: ['Rahu 7th + female marriage before 22 → divorce/spouse death risk'] })
};

// ===== KETU IN 7TH HOUSE COMBINATIONS =====
const ketu7thJupiterAspectMitigatesDissatisfaction: Rule = {
  key: 'ketu_7th_jupiter_aspect_mitigates_dissatisfaction_natal',
  areas: ['Spouse'],
  templateKey: 'relationship_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MARITAL DISSATISFACTION MITIGATED: Ketu in 7th + Jupiter aspect = mitigates marital dissatisfaction. BLESSING: Jupiter\'s grace reduces Ketu\'s detachment in marriage, relationship satisfaction improved, spouse connection strengthened, marital wisdom. LEVERAGE: Strengthen Jupiter, spiritual approach to marriage, patience.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    
    if (!ketuPlacement || ketuPlacement.house !== 7 || !jupiterPlacement) return false;
    
    // Check if Jupiter aspects 7th house
    const jupiterAspects7th = jupiterPlacement.aspects?.includes(7 as HouseNum);
    // Also check conjunction
    const jupiterIn7th = jupiterPlacement.house === 7;
    
    return jupiterAspects7th || jupiterIn7th || false;
  },
  score: () => ({ value: 0.45, notes: ['Ketu 7th + Jupiter aspect → mitigates marital dissatisfaction'] })
};

const ketu7thMercuryVenusJupiterWellPlacedNoDisappointments: Rule = {
  key: 'ketu_7th_mercury_venus_jupiter_well_placed_no_disappointments_natal',
  areas: ['Self', 'Spouse', 'Spirituality'],
  templateKey: 'spiritual_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'LIFE FREE OF MAJOR DISAPPOINTMENTS: Ketu in 7th + Mercury/Venus/Jupiter well-placed = life free of major disappointments. BLESSING: Contentment, fulfillment despite detachment, spiritual satisfaction, minimal regrets, balanced life. Well-placed benefics compensate Ketu\'s void. FORTUNE: Peaceful existence, inner happiness, acceptance.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const venusPlacement = dasha.natalPlacements?.Venus;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    
    if (!ketuPlacement || ketuPlacement.house !== 7) return false;
    
    // Check if Mercury/Venus/Jupiter are well-placed (in kendra/trikona: 1,4,5,7,9,10)
    const goodHouses = [1, 4, 5, 7, 9, 10];
    const mercuryWell = !!mercuryPlacement && goodHouses.includes(mercuryPlacement.house);
    const venusWell = !!venusPlacement && goodHouses.includes(venusPlacement.house);
    const jupiterWell = !!jupiterPlacement && goodHouses.includes(jupiterPlacement.house);
    
    // At least one should be well-placed
    return mercuryWell || venusWell || jupiterWell;
  },
  score: () => ({ value: 0.60, notes: ['Ketu 7th + Mercury/Venus/Jupiter well-placed → life free of major disappointments'] })
};

const ketu7thMercuryConjunctionEnemiesVanish34: Rule = {
  key: 'ketu_7th_mercury_conjunction_enemies_vanish_34_natal',
  areas: ['Litigation', 'Spirituality'],
  templateKey: 'spiritual_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'ENEMIES VANISH AFTER 34: Mercury + Ketu in 7th = enemies vanish after age 34. BLESSING: Opposition dissolves post-34, legal victories, adversaries disappear, peace after mid-life, maturity brings resolution. TIMING: Major relief after 34 years. LEVERAGE: Handle conflicts until 34, then expect natural resolution.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    return !!ketuPlacement && ketuPlacement.house === 7 && 
           !!mercuryPlacement && mercuryPlacement.house === 7;
  },
  score: () => ({ value: 0.55, notes: ['Mercury + Ketu in 7th → enemies vanish after age 34'] })
};

const ketu7thMultiplePlanets1stChildrenDestroyed: Rule = {
  key: 'ketu_7th_multiple_planets_1st_children_destroyed_natal',
  areas: ['Children'],
  templateKey: 'children_challenges',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'CHILDREN\'S LIVES DESTROYED: Ketu in 7th + more than 1 planet in 1st = children\'s lives destroyed. CAUTION: Progeny face severe life challenges, children\'s futures ruined, offspring suffer, profound parenting pain. Multiple planets in 1st with Ketu in 7th severely afflicts children. REMEDY: Extra support for children, protective rituals, Santana Gopala worship, therapy for children.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    if (!ketuPlacement || ketuPlacement.house !== 7) return false;
    
    // Check if more than 1 planet in 1st house
    const planetsIn1st = Object.entries(dasha.natalPlacements || {}).filter(
      ([name, placement]) => placement?.house === 1 && name !== 'Ketu' // Exclude Ketu itself
    );
    return planetsIn1st.length > 1;
  },
  score: () => ({ value: -0.80, notes: ['Ketu 7th + >1 planet in 1st → children lives destroyed'] })
};

// ===== SUN IN 8TH HOUSE COMBINATIONS =====
const sun8thMercury2ndEconomicCrisis: Rule = {
  key: 'sun_8th_mercury_2nd_economic_crisis_natal',
  areas: ['Wealth', 'Health'],
  templateKey: 'wealth_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'ECONOMIC CRISIS: Sun in 8th + Mercury in 2nd = economic crisis. CAUTION: Financial disasters, wealth house afflicted, sudden monetary losses, family wealth destroyed, business failures. Sun in transformation house with Mercury in wealth creates economic instability. REMEDY: Financial planning, emergency funds, Sun-Mercury remedies, avoid speculation.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    return !!sunPlacement && sunPlacement.house === 8 && 
           !!mercuryPlacement && mercuryPlacement.house === 2;
  },
  score: () => ({ value: -0.70, notes: ['Sun 8th + Mercury 2nd → economic crisis'] })
};

// ===== MOON IN 8TH HOUSE COMBINATIONS =====
const moon8thJupiterSaturn2ndCancelsEvil: Rule = {
  key: 'moon_8th_jupiter_saturn_2nd_cancels_evil_natal',
  areas: ['Health', 'Wealth'],
  templateKey: 'spiritual_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'MOON\'S EVIL EFFECTS CANCELLED: Moon in 8th + Jupiter/Saturn in 2nd = cancels Moon\'s evil effects. BLESSING: Protective influence, 8th house challenges mitigated, longevity protected, family wealth safeguards health. Jupiter/Saturn in 2nd neutralizes Moon 8th afflictions. FORTUNE: Avoid major 8th house traumas, financial stability protects.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    if (!moonPlacement || moonPlacement.house !== 8) return false;
    
    const jupiterIn2nd = !!jupiterPlacement && jupiterPlacement.house === 2;
    const saturnIn2nd = !!saturnPlacement && saturnPlacement.house === 2;
    
    return jupiterIn2nd || saturnIn2nd;
  },
  score: () => ({ value: 0.65, notes: ['Moon 8th + Jupiter/Saturn 2nd → cancels Moon evil effects'] })
};

// ===== JUPITER IN 8TH HOUSE COMBINATIONS =====
const jupiter8thMars4thHeartProblems: Rule = {
  key: 'jupiter_8th_mars_4th_heart_problems_natal',
  areas: ['Health'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'HEART PROBLEMS RISK: Jupiter in 8th + Mars in 4th = heart problems risk. CAUTION: Cardiovascular disease risk, heart attacks, cardiac stress, chest pain, circulation issues. Mars in heart house (4th) with Jupiter in longevity house creates cardiac vulnerability. REMEDY: Regular cardiac checkups, heart-healthy lifestyle, Mars-Jupiter remedies, stress management, exercise moderation.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    return !!jupiterPlacement && jupiterPlacement.house === 8 && 
           !!marsPlacement && marsPlacement.house === 4;
  },
  score: () => ({ value: -0.60, notes: ['Jupiter 8th + Mars 4th → heart problems risk'] })
};

// ===== MERCURY IN 8TH HOUSE COMBINATIONS =====
const mercury8thMalefic11thIncomeDip32: Rule = {
  key: 'mercury_8th_malefic_11th_income_dip_32_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'wealth_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'INCOME DIP AGE 32-34: Mercury in 8th + malefic in 11th = income dip age 32-34. CAUTION: Earnings decline between 32-34, gains house afflicted, career setback mid-thirties, financial downturn. TIMING: Critical period 32-34 years. REMEDY: Save money before 32, diversify income, Mercury remedies, career planning.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    if (!mercuryPlacement || mercuryPlacement.house !== 8) return false;
    
    const marsIn11th = !!marsPlacement && marsPlacement.house === 11;
    const saturnIn11th = !!saturnPlacement && saturnPlacement.house === 11;
    const rahuIn11th = !!rahuPlacement && rahuPlacement.house === 11;
    const ketuIn11th = !!ketuPlacement && ketuPlacement.house === 11;
    
    return marsIn11th || saturnIn11th || rahuIn11th || ketuIn11th;
  },
  score: () => ({ value: -0.50, notes: ['Mercury 8th + malefic 11th → income dip age 32-34'] })
};

const mercury8thSaturnMarsShortensLifespan: Rule = {
  key: 'mercury_8th_saturn_mars_shortens_lifespan_natal',
  areas: ['Health'],
  templateKey: 'health_alert',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'LIFESPAN SHORTENED: Mercury in 8th + Saturn/Mars in 8th = shortens lifespan (softened if Sun/Mars in Lagna). CAUTION: Reduced longevity, early death risk, chronic health issues, vitality depleted. Multiple malefics in longevity house damage lifespan. PROTECTION: Sun or Mars in 1st mitigates. REMEDY: Mahamrityunjaya mantra, health vigilance, Saturn-Mars-Mercury remedies.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const sunPlacement = dasha.natalPlacements?.Sun;
    
    if (!mercuryPlacement || mercuryPlacement.house !== 8) return false;
    
    const saturnIn8th = !!saturnPlacement && saturnPlacement.house === 8;
    const marsIn8th = !!marsPlacement && marsPlacement.house === 8;
    
    if (!saturnIn8th && !marsIn8th) return false;
    
    // Check protection: Sun or Mars in 1st
    const sunIn1st = !!sunPlacement && sunPlacement.house === 1;
    const marsIn1st = !!marsPlacement && marsPlacement.house === 1;
    const isProtected = sunIn1st || marsIn1st;
    
    // Only trigger if not protected
    return !isProtected;
  },
  score: () => ({ value: -0.80, notes: ['Mercury 8th + Saturn/Mars 8th → shortens lifespan (unless Sun/Mars in 1st)'] })
};

const mercury8thSunMarsLagnaProtection: Rule = {
  key: 'mercury_8th_sun_mars_lagna_protection_natal',
  areas: ['Health', 'Wealth'],
  templateKey: 'spiritual_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'PROTECTION FROM LOSSES: Mercury in 8th + Sun/Mars in Lagna = protection from losses. BLESSING: Longevity protected, financial losses prevented, vitality strong, resilience against 8th house challenges. Sun/Mars in 1st shields from Mercury 8th afflictions. FORTUNE: Overcome obstacles, survival instinct strong.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const sunPlacement = dasha.natalPlacements?.Sun;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    if (!mercuryPlacement || mercuryPlacement.house !== 8) return false;
    
    const sunIn1st = !!sunPlacement && sunPlacement.house === 1;
    const marsIn1st = !!marsPlacement && marsPlacement.house === 1;
    
    return sunIn1st || marsIn1st;
  },
  score: () => ({ value: 0.60, notes: ['Mercury 8th + Sun/Mars in Lagna → protection from losses'] })
};

const mercury8thRahuImprisonmentInstability: Rule = {
  key: 'mercury_8th_rahu_imprisonment_instability_natal',
  areas: ['Health', 'Litigation'],
  templateKey: 'health_alert',
  priority: 9,
  ruleType: 'natal',
  private: false,
  customPrediction: 'IMPRISONMENT/HOSPITALIZATION/INSTABILITY: Rahu + Mercury in 8th = imprisonment, hospitalization, instability. CAUTION: Legal confinement risk, long hospital stays, mental instability, chaotic transformations, sudden crises. Rahu-Mercury in 8th creates extreme uncertainty. REMEDY: Legal caution, health insurance, mental health support, Rahu-Mercury remedies, avoid illegal activities.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    
    return !!mercuryPlacement && mercuryPlacement.house === 8 && 
           !!rahuPlacement && rahuPlacement.house === 8;
  },
  score: () => ({ value: -0.75, notes: ['Rahu + Mercury in 8th → imprisonment/hospitalization/instability'] })
};

const mercury8thMarsAdverseOutcomes: Rule = {
  key: 'mercury_8th_mars_adverse_outcomes_natal',
  areas: ['Health', 'AccidentRisk'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'ADVERSE OUTCOMES: Mars + Mercury in 8th = adverse outcomes (unless offset by benefics). CAUTION: Accidents, surgical risks, sudden health crises, aggressive transformations, danger from Mars-Mercury volatility. PROTECTION: Jupiter/Venus aspects or placements mitigate. REMEDY: Careful behavior, Mars-Mercury remedies, avoid risky activities.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const venusPlacement = dasha.natalPlacements?.Venus;
    
    if (!mercuryPlacement || mercuryPlacement.house !== 8 || !marsPlacement || marsPlacement.house !== 8) return false;
    
    // Check if benefics offset (Jupiter/Venus in kendra/trikona or aspecting 8th)
    const goodHouses = [1, 4, 5, 7, 9, 10];
    const jupiterWell = !!jupiterPlacement && goodHouses.includes(jupiterPlacement.house);
    const venusWell = !!venusPlacement && goodHouses.includes(venusPlacement.house);
    const beneficOffset = jupiterWell || venusWell;
    
    // Only trigger if not offset
    return !beneficOffset;
  },
  score: () => ({ value: -0.65, notes: ['Mars + Mercury in 8th → adverse outcomes (unless benefics offset)'] })
};

// ===== VENUS IN 8TH HOUSE COMBINATIONS =====
const venus8thEmpty2ndMarryAfter25: Rule = {
  key: 'venus_8th_empty_2nd_marry_after_25_natal',
  areas: ['Spouse'],
  templateKey: 'relationship_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MARRY AFTER 25 FOR BETTER RESULTS: Venus in 8th + empty 2nd = marry after 25 for better results. BLESSING: Late marriage brings stability, maturity improves marital outcomes, delayed marriage avoids troubles, post-25 unions more successful. TIMING: Wait until 25+ for marriage. LEVERAGE: Use time to mature, build career, find right partner.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    if (!venusPlacement || venusPlacement.house !== 8) return false;
    
    // Check if 2nd house is empty
    const planetsIn2nd = Object.entries(dasha.natalPlacements || {}).filter(
      ([_, placement]) => placement?.house === 2
    );
    return planetsIn2nd.length === 0;
  },
  score: () => ({ value: 0.45, notes: ['Venus 8th + empty 2nd → marry after 25 for better results'] })
};

// ===== SATURN IN 8TH HOUSE COMBINATIONS =====
const saturn8thMercuryRahuKetuMaleficHardships: Rule = {
  key: 'saturn_8th_mercury_rahu_ketu_malefic_hardships_natal',
  areas: ['Health', 'Wealth'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'ADDED HARDSHIPS: Saturn in 8th + Mercury/Rahu/Ketu malefic = adds hardships. CAUTION: Severe life difficulties, longevity challenges, chronic suffering, financial-health double burden, prolonged struggles. Multiple malefics in 8th create extreme hardships. REMEDY: Saturn-Mercury-Rahu-Ketu remedies, patience, spiritual practices, endurance.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    if (!saturnPlacement || saturnPlacement.house !== 8) return false;
    
    // Check if Mercury/Rahu/Ketu are malefic (in dusthana 6/8/12 or afflicted)
    const mercuryMalefic = !!mercuryPlacement && (mercuryPlacement.house === 6 || mercuryPlacement.house === 8 || mercuryPlacement.house === 12);
    const rahuMalefic = !!rahuPlacement && (rahuPlacement.house === 6 || rahuPlacement.house === 8 || rahuPlacement.house === 12);
    const ketuMalefic = !!ketuPlacement && (ketuPlacement.house === 6 || ketuPlacement.house === 8 || ketuPlacement.house === 12);
    
    return mercuryMalefic || rahuMalefic || ketuMalefic;
  },
  score: () => ({ value: -0.70, notes: ['Saturn 8th + Mercury/Rahu/Ketu malefic → added hardships'] })
};

const saturn8thVehicleBacklightOmen: Rule = {
  key: 'saturn_8th_vehicle_backlight_omen_natal',
  areas: ['AccidentRisk', 'Health'],
  templateKey: 'health_alert',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'VEHICLE BACKLIGHT BREAK OMEN: Saturn malefic or transiting 8th + vehicle backlight breaks = omen of danger. CAUTION: Vehicle accidents omen, mechanical failures signal crisis, transportation dangers, heed warning signs. Broken backlights during Saturn 8th periods indicate increased accident risk. REMEDY: Fix vehicle immediately, extra caution driving, Saturn remedies, avoid long journeys.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    // Note: This is partially timing-based (transit), we check natal position only
    return !!saturnPlacement && saturnPlacement.house === 8;
  },
  score: () => ({ value: -0.40, notes: ['Saturn malefic/transiting 8th → vehicle backlight break omen'] })
};

// ===== RAHU IN 8TH HOUSE COMBINATIONS =====
const rahu8thMarsBenefic1st5th8thRich: Rule = {
  key: 'rahu_8th_mars_benefic_1st_5th_8th_rich_natal',
  areas: ['Wealth', 'Health'],
  templateKey: 'wealth_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'RICH NATIVE: Rahu in 8th + benefic Mars in 1st/5th/8th OR benefic Saturn in 8th = rich native. BLESSING: Sudden wealth, inheritance, insurance gains, longevity-based prosperity, occult income. Benefic malefics in key houses with Rahu create wealth. LEVERAGE: Inheritance planning, insurance, research/occult professions, sudden gains.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    if (!rahuPlacement || rahuPlacement.house !== 8) return false;
    
    // Check if Mars is benefic and in 1st/5th/8th
    // Simplified: Mars in 1st/5th/8th (assume benefic if strong placement)
    const marsIn1st5th8th = !!marsPlacement && (marsPlacement.house === 1 || marsPlacement.house === 5 || marsPlacement.house === 8);
    
    // Check if Saturn is benefic and in 8th
    const saturnIn8th = !!saturnPlacement && saturnPlacement.house === 8;
    
    return marsIn1st5th8th || saturnIn8th;
  },
  score: () => ({ value: 0.70, notes: ['Rahu 8th + benefic Mars 1st/5th/8th OR benefic Saturn 8th → rich native'] })
};

// ===== KETU IN 8TH HOUSE COMBINATIONS =====
const ketu8thJupiterMarsAbsent6thNonMalefic: Rule = {
  key: 'ketu_8th_jupiter_mars_absent_6th_non_malefic_natal',
  areas: ['Health', 'Spirituality'],
  templateKey: 'spiritual_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'KETU NON-MALEFIC: Ketu in 8th + Jupiter/Mars absent in 6th = Ketu non-malefic. BLESSING: 8th house Ketu becomes spiritual, longevity protected, occult talents, transformation benefic. Absence of Jupiter/Mars in enemy house allows Ketu to function positively. FORTUNE: Spiritual evolution, mystical experiences, peaceful transformations.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    if (!ketuPlacement || ketuPlacement.house !== 8) return false;
    
    // Check if Jupiter/Mars absent in 6th
    const jupiterIn6th = !!jupiterPlacement && jupiterPlacement.house === 6;
    const marsIn6th = !!marsPlacement && marsPlacement.house === 6;
    
    return !jupiterIn6th && !marsIn6th;
  },
  score: () => ({ value: 0.55, notes: ['Ketu 8th + Jupiter/Mars absent 6th → Ketu non-malefic'] })
};

const ketu8thMoonNot2ndNegativeReduced: Rule = {
  key: 'ketu_8th_moon_not_2nd_negative_reduced_natal',
  areas: ['Health', 'Wealth'],
  templateKey: 'spiritual_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'NEGATIVE EFFECTS REDUCED: Ketu in 8th + Moon not in 2nd = negative effects reduced. BLESSING: Ketu 8th challenges mitigated, family wealth protected, emotional stability maintained, longevity less affected. Moon absent from 2nd reduces Ketu 8th afflictions. FORTUNE: Manageable transformations, financial stability.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    if (!ketuPlacement || ketuPlacement.house !== 8) return false;
    
    // Check if Moon is NOT in 2nd
    const moonNot2nd = !moonPlacement || moonPlacement.house !== 2;
    return moonNot2nd;
  },
  score: () => ({ value: 0.45, notes: ['Ketu 8th + Moon not in 2nd → negative effects reduced'] })
};

const ketu8thSaturnMars7thBadLuck: Rule = {
  key: 'ketu_8th_saturn_mars_7th_bad_luck_natal',
  areas: ['Spouse', 'Health'],
  templateKey: 'relationship_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'BAD LUCK (AFFECTS SPOUSE): Ketu in 8th + Saturn/Mars in 7th = bad luck (also affects spouse). CAUTION: Marital misfortune, spouse health issues, partnership troubles, longevity-marriage challenges linked, spouse suffers. The 7th-8th malefic axis damages marriage and health. REMEDY: Spouse protection rituals, Ketu-Saturn-Mars remedies, marriage counseling, health vigilance for partner.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    if (!ketuPlacement || ketuPlacement.house !== 8) return false;
    
    const saturnIn7th = !!saturnPlacement && saturnPlacement.house === 7;
    const marsIn7th = !!marsPlacement && marsPlacement.house === 7;
    
    return saturnIn7th || marsIn7th;
  },
  score: () => ({ value: -0.65, notes: ['Ketu 8th + Saturn/Mars 7th → bad luck, affects spouse'] })
};

// ===== SUN IN 9TH HOUSE COMBINATIONS =====
const sun9thMercury5thFortuneAfter34: Rule = {
  key: 'sun_9th_mercury_5th_fortune_after_34_natal',
  areas: ['Wealth', 'Career', 'Spirituality'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'GOOD FORTUNE AFTER 34: Sun in 9th + Mercury in 5th = good fortune after age 34. BLESSING: Late-blooming success, fortune increases post-34, dharma-intelligence axis matures, wisdom brings wealth. TIMING: Major improvements after 34 years. LEVERAGE: Long-term planning, intellectual pursuits, teaching, publishing.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    return !!sunPlacement && sunPlacement.house === 9 && 
           !!mercuryPlacement && mercuryPlacement.house === 5;
  },
  score: () => ({ value: 0.60, notes: ['Sun 9th + Mercury 5th → good fortune after 34'] })
};

// ===== JUPITER IN 9TH HOUSE COMBINATIONS =====
const jupiter9thMars3rdInLawsFinancialHelp: Rule = {
  key: 'jupiter_9th_mars_3rd_in_laws_financial_help_natal',
  areas: ['Wealth', 'Spouse'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'FINANCIAL HELP FROM IN-LAWS: Jupiter in 9th + Mars in 3rd = financial help from in-laws. BLESSING: Spouse\'s family provides money, in-law support, marital alliance brings financial aid, courage-fortune axis creates family assistance. LEVERAGE: In-law business partnerships, family loans, spousal family connections.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    return !!jupiterPlacement && jupiterPlacement.house === 9 && 
           !!marsPlacement && marsPlacement.house === 3;
  },
  score: () => ({ value: 0.55, notes: ['Jupiter 9th + Mars 3rd → financial help from in-laws'] })
};

const jupiter9thSaturn5thWealthAfter36: Rule = {
  key: 'jupiter_9th_saturn_5th_wealth_after_36_natal',
  areas: ['Wealth', 'Children'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'WEALTH AFTER 36: Jupiter in 9th + Saturn in 5th = wealth after age 36. BLESSING: Late financial success, Saturn delays then delivers, fortune-intelligence axis matures, disciplined wealth-building pays off. TIMING: Major wealth accumulation after 36 years. LEVERAGE: Long-term investments, patience, children support in later years.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    return !!jupiterPlacement && jupiterPlacement.house === 9 && 
           !!saturnPlacement && saturnPlacement.house === 5;
  },
  score: () => ({ value: 0.65, notes: ['Jupiter 9th + Saturn 5th → wealth after 36'] })
};

// ===== MERCURY IN 9TH HOUSE COMBINATIONS =====
const mercury9thVenusInfluenceSingingCareer: Rule = {
  key: 'mercury_9th_venus_influence_singing_career_natal',
  areas: ['Career', 'Spirituality'],
  templateKey: 'career_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'SUCCESSFUL SINGING CAREER: Mercury in 9th + Venus influences Mercury = successful career in singing. BLESSING: Musical talent, vocal arts success, artistic fortune, creative dharma, Venus-Mercury harmony creates singing prowess. LEVERAGE: Music education, performing arts, artistic pursuits, voice training.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const venusPlacement = dasha.natalPlacements?.Venus;
    
    if (!mercuryPlacement || mercuryPlacement.house !== 9 || !venusPlacement) return false;
    
    // Venus influences Mercury: conjunction, aspect, or in adjacent houses
    const venusIn9th = venusPlacement.house === 9; // Conjunction
    const venusAspects9th = venusPlacement.aspects?.includes(9 as HouseNum);
    // Adjacent houses (8th or 10th from 9th)
    const venusAdjacent = venusPlacement.house === 8 || venusPlacement.house === 10;
    
    return venusIn9th || venusAspects9th || venusAdjacent || false;
  },
  score: () => ({ value: 0.60, notes: ['Mercury 9th + Venus influence → singing career success'] })
};

// ===== VENUS IN 9TH HOUSE COMBINATIONS =====
const venus9thMercuryMaleficAddictionsPost17: Rule = {
  key: 'venus_9th_mercury_malefic_addictions_post_17_natal',
  areas: ['Health', 'Spirituality'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'ADDICTION/HEALTH ISSUES POST 17: Venus in 9th + Mercury/malefic planet = addiction/health issues post 17. CAUTION: Substance abuse after 17, fortune-related vices, dharma house afflicted, spiritual corruption through pleasure. TIMING: Risk increases after 17 years. REMEDY: Avoid substances, spiritual discipline, Venus-Mercury remedies, healthy lifestyle choices.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    if (!venusPlacement || venusPlacement.house !== 9) return false;
    
    const mercuryIn9th = !!mercuryPlacement && mercuryPlacement.house === 9;
    const marsIn9th = !!marsPlacement && marsPlacement.house === 9;
    const saturnIn9th = !!saturnPlacement && saturnPlacement.house === 9;
    const rahuIn9th = !!rahuPlacement && rahuPlacement.house === 9;
    const ketuIn9th = !!ketuPlacement && ketuPlacement.house === 9;
    
    return mercuryIn9th || marsIn9th || saturnIn9th || rahuIn9th || ketuIn9th;
  },
  score: () => ({ value: -0.55, notes: ['Venus 9th + Mercury/malefic → addiction/health issues post 17'] })
};

const venus9thMarsLosesPotency: Rule = {
  key: 'venus_9th_mars_loses_potency_natal',
  areas: ['Health', 'Career'],
  templateKey: 'health_alert',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'MARS LOSES POTENCY: Venus in 9th + Mars affected by Venus placement = Mars loses potency. CAUTION: Masculine energy weakened, sexual vitality reduced, courage diminished, Mars qualities suppressed by Venus. Venus in dharma house feminizes Mars influence. REMEDY: Mars strengthening, physical exercise, martial arts, avoid over-indulgence.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    if (!venusPlacement || venusPlacement.house !== 9 || !marsPlacement) return false;
    
    // Mars affected: conjunction with Venus, aspect from Venus, or Mars in 3rd/5th/9th (aspected by Venus in 9th)
    const marsIn9th = marsPlacement.house === 9; // Conjunction
    const venusAspectsMars = venusPlacement.aspects?.includes(marsPlacement.house);
    
    return marsIn9th || venusAspectsMars || false;
  },
  score: () => ({ value: -0.40, notes: ['Venus 9th affecting Mars → Mars loses potency'] })
};

// ===== SATURN IN 9TH HOUSE COMBINATIONS =====
const saturn9thMalefic3rdConstructionMisfortunes: Rule = {
  key: 'saturn_9th_malefic_3rd_construction_misfortunes_natal',
  areas: ['Property', 'Health'],
  templateKey: 'health_alert',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'CONSTRUCTION TIMING MISFORTUNES: Saturn malefic in 9th + 3rd house construction during bad timing = misfortunes. CAUTION: Building house during Saturn afflictions brings troubles, property construction disasters, real estate problems, timing-sensitive construction risks. REMEDY: Consult astrologer before construction, avoid building during malefic Saturn periods, perform Vastu rituals.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const marsPlacement = dasha.natalPlacements?.Mars;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    if (!saturnPlacement || saturnPlacement.house !== 9) return false;
    
    // Check if Saturn is malefic (afflicted or with malefics)
    // Simplified: Check if malefics in 3rd house or Saturn in dusthana context
    const marsIn3rd = !!marsPlacement && marsPlacement.house === 3;
    const rahuIn3rd = !!rahuPlacement && rahuPlacement.house === 3;
    const ketuIn3rd = !!ketuPlacement && ketuPlacement.house === 3;
    
    return marsIn3rd || rahuIn3rd || ketuIn3rd;
  },
  score: () => ({ value: -0.50, notes: ['Saturn malefic 9th + 3rd house construction timing → misfortunes'] })
};

const saturn9thMaleConstructsWifePregnantProblems: Rule = {
  key: 'saturn_9th_male_constructs_wife_pregnant_problems_natal',
  areas: ['Property', 'Children', 'Spouse'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'CONSTRUCTION DURING PREGNANCY PROBLEMS: Saturn in 9th + male constructs house while wife pregnant = problems. CAUTION: Building during pregnancy brings complications, spouse/child health risks, property-family conflicts, timing curse. Saturn in father/dharma house damages progeny during construction. REMEDY: Avoid construction when wife pregnant, protective rituals, delay building projects.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    // Note: This is timing and gender-specific, we can only check Saturn in 9th
    return !!saturnPlacement && saturnPlacement.house === 9;
  },
  score: () => ({ value: -0.55, notes: ['Saturn 9th + male constructs house during wife pregnancy → problems'] })
};

// ===== RAHU IN 9TH HOUSE COMBINATIONS =====
const rahu9thEmpty1stHealthInsultsElders: Rule = {
  key: 'rahu_9th_empty_1st_health_insults_elders_natal',
  areas: ['Health', 'Father', 'Spirituality'],
  templateKey: 'health_alert',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'HEALTH ISSUES & INSULTS FROM ELDERS: Rahu in 9th + empty 1st house = health issues, insults from elders. CAUTION: Elder disrespect, father/guru conflicts, health problems, dharma house Rahu with weak 1st creates double affliction. Seniors humiliate, body weakness. REMEDY: Respect elders, Rahu remedies, health care, strengthen 1st house through Saturn remedies.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    if (!rahuPlacement || rahuPlacement.house !== 9) return false;
    
    // Check if 1st house is empty
    const planetsIn1st = Object.entries(dasha.natalPlacements || {}).filter(
      ([_, placement]) => placement?.house === 1
    );
    return planetsIn1st.length === 0;
  },
  score: () => ({ value: -0.50, notes: ['Rahu 9th + empty 1st → health issues, insults from elders'] })
};

// ===== KETU IN 9TH HOUSE COMBINATIONS =====
const ketu9th2ndAuspiciousExcellent: Rule = {
  key: 'ketu_9th_2nd_auspicious_excellent_natal',
  areas: ['Wealth', 'Spirituality'],
  templateKey: 'spiritual_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: '2ND HOUSE AUSPICIOUS - EXCELLENT RESULTS: Ketu in 9th + 2nd house auspicious = excellent results. BLESSING: Family wealth protected, spiritual detachment with financial stability, dharma house Ketu becomes benefic, speech/values aligned with spirituality. FORTUNE: Wealth through non-attachment, spiritual prosperity, family harmony.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const venusPlacement = dasha.natalPlacements?.Venus;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    if (!ketuPlacement || ketuPlacement.house !== 9) return false;
    
    // Check if 2nd house is auspicious (has benefics)
    const jupiterIn2nd = !!jupiterPlacement && jupiterPlacement.house === 2;
    const venusIn2nd = !!venusPlacement && venusPlacement.house === 2;
    const moonIn2nd = !!moonPlacement && moonPlacement.house === 2;
    
    return jupiterIn2nd || venusIn2nd || moonIn2nd;
  },
  score: () => ({ value: 0.70, notes: ['Ketu 9th + 2nd auspicious → excellent results'] })
};

const ketu9thMoonWellPlacedMotherFamilySupport: Rule = {
  key: 'ketu_9th_moon_well_placed_mother_family_support_natal',
  areas: ['Mother', 'Spirituality', 'Wealth'],
  templateKey: 'family_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MOTHER\'S FAMILY SUPPORT: Ketu in 9th + Moon well-placed = support from mother\'s family. BLESSING: Maternal side helps financially/emotionally, mother\'s relatives provide backing, maternal inheritance, moon-ketu balance creates family support. LEVERAGE: Maternal family business, inheritance from mother\'s side, emotional backing.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    if (!ketuPlacement || ketuPlacement.house !== 9 || !moonPlacement) return false;
    
    // Check if Moon is well-placed (in kendra/trikona: 1,4,5,7,9,10)
    const goodHouses = [1, 4, 5, 7, 9, 10];
    return goodHouses.includes(moonPlacement.house);
  },
  score: () => ({ value: 0.60, notes: ['Ketu 9th + Moon well-placed → mother family support'] })
};

const ketu9thMoonMars3rdMaleChildProblems: Rule = {
  key: 'ketu_9th_moon_mars_3rd_male_child_problems_natal',
  areas: ['Children'],
  templateKey: 'children_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'MALE CHILD SUFFERS PROBLEMS: Ketu in 9th + Moon/Mars in 3rd = male child suffers problems. CAUTION: Sons face difficulties, male progeny challenges, courage house afflicted damages sons, Ketu in father house with Moon/Mars in 3rd harms male children. REMEDY: Extra care for sons, protective rituals, Santana Gopala worship, Mars-Moon remedies.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const moonPlacement = dasha.natalPlacements?.Moon;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    if (!ketuPlacement || ketuPlacement.house !== 9) return false;
    
    const moonIn3rd = !!moonPlacement && moonPlacement.house === 3;
    const marsIn3rd = !!marsPlacement && marsPlacement.house === 3;
    
    return moonIn3rd || marsIn3rd;
  },
  score: () => ({ value: -0.55, notes: ['Ketu 9th + Moon/Mars 3rd → male child suffers problems'] })
};

// ===== SUN IN 10TH HOUSE COMBINATIONS =====
const sun10thSaturn4thFatherLossChildhood: Rule = {
  key: 'sun_10th_saturn_4th_father_loss_childhood_natal',
  areas: ['Father', 'Health'],
  templateKey: 'family_challenges',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'LOSS OF FATHER IN CHILDHOOD: Sun in 10th + Saturn in 4th = loss of father in childhood. CAUTION: Early paternal death, father mortality in youth, childhood trauma, family foundation damaged. Sun (father) in career opposed by Saturn in home creates paternal loss. REMEDY: Mahamrityunjaya mantra for father, Sun-Saturn remedies, ancestor worship, emotional healing.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    return !!sunPlacement && sunPlacement.house === 10 && 
           !!saturnPlacement && saturnPlacement.house === 4;
  },
  score: () => ({ value: -0.85, notes: ['Sun 10th + Saturn 4th → father loss in childhood'] })
};

const sun10thMoon5thShortLifespan: Rule = {
  key: 'sun_10th_moon_5th_short_lifespan_natal',
  areas: ['Health'],
  templateKey: 'health_alert',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'SHORT LIFESPAN: Sun in 10th + Moon in 5th = short lifespan. CAUTION: Reduced longevity, early death risk, vitality compromised, intelligence-career axis creates life-shortening. The 5th-10th Sun-Moon combination affects longevity. REMEDY: Mahamrityunjaya mantra, Sun-Moon strengthening, health vigilance, spiritual practices, longevity rituals.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    return !!sunPlacement && sunPlacement.house === 10 && 
           !!moonPlacement && moonPlacement.house === 5;
  },
  score: () => ({ value: -0.80, notes: ['Sun 10th + Moon 5th → short lifespan'] })
};

const sun10thEmpty4thNoGovtBenefits: Rule = {
  key: 'sun_10th_empty_4th_no_govt_benefits_natal',
  areas: ['Career', 'Property'],
  templateKey: 'career_challenges',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'NO GOVT BENEFITS: Sun in 10th + empty 4th house = no government benefits. CAUTION: Public sector disadvantages, governmental neglect, no official support, property-career disconnect. Empty 4th with Sun in 10th blocks government favors. REMEDY: Private sector focus, self-employment, avoid relying on government, build own security.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    if (!sunPlacement || sunPlacement.house !== 10) return false;
    
    // Check if 4th house is empty
    const planetsIn4th = Object.entries(dasha.natalPlacements || {}).filter(
      ([_, placement]) => placement?.house === 4
    );
    return planetsIn4th.length === 0;
  },
  score: () => ({ value: -0.35, notes: ['Sun 10th + empty 4th → no govt benefits'] })
};

const sun10thRahu5thInLawIssues: Rule = {
  key: 'sun_10th_rahu_5th_in_law_issues_natal',
  areas: ['Spouse', 'Children'],
  templateKey: 'relationship_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'IN-LAW ISSUES: Sun in 10th + Rahu in 5th = in-law issues. CAUTION: Conflicts with spouse\'s family, in-law interference, children-in-law tensions, career-intelligence axis with Rahu creates family friction. REMEDY: Diplomacy with in-laws, Rahu remedies, set boundaries, avoid in-law business entanglements.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    
    return !!sunPlacement && sunPlacement.house === 10 && 
           !!rahuPlacement && rahuPlacement.house === 5;
  },
  score: () => ({ value: -0.45, notes: ['Sun 10th + Rahu 5th → in-law issues'] })
};

const sun10thKetu5thMaleProgeny: Rule = {
  key: 'sun_10th_ketu_5th_male_progeny_natal',
  areas: ['Children'],
  templateKey: 'children_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MALE PROGENY: Sun in 10th + Ketu in 5th = male progeny. BLESSING: Sons born, male children favored, career-intelligence axis with Ketu creates male offspring, spiritual detachment in progeny house yields sons. FORTUNE: Male lineage continuation, sons support career.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    return !!sunPlacement && sunPlacement.house === 10 && 
           !!ketuPlacement && ketuPlacement.house === 5;
  },
  score: () => ({ value: 0.50, notes: ['Sun 10th + Ketu 5th → male progeny'] })
};

const sun10thMoon5thMoneyMotherTroubles: Rule = {
  key: 'sun_10th_moon_5th_money_mother_troubles_natal',
  areas: ['Mother', 'Wealth'],
  templateKey: 'family_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MONEY & MOTHER TROUBLES: Sun in 10th + Moon in 5th = money and mother troubles. CAUTION: Financial struggles linked to mother, maternal financial burden, wealth-mother conflicts, career success doesn\'t translate to family wealth. REMEDY: Financial planning separate from family, support mother within limits, Moon remedies.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    return !!sunPlacement && sunPlacement.house === 10 && 
           !!moonPlacement && moonPlacement.house === 5;
  },
  score: () => ({ value: -0.50, notes: ['Sun 10th + Moon 5th → money and mother troubles'] })
};

const sun10thMars5thBrothersFaceIssues: Rule = {
  key: 'sun_10th_mars_5th_brothers_face_issues_natal',
  areas: ['Self'],
  templateKey: 'family_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'BROTHERS FACE ISSUES: Sun in 10th + Mars in 5th = brothers face issues. CAUTION: Siblings suffer, brother\'s problems, sibling health/career challenges, Mars in intelligence house with Sun in career damages brothers. REMEDY: Support siblings, Mars remedies, sibling protection rituals, avoid sibling business partnerships.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    return !!sunPlacement && sunPlacement.house === 10 && 
           !!marsPlacement && marsPlacement.house === 5;
  },
  score: () => ({ value: -0.45, notes: ['Sun 10th + Mars 5th → brothers face issues'] })
};

const sun10thSaturn5thConstructionPaternalUncleIssues: Rule = {
  key: 'sun_10th_saturn_5th_construction_paternal_uncle_issues_natal',
  areas: ['Property', 'Father'],
  templateKey: 'property_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'CONSTRUCTION TROUBLES / PATERNAL UNCLE ISSUES: Sun in 10th + Saturn in 5th = construction troubles, paternal uncle issues. CAUTION: Property construction problems, delays/disasters in building, father\'s brother (paternal uncle) faces difficulties, real estate challenges. REMEDY: Vastu consultation before construction, Saturn remedies, support paternal uncle, delay construction projects.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    return !!sunPlacement && sunPlacement.house === 10 && 
           !!saturnPlacement && saturnPlacement.house === 5;
  },
  score: () => ({ value: -0.50, notes: ['Sun 10th + Saturn 5th → construction troubles, paternal uncle issues'] })
};

// ===== MOON IN 10TH HOUSE COMBINATIONS =====
const moon10thSaturnConjunctionDiseaseFinancialLoss: Rule = {
  key: 'moon_10th_saturn_conjunction_disease_financial_loss_natal',
  areas: ['Health', 'Wealth', 'Career'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'DISEASE & FINANCIAL LOSS: Moon + Saturn in 10th = disease, financial loss. CAUTION: Chronic illness, career-related health issues, financial disasters, depression, Saturn afflicts Moon in career house. REMEDY: Health monitoring, financial planning, Moon-Saturn remedies, mental health support, avoid career stress.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    return !!moonPlacement && moonPlacement.house === 10 && 
           !!saturnPlacement && saturnPlacement.house === 10;
  },
  score: () => ({ value: -0.65, notes: ['Moon + Saturn in 10th → disease, financial loss'] })
};

const moon10thEmpty2nd4thWealthGain: Rule = {
  key: 'moon_10th_empty_2nd_4th_wealth_gain_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'WEALTH GAIN: Moon in 10th + empty 2nd and 4th houses = wealth gain. BLESSING: Career success translates to wealth, unencumbered by family/property issues, professional income flows freely, mind focused on earnings. LEVERAGE: Career-driven wealth, professional investments, avoid family business.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!moonPlacement || moonPlacement.house !== 10) return false;
    
    // Check if 2nd house is empty
    const planetsIn2nd = Object.entries(dasha.natalPlacements || {}).filter(
      ([_, placement]) => placement?.house === 2
    );
    // Check if 4th house is empty
    const planetsIn4th = Object.entries(dasha.natalPlacements || {}).filter(
      ([_, placement]) => placement?.house === 4
    );
    
    return planetsIn2nd.length === 0 && planetsIn4th.length === 0;
  },
  score: () => ({ value: 0.60, notes: ['Moon 10th + empty 2nd & 4th → wealth gain'] })
};

const moon10thSaturn1stDestructionOppositeSex: Rule = {
  key: 'moon_10th_saturn_1st_destruction_opposite_sex_natal',
  areas: ['Spouse', 'Health'],
  templateKey: 'relationship_challenges',
  priority: 8,
  ruleType: 'natal',
  private: false,
  customPrediction: 'DESTRUCTION THROUGH OPPOSITE SEX: Moon in 10th + Saturn in 1st = destruction through opposite sex (widow/widower). CAUTION: Spouse loss, relationships cause downfall, attraction to widows/widowers, opposite sex brings ruin. The 1st-10th Saturn-Moon axis creates relationship-based destruction. REMEDY: Careful partner selection, avoid hasty relationships, Saturn-Moon remedies.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    return !!moonPlacement && moonPlacement.house === 10 && 
           !!saturnPlacement && saturnPlacement.house === 1;
  },
  score: () => ({ value: -0.60, notes: ['Moon 10th + Saturn 1st → destruction through opposite sex (widow/er)'] })
};

// ===== JUPITER IN 10TH HOUSE COMBINATIONS =====
const jupiter10thMoon4thGenerosityHardship: Rule = {
  key: 'jupiter_10th_moon_4th_generosity_hardship_natal',
  areas: ['Wealth', 'Property'],
  templateKey: 'wealth_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'GENEROSITY CAUSES HARDSHIP: Jupiter in 10th + Moon in 4th = generosity causes hardship. CAUTION: Over-giving creates financial problems, emotional generosity depletes resources, helping others backfires, charity damages own family. REMEDY: Set giving limits, balance generosity with self-care, protect family assets, strategic charity.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    return !!jupiterPlacement && jupiterPlacement.house === 10 && 
           !!moonPlacement && moonPlacement.house === 4;
  },
  score: () => ({ value: -0.40, notes: ['Jupiter 10th + Moon 4th → generosity causes hardship'] })
};

const jupiter10thSaturn4thButcherPragmatism: Rule = {
  key: 'jupiter_10th_saturn_4th_butcher_pragmatism_natal',
  areas: ['Career', 'Self'],
  templateKey: 'career_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'BUTCHER-LIKE PRAGMATISM: Jupiter in 10th + Saturn in 4th = butcher-like pragmatism. BLESSING/CAUTION: Ruthless efficiency, cold business decisions, practical success without sentimentality, harsh but effective career approach. Jupiter\'s wisdom with Saturn\'s harshness creates brutal pragmatism. LEVERAGE: Tough industries, crisis management, surgical business decisions.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    return !!jupiterPlacement && jupiterPlacement.house === 10 && 
           !!saturnPlacement && saturnPlacement.house === 4;
  },
  score: () => ({ value: 0.35, notes: ['Jupiter 10th + Saturn 4th → butcher-like pragmatism'] })
};

const jupiter10thVenusMars4thMultipleMarriages: Rule = {
  key: 'jupiter_10th_venus_mars_4th_multiple_marriages_natal',
  areas: ['Spouse'],
  templateKey: 'relationship_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'MULTIPLE MARRIAGES: Jupiter in 10th + Venus and Mars in 4th = multiple marriages. CAUTION: Repeated marriages, spouse turnover, marital instability, passion-comfort conflicts in home. Venus-Mars in 4th with Jupiter in 10th creates serial marriages. REMEDY: Marriage counseling, relationship maturity, Venus-Mars remedies, commitment work.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const venusPlacement = dasha.natalPlacements?.Venus;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    return !!jupiterPlacement && jupiterPlacement.house === 10 && 
           !!venusPlacement && venusPlacement.house === 4 && 
           !!marsPlacement && marsPlacement.house === 4;
  },
  score: () => ({ value: -0.50, notes: ['Jupiter 10th + Venus & Mars 4th → multiple marriages'] })
};

// ===== MARS IN 10TH HOUSE COMBINATIONS =====
const mars10thSaturn3rdHugePropertyGains: Rule = {
  key: 'mars_10th_saturn_3rd_huge_property_gains_natal',
  areas: ['Property', 'Wealth'],
  templateKey: 'wealth_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'HUGE PROPERTY GAINS LATER IN LIFE: Mars in 10th + Saturn in 3rd = huge property gains later in life. BLESSING: Massive real estate success in old age, late property accumulation, disciplined effort yields land wealth, Mars-Saturn discipline creates property empire. TIMING: Major gains after 50+. LEVERAGE: Long-term real estate, patient property investing.',
  triggers: (dasha) => {
    const marsPlacement = dasha.natalPlacements?.Mars;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    return !!marsPlacement && marsPlacement.house === 10 && 
           !!saturnPlacement && saturnPlacement.house === 3;
  },
  score: () => ({ value: 0.70, notes: ['Mars 10th + Saturn 3rd → huge property gains later in life'] })
};

const mars10thEmpty5thOverallProsperity: Rule = {
  key: 'mars_10th_empty_5th_overall_prosperity_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'OVERALL PROSPERITY: Mars in 10th + empty 5th house = overall prosperity. BLESSING: Career success, unencumbered by children issues, professional focus yields wealth, Mars energy directed purely to career. FORTUNE: Financial stability, business success, achievement-oriented prosperity.',
  triggers: (dasha) => {
    const marsPlacement = dasha.natalPlacements?.Mars;
    if (!marsPlacement || marsPlacement.house !== 10) return false;
    
    // Check if 5th house is empty
    const planetsIn5th = Object.entries(dasha.natalPlacements || {}).filter(
      ([_, placement]) => placement?.house === 5
    );
    return planetsIn5th.length === 0;
  },
  score: () => ({ value: 0.60, notes: ['Mars 10th + empty 5th → overall prosperity'] })
};

const mars10thMalefics2ndReducedBenefits: Rule = {
  key: 'mars_10th_malefics_2nd_reduced_benefits_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'wealth_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'REDUCED BENEFITS: Mars in 10th + malefics (Rahu/Ketu/Saturn/Venus/Moon) in 2nd = reduced benefits. CAUTION: Career gains diminished by family wealth problems, income eroded, wealth house afflictions damage Mars 10th success, earnings don\'t accumulate. REMEDY: Protect wealth, separate career/family finances, malefic remedies, financial planning.',
  triggers: (dasha) => {
    const marsPlacement = dasha.natalPlacements?.Mars;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const venusPlacement = dasha.natalPlacements?.Venus;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    if (!marsPlacement || marsPlacement.house !== 10) return false;
    
    // Check if any malefics in 2nd (Rahu/Ketu/Saturn, and also Venus/Moon counted as malefic here)
    const rahuIn2nd = !!rahuPlacement && rahuPlacement.house === 2;
    const ketuIn2nd = !!ketuPlacement && ketuPlacement.house === 2;
    const saturnIn2nd = !!saturnPlacement && saturnPlacement.house === 2;
    const venusIn2nd = !!venusPlacement && venusPlacement.house === 2;
    const moonIn2nd = !!moonPlacement && moonPlacement.house === 2;
    
    return rahuIn2nd || ketuIn2nd || saturnIn2nd || venusIn2nd || moonIn2nd;
  },
  score: () => ({ value: -0.45, notes: ['Mars 10th + malefics in 2nd → reduced benefits'] })
};

const mars10thFriendlyPlanet3rdReductionProsperity: Rule = {
  key: 'mars_10th_friendly_planet_3rd_reduction_prosperity_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'wealth_challenges',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'REDUCTION OF PROSPERITY: Mars in 10th + friendly planet in 3rd = reduction of prosperity. CAUTION: Unexpected prosperity decline, friends/siblings drain resources, courage house friendly planets paradoxically reduce Mars 10th gains. REMEDY: Set boundaries with friends/siblings, protect assets, focus on self-reliance.',
  triggers: (dasha) => {
    const marsPlacement = dasha.natalPlacements?.Mars;
    const sunPlacement = dasha.natalPlacements?.Sun;
    const moonPlacement = dasha.natalPlacements?.Moon;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    
    if (!marsPlacement || marsPlacement.house !== 10) return false;
    
    // Friendly planets to Mars: Sun, Moon, Jupiter (simplified)
    const sunIn3rd = !!sunPlacement && sunPlacement.house === 3;
    const moonIn3rd = !!moonPlacement && moonPlacement.house === 3;
    const jupiterIn3rd = !!jupiterPlacement && jupiterPlacement.house === 3;
    
    return sunIn3rd || moonIn3rd || jupiterIn3rd;
  },
  score: () => ({ value: -0.40, notes: ['Mars 10th + friendly planet 3rd → reduction of prosperity'] })
};

// ===== SATURN IN 10TH HOUSE COMBINATIONS =====
const saturn10thJupiterMoon4thFinancialStability: Rule = {
  key: 'saturn_10th_jupiter_moon_4th_financial_stability_natal',
  areas: ['Wealth', 'Property', 'Career'],
  templateKey: 'wealth_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'FINANCIAL STABILITY: Saturn in 10th + Jupiter/Moon in 4th = financial stability. BLESSING: Stable career income, secure property, disciplined wealth-building, home comforts protected, Saturn\'s career discipline with benefic home creates financial security. FORTUNE: Long-term prosperity, real estate value, family wealth.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    if (!saturnPlacement || saturnPlacement.house !== 10) return false;
    
    const jupiterIn4th = !!jupiterPlacement && jupiterPlacement.house === 4;
    const moonIn4th = !!moonPlacement && moonPlacement.house === 4;
    
    return jupiterIn4th || moonIn4th;
  },
  score: () => ({ value: 0.65, notes: ['Saturn 10th + Jupiter/Moon 4th → financial stability'] })
};

const saturn10thConstructionTimingLoss: Rule = {
  key: 'saturn_10th_construction_timing_loss_natal',
  areas: ['Property'],
  templateKey: 'property_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'CONSTRUCTION TIMING LOSS RULE: Saturn in 10th → avoid house construction at ages 21, 33, 39, 45, 51, 57, 63, 69, 75, 81 = loss. CAUTION: Specific age timing for construction disasters, building at wrong ages causes financial loss, property troubles. Saturn in career house creates timing-sensitive construction risks. REMEDY: Consult astrologer before construction, avoid critical ages, perform Vastu rituals.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    // Note: This is timing-based (age-specific), we can only check Saturn in 10th
    return !!saturnPlacement && saturnPlacement.house === 10;
  },
  score: () => ({ value: -0.45, notes: ['Saturn 10th → avoid construction at ages 21,33,39,45,51,57,63,69,75,81'] })
};

// ===== KETU IN 10TH HOUSE COMBINATIONS =====
const ketu10thSaturn6thRenownedSportsperson: Rule = {
  key: 'ketu_10th_saturn_6th_renowned_sportsperson_natal',
  areas: ['Career'],
  templateKey: 'career_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'RENOWNED SPORTSPERSON: Ketu in 10th + Saturn in 6th = renowned sportsperson. BLESSING: Athletic fame, sports career success, competitive excellence, discipline-detachment creates sports mastery. Ketu in career with Saturn in competition house yields sports renown. LEVERAGE: Professional sports, athletics, competitive careers, physical excellence.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    return !!ketuPlacement && ketuPlacement.house === 10 && 
           !!saturnPlacement && saturnPlacement.house === 6;
  },
  score: () => ({ value: 0.65, notes: ['Ketu 10th + Saturn 6th → renowned sportsperson'] })
};

const ketu10thSaturn4thSonsDifficulties: Rule = {
  key: 'ketu_10th_saturn_4th_sons_difficulties_natal',
  areas: ['Children'],
  templateKey: 'children_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'SONS FACE DIFFICULTIES: Ketu in 10th + Saturn in 4th = sons face difficulties. CAUTION: Male children suffer, sons\' problems, progeny challenges, home-career axis with Ketu-Saturn damages sons. REMEDY: Extra support for male children, protective rituals, Santana Gopala worship, Saturn-Ketu remedies, therapy for sons.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    return !!ketuPlacement && ketuPlacement.house === 10 && 
           !!saturnPlacement && saturnPlacement.house === 4;
  },
  score: () => ({ value: -0.55, notes: ['Ketu 10th + Saturn 4th → sons face difficulties'] })
};

// ===== SUN IN 11TH HOUSE COMBINATIONS =====
const sun11thMoon5thUnaspectedShortLife: Rule = {
  key: 'sun_11th_moon_5th_unaspected_short_life_natal',
  areas: ['Health'],
  templateKey: 'health_alert',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'SHORT LIFE: Sun in 11th + Moon in 5th + Sun unaspected by benefics = short life. CAUTION: Reduced longevity, early death risk, gains-intelligence axis with unprotected Sun creates life-shortening. Without benefic aspects, Sun-Moon combination damages lifespan. REMEDY: Mahamrityunjaya mantra, Sun strengthening through Jupiter/Venus aspects, health vigilance, longevity rituals.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const moonPlacement = dasha.natalPlacements?.Moon;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const venusPlacement = dasha.natalPlacements?.Venus;
    
    if (!sunPlacement || sunPlacement.house !== 11 || !moonPlacement || moonPlacement.house !== 5) return false;
    
    // Check if Sun is unaspected by benefics (Jupiter/Venus)
    const jupiterAspectsSun = jupiterPlacement?.aspects?.includes(11 as HouseNum);
    const venusAspectsSun = venusPlacement?.aspects?.includes(11 as HouseNum);
    const jupiterIn11th = !!jupiterPlacement && jupiterPlacement.house === 11;
    const venusIn11th = !!venusPlacement && venusPlacement.house === 11;
    
    const beneficAspect = jupiterAspectsSun || venusAspectsSun || jupiterIn11th || venusIn11th;
    
    return !beneficAspect; // Trigger only if unaspected
  },
  score: () => ({ value: -0.80, notes: ['Sun 11th + Moon 5th + Sun unaspected by benefics → short life'] })
};

// ===== MOON IN 11TH HOUSE COMBINATIONS =====
const moon11thKetu4thLifeEndangeredMercuryBusinessHarmful: Rule = {
  key: 'moon_11th_ketu_4th_life_endangered_mercury_business_harmful_natal',
  areas: ['Health', 'Career', 'Property'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'LIFE ENDANGERED & MERCURY BUSINESS HARMFUL: Moon in 11th + Ketu in 4th = native\'s life endangered + Mercury business harmful. CAUTION: Life-threatening situations, mortality risks, Mercury-related businesses fail (trading, communication, intellect-based work), home-gains axis afflicted. REMEDY: Protective rituals, avoid Mercury businesses, Moon-Ketu remedies, health vigilance, life insurance.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    return !!moonPlacement && moonPlacement.house === 11 && 
           !!ketuPlacement && ketuPlacement.house === 4;
  },
  score: () => ({ value: -0.70, notes: ['Moon 11th + Ketu 4th → life endangered, Mercury business harmful'] })
};

const moon11thMercury3rdBrotherConflictsFinancial: Rule = {
  key: 'moon_11th_mercury_3rd_brother_conflicts_financial_natal',
  areas: ['Wealth', 'Self'],
  templateKey: 'relationship_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'BROTHER CONFLICTS & FINANCIAL ISSUES: Moon in 11th + Mercury in 3rd = brother conflicts and financial issues. CAUTION: Sibling rivalry damages finances, brother-related money problems, communication issues with siblings, courage-gains axis creates family-wealth conflicts. REMEDY: Set financial boundaries with siblings, avoid sibling business, Mercury-Moon remedies.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    return !!moonPlacement && moonPlacement.house === 11 && 
           !!mercuryPlacement && mercuryPlacement.house === 3;
  },
  score: () => ({ value: -0.50, notes: ['Moon 11th + Mercury 3rd → brother conflicts, financial issues'] })
};

// ===== MARS IN 11TH HOUSE COMBINATIONS =====
const mars11thJupiterExaltedBenefic: Rule = {
  key: 'mars_11th_jupiter_exalted_benefic_natal',
  areas: ['Wealth', 'Career', 'Health'],
  templateKey: 'wealth_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'BENEFIC MARS RESULTS (STRENGTH BOOSTER): Mars in 11th + Jupiter exalted = benefic Mars results, strength booster. BLESSING: Powerful Mars gains, physical vitality, competitive success, wealth through Mars activities, Jupiter empowers Mars in gains house. LEVERAGE: Sports, military, real estate, competitive businesses, physical strength careers.',
  triggers: (dasha) => {
    const marsPlacement = dasha.natalPlacements?.Mars;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    
    if (!marsPlacement || marsPlacement.house !== 11 || !jupiterPlacement) return false;
    
    // Check if Jupiter is exalted (simplified: Jupiter in kendra/trikona or strong houses)
    const strongHouses = [1, 4, 5, 7, 9, 10];
    const jupiterExalted = strongHouses.includes(jupiterPlacement.house);
    
    return jupiterExalted;
  },
  score: () => ({ value: 0.70, notes: ['Mars 11th + Jupiter exalted → benefic Mars, strength booster'] })
};

// ===== VENUS IN 11TH HOUSE COMBINATIONS =====
const venus11thMercuryRemediesCrossDependency: Rule = {
  key: 'venus_11th_mercury_remedies_cross_dependency_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'wealth_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'MERCURY-RELATED REMEDIES BENEFICIAL: Venus in 11th + Mercury-related remedies = cross-dependency beneficial. BLESSING: Mercury remedies enhance Venus gains, intellectual pursuits support artistic income, communication-pleasure synergy, gains through Venus-Mercury harmony. LEVERAGE: Creative communication, artistic businesses, Mercury gemstones benefit Venus house.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    // Note: This is remedy-based, we can only check Venus in 11th
    return !!venusPlacement && venusPlacement.house === 11;
  },
  score: () => ({ value: 0.45, notes: ['Venus 11th → Mercury remedies cross-dependency beneficial'] })
};

// ===== SATURN IN 11TH HOUSE COMBINATIONS =====
const saturn11thMercury3rdFinancialStruggles: Rule = {
  key: 'saturn_11th_mercury_3rd_financial_struggles_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'wealth_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'FINANCIAL STRUGGLES: Saturn in 11th + Mercury in 3rd = financial struggles. CAUTION: Income difficulties, delayed gains, communication-based earnings blocked, courage-gains axis with Saturn-Mercury creates money problems. REMEDY: Patience with finances, Saturn-Mercury remedies, diversify income, avoid quick-money schemes.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    return !!saturnPlacement && saturnPlacement.house === 11 && 
           !!mercuryPlacement && mercuryPlacement.house === 3;
  },
  score: () => ({ value: -0.55, notes: ['Saturn 11th + Mercury 3rd → financial struggles'] })
};

const saturn11thRahuKetuModifyOutcomes: Rule = {
  key: 'saturn_11th_rahu_ketu_modify_outcomes_natal',
  areas: ['Wealth', 'Spirituality'],
  templateKey: 'spiritual_blessing',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'RAHU/KETU MODIFY OUTCOMES: Saturn in 11th + Rahu/Ketu positions = modify outcomes. MIXED: Nodes with Saturn in gains create unpredictable results, sudden gains/losses, spiritual-material conflicts, outcomes vary by Rahu/Ketu house positions. INSIGHT: Analyze Rahu/Ketu placements for specific effects on Saturn 11th gains.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    // This is a general dependency note - triggers if Saturn in 11th and nodes exist
    return !!saturnPlacement && saturnPlacement.house === 11 && (!!rahuPlacement || !!ketuPlacement);
  },
  score: () => ({ value: 0.20, notes: ['Saturn 11th → Rahu/Ketu positions modify outcomes'] })
};

// ===== RAHU IN 11TH HOUSE COMBINATIONS =====
const rahu11thVarshphal1stFatherGrandfatherDeath: Rule = {
  key: 'rahu_11th_varshphal_1st_father_grandfather_death_natal',
  areas: ['Father'],
  templateKey: 'family_challenges',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'FATHER/GRANDFATHER DEATH POSSIBLE: Rahu in 11th → Rahu in 1st in Varshphal = father/grandfather death possible. CAUTION: Paternal mortality during Rahu transit to 1st in annual chart, elder male family members at risk, timing-specific danger. TIMING: Critical when Rahu transits to 1st in Varshphal. REMEDY: Mahamrityunjaya for father/grandfather, Rahu remedies, protective rituals during transit.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    // Note: This is Varshphal (annual chart) transit-based, we can only check natal Rahu in 11th
    return !!rahuPlacement && rahuPlacement.house === 11;
  },
  score: () => ({ value: -0.75, notes: ['Rahu 11th → Rahu in 1st Varshphal → father/grandfather death possible'] })
};

const rahu11thMercury3rdIsolationLoneliness: Rule = {
  key: 'rahu_11th_mercury_3rd_isolation_loneliness_natal',
  areas: ['Self', 'Wealth'],
  templateKey: 'mental_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'ISOLATION & LONELINESS: Rahu in 11th + Mercury in 3rd = isolation, loneliness. CAUTION: Social isolation despite gains, wealth without companionship, communication breakdown, courage-gains axis with Rahu-Mercury creates loneliness. REMEDY: Build genuine relationships, spiritual practices, Rahu-Mercury remedies, community involvement.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    return !!rahuPlacement && rahuPlacement.house === 11 && 
           !!mercuryPlacement && mercuryPlacement.house === 3;
  },
  score: () => ({ value: -0.50, notes: ['Rahu 11th + Mercury 3rd → isolation, loneliness'] })
};

// ===== KETU IN 11TH HOUSE COMBINATIONS =====
const ketu11thSaturn2ndEnormousWealth: Rule = {
  key: 'ketu_11th_saturn_2nd_enormous_wealth_natal',
  areas: ['Wealth'],
  templateKey: 'wealth_blessing',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'ENORMOUS WEALTH: Ketu in 11th + Saturn in 2nd = enormous wealth. BLESSING: Massive financial accumulation, family wealth through discipline-detachment, Saturn-Ketu combination creates wealth empire, gains-family axis yields riches. FORTUNE: Long-term wealth building, inheritance, conservative investments pay massively.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    return !!ketuPlacement && ketuPlacement.house === 11 && 
           !!saturnPlacement && saturnPlacement.house === 2;
  },
  score: () => ({ value: 0.80, notes: ['Ketu 11th + Saturn 2nd → enormous wealth'] })
};

const ketu11thMercury2ndRajYoga: Rule = {
  key: 'ketu_11th_mercury_2nd_raj_yoga_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'wealth_blessing',
  priority: 9,
  ruleType: 'natal',
  customPrediction: 'RAJ YOGA: Ketu in 11th + Mercury in 2nd = Raj Yoga. BLESSING: Royal combination, exceptional prosperity, intelligence-based wealth, communication creates fortune, gains-family axis with Mercury-Ketu yields kingship. FORTUNE: High status, abundant wealth, influential position, success through intellect.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    return !!ketuPlacement && ketuPlacement.house === 11 && 
           !!mercuryPlacement && mercuryPlacement.house === 2;
  },
  score: () => ({ value: 0.85, notes: ['Ketu 11th + Mercury 2nd → Raj Yoga'] })
};

const ketu11thJupiter3rdEnhancedStabilityWisdom: Rule = {
  key: 'ketu_11th_jupiter_3rd_enhanced_stability_wisdom_natal',
  areas: ['Wealth', 'Spirituality', 'Self'],
  templateKey: 'spiritual_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'ENHANCED STABILITY & WISDOM: Ketu in 11th + Jupiter in 3rd = enhanced stability and wisdom. BLESSING: Financial stability through wisdom, spiritual detachment with courage, Jupiter-Ketu balance creates grounded prosperity, knowledge-based gains. FORTUNE: Stable income, philosophical approach to wealth, teaching/wisdom careers.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    
    return !!ketuPlacement && ketuPlacement.house === 11 && 
           !!jupiterPlacement && jupiterPlacement.house === 3;
  },
  score: () => ({ value: 0.65, notes: ['Ketu 11th + Jupiter 3rd → enhanced stability, wisdom'] })
};

const ketu11thWeakSaturnMotherGrandmotherProblems: Rule = {
  key: 'ketu_11th_weak_saturn_mother_grandmother_problems_natal',
  areas: ['Mother', 'Health'],
  templateKey: 'family_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'MOTHER/GRANDMOTHER PROBLEMS: Ketu in 11th + weak Saturn = mother/grandmother problems. CAUTION: Maternal health issues, grandmother suffering, female elders afflicted, weak Saturn with Ketu in gains damages maternal lineage. REMEDY: Support mother/grandmother health, Saturn strengthening, Durga worship, maternal care.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    if (!ketuPlacement || ketuPlacement.house !== 11 || !saturnPlacement) return false;
    
    // Check if Saturn is weak (in dusthana 6/8/12 or debilitated)
    const weakHouses = [6, 8, 12];
    const saturnWeak = weakHouses.includes(saturnPlacement.house);
    
    return saturnWeak;
  },
  score: () => ({ value: -0.55, notes: ['Ketu 11th + weak Saturn → mother/grandmother problems'] })
};

const ketu11thMoon3rdMotherPain: Rule = {
  key: 'ketu_11th_moon_3rd_mother_pain_natal',
  areas: ['Mother', 'Health'],
  templateKey: 'family_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MOTHER PAIN: Ketu in 11th + Moon in 3rd = mother pain. CAUTION: Mother suffers physically/emotionally, maternal distress, courage house Moon with Ketu in gains creates maternal pain. REMEDY: Support mother, Moon remedies, Durga mantras, maternal care, pain management for mother.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    return !!ketuPlacement && ketuPlacement.house === 11 && 
           !!moonPlacement && moonPlacement.house === 3;
  },
  score: () => ({ value: -0.50, notes: ['Ketu 11th + Moon 3rd → mother pain'] })
};

const ketu11thMars3rdMotherSurgeries: Rule = {
  key: 'ketu_11th_mars_3rd_mother_surgeries_natal',
  areas: ['Mother', 'Health'],
  templateKey: 'health_alert',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'MOTHER SURGERIES: Ketu in 11th + Mars in 3rd = mother surgeries. CAUTION: Mother requires surgical interventions, maternal health crises, operations needed, Mars in courage with Ketu in gains creates surgical necessity for mother. REMEDY: Medical vigilance for mother, Mars-Ketu remedies, Mahamrityunjaya for mother, health insurance.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    return !!ketuPlacement && ketuPlacement.house === 11 && 
           !!marsPlacement && marsPlacement.house === 3;
  },
  score: () => ({ value: -0.60, notes: ['Ketu 11th + Mars 3rd → mother surgeries'] })
};

// ===== 12TH HOUSE NATAL PREDICTION RULES =====

const sun12thKetu2ndWealthAfter24Family: Rule = {
  key: 'sun_12th_ketu_2nd_wealth_after_24_family_natal',
  areas: ['Wealth', 'Self'],
  templateKey: 'financial_growth',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'WEALTH AFTER 24 + FAMILY: Sun in 12th + Ketu in 2nd = wealth after 24, good family life. ACTIONABLE: Wealth materializes post-24, family harmony despite losses house Sun, Ketu in wealth house delays but delivers, family relationships strong. REMEDY: Patience until 24, Sun remedies, Ketu remedies for wealth.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    return !!sunPlacement && sunPlacement.house === 12 && 
           !!ketuPlacement && ketuPlacement.house === 2;
  },
  score: () => ({ value: 0.65, notes: ['Sun 12th + Ketu 2nd → wealth after 24, family life'] })
};

const sun12thMercuryVenusTogetherBusinessSuccess: Rule = {
  key: 'sun_12th_mercury_venus_together_business_success_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'financial_growth',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'BUSINESS SUCCESS: Sun in 12th + Mercury-Venus conjunction = steady income, business success. ACTIONABLE: Mercury-Venus budha-aditya creates business acumen, steady income streams, creative commerce, losses house Sun neutralized by merchant planets. REMEDY: Mercury-Venus remedies, business ventures, creative entrepreneurship.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const venusPlacement = dasha.natalPlacements?.Venus;
    
    if (!sunPlacement || sunPlacement.house !== 12) return false;
    if (!mercuryPlacement || !venusPlacement) return false;
    
    // Check if Mercury and Venus are in same house (conjunction)
    return mercuryPlacement.house === venusPlacement.house;
  },
  score: () => ({ value: 0.70, notes: ['Sun 12th + Mercury-Venus conjunction → business success'] })
};

const sun12thEvilPlanet1stSleeplessness: Rule = {
  key: 'sun_12th_evil_planet_1st_sleeplessness_natal',
  areas: ['Health', 'Self'],
  templateKey: 'health_alert',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'SLEEPLESSNESS: Sun in 12th + malefic in 1st = sleeplessness. CAUTION: Sleep disorders, insomnia, restlessness, Sun in sleep/moksha house with malefic in ascendant disturbs rest, anxiety. REMEDY: Sleep hygiene, Sun remedies, malefic pacification, meditation before bed, relaxation practices.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    
    if (!sunPlacement || sunPlacement.house !== 12) return false;
    
    // Check for malefics (Mars, Saturn, Rahu, Ketu) in 1st house
    const malefics: ('Mars' | 'Saturn' | 'Rahu' | 'Ketu')[] = ['Mars', 'Saturn', 'Rahu', 'Ketu'];
    for (const malefic of malefics) {
      const placement = dasha.natalPlacements?.[malefic];
      if (placement && placement.house === 1) return true;
    }
    
    return false;
  },
  score: () => ({ value: -0.55, notes: ['Sun 12th + malefic 1st → sleeplessness'] })
};

const moon12thKetu4thWeakKetuMotherChildHarm: Rule = {
  key: 'moon_12th_ketu_4th_weak_ketu_mother_child_harm_natal',
  areas: ['Mother', 'Children', 'Health'],
  templateKey: 'family_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'MOTHER & MALE CHILD HARM: Moon in 12th + Ketu in 4th = weak Ketu harms mother, male child. CAUTION: Mother suffers, male children afflicted, Ketu weakened in mother house, Moon in losses creates maternal-child damage. REMEDY: Strong maternal care, Ketu strengthening, Gauri-Shankar Rudraksha, protect male children, Moon remedies.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    return !!moonPlacement && moonPlacement.house === 12 && 
           !!ketuPlacement && ketuPlacement.house === 4;
  },
  score: () => ({ value: -0.70, notes: ['Moon 12th + Ketu 4th → weak Ketu, mother/male child harm'] })
};

const moon12thMarsBusinessGoodMercuryKetuBad: Rule = {
  key: 'moon_12th_mars_business_good_mercury_ketu_bad_natal',
  areas: ['Career', 'Wealth'],
  templateKey: 'career_guidance',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'BUSINESS DOMAINS: Moon in 12th = Mars businesses good, Mercury/Ketu businesses bad. ACTIONABLE: Pursue Mars-related ventures (real estate, sports, defense, surgery), avoid Mercury (trading, communication) and Ketu (occult, isolation) businesses. REMEDY: Mars business focus, avoid Mercury/Ketu domains.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    return !!moonPlacement && moonPlacement.house === 12;
  },
  score: () => ({ value: 0.40, notes: ['Moon 12th → Mars business good, Mercury/Ketu bad'] })
};

const mercury12thEarlyMarriageHarmFatherSpouse: Rule = {
  key: 'mercury_12th_early_marriage_harm_father_spouse_natal',
  areas: ['Spouse', 'Father', 'Self'],
  templateKey: 'family_challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'EARLY MARRIAGE HARM: Mercury in 12th + early marriage (before 25) = harm to father & spouse. CAUTION: Marriage before 25 damages father and spouse, Mercury in losses house creates relationship destruction, premature commitment harmful. REMEDY: Delay marriage until after 25, Mercury remedies, father protection, spouse care.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    return !!mercuryPlacement && mercuryPlacement.house === 12;
  },
  score: () => ({ value: -0.65, notes: ['Mercury 12th + early marriage → father/spouse harm'] })
};

const mercury12thAffects6thHouseDifficulties: Rule = {
  key: 'mercury_12th_affects_6th_house_difficulties_natal',
  areas: ['Health', 'Litigation', 'Career'],
  templateKey: 'challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'AFFECTS 6TH HOUSE: Mercury in 12th affects planets in 6th house = difficulties. CAUTION: Mercury in losses afflicts 6th house planets, creates service challenges, litigation issues, health problems, enemy conflicts amplified. REMEDY: Mercury remedies, 6th house planet strengthening, dispute resolution, health care.',
  triggers: (dasha) => {
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    if (!mercuryPlacement || mercuryPlacement.house !== 12) return false;
    
    // Check if any planet is in 6th house
    const planetsIn6th = Object.entries(dasha.natalPlacements || {}).filter(
      ([_, placement]) => placement?.house === 6
    );
    
    return planetsIn6th.length > 0;
  },
  score: () => ({ value: -0.50, notes: ['Mercury 12th → affects 6th house planets negatively'] })
};

const venus12thMercury2nd6thCreativeSuccess: Rule = {
  key: 'venus_12th_mercury_2nd_6th_creative_success_natal',
  areas: ['Career', 'Wealth'],
  templateKey: 'career_growth',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'CREATIVE SUCCESS: Venus in 12th + Mercury in 2nd or 6th = creative success, recognition. ACTIONABLE: Creative arts flourish, artistic recognition, Mercury in wealth/service supports Venus creativity, fame through artistic pursuits. REMEDY: Pursue creative careers, Venus-Mercury synergy, artistic development.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    if (!venusPlacement || venusPlacement.house !== 12 || !mercuryPlacement) return false;
    
    return mercuryPlacement.house === 2 || mercuryPlacement.house === 6;
  },
  score: () => ({ value: 0.70, notes: ['Venus 12th + Mercury 2nd/6th → creative success'] })
};

const ketu12thRahu6thMercuryExcellent: Rule = {
  key: 'ketu_12th_rahu_6th_mercury_excellent_natal',
  areas: ['Career', 'Health', 'Spirituality'],
  templateKey: 'major_yoga',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'EXCELLENT RESULTS: Ketu in 12th + Rahu in 6th + Mercury = excellent results. ACTIONABLE: Rahu-Ketu axis (6-12) with Mercury creates success in service, foreign lands, healing, spiritual-material balance, enemy victory. REMEDY: Leverage this yoga, foreign opportunities, service excellence, spiritual practices.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    
    if (!ketuPlacement || ketuPlacement.house !== 12) return false;
    if (!rahuPlacement || rahuPlacement.house !== 6) return false;
    
    // Mercury must be present and with Rahu in 6th
    return !!mercuryPlacement && mercuryPlacement.house === 6;
  },
  score: () => ({ value: 0.80, notes: ['Ketu 12th + Rahu 6th with Mercury → excellent results'] })
};

const ketu12th2ndHasMoonVenusMarsNegative: Rule = {
  key: 'ketu_12th_2nd_has_moon_venus_mars_negative_natal',
  areas: ['Wealth', 'Self'],
  templateKey: 'financial_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'NEGATIVE EFFECTS: Ketu in 12th + 2nd house has Moon/Venus/Mars = negative effects. CAUTION: Wealth house afflicted by Ketu in losses, Moon/Venus/Mars in 2nd disturbed, financial instability, speech issues, family conflicts. REMEDY: Ketu remedies, strengthen 2nd house planets, wealth protection, family harmony practices.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    if (!ketuPlacement || ketuPlacement.house !== 12) return false;
    
    // Check if Moon, Venus, or Mars is in 2nd house
    const moonPlacement = dasha.natalPlacements?.Moon;
    const venusPlacement = dasha.natalPlacements?.Venus;
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    return (moonPlacement?.house === 2) || 
           (venusPlacement?.house === 2) || 
           (marsPlacement?.house === 2);
  },
  score: () => ({ value: -0.60, notes: ['Ketu 12th + Moon/Venus/Mars in 2nd → negative effects'] })
};

// ===== ADDITIONAL GENERAL NATAL PREDICTION RULES =====

const saturn7thLordIn8thMarriagePain: Rule = {
  key: 'saturn_7th_lord_in_8th_marriage_pain_natal',
  areas: ['Spouse', 'Health'],
  templateKey: 'spouse_challenges',
  priority: 9,
  ruleType: 'natal',
  customPrediction: 'MARRIAGE PAIN: 7th lord Saturn sits in 8th house = lot of pain after marriage. CAUTION: Marital suffering, attachment to partner causes problems, transformational pain through relationships, 8th house placement of marriage lord creates intense challenges. REMEDY: Saturn remedies, marriage counseling, detachment practices, Shani pooja, patience in relationships.',
  triggers: (dasha) => {
    // Check if Saturn is 7th lord (ruling sign in 7th house)
    // 7th house has Capricorn (9) or Aquarius (10) as ascendant's 7th sign
    // This happens when ascendant is Cancer (3) or Leo (4)
    const ascendantSign = dasha.natalPlacements?.Sun?.sign; // Approximation - we need actual ascendant
    if (ascendantSign === undefined) {
      // Fallback: Check if Saturn is in 8th house and rules a house (any lordship)
      const saturnPlacement = dasha.natalPlacements?.Saturn;
      return !!saturnPlacement && saturnPlacement.house === 8 && (saturnPlacement.rules?.length || 0) > 0;
    }
    
    // Calculate 7th sign from ascendant
    const seventhSign = (ascendantSign + 6) % 12;
    const seventhLord = RASHI_LORDS[seventhSign];
    
    // Check if 7th lord is Saturn and Saturn is in 8th house
    return seventhLord === 'Saturn' && dasha.natalPlacements?.Saturn?.house === 8;
  },
  score: () => ({ value: -0.75, notes: ['7th lord Saturn in 8th → marriage pain'] })
};

const blindHoroscopeEnimicalPlanets10th: Rule = {
  key: 'blind_horoscope_enimical_planets_10th_natal',
  areas: ['Career', 'Health'],
  templateKey: 'challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'BLIND HOROSCOPE: Two inimical planets together in 10th house = blind horoscope. CAUTION: Career obstacles, vision/sight concerns, enemies in professional sphere, conflicting energies in career house. REMEDY: Feed 10 blind people with your own hands, donate to blind schools, eye care camps, charitable acts for visually impaired.',
  triggers: (dasha) => {
    // Check for conjunctions in 10th house of natural enemies
    const planetsIn10th: PlanetName[] = [];
    for (const [planet, placement] of Object.entries(dasha.natalPlacements || {})) {
      if (placement?.house === 10) {
        planetsIn10th.push(planet as PlanetName);
      }
    }
    
    if (planetsIn10th.length < 2) return false;
    
    // Check for natural enemies (based on traditional enmities)
    const NATURAL_ENEMIES: Record<PlanetName, PlanetName[]> = {
      Sun: ['Venus', 'Saturn'],
      Moon: ['Saturn', 'Mercury'],
      Mars: ['Mercury'],
      Mercury: ['Moon', 'Mars'],
      Jupiter: ['Mercury', 'Venus'],
      Venus: ['Sun', 'Moon', 'Jupiter'],
      Saturn: ['Sun', 'Moon', 'Mars'],
      Rahu: ['Sun', 'Moon', 'Mars'],
      Ketu: ['Sun', 'Moon', 'Mars']
    };
    
    // Check if any two planets in 10th are natural enemies
    for (let i = 0; i < planetsIn10th.length; i++) {
      for (let j = i + 1; j < planetsIn10th.length; j++) {
        const planet1 = planetsIn10th[i];
        const planet2 = planetsIn10th[j];
        
        const enemies1 = NATURAL_ENEMIES[planet1] || [];
        const enemies2 = NATURAL_ENEMIES[planet2] || [];
        
        if (enemies1.includes(planet2) || enemies2.includes(planet1)) {
          return true;
        }
      }
    }
    
    return false;
  },
  score: () => ({ value: -0.65, notes: ['Inimical planets in 10th → blind horoscope'] })
};

const marsKetuFemaleMarriageInnocentSpouse: Rule = {
  key: 'mars_ketu_female_marriage_innocent_spouse_natal',
  areas: ['Spouse'],
  templateKey: 'marriage_guidance',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'SPOUSE SELECTION (FEMALE): Mars + Ketu together in female chart = marry childlike/innocent person. ACTIONABLE: For sustaining marriage, choose spouse who is innocent, little crazy, childlike nature helps balance Mars-Ketu energy, innocence counters aggression. REMEDY: Select innocent partner, cultivate gentleness in relationship, Mars-Ketu remedies.',
  triggers: (dasha) => {
    // Only for female natives
    if (dasha.gender !== 'female') return false;
    
    const marsPlacement = dasha.natalPlacements?.Mars;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    // Check if Mars and Ketu are in same house (conjunction)
    return !!marsPlacement && !!ketuPlacement && marsPlacement.house === ketuPlacement.house;
  },
  score: () => ({ value: 0.50, notes: ['Mars-Ketu conjunction in female chart → innocent spouse sustains marriage'] })
};

const venusKetuMaleMarriageInnocentSpouse: Rule = {
  key: 'venus_ketu_male_marriage_innocent_spouse_natal',
  areas: ['Spouse'],
  templateKey: 'marriage_guidance',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'SPOUSE SELECTION (MALE): Venus + Ketu together in male chart = marry childlike/innocent person. ACTIONABLE: For sustaining marriage, choose spouse who is innocent, little crazy, childlike nature helps balance Venus-Ketu energy, innocence supports relationship harmony. REMEDY: Select innocent partner, nurture playfulness in relationship, Venus-Ketu remedies.',
  triggers: (dasha) => {
    // Only for male natives
    if (dasha.gender !== 'male') return false;
    
    const venusPlacement = dasha.natalPlacements?.Venus;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    // Check if Venus and Ketu are in same house (conjunction)
    return !!venusPlacement && !!ketuPlacement && venusPlacement.house === ketuPlacement.house;
  },
  score: () => ({ value: 0.50, notes: ['Venus-Ketu conjunction in male chart → innocent spouse sustains marriage'] })
};

const venus12thGoodResults: Rule = {
  key: 'venus_12th_good_results_natal',
  areas: ['Spirituality', 'Wealth'],
  templateKey: 'positive_influence',
  priority: 6,
  ruleType: 'natal',
  customPrediction: 'VENUS IN 12TH: Venus in 12th house gives good results. ACTIONABLE: Spiritual pleasures, foreign connections beneficial, luxury through foreign lands, expenses on good causes, charitable nature rewarded, creative fulfillment through isolation/spirituality. REMEDY: Venus worship, donate on Fridays, creative charitable works.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    return !!venusPlacement && venusPlacement.house === 12;
  },
  score: () => ({ value: 0.60, notes: ['Venus in 12th → good results'] })
};

const sun11thMoneyEnemiesVictory: Rule = {
  key: 'sun_11th_money_enemies_victory_natal',
  areas: ['Wealth', 'Career'],
  templateKey: 'financial_growth',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'SUN IN 11TH: Sun in 11th gives good money/wellbeing BUT increases enemies (you win over them). ACTIONABLE: Financial gains, income growth, wellbeing increases, BUT also attracts enemies/competitors, ultimate victory over opponents, 11th house Sun creates both wealth and opposition. REMEDY: Sun remedies, avoid ego in gains, charity to reduce enemy malice.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    return !!sunPlacement && sunPlacement.house === 11;
  },
  score: () => ({ value: 0.55, notes: ['Sun in 11th → money/wellbeing + enemies (victory)'] })
};

const ketu8thSuddenWealthStomachProblems: Rule = {
  key: 'ketu_8th_sudden_wealth_stomach_problems_natal',
  areas: ['Wealth', 'Health'],
  templateKey: 'mixed_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'KETU IN 8TH: Ketu in 8th = sudden/unexpected wealth gains. BUT if Aries/Taurus/Scorpio/Gemini/Virgo rashi in 8th = stomach problems. ACTIONABLE: Sudden financial windfalls, inheritance, occult gains. CAUTION: Digestive issues if 8th has movable/earthy/dual signs. REMEDY: Ketu remedies, digestive care, avoid overeating.',
  triggers: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    if (!ketuPlacement || ketuPlacement.house !== 8) return false;
    
    // Always true if Ketu is in 8th (wealth aspect always applies)
    return true;
  },
  score: (dasha) => {
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    const sign8th = ketuPlacement?.sign;
    
    // Check if 8th house has problematic signs (Aries=0, Taurus=1, Scorpio=7, Gemini=2, Virgo=5)
    const problematicSigns = [0, 1, 2, 5, 7]; // Aries, Taurus, Gemini, Virgo, Scorpio
    const hasStomachIssue = sign8th !== undefined && problematicSigns.includes(sign8th);
    
    if (hasStomachIssue) {
      return { value: 0.35, notes: ['Ketu 8th → sudden wealth BUT stomach problems (Aries/Taurus/Gemini/Virgo/Scorpio)'] };
    }
    
    return { value: 0.65, notes: ['Ketu in 8th → sudden wealth gains'] };
  }
};

const saturnTaurusAngerMarriageExpense: Rule = {
  key: 'saturn_taurus_anger_marriage_expense_natal',
  areas: ['Spouse', 'Wealth', 'Health'],
  templateKey: 'spouse_challenges',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'SATURN IN TAURUS: Saturn in Taurus = angry person, post-marriage earnings less than spend. CAUTION: Temperament issues, anger problems, financial imbalance after marriage, expenses exceed income post-wedding, material frustration, Taurus Saturn creates stubborn anger. REMEDY: Anger management, budgeting post-marriage, Saturn remedies, financial planning.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    // Taurus is sign index 1
    return !!saturnPlacement && saturnPlacement.sign === 1;
  },
  score: () => ({ value: -0.60, notes: ['Saturn in Taurus → anger + post-marriage spend > earn'] })
};

const stableRashiLagna6thLordAspectWealth: Rule = {
  key: 'stable_rashi_lagna_6th_lord_aspect_wealth_natal',
  areas: ['Wealth'],
  templateKey: 'financial_growth',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'STABLE LAGNA + 6TH LORD ASPECT: Stable rashi in lagna (Taurus/Leo/Scorpio/Aquarius) + 6th lord aspects lagna = lot of wealth. ACTIONABLE: Fixed signs in ascendant create stability, 6th lord aspect brings service-based wealth, enemy victory through persistence, litigation success brings money. REMEDY: Strengthen ascendant lord, 6th lord remedies for enhanced gains.',
  triggers: (dasha) => {
    // Need to determine lagna sign - use approximation from planet positions
    // Stable rashis: Taurus (1), Leo (4), Scorpio (7), Aquarius (10)
    const stableSigns = [1, 4, 7, 10];
    
    // We need ascendant sign - approximate using Sun's sign as proxy (not perfect but workable)
    const lagnaSign = dasha.natalPlacements?.Sun?.sign;
    if (lagnaSign === undefined || !stableSigns.includes(lagnaSign)) return false;
    
    // Calculate 6th sign from lagna
    const sixthSign = (lagnaSign + 5) % 12;
    const sixthLord = RASHI_LORDS[sixthSign];
    
    // Check if 6th lord aspects house 1 (lagna)
    const sixthLordPlacement = dasha.natalPlacements?.[sixthLord];
    const aspects1st = sixthLordPlacement?.aspects?.includes(1) || false;
    
    return aspects1st;
  },
  score: () => ({ value: 0.75, notes: ['Stable lagna + 6th lord aspects → wealth'] })
};

const moonAspectedByExaltedPlanetWealth: Rule = {
  key: 'moon_aspected_by_exalted_planet_wealth_natal',
  areas: ['Wealth'],
  templateKey: 'financial_growth',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'MOON ASPECTED BY EXALTED: Moon aspected by any exalted planet = wealthy person. ACTIONABLE: Exalted planet blessing Moon creates abundant wealth, mental peace with prosperity, emotional fulfillment through riches, strong financial foundation. REMEDY: Moon and exalted planet worship for sustained wealth.',
  triggers: (dasha) => {
    const moonPlacement = dasha.natalPlacements?.Moon;
    if (!moonPlacement) return false;
    
    const moonHouse = moonPlacement.house;
    
    // Check if any exalted planet aspects Moon's house
    for (const [planet, placement] of Object.entries(dasha.natalPlacements || {})) {
      if (placement?.exalted === true) {
        const aspectsHouses = placement.aspects || [];
        if (aspectsHouses.includes(moonHouse)) {
          return true;
        }
      }
    }
    
    return false;
  },
  score: () => ({ value: 0.80, notes: ['Moon aspected by exalted planet → wealthy'] })
};

const sunWithLagnaLordBeneficial: Rule = {
  key: 'sun_with_lagna_lord_beneficial_natal',
  areas: ['Self', 'Career', 'Wealth'],
  templateKey: 'positive_influence',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'SUN WITH LAGNA LORD: Sun sits anywhere with Lagna lord = beneficial and gainful. ACTIONABLE: Self-confidence amplified, career success, authority gains, leadership recognition, lagna lord empowered by Sun creates strong personality, beneficial outcomes in all endeavors. REMEDY: Sun worship for maximizing gains, strengthen lagna lord.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    if (!sunPlacement) return false;
    
    // Determine lagna lord - approximate using Sun's sign
    const lagnaSign = dasha.natalPlacements?.Sun?.sign;
    if (lagnaSign === undefined) return false;
    
    const lagnaLord = RASHI_LORDS[lagnaSign];
    const lagnaLordPlacement = dasha.natalPlacements?.[lagnaLord];
    
    if (!lagnaLordPlacement) return false;
    
    // Check if Sun and lagna lord are in same house (conjunction)
    return sunPlacement.house === lagnaLordPlacement.house;
  },
  score: () => ({ value: 0.70, notes: ['Sun with lagna lord → beneficial gains'] })
};

const lagnaLordExaltedPopularWealthy: Rule = {
  key: 'lagna_lord_exalted_popular_wealthy_natal',
  areas: ['Self', 'Wealth', 'Career'],
  templateKey: 'major_yoga',
  priority: 9,
  ruleType: 'natal',
  customPrediction: 'LAGNA LORD EXALTED: Lagna lord exalted = popular, wise, successful, wealthy. ACTIONABLE: Exceptional success, public recognition, wisdom and intelligence, wealth accumulation, strong personality, exalted lagna lord creates powerful chart, fame and fortune. REMEDY: Maximize this yoga through lagna lord worship, charitable acts.',
  triggers: (dasha) => {
    // Determine lagna lord - approximate using Sun's sign
    const lagnaSign = dasha.natalPlacements?.Sun?.sign;
    if (lagnaSign === undefined) return false;
    
    const lagnaLord = RASHI_LORDS[lagnaSign];
    const lagnaLordPlacement = dasha.natalPlacements?.[lagnaLord];
    
    return !!lagnaLordPlacement && lagnaLordPlacement.exalted === true;
  },
  score: () => ({ value: 0.90, notes: ['Lagna lord exalted → popular, wise, successful, wealthy'] })
};

const lord6thIn8thGainfulNoTrust: Rule = {
  key: 'lord_6th_in_8th_gainful_no_trust_natal',
  areas: ['Wealth', 'Health'],
  templateKey: 'mixed_blessing',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'LORD 6TH IN 8TH: 6th house lord sits in 8th = gainful BUT person doesn\'t trust anyone. ACTIONABLE: Financial gains through service/enemies, inheritance benefits, occult earnings. CAUTION: Deep trust issues, paranoia, emotional guardedness, gains come with psychological cost. REMEDY: Trust-building therapy, 6th lord remedies, psychological healing.',
  triggers: (dasha) => {
    // Determine lagna to calculate 6th house
    const lagnaSign = dasha.natalPlacements?.Sun?.sign;
    if (lagnaSign === undefined) return false;
    
    // Calculate 6th sign from lagna
    const sixthSign = (lagnaSign + 5) % 12;
    const sixthLord = RASHI_LORDS[sixthSign];
    const sixthLordPlacement = dasha.natalPlacements?.[sixthLord];
    
    return !!sixthLordPlacement && sixthLordPlacement.house === 8;
  },
  score: () => ({ value: 0.40, notes: ['6th lord in 8th → gainful but no trust'] })
};

const jupiterVenus8thLegalProblems: Rule = {
  key: 'jupiter_venus_8th_legal_problems_natal',
  areas: ['Litigation'],
  templateKey: 'challenges',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'JUPITER-VENUS IN 8TH: Jupiter + Venus jointly in 8th = legal issue problems. CAUTION: Litigation complications, legal entanglements, inheritance disputes, joint assets conflicts, benefics in 8th create legal chaos, court case difficulties. REMEDY: Legal counsel, Jupiter-Venus remedies, dispute resolution, avoid litigation.',
  triggers: (dasha) => {
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    const venusPlacement = dasha.natalPlacements?.Venus;
    
    // Check if both are in 8th house (conjunction in 8th)
    return !!jupiterPlacement && !!venusPlacement && 
           jupiterPlacement.house === 8 && venusPlacement.house === 8;
  },
  score: () => ({ value: -0.70, notes: ['Jupiter-Venus in 8th → legal problems'] })
};

const twothreePlanets10thPopular: Rule = {
  key: 'two_three_planets_10th_popular_natal',
  areas: ['Career'],
  templateKey: 'career_growth',
  priority: 7,
  ruleType: 'natal',
  customPrediction: 'MULTIPLE PLANETS IN 10TH: 2 or 3 planets in 10th house = person is popular. ACTIONABLE: Public recognition, career visibility, professional fame, multiple talents converge, strong professional presence, leadership roles, popularity in field. REMEDY: Leverage popularity wisely, strengthen 10th house planets.',
  triggers: (dasha) => {
    // Count planets in 10th house
    let count = 0;
    for (const placement of Object.values(dasha.natalPlacements || {})) {
      if (placement?.house === 10) count++;
    }
    
    return count >= 2 && count <= 3;
  },
  score: () => ({ value: 0.70, notes: ['2-3 planets in 10th → popular'] })
};

const sunMoon9thVeryWealthy: Rule = {
  key: 'sun_moon_9th_very_wealthy_natal',
  areas: ['Wealth', 'Father', 'Spirituality'],
  templateKey: 'financial_growth',
  priority: 9,
  ruleType: 'natal',
  customPrediction: 'SUN-MOON IN 9TH: Sun + Moon in 9th house = very wealthy person. ACTIONABLE: Exceptional wealth, father\'s blessings bring riches, spiritual-material balance, fortune through dharma, luminaries in dharma house create abundant prosperity, royal wealth. REMEDY: Sun-Moon worship for sustained wealth, charitable acts, respect elders.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const moonPlacement = dasha.natalPlacements?.Moon;
    
    // Check if both Sun and Moon are in 9th house
    return !!sunPlacement && !!moonPlacement && 
           sunPlacement.house === 9 && moonPlacement.house === 9;
  },
  score: () => ({ value: 0.85, notes: ['Sun-Moon in 9th → very wealthy'] })
};

// Saturn aspects 4th house - vehicles, land, property
const saturnAspect4thHouseVehicleLand: Rule = {
  key: 'saturn_aspect_4th_house_vehicle_land_natal',
  areas: ['Property', 'Wealth', 'Mother'],
  templateKey: 'property_gains',
  priority: 8,
  ruleType: 'natal',
  customPrediction: 'SATURN ASPECT ON 4TH: Saturn\'s aspect on 4th house gives house, vehicles, and land in PLURAL (multiple properties). ACTIONABLE: Long-term real estate investments highly favored, accumulate property over time, disciplined approach to asset building, delayed but substantial gains in mother-related matters. Vehicle purchases after thorough research. REMEDY: Saturn remedies on Saturdays, blue sapphire (after consultation), charity to elderly.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    if (!saturnPlacement) return false;
    
    // Saturn aspects 3rd, 7th, and 10th houses from its position
    const saturnHouse = saturnPlacement.house;
    const aspect3rd = ((saturnHouse + 2) % 12) || 12;
    const aspect7th = ((saturnHouse + 6) % 12) || 12;
    const aspect10th = ((saturnHouse + 9) % 12) || 12;
    
    return aspect3rd === 4 || aspect7th === 4 || aspect10th === 4;
  },
  score: () => ({ value: 0.75, notes: ['Saturn aspects 4th → multiple properties, vehicles, land'] })
};

// Sun in 2nd aspected by Saturn - money problems
const sunIn2ndSaturnAspectMoneyProblems: Rule = {
  key: 'sun_2nd_saturn_aspect_money_problems_natal',
  areas: ['Wealth'],
  templateKey: 'financial_challenges',
  priority: 9,
  ruleType: 'natal',
  customPrediction: 'SUN IN 2ND SATURN ASPECT: Sun in 2nd house aspected by Saturn creates money-related problems. ACTIONABLE: Financial discipline essential, avoid speculation, save for emergencies, expect delays in wealth accumulation, father may have financial struggles affecting you. Conservative financial planning mandatory. REMEDY: Surya-Shani peace mantras, Saturday fasting, charity to poor on Saturdays, avoid loans.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    
    if (!sunPlacement || !saturnPlacement || sunPlacement.house !== 2) return false;
    
    // Saturn aspects 3rd, 7th, 10th from its position
    const saturnHouse = saturnPlacement.house;
    const aspect3rd = ((saturnHouse + 2) % 12) || 12;
    const aspect7th = ((saturnHouse + 6) % 12) || 12;
    const aspect10th = ((saturnHouse + 9) % 12) || 12;
    
    return aspect3rd === 2 || aspect7th === 2 || aspect10th === 2;
  },
  score: () => ({ value: -0.65, notes: ['Sun in 2nd + Saturn aspect → money problems'] })
};

// 9th lord in kendra - fortune from childhood
const lord9thInKendraFortuneFromChildhood: Rule = {
  key: 'lord_9th_kendra_fortune_childhood_natal',
  areas: ['Wealth', 'Father', 'Spirituality'],
  templateKey: 'early_fortune',
  priority: 10,
  ruleType: 'natal',
  customPrediction: '9TH LORD IN KENDRA: Lord of 9th house in any kendra (1st, 4th, 7th, 10th) makes person fortunate from CHILDHOOD. ACTIONABLE: Early life blessings, father brings fortune, education highly favored, spiritual inclinations from young age, dharmic path leads to success, divine grace throughout life. Natural luck in important matters. REMEDY: Respect father and elders, Jupiter worship, maintain dharma, gratitude prayers.',
  triggers: (dasha) => {
    // Get ascendant sign to determine 9th house sign
    const ascSign = (dasha as any).ascendantSign || 1; // 1-12
    const ninthHouseSign = ((ascSign + 8 - 1) % 12) + 1; // 9th sign from ascendant
    
    // Determine 9th lord based on sign
    const SIGN_LORDS: Record<number, string> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    const lord9th = SIGN_LORDS[ninthHouseSign];
    if (!lord9th) return false;
    
    // @ts-ignore - Dynamic planet name lookup
    const lordPlacement = dasha.natalPlacements?.[lord9th as any];
    if (!lordPlacement) return false;
    
    // Check if in kendra (1, 4, 7, 10)
    return [1, 4, 7, 10].includes(lordPlacement.house);
  },
  score: () => ({ value: 0.90, notes: ['9th lord in kendra → fortunate from childhood'] })
};

// Lord of 5th and 9th together in 7th - Raj Yoga
const lord5th9thIn7thRajYoga: Rule = {
  key: 'lord_5th_9th_in_7th_rajyoga_natal',
  areas: ['Wealth', 'Spouse', 'Career'],
  templateKey: 'rajyoga',
  priority: 10,
  ruleType: 'natal',
  customPrediction: 'RAJ YOGA: Lord of 5th and 9th house sitting together in 7th house creates powerful Raj Yoga. ACTIONABLE: Royal combination for wealth and status, spouse brings fortune, partnerships highly auspicious, business success through collaborations, dharmic wealth through spouse/partners, exceptional prosperity. UTILIZE: Marriage/partnerships for success, spiritual spouse, collaborative ventures. REMEDY: Worship of both lords, maintain marital harmony, charitable acts.',
  triggers: (dasha) => {
    const ascSign = (dasha as any).ascendantSign || 1;
    const fifthHouseSign = ((ascSign + 4 - 1) % 12) + 1;
    const ninthHouseSign = ((ascSign + 8 - 1) % 12) + 1;
    
    const lord5th = getSignLord(fifthHouseSign);
    const lord9th = getSignLord(ninthHouseSign);
    
    if (!lord5th || !lord9th) return false;
    
    // @ts-ignore - Dynamic planet name lookup
    const lord5thPlacement = dasha.natalPlacements?.[lord5th as any];
    // @ts-ignore - Dynamic planet name lookup
    const lord9thPlacement = dasha.natalPlacements?.[lord9th as any];
    
    // Both must be in 7th house
    return !!lord5thPlacement && !!lord9thPlacement &&
           lord5thPlacement.house === 7 && lord9thPlacement.house === 7;
  },
  score: () => ({ value: 0.95, notes: ['5th lord + 9th lord in 7th → powerful Raj Yoga'] })
};

// Rahu-Ketu in kendra/trikona with kendra lord - Kendra-Trikona Yoga
const rahuKetuKendraTrikonaYoga: Rule = {
  key: 'rahu_ketu_kendra_trikona_yoga_natal',
  areas: ['Wealth', 'Career', 'Spirituality'],
  templateKey: 'rajyoga',
  priority: 9,
  ruleType: 'natal',
  customPrediction: 'KENDRA-TRIKONA YOGA: Rahu/Ketu in kendra (1,4,7,10) or trikona (1,5,9) houses WITH kendra lord creates powerful yoga. ACTIONABLE: Unexpected rise to power, unconventional success path, fame through unique methods, material and spiritual gains, sudden elevation in status. Foreign connections beneficial. UTILIZE: Embrace unconventional opportunities, spiritual-material balance. REMEDY: Rahu-Ketu remedies, Jupiter worship, dharmic path.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    const kendraHouses = [1, 4, 7, 10];
    const trikonaHouses = [1, 5, 9];
    const kendraTrikonaHouses = Array.from(new Set([...kendraHouses, ...trikonaHouses]));
    
    // Check if Rahu or Ketu is in kendra/trikona
    const rahuInKendraTrikona = rahuPlacement && kendraTrikonaHouses.includes(rahuPlacement.house);
    const ketuInKendraTrikona = ketuPlacement && kendraTrikonaHouses.includes(ketuPlacement.house);
    
    if (!rahuInKendraTrikona && !ketuInKendraTrikona) return false;
    
    // Check if any kendra lord is with Rahu or Ketu
    const ascSign = (dasha as any).ascendantSign || 1;
    const SIGN_LORDS: Record<number, string> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    // Get lords of 1st, 4th, 7th, 10th houses
    const kendraLords: string[] = [];
    [1, 4, 7, 10].forEach(kendraOffset => {
      const kendraSign = ((ascSign + kendraOffset - 1 - 1) % 12) + 1;
      const lord = SIGN_LORDS[kendraSign];
      if (lord && !kendraLords.includes(lord)) kendraLords.push(lord);
    });
    
    // Check if any kendra lord sits with Rahu or Ketu
    for (const lord of kendraLords) {
      // @ts-ignore - Dynamic planet name lookup
      const lordPlacement = dasha.natalPlacements?.[lord as any];
      if (!lordPlacement) continue;
      
      if (rahuPlacement && lordPlacement.house === rahuPlacement.house) return true;
      if (ketuPlacement && lordPlacement.house === ketuPlacement.house) return true;
    }
    
    return false;
  },
  score: () => ({ value: 0.85, notes: ['Rahu/Ketu in kendra/trikona with kendra lord → powerful yoga'] })
};

// Debilitated Saturn retrograde - very good results + blended percentage bonus
const debilitatedSaturnRetrogradeBonus: Rule = {
  key: 'debilitated_saturn_retrograde_bonus_natal',
  areas: ['Career', 'Wealth', 'Self'],
  templateKey: 'special_blessing',
  priority: 10,
  ruleType: 'natal',
  customPrediction: 'NEECHA BHANGA RAJ YOGA: Debilitated Saturn when RETROGRADE gives VERY GOOD results (cancels debilitation). ACTIONABLE: Saturn\'s challenges transform into blessings, delayed success becomes exceptional, hardships build extraordinary character, mature wisdom beyond years, ultimate triumph over obstacles. BIG BONUS TO STRENGTH. UTILIZE: Patience brings massive rewards, discipline creates empire, accept delays as divine timing. REMEDY: Shani worship intensifies blessings, blue sapphire amplifies power.',
  triggers: (dasha) => {
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    if (!saturnPlacement || saturnPlacement.sign === undefined) return false;
    
    // Saturn debilitated in Aries (sign 0)
    const isDebilitated = saturnPlacement.sign === 0;
    
    // Check if retrograde (we'd need retrograde data in placements)
    const isRetrograde = (saturnPlacement as any).isRetrograde || false;
    
    return isDebilitated && isRetrograde;
  },
  score: () => ({ 
    value: 0.95, 
    notes: ['Debilitated Saturn + Retrograde → Neecha Bhanga Raj Yoga', 'BIG BONUS: Add +15% to blended strength percentage'] 
  })
};

// Lagna lord in 2nd, 2nd lord in 11th, 11th lord in lagna - Raj Yoga
const lagnaLordIn2nd2ndLordIn11th11thLordInLagnaRajYoga: Rule = {
  key: 'lagna_2nd_11th_lord_exchange_rajyoga_natal',
  areas: ['Wealth', 'Self'],
  templateKey: 'rajyoga',
  priority: 10,
  ruleType: 'natal',
  customPrediction: 'DHANA RAJ YOGA: Lagna lord in 2nd, 2nd lord in 11th, 11th lord in lagna = highly auspicious Raj Yoga like a KING. ACTIONABLE: Royal wealth combination, self-made riches, income from multiple sources, financial brilliance, gains through speech/family/knowledge, exceptional earning capacity. Live like royalty through earned wealth. UTILIZE: Financial ventures, family business, communication-based income. REMEDY: Worship ascendant lord, gratitude for wealth, charitable giving.',
  triggers: (dasha) => {
    const ascSign = (dasha as any).ascendantSign || 1;
    
    const SIGN_LORDS: Record<number, string> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    const lagnaLord = SIGN_LORDS[ascSign];
    const secondHouseSign = ((ascSign + 1 - 1) % 12) + 1;
    const eleventhHouseSign = ((ascSign + 10 - 1) % 12) + 1;
    
    const lord2nd = SIGN_LORDS[secondHouseSign];
    const lord11th = SIGN_LORDS[eleventhHouseSign];
    
    if (!lagnaLord || !lord2nd || !lord11th) return false;
    
    // @ts-ignore - Dynamic planet name lookup
    const lagnaLordPlacement = dasha.natalPlacements?.[lagnaLord as any];
    // @ts-ignore - Dynamic planet name lookup
    const lord2ndPlacement = dasha.natalPlacements?.[lord2nd as any];
    // @ts-ignore - Dynamic planet name lookup
    const lord11thPlacement = dasha.natalPlacements?.[lord11th as any];
    
    // Check the exchange pattern
    return !!lagnaLordPlacement && !!lord2ndPlacement && !!lord11thPlacement &&
           lagnaLordPlacement.house === 2 &&
           lord2ndPlacement.house === 11 &&
           lord11thPlacement.house === 1;
  },
  score: () => ({ value: 0.98, notes: ['Lagna-2nd-11th lord exchange → Dhana Raj Yoga (king-like wealth)'] })
};

// 6th lord in 7th - job then business
const lord6thIn7thJobThenBusiness: Rule = {
  key: 'lord_6th_in_7th_job_then_business_natal',
  areas: ['Career'],
  templateKey: 'career_transition',
  priority: 7,
  ruleType: 'natal',
  customPrediction: '6TH LORD IN 7TH: Person first does JOB and then might do BUSINESS. ACTIONABLE: Career path transitions from employment to entrepreneurship, gain experience in service before going independent, partnerships after learning ropes, business success after job expertise. Natural progression: employee → entrepreneur. TIMING: Mid-career shift likely. REMEDY: Worship 6th & 7th lords, gradual transition, maintain good partnerships.',
  triggers: (dasha) => {
    const ascSign = (dasha as any).ascendantSign || 1;
    const sixthHouseSign = ((ascSign + 5 - 1) % 12) + 1;
    
    const SIGN_LORDS: Record<number, string> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    const lord6th = SIGN_LORDS[sixthHouseSign];
    if (!lord6th) return false;
    
    // @ts-ignore - Dynamic planet name lookup
    const lord6thPlacement = dasha.natalPlacements?.[lord6th as any];
    return !!lord6thPlacement && lord6thPlacement.house === 7;
  },
  score: () => ({ value: 0.55, notes: ['6th lord in 7th → job first, then business'] })
};

// 7th lord in 5th - childbirth or marriage problems
const lord7thIn5thChildbirthMaritalProblems: Rule = {
  key: 'lord_7th_in_5th_childbirth_marital_problems_natal',
  areas: ['Spouse', 'Children'],
  templateKey: 'marital_challenges',
  priority: 8,
  ruleType: 'natal',
  private: true,
  customPrediction: '7TH LORD IN 5TH: Creates problems in CHILD BIRTH or MARRIAGE RELATIONS. ACTIONABLE: Fertility challenges possible, marital stress around children, spouse-children relationship tensions, delayed conception, or marital discord affecting family planning. COUNSELING: Marriage counseling before children, fertility consultation if needed, patience in family matters. REMEDY: Worship Jupiter and Venus, Santana Gopala mantra, marital harmony rituals, 5th & 7th lord remedies.',
  triggers: (dasha) => {
    const ascSign = (dasha as any).ascendantSign || 1;
    const seventhHouseSign = ((ascSign + 6 - 1) % 12) + 1;
    
    const SIGN_LORDS: Record<number, string> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    const lord7th = SIGN_LORDS[seventhHouseSign];
    if (!lord7th) return false;
    
    // @ts-ignore - Dynamic planet name lookup
    const lord7thPlacement = dasha.natalPlacements?.[lord7th as any];
    return !!lord7thPlacement && lord7thPlacement.house === 5;
  },
  score: () => ({ value: -0.70, notes: ['7th lord in 5th → childbirth/marriage problems'] })
};

// 10th lord in 7th or exalted - supportive spouse
const lord10thIn7thOrExaltedSupportiveSpouse: Rule = {
  key: 'lord_10th_in_7th_exalted_supportive_spouse_natal',
  areas: ['Spouse', 'Career'],
  templateKey: 'spousal_support',
  priority: 9,
  ruleType: 'natal',
  customPrediction: '10TH LORD IN 7TH/EXALTED: Leads to VERY SUPPORTIVE SPOUSE. ACTIONABLE: Marriage brings career success, spouse actively helps profession, business partnerships with spouse beneficial, career elevation through marriage, spouse\'s support crucial for achievements. Power couple dynamics. UTILIZE: Involve spouse in career decisions, joint ventures, spouse as advisor. REMEDY: Respect spouse, Venus worship, gratitude for partner.',
  triggers: (dasha) => {
    const ascSign = (dasha as any).ascendantSign || 1;
    const tenthHouseSign = ((ascSign + 9 - 1) % 12) + 1;
    
    const SIGN_LORDS: Record<number, string> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    const lord10th = SIGN_LORDS[tenthHouseSign];
    if (!lord10th) return false;
    
    // @ts-ignore - Dynamic planet name lookup
    const lord10thPlacement = dasha.natalPlacements?.[lord10th as any];
    if (!lord10thPlacement) return false;
    
    // Check if in 7th house OR exalted
    const isIn7th = lord10thPlacement.house === 7;
    
    // Check exaltation (would need sign data)
    const EXALTATION_SIGNS: Record<string, number> = {
      'Sun': 0, 'Moon': 1, 'Mars': 9, 'Mercury': 5, 
      'Jupiter': 3, 'Venus': 11, 'Saturn': 6
    };
    const isExalted = lord10thPlacement.sign !== undefined && 
                      EXALTATION_SIGNS[lord10th] === lord10thPlacement.sign;
    
    return isIn7th || isExalted;
  },
  score: () => ({ value: 0.85, notes: ['10th lord in 7th/exalted → very supportive spouse'] })
};

// Sun + Mercury in lagna with Jupiter aspect - sharp mind
const sunMercuryLagnaJupiterAspectSharpMind: Rule = {
  key: 'sun_mercury_lagna_jupiter_aspect_sharp_mind_natal',
  areas: ['Self', 'Career'],
  templateKey: 'intellectual_excellence',
  priority: 9,
  ruleType: 'natal',
  customPrediction: 'BUDHA-ADITYA YOGA WITH JUPITER: Sun + Mercury in lagna with Jupiter\'s aspect makes person EXTREMELY SHARP MINDED and professionally successful. ACTIONABLE: Exceptional intelligence, communication brilliance, analytical genius, wisdom combined with intellect, professional excellence guaranteed, leadership through knowledge. Natural teacher/advisor. UTILIZE: Intellectual careers (law, education, consulting, research), public speaking, writing. REMEDY: Worship Sun, Mercury, Jupiter, Gayatri mantra, education in dharmic subjects.',
  triggers: (dasha) => {
    const sunPlacement = dasha.natalPlacements?.Sun;
    const mercuryPlacement = dasha.natalPlacements?.Mercury;
    const jupiterPlacement = dasha.natalPlacements?.Jupiter;
    
    // Sun and Mercury must be in 1st house
    if (!sunPlacement || !mercuryPlacement || 
        sunPlacement.house !== 1 || mercuryPlacement.house !== 1) return false;
    
    // Jupiter must aspect 1st house (Jupiter aspects 5th, 7th, 9th from its position)
    if (!jupiterPlacement) return false;
    
    const jupiterHouse = jupiterPlacement.house;
    const aspect5th = ((jupiterHouse + 4) % 12) || 12;
    const aspect7th = ((jupiterHouse + 6) % 12) || 12;
    const aspect9th = ((jupiterHouse + 8) % 12) || 12;
    
    return aspect5th === 1 || aspect7th === 1 || aspect9th === 1;
  },
  score: () => ({ value: 0.92, notes: ['Sun+Mercury in lagna + Jupiter aspect → extremely sharp mind, professional success'] })
};

// 5th lord in 6/8/12 with Mars - children conspiracy
const lord5thIn6th8th12thWithMarsChildrenConspiracy: Rule = {
  key: 'lord_5th_dusthana_mars_children_conspiracy_natal',
  areas: ['Children'],
  templateKey: 'children_challenges',
  priority: 8,
  ruleType: 'natal',
  private: true,
  customPrediction: '5TH LORD IN DUSTHANA WITH MARS: If 5th lord sits in 6th, 8th, or 12th house WITH Mars, can cause CONSPIRACY against native\'s children. ACTIONABLE: Children face enemies/dangers, protection needed, legal issues involving children possible, conflicts through children, hidden threats. VIGILANCE: Monitor children\'s associations, legal safeguards for children, avoid exposing children to risks. REMEDY: Hanuman Chalisa for protection, Mars-5th lord remedies, Santana Raksha mantras, protective talismans for children.',
  triggers: (dasha) => {
    const ascSign = (dasha as any).ascendantSign || 1;
    const fifthHouseSign = ((ascSign + 4 - 1) % 12) + 1;
    
    const SIGN_LORDS: Record<number, string> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    const lord5th = SIGN_LORDS[fifthHouseSign];
    if (!lord5th) return false;
    
    // @ts-ignore - Dynamic planet name lookup
    const lord5thPlacement = dasha.natalPlacements?.[lord5th as any];
    const marsPlacement = dasha.natalPlacements?.Mars;
    
    if (!lord5thPlacement || !marsPlacement) return false;
    
    // 5th lord must be in 6, 8, or 12
    const isDusthana = [6, 8, 12].includes(lord5thPlacement.house);
    
    // Mars must be in same house
    const marsWithLord5th = marsPlacement.house === lord5thPlacement.house;
    
    return isDusthana && marsWithLord5th;
  },
  score: () => ({ value: -0.75, notes: ['5th lord in dusthana + Mars → conspiracy against children'] })
};

// Mars + Rahu in 5th/11th - Mrit Santan Yoga (child loss)
const marsRahu5th11thMritSantanYoga: Rule = {
  key: 'mars_rahu_5th_11th_mrit_santan_yoga_natal',
  areas: ['Children'],
  templateKey: 'children_loss',
  priority: 10,
  ruleType: 'natal',
  private: true,
  customPrediction: 'MRIT SANTAN YOGA: Mars + Rahu together in 5th/11th OR Mars in 5th & Rahu in 11th (or vice versa) creates child loss yoga. SAME for Sun + Ketu. ACTIONABLE: High-risk pregnancy indication, child loss possible, fertility challenges, miscarriage risk, stillbirth concerns. IMMEDIATE: Consult fertility specialist early, high-risk pregnancy monitoring, spiritual remedies BEFORE conception. REMEDY: Santana Gopala mantra (daily), Jupiter worship, Mars-Rahu/Sun-Ketu peace rituals, adoption consideration, protective mantras during pregnancy, medical supervision mandatory.',
  triggers: (dasha) => {
    const marsPlacement = dasha.natalPlacements?.Mars;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const sunPlacement = dasha.natalPlacements?.Sun;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    // Mars + Rahu conditions
    if (marsPlacement && rahuPlacement) {
      // Together in 5th or 11th
      const togetherIn5th = marsPlacement.house === 5 && rahuPlacement.house === 5;
      const togetherIn11th = marsPlacement.house === 11 && rahuPlacement.house === 11;
      
      // Mars in 5th, Rahu in 11th (or vice versa)
      const marsRahuOpposite = (marsPlacement.house === 5 && rahuPlacement.house === 11) ||
                               (marsPlacement.house === 11 && rahuPlacement.house === 5);
      
      if (togetherIn5th || togetherIn11th || marsRahuOpposite) return true;
    }
    
    // Sun + Ketu conditions
    if (sunPlacement && ketuPlacement) {
      // Together in 5th or 11th
      const togetherIn5th = sunPlacement.house === 5 && ketuPlacement.house === 5;
      const togetherIn11th = sunPlacement.house === 11 && ketuPlacement.house === 11;
      
      // Sun in 5th, Ketu in 11th (or vice versa)
      const sunKetuOpposite = (sunPlacement.house === 5 && ketuPlacement.house === 11) ||
                              (sunPlacement.house === 11 && ketuPlacement.house === 5);
      
      if (togetherIn5th || togetherIn11th || sunKetuOpposite) return true;
    }
    
    return false;
  },
  score: () => ({ value: -0.95, notes: ['Mrit Santan Yoga → child loss indication, high-risk pregnancy'] })
};

// Malefic aspect on 7th or 7th lord afflicted - marital disturbance
const maleficAspect7thOrLord7thAfflictedMaritalDisturbance: Rule = {
  key: 'malefic_aspect_7th_lord_afflicted_marital_disturbance_natal',
  areas: ['Spouse'],
  templateKey: 'marital_challenges',
  priority: 8,
  ruleType: 'natal',
  private: true,
  customPrediction: '7TH HOUSE AFFLICTIONS: 7th house aspected by malefics (Mars, Saturn, Rahu, Ketu) OR 7th lord retrograde/debilitated/combust indicates DISTURBANCE or DISSATISFACTION in marriage. ACTIONABLE: Marital discord likely, spouse relationship challenges, communication issues, compatibility problems, external pressures on marriage. COUNSELING: Pre-marital counseling essential, regular marriage therapy, patience and compromise, realistic expectations. REMEDY: Venus worship, marriage mantras, 7th lord remedies, avoid Mars-Saturn-Rahu periods for marriage, match-making essential.',
  triggers: (dasha) => {
    const marsPlacement = dasha.natalPlacements?.Mars;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    // Check malefic aspects on 7th house
    let maleficAspects7th = false;
    
    const checkAspect = (planetHouse: number) => {
      // Mars aspects 4th, 7th, 8th from its position
      if (marsPlacement && marsPlacement.house === planetHouse) {
        const asp4 = ((planetHouse + 3) % 12) || 12;
        const asp7 = ((planetHouse + 6) % 12) || 12;
        const asp8 = ((planetHouse + 7) % 12) || 12;
        if (asp4 === 7 || asp7 === 7 || asp8 === 7) return true;
      }
      
      // Saturn aspects 3rd, 7th, 10th from its position
      if (saturnPlacement && saturnPlacement.house === planetHouse) {
        const asp3 = ((planetHouse + 2) % 12) || 12;
        const asp7 = ((planetHouse + 6) % 12) || 12;
        const asp10 = ((planetHouse + 9) % 12) || 12;
        if (asp3 === 7 || asp7 === 7 || asp10 === 7) return true;
      }
      
      // Rahu/Ketu aspect 7th from their position (opposite)
      if (rahuPlacement && ((rahuPlacement.house + 6) % 12 || 12) === 7) return true;
      if (ketuPlacement && ((ketuPlacement.house + 6) % 12 || 12) === 7) return true;
      
      return false;
    };
    
    // Check all planets for aspects
    for (let h = 1; h <= 12; h++) {
      if (checkAspect(h)) {
        maleficAspects7th = true;
        break;
      }
    }
    
    // Check 7th lord afflictions
    const ascSign = (dasha as any).ascendantSign || 1;
    const seventhHouseSign = ((ascSign + 6 - 1) % 12) + 1;
    
    const SIGN_LORDS: Record<number, string> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    const lord7th = SIGN_LORDS[seventhHouseSign];
    // @ts-ignore - Dynamic planet name lookup
    const lord7thPlacement = lord7th ? dasha.natalPlacements?.[lord7th as any] : null;
    
    if (lord7thPlacement) {
      // Check retrograde
      const isRetrograde = (lord7thPlacement as any).isRetrograde || false;
      
      // Check debilitation
      const DEBILITATION_SIGNS: Record<string, number> = {
        'Sun': 6, 'Moon': 7, 'Mars': 3, 'Mercury': 11,
        'Jupiter': 9, 'Venus': 5, 'Saturn': 0
      };
      const isDebilitated = lord7thPlacement.sign !== undefined &&
                           DEBILITATION_SIGNS[lord7th] === lord7thPlacement.sign;
      
      // Check combustion (would need combustion map)
      const isCombust = false; // placeholder
      
      if (isRetrograde || isDebilitated || isCombust) return true;
    }
    
    return maleficAspects7th;
  },
  score: () => ({ value: -0.75, notes: ['7th house/lord afflicted → marital disturbance/dissatisfaction'] })
};

// 7th lord in 6/8/12 - marital stress/separation
const lord7thIn6th8th12thMaritalStress: Rule = {
  key: 'lord_7th_in_dusthana_marital_stress_natal',
  areas: ['Spouse'],
  templateKey: 'marital_separation',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: '7TH LORD IN DUSTHANA: 7th lord in 6th, 8th, or 12th house signifies MARITAL STRESS or SEPARATION. ACTIONABLE: Serious marital challenges, spouse health issues, separation periods likely, distance in marriage, spouse obstacles, divorce possibility. TIMING: Especially in 7th lord dashas. COUNSELING: Marriage therapy mandatory, legal consultation for agreements, separation readiness, financial independence. REMEDY: Strong Venus remedies, 7th lord worship, marriage-saving mantras, patience and forgiveness practices.',
  triggers: (dasha) => {
    const ascSign = (dasha as any).ascendantSign || 1;
    const seventhHouseSign = ((ascSign + 6 - 1) % 12) + 1;
    
    const SIGN_LORDS: Record<number, string> = {
      1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
      7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter'
    };
    
    const lord7th = SIGN_LORDS[seventhHouseSign];
    if (!lord7th) return false;
    
    // @ts-ignore - Dynamic planet name lookup
    const lord7thPlacement = dasha.natalPlacements?.[lord7th as any];
    if (!lord7thPlacement) return false;
    
    return [6, 8, 12].includes(lord7thPlacement.house);
  },
  score: () => ({ value: -0.85, notes: ['7th lord in 6/8/12 → marital stress/separation'] })
};

// Multiple planets in 7th house - multiple relationships
const multiplePlanets7thMultipleRelationships: Rule = {
  key: 'multiple_planets_7th_multiple_relationships_natal',
  areas: ['Spouse'],
  templateKey: 'multiple_marriages',
  priority: 8,
  ruleType: 'natal',
  private: true,
  customPrediction: 'MULTIPLE PLANETS IN 7TH: Two or more planets in 7th house, especially Mars, Rahu, or Saturn, indicate MORE THAN ONE significant relationship or marriage. ACTIONABLE: Multiple marriage indication, serial relationships, remarriage after divorce/separation, significant pre-marital relationships. Especially strong if malefics present. AWARENESS: First marriage challenges likely, second marriage possibility, relationship patterns. REMEDY: Delay marriage for maturity, thorough compatibility check, Venus-7th lord worship, realistic relationship expectations.',
  triggers: (dasha) => {
    const placements = dasha.natalPlacements;
    if (!placements) return false;
    
    // Count planets in 7th house
    let planetsIn7th: string[] = [];
    Object.entries(placements).forEach(([planet, data]) => {
      if (data.house === 7) planetsIn7th.push(planet);
    });
    
    // Need 2+ planets
    if (planetsIn7th.length < 2) return false;
    
    // Extra strong if Mars, Rahu, or Saturn present
    const hasMalefics = planetsIn7th.some(p => ['Mars', 'Rahu', 'Saturn'].includes(p));
    
    return hasMalefics || planetsIn7th.length >= 2;
  },
  score: () => ({ value: -0.60, notes: ['Multiple planets in 7th → multiple relationships/marriages'] })
};

// 7th lord D1 in duality sign in D9 - second marriage
const lord7thD1DualitySignD9SecondMarriage: Rule = {
  key: 'lord_7th_d1_duality_sign_d9_second_marriage_natal',
  areas: ['Spouse'],
  templateKey: 'multiple_marriages',
  priority: 7,
  ruleType: 'natal',
  private: true,
  customPrediction: '7TH LORD IN DUALITY SIGN (D9): If 7th lord of D1 is in duality sign (Gemini, Virgo, Pisces, Sagittarius) in D9 chart, indicates MORE THAN ONE MARRIAGE. ACTIONABLE: Second marriage strongly indicated, dual relationship nature, remarriage after first marriage issues, multiple significant partnerships. D9 shows true nature of marriage. TIMING: First marriage in youth may not sustain, second marriage in maturity. REMEDY: Delay first marriage, choose mature partner, realistic expectations, Venus worship.',
  triggers: (dasha) => {
    // This requires D9 data which we may not have
    // Placeholder - would need to check 7th lord's sign in D9
    return false; // Implement when D9 data available
  },
  score: () => ({ value: -0.65, notes: ['7th lord in duality sign (D9) → second marriage indication'] })
};

// 7th lord D9 afflicted - marriage trouble/remarriage
const lord7thD9AfflictedMarriageTrouble: Rule = {
  key: 'lord_7th_d9_afflicted_marriage_trouble_natal',
  areas: ['Spouse'],
  templateKey: 'marital_challenges',
  priority: 8,
  ruleType: 'natal',
  private: true,
  customPrediction: '7TH LORD AFFLICTED IN D9: 7th lord in D9 afflicted, or Venus/Jupiter debilitated/with malefics in D9 indicates TROUBLE IN MARRIAGE or REMARRIAGE. ACTIONABLE: Deep marital incompatibility, marriage breakdown likely, remarriage after challenges, spouse character issues, values mismatch. D9 shows marriage destiny. PROTECTION: Extremely thorough compatibility analysis, pre-marital counseling, realistic expectations, spiritual maturity before marriage. REMEDY: D9 7th lord remedies, Venus-Jupiter worship, marriage-saving rituals.',
  triggers: (dasha) => {
    // Requires D9 data
    return false; // Implement when D9 data available
  },
  score: () => ({ value: -0.80, notes: ['7th lord/Venus/Jupiter afflicted in D9 → marriage trouble/remarriage'] })
};

// Venus with Rahu/Ketu - multiple marriages
const venusRahuKetuMultipleMarriages: Rule = {
  key: 'venus_rahu_ketu_multiple_marriages_natal',
  areas: ['Spouse'],
  templateKey: 'multiple_marriages',
  priority: 8,
  ruleType: 'natal',
  private: true,
  customPrediction: 'VENUS WITH RAHU/KETU: Venus with Rahu or Ketu, ESPECIALLY if Venus also retrograde or debilitated, indicates multiple marriages. ACTIONABLE: Unconventional relationships, foreign/unusual spouse, multiple marriages likely, relationship instability, intense desires affecting marriage. STRONGEST if Venus both afflicted AND with nodes. AWARENESS: First relationship may not sustain, non-traditional marriage patterns, learn from relationship failures. REMEDY: Venus strengthening remedies, Rahu/Ketu peace rituals, delay marriage, spiritual approach to relationships.',
  triggers: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const ketuPlacement = dasha.natalPlacements?.Ketu;
    
    if (!venusPlacement) return false;
    
    // Check if Venus with Rahu or Ketu
    const withRahu = rahuPlacement && venusPlacement.house === rahuPlacement.house;
    const withKetu = ketuPlacement && venusPlacement.house === ketuPlacement.house;
    
    if (!withRahu && !withKetu) return false;
    
    // Check if Venus is also retrograde or debilitated (extra strong)
    const isRetrograde = (venusPlacement as any).isRetrograde || false;
    const isDebilitated = venusPlacement.sign === 5; // Venus debilitated in Virgo (sign 5)
    
    // Trigger if with nodes, stronger if also afflicted
    return (withRahu || withKetu) && (isRetrograde || isDebilitated || true);
  },
  score: (dasha) => {
    const venusPlacement = dasha.natalPlacements?.Venus;
    const isRetrograde = venusPlacement ? (venusPlacement as any).isRetrograde || false : false;
    const isDebilitated = venusPlacement?.sign === 5;
    const extraAfflicted = isRetrograde || isDebilitated;
    
    return { 
      value: extraAfflicted ? -0.85 : -0.70, 
      notes: ['Venus with Rahu/Ketu → multiple marriages' + (extraAfflicted ? ' (Venus also afflicted - very strong)' : '')] 
    };
  }
};

// Rahu in 7th with Venus afflicted - multiple marriages
const rahu7thVenusAfflictedMultipleMarriages: Rule = {
  key: 'rahu_7th_venus_afflicted_multiple_marriages_natal',
  areas: ['Spouse'],
  templateKey: 'multiple_marriages',
  priority: 8,
  ruleType: 'natal',
  private: true,
  customPrediction: 'RAHU IN 7TH + VENUS AFFLICTED: Rahu in 7th house, especially if Venus is also afflicted, indicates MULTIPLE MARRIAGES. ACTIONABLE: Strong remarriage indication, unconventional marriage circumstances, foreign spouse possible, marriage instability, intense relationship desires leading to multiple partnerships. TIMING: Rahu dashas critical for marriage events. AWARENESS: First marriage may be learning experience, unusual marriage circumstances, expect the unexpected. REMEDY: Rahu remedies essential, Venus strengthening, realistic marriage expectations, spiritual maturity before marriage.',
  triggers: (dasha) => {
    const rahuPlacement = dasha.natalPlacements?.Rahu;
    const venusPlacement = dasha.natalPlacements?.Venus;
    
    if (!rahuPlacement || rahuPlacement.house !== 7) return false;
    
    // Check if Venus is afflicted
    if (!venusPlacement) return false;
    
    const venusRetrograde = (venusPlacement as any).isRetrograde || false;
    const venusDebilitated = venusPlacement.sign === 5; // Virgo
    const venusCombust = false; // Would need combustion data
    
    return venusRetrograde || venusDebilitated || venusCombust || true; // Rahu in 7th alone is significant
  },
  score: () => ({ value: -0.75, notes: ['Rahu in 7th + Venus afflicted → multiple marriages'] })
};

// Mars + Saturn + Venus connection - disharmony/divorce
const marsSaturnVenusDisharmonyDivorce: Rule = {
  key: 'mars_saturn_venus_disharmony_divorce_natal',
  areas: ['Spouse'],
  templateKey: 'divorce_indication',
  priority: 9,
  ruleType: 'natal',
  private: true,
  customPrediction: 'MARS-SATURN-VENUS CONNECTION: Mars + Saturn + Venus connection (conjunction, aspect, or exchange) indicates DISHARMONY or DIVORCE. ACTIONABLE: Severe marital discord, passion vs duty conflict, violence in relationship possible, separation/divorce strong indication, cold and heated relationship dynamics. CRITICAL: Relationship counseling essential, anger management, realistic expectations, avoid impulsive marriage decisions. REMEDY: Mars-Saturn peace rituals, Venus strengthening, Hanuman worship for Mars control, Shani remedies, marriage-saving mantras, professional mediation.',
  triggers: (dasha) => {
    const marsPlacement = dasha.natalPlacements?.Mars;
    const saturnPlacement = dasha.natalPlacements?.Saturn;
    const venusPlacement = dasha.natalPlacements?.Venus;
    
    if (!marsPlacement || !saturnPlacement || !venusPlacement) return false;
    
    // Check conjunction (all three in same house)
    const allInSameHouse = marsPlacement.house === saturnPlacement.house && 
                           marsPlacement.house === venusPlacement.house;
    if (allInSameHouse) return true;
    
    // Check if any two are together and aspect the third
    // Simplified: check if Mars and Saturn together, or Mars and Venus together, or Saturn and Venus together
    const marsSaturn = marsPlacement.house === saturnPlacement.house;
    const marsVenus = marsPlacement.house === venusPlacement.house;
    const saturnVenus = saturnPlacement.house === venusPlacement.house;
    
    return marsSaturn || marsVenus || saturnVenus;
  },
  score: () => ({ value: -0.90, notes: ['Mars+Saturn+Venus connection → disharmony/divorce indication'] })
};

// Add all new natal prediction rules to the RULES array
export const NATAL_PREDICTION_RULES: Rule[] = [
  // Sun in 1st combinations
  sunMalefic1stVenus7th,
  sunMalefic1stMars5th,
  sunMalefic1stSaturn8th,
  sunMalefic1stEmpty7thMarriageTiming,
  // Moon in 1st combinations
  moon1stVenus7thInLawHarmony,
  moon1stVenus7thSaturnSun6thWealth,
  // Mars in 1st combinations
  mars1stSaturnConjunctionHealth,
  // Mercury in 1st combinations
  mercury1stSunAspectRichSpouse,
  mercury1stSaturnMoonMentalHealth,
  mercury1stRahuConfusion,
  mercury1stAloneFortune34,
  // General Rahu-Ketu
  rahuKetuInLawsOffspring,
  // Venus in 1st combinations
  venus1stRahu3rdSpouseMentalHealth,
  venus1stMoon8thMotherInLawTension,
  // Saturn in 1st combinations
  saturn1stPlanets7thGoodResults,
  saturn1stVacantHousesBenefic,
  saturn1stMars2ndFinancialStruggle,
  // Rahu in 1st combinations
  rahu1stSunGlobalDebility,
  rahu1stSun4thCashFlow,
  rahu1stSun12thBedroomFights,
  // Ketu in 1st combinations
  ketu1stMaleficSaturnFatherTeacher,
  ketu1stEmpty2nd7thMercuryVenusBad,
  // Sun in 2nd combinations
  sun2ndMoon6thAuspicious,
  sun2ndRahu9thArtistRecognition,
  sun2ndKetu7thHonesty,
  sun2ndMars9thStylish,
  sun2ndMoon12thMars1stDifficulties,
  sun2ndMars8thGreedConflicts,
  // Moon in 2nd combinations
  moon2ndKetu12thEducationOffspring,
  // Jupiter in 2nd combinations
  jupiter2ndKetu6thDeathFear,
  // Mars in 2nd combinations
  mars2ndMercuryWeakWillpower,
  // Mercury in 2nd combinations
  mercury2ndEmpty8thEarnNoSave,
  // Venus in 2nd combinations
  venus2ndSaturnDisciplinedSavings,
  // Saturn in 2nd combinations
  saturn2ndDepends8thPlanets,
  // Rahu in 2nd combinations
  rahu2ndJupiterStrongProsperity,
  // Ketu in 2nd combinations
  ketu2ndJupiterStrongCrores,
  ketu2ndVenusWellPlaced,
  ketu2ndMoonBadResults,
  ketu2ndMoonMars8thShorterLifespan,
  ketu2ndEmpty8thMaleficIntensify,
  // Sun in 3rd combinations
  sun3rdSaturnRahuVenus1stMalefic,
  sun3rdConjunctMalefic,
  sun3rdMaleficMoonRobbery,
  sun3rd9thAfflictedImpoverishedAncestors,
  sun3rd1stAfflictedNeighborDownfall,
  sun3rdMaternalUncleNoMaleProgeny,
  // Moon in 3rd combinations
  moon3rdEmpty9th11thMarsVenusGood,
  moon3rdBeneficKetuEducation,
  moon3rdMaleficWealthLoss,
  // Mercury in 3rd combinations
  mercury3rdEmpty9th11thWealth,
  mercury3rdPlanets9th11thAdverse,
  mercury3rdKetuOverthinking,
  mercury3rdSaturnHardworkingObstacles,
  mercury3rdRahuClever,
  mercury3rdKetu11thWalkingChildrenIssues,
  // Venus in 3rd combinations
  venus3rdPlanets9th11thManyDaughters,
  // Saturn in 3rd combinations
  saturn3rdEmpty9th12thWealth,
  // Rahu in 3rd combinations
  rahu3rdSunMercurySisterWidow,
  // Ketu in 3rd combinations
  ketu3rdMars12thSonBefore24,
  // Sun in 4th combinations
  sun4thMoonResearchProfits,
  sun4thMercury4th10thTrader,
  sun4thJupiterGoldSilverBusiness,
  sun4thJupiter10thDailyLuck,
  sun4thSaturn7thNightBlindness,
  sun4thMaleficMars10thEyeDamage,
  sun4thComplexImpotency,
  // Moon in 4th combinations
  moon4thJupiter6thAncestralProfession,
  moon4thRahuJupiter10thHeadInjuries,
  moon4thFourPlanetsWealth,
  moon4thJupiter10thNotGood,
  // Jupiter in 4th combinations
  jupiter4thSaturn2ndClevernessWealth,
  jupiter4thMercury10thSociallyAdaptive,
  // Mercury in 4th combinations
  mercury4thMarsElderSiblingsHealth,
  // Venus in 4th combinations
  venus4thJupiter10thMidasTouch,
  venus4thJupiter1stMILDiscord,
  // Rahu in 4th combinations
  rahu4thMoonExaltedWealthy,
  // Ketu in 4th combinations
  ketu4thMoon3rd4thAdvisorySkills,
  // Sun in 5th combinations
  sun5thMarsRahuKetuSaturnLuxuryLifestyle,
  sun5thJupiter9th12thResilience,
  sun5thAfflictedJupiter10thSpouseLoss,
  sun5thSaturn3rdDeathOfSons,
  // Moon in 5th combinations
  moon5thBeneficKetuFiveSons,
  // Jupiter in 5th combinations
  jupiter5thMercuryVenusRahuHarmsChildren,
  jupiter5thSunKetuMotherFamily,
  // Mercury in 5th combinations
  mercury5thJupiterAspectInsecurities,
  // Venus in 5th combinations
  venus5thRahuExtramaritalPublic,
  venus5thMarsMiscarriages,
  // Rahu in 5th combinations
  rahu5thSonBirthSpouseHealth,
  // Ketu in 5th combinations
  ketu5thJupiterSunMoonExcellentFinances,
  ketu5thBeneficAfter24,
  // Sun in 6th combinations
  sun6thJupiterMarsMoon2ndBoostsFortune,
  sun6thKetu1st7thMaleChild,
  sun6thEmpty2ndGovtJob22,
  sun6thMercury12thHealthBP,
  sun6thMars10thTragicLossSons,
  // Moon in 6th combinations
  moon6thMaleficMercury2nd12thSuicidal,
  moon6thMaleficSun12thEyeDefects,
  moon6thRahuKetu4thMaternalAffection,
  // Jupiter in 6th combinations
  jupiter6thMercury2nd12thFatherDeath,
  // Mars in 6th combinations
  mars6thSunSaturnMarsStrain,
  // Mercury in 6th combinations
  mercury6thMars4th8thMotherIssues,
  mercury6thMarsConjunctionAccidents,
  mercury6thMaleficsSaturnNegativity,
  // Rahu in 6th combinations
  rahu6thMercuryMars12thHealthIssues,
  // Ketu in 6th combinations
  ketu6thTwoExaltedStronglyFavorable,
  ketu6thMoon6thMotherUncleHealth,
  ketu6thMoon2ndMotherDifficulties,
  // Sun in 7th combinations
  sun7thMoonMarsJupiter2ndGovtPosition,
  sun7thMercury5th7thContinuousIncome,
  sun7thJupiterVenusMalefic12thFamilyLosses,
  sun7thAfflictedMarsSaturnMoonWorsenedHealth,
  // Moon in 7th combinations
  moon7thMotherLegProblemsMaritalStress,
  // Mars in 7th combinations
  mars7thMercuryConjunctionSistersProblems,
  // Mercury in 7th combinations
  mercury7thSaturn3rdSpouseFamilyRich,
  mercury7thMoon1stOverseasBeneficial,
  // Venus in 7th combinations
  venus7thInimicalPlanet1stMaritalDiscord,
  venus7thMercuryMaleficAddictions,
  // Saturn in 7th combinations
  saturn7thJupiter1stGovtBenefits,
  saturn7thEmpty1stBenefic,
  // Rahu in 7th combinations
  rahu7thJupiterAspectReducesDissatisfaction,
  rahu7thMercuryVenusKetu11thFemaleDestruction,
  rahu7thFemaleMarriageBefore22DivorceRisk,
  // Ketu in 7th combinations
  ketu7thJupiterAspectMitigatesDissatisfaction,
  ketu7thMercuryVenusJupiterWellPlacedNoDisappointments,
  ketu7thMercuryConjunctionEnemiesVanish34,
  ketu7thMultiplePlanets1stChildrenDestroyed,
  // Sun in 8th combinations
  sun8thMercury2ndEconomicCrisis,
  // Moon in 8th combinations
  moon8thJupiterSaturn2ndCancelsEvil,
  // Jupiter in 8th combinations
  jupiter8thMars4thHeartProblems,
  // Mercury in 8th combinations
  mercury8thMalefic11thIncomeDip32,
  mercury8thSaturnMarsShortensLifespan,
  mercury8thSunMarsLagnaProtection,
  mercury8thRahuImprisonmentInstability,
  mercury8thMarsAdverseOutcomes,
  // Venus in 8th combinations
  venus8thEmpty2ndMarryAfter25,
  // Saturn in 8th combinations
  saturn8thMercuryRahuKetuMaleficHardships,
  saturn8thVehicleBacklightOmen,
  // Rahu in 8th combinations
  rahu8thMarsBenefic1st5th8thRich,
  // Ketu in 8th combinations
  ketu8thJupiterMarsAbsent6thNonMalefic,
  ketu8thMoonNot2ndNegativeReduced,
  ketu8thSaturnMars7thBadLuck,
  // Sun in 9th combinations
  sun9thMercury5thFortuneAfter34,
  // Jupiter in 9th combinations
  jupiter9thMars3rdInLawsFinancialHelp,
  jupiter9thSaturn5thWealthAfter36,
  // Mercury in 9th combinations
  mercury9thVenusInfluenceSingingCareer,
  // Venus in 9th combinations
  venus9thMercuryMaleficAddictionsPost17,
  venus9thMarsLosesPotency,
  // Saturn in 9th combinations
  saturn9thMalefic3rdConstructionMisfortunes,
  saturn9thMaleConstructsWifePregnantProblems,
  // Rahu in 9th combinations
  rahu9thEmpty1stHealthInsultsElders,
  // Ketu in 9th combinations
  ketu9th2ndAuspiciousExcellent,
  ketu9thMoonWellPlacedMotherFamilySupport,
  ketu9thMoonMars3rdMaleChildProblems,
  // Sun in 10th combinations
  sun10thSaturn4thFatherLossChildhood,
  sun10thMoon5thShortLifespan,
  sun10thEmpty4thNoGovtBenefits,
  sun10thRahu5thInLawIssues,
  sun10thKetu5thMaleProgeny,
  sun10thMoon5thMoneyMotherTroubles,
  sun10thMars5thBrothersFaceIssues,
  sun10thSaturn5thConstructionPaternalUncleIssues,
  // Moon in 10th combinations
  moon10thSaturnConjunctionDiseaseFinancialLoss,
  moon10thEmpty2nd4thWealthGain,
  moon10thSaturn1stDestructionOppositeSex,
  // Jupiter in 10th combinations
  jupiter10thMoon4thGenerosityHardship,
  jupiter10thSaturn4thButcherPragmatism,
  jupiter10thVenusMars4thMultipleMarriages,
  // Mars in 10th combinations
  mars10thSaturn3rdHugePropertyGains,
  mars10thEmpty5thOverallProsperity,
  mars10thMalefics2ndReducedBenefits,
  mars10thFriendlyPlanet3rdReductionProsperity,
  // Saturn in 10th combinations
  saturn10thJupiterMoon4thFinancialStability,
  saturn10thConstructionTimingLoss,
  // Ketu in 10th combinations
  ketu10thSaturn6thRenownedSportsperson,
  ketu10thSaturn4thSonsDifficulties,
  // Sun in 11th combinations
  sun11thMoon5thUnaspectedShortLife,
  // Moon in 11th combinations
  moon11thKetu4thLifeEndangeredMercuryBusinessHarmful,
  moon11thMercury3rdBrotherConflictsFinancial,
  // Mars in 11th combinations
  mars11thJupiterExaltedBenefic,
  // Venus in 11th combinations
  venus11thMercuryRemediesCrossDependency,
  // Saturn in 11th combinations
  saturn11thMercury3rdFinancialStruggles,
  saturn11thRahuKetuModifyOutcomes,
  // Rahu in 11th combinations
  rahu11thVarshphal1stFatherGrandfatherDeath,
  rahu11thMercury3rdIsolationLoneliness,
  // Ketu in 11th combinations
  ketu11thSaturn2ndEnormousWealth,
  ketu11thMercury2ndRajYoga,
  ketu11thJupiter3rdEnhancedStabilityWisdom,
  ketu11thWeakSaturnMotherGrandmotherProblems,
  ketu11thMoon3rdMotherPain,
  ketu11thMars3rdMotherSurgeries,
  // Sun in 12th combinations
  sun12thKetu2ndWealthAfter24Family,
  sun12thMercuryVenusTogetherBusinessSuccess,
  sun12thEvilPlanet1stSleeplessness,
  // Moon in 12th combinations
  moon12thKetu4thWeakKetuMotherChildHarm,
  moon12thMarsBusinessGoodMercuryKetuBad,
  // Mercury in 12th combinations
  mercury12thEarlyMarriageHarmFatherSpouse,
  mercury12thAffects6thHouseDifficulties,
  // Venus in 12th combinations
  venus12thMercury2nd6thCreativeSuccess,
  // Ketu in 12th combinations
  ketu12thRahu6thMercuryExcellent,
  ketu12th2ndHasMoonVenusMarsNegative,
  // General additional natal rules
  saturn7thLordIn8thMarriagePain,
  blindHoroscopeEnimicalPlanets10th,
  marsKetuFemaleMarriageInnocentSpouse,
  venusKetuMaleMarriageInnocentSpouse,
  venus12thGoodResults,
  sun11thMoneyEnemiesVictory,
  ketu8thSuddenWealthStomachProblems,
  saturnTaurusAngerMarriageExpense,
  stableRashiLagna6thLordAspectWealth,
  moonAspectedByExaltedPlanetWealth,
  sunWithLagnaLordBeneficial,
  lagnaLordExaltedPopularWealthy,
  lord6thIn8thGainfulNoTrust,
  jupiterVenus8thLegalProblems,
  twothreePlanets10thPopular,
  sunMoon9thVeryWealthy,
  // Saturn aspects and placements
  saturnAspect4thHouseVehicleLand,
  sunIn2ndSaturnAspectMoneyProblems,
  // Lordship yogas
  lord9thInKendraFortuneFromChildhood,
  lord5th9thIn7thRajYoga,
  // Kendra-Trikona yogas
  rahuKetuKendraTrikonaYoga,
  // Retrograde benefits
  debilitatedSaturnRetrogradeBonus,
  // Dhana yogas
  lagnaLordIn2nd2ndLordIn11th11thLordInLagnaRajYoga,
  // Career and business
  lord6thIn7thJobThenBusiness,
  // Children and marriage
  lord7thIn5thChildbirthMaritalProblems,
  lord10thIn7thOrExaltedSupportiveSpouse,
  sunMercuryLagnaJupiterAspectSharpMind,
  lord5thIn6th8th12thWithMarsChildrenConspiracy,
  marsRahu5th11thMritSantanYoga,
  // Marriage disturbances
  maleficAspect7thOrLord7thAfflictedMaritalDisturbance,
  lord7thIn6th8th12thMaritalStress,
  multiplePlanets7thMultipleRelationships,
  lord7thD1DualitySignD9SecondMarriage,
  lord7thD9AfflictedMarriageTrouble,
  venusRahuKetuMultipleMarriages,
  rahu7thVenusAfflictedMultipleMarriages,
  marsSaturnVenusDisharmonyDivorce,
  // Marriage Doshas
  mangalDosha,
  seventhLordDusthanaAffliction,  // Natal version - shows in Yogas tab
  seventhLordDusthanaAfflictionTiming,  // Timing version - shows in Current Period when dasha active
  rahuNinthIntercasteYoga,  // Natal version - permanent yoga in Yogas tab
  rahuNinthIntercasteYogaTiming,  // Timing version - activates during Rahu or 7th lord dasha
];

// Update RULES to include natal predictions
RULES.push(...NATAL_PREDICTION_RULES);

// END FILE
